// Main Viewer Controller

class CoandaViewer {
  constructor() {
    this.engine = null;
    this.dataLoader = null;
    this.filters = null;
    this.controls = null;
    this.websocket = null;
    this.annotationRenderer = null;

    this.currentData = null;
    this.currentTimestep = 0;
    this.totalTimesteps = 1;
    this.isPlaying = false;
    this.playbackInterval = null;
    this.playbackIntervalMs = 400;
    this.playbackTargetFps = 2; // user-configurable fps (0 = max speed)

    this.needsCameraFit = false;
    this.fieldControlsInitialized = false;

    this.colormap = {
      variable: 'velocity',
      preset: 'jet',
      min: 0,
      max: 1,
      autoRange: true
    };

    this.pipelineItems = [];
    this.selectedPipelineId = 'data-root';

    // Mesh parts selection
    this.meshParts = {
      boundaries: {},
      visibleParts: new Set(),
      currentMesh: null,
      activeMeshObjectId: null,  // Track the active mesh object ID dynamically
      internalMeshPartName: null  // Track the internal mesh part name dynamically
    };

    // Display settings
    this.displaySettings = {
      representation: 'surface', // 'surface', 'wireframe', 'points', 'surface-with-edges'
      opacity: 1.0,
      lightLevel: 1.0,    // 0–2  (slider 0–200%)
      shadowLevel: 0.5    // 0–1  (slider 0–100%)
    };


    // Geometry cache for timestep preloading
    this.geometryCache = {
      cache: new Map(),      // Map<cacheKey, {geometry, scalars, scalarRange, boundaries, timestamp}>
      maxSize: 10,           // Maximum number of timesteps to cache
      preloadQueue: [],      // Queue of timesteps to preload
      isPreloading: false,   // Whether preloading is in progress
      preloadAborted: false, // Flag to abort preloading
      preloadPaused: false,  // Flag to pause preloading for priority requests
      preloadResumeTimeout: null,  // Timeout to resume preloading after priority request
      preloadDelay: 250      // Delay between preloads (can be increased when following sync)
    };

    // Batch update mechanism for mesh parts
    this.meshUpdateBatch = {
      pending: false,
      timeout: null
    };

    // Probe tool state
    this.probeEnabled = false;
    this.raycaster = null;
    this.mouse = new THREE.Vector2();

    // Annotation tool state
    this.annotationIdCounter = 0;
    this.annotationTextPosition = null; // For text annotation position
    this.annotationArrowStart = null; // For arrow start point
    this.annotationArrowEnd = null; // For arrow end point
    this.annotationAxesOrigin = null; // For coordinate axes origin
    this.annotationPickingActive = false; // Whether annotation point picking is active
    this.annotationPickMode = null; // 'text', 'arrow_start', 'arrow_end', or 'axes_origin'
    this.annotationTempMarkers = {}; // Store temp markers for each annotation point type

    // Streamline helpers and picking
    this.streamlineHelpers = {};
    this.streamlinePickState = null;
    this.pickPointer = new THREE.Vector2();
    this.overlayGroup = null;
    this.previousControlState = null;
    this.streamlinePreviews = new Map();
    this.pendingStreamlineMessage = null;
    this.streamlineRecomputeTimeout = null;

    // Recording helpers
    this.isRecording = false;
    this.mediaRecorder = null;
    this.recordedChunks = [];
    this.recordingMimeType = null;

    // Auto-rotation state
    this.autoRotation = {
      isActive: false,
      speed: 10,           // degrees per second
      duration: 10,        // seconds (ignored if continuous)
      continuous: false,   // if true, rotate indefinitely
      startTime: null,     // when rotation started
      animationId: null,   // requestAnimationFrame ID
      rotationAxis: null,  // THREE.Vector3 - axis parallel to screen
      meshCenter: null     // THREE.Vector3 - center of mesh
    };

    this.initialize();
  }

  initialize() {
    // Initialize rendering engine
    const viewport = document.getElementById('viewport');
    this.engine = new RenderingEngine(viewport);

    // Alias for backward compatibility with code that uses viewer.renderingEngine
    this.renderingEngine = this.engine;

    // Initialize other modules
    this.dataLoader = new DataLoader();
    this.openfoamLoader = new OpenFOAMLoader(this);
    this.filters = new Filters(this.engine);
    this.filters.viewer = this;

    // Initialize interactive slice widget
    if (typeof InteractiveSliceWidget !== 'undefined') {
      this.filters.sliceWidget = new InteractiveSliceWidget(this, this.filters);
      console.log('Interactive slice widget initialized');

      // Initialize streamline plane widget (shares same class, same colors)
      this.filters.streamlinePlaneWidget = new InteractiveSliceWidget(this, this.filters, {
        inputPrefix: 'streamline',
        enableSliceUpdates: false,
        checkboxId: 'streamlinePlaneShowWidget',
        planeChangeDebounce: 1000,  // Wait 1 second after plane stops moving
        onPlaneChange: (planeData) => {
          // AUTO-APPLY DISABLED: Users must click "Apply Streamlines" button
          console.log('[Streamline Plane Widget] Plane changed - auto-apply disabled, use Apply button');
          // if (this.filters && this.filters.applyStreamlinesFromUI) {
          //   this.filters.applyStreamlinesFromUI();
          // }
        }
        // Uses default blue colors (same as slice widget)
      });
      console.log('Streamline plane widget initialized');
    }

    // Initialize cutting plane widget for mesh cutter
    if (typeof CuttingPlaneWidget !== 'undefined') {
      this.filters.cuttingPlaneWidget = new CuttingPlaneWidget(this, this.filters);
      console.log('Cutting plane widget initialized');
    }

    // Initialize mesh eraser
    if (typeof MeshEraser !== 'undefined') {
      this.filters.meshEraser = new MeshEraser(this, this.filters);
      console.log('Mesh eraser initialized');
    }

    // Initialize mesh solidify filter
    if (typeof MeshSolidify !== 'undefined') {
      this.filters.meshSolidify = new MeshSolidify(this, this.filters);
      console.log('Mesh solidify initialized');
    }

    this.controls = new Controls(this);
    this.refreshPipelineList();

    // Check if in CAD mode from URL parameter
    const urlParams = new URLSearchParams(window.location.search);
    const mode = urlParams.get('mode');
    const documentId = urlParams.get('document');

    // Initialize CAD viewer ONLY in CAD mode
    if (mode === 'cad' && typeof CADViewer !== 'undefined') {
      this.cadViewer = new CADViewer(this);
      console.log('CAD viewer initialized');

      // Initialize CAD integration orchestrator (loads all enhancement modules)
      if (typeof CADIntegration !== 'undefined') {
        this.cadIntegration = new CADIntegration(this.cadViewer);
        this.cadIntegration.initializeAllModules().then(() => {
          console.log('CAD integration complete - all modules loaded');
        }).catch(err => {
          console.error('CAD integration error:', err);
        });
      }

      if (mode === 'cad') {
        this.enterCADMode(documentId);
      }
    }

    // Setup streamline picking & overlays
    this.setupPicking();

    // Initialize probe tool
    this.raycaster = new THREE.Raycaster();
    this.setupProbeEventListeners();

    // Initialize annotation system
    if (typeof AnnotationRenderer !== 'undefined') {
      this.annotationRenderer = new AnnotationRenderer(
        this.engine.scene,
        this.engine.camera,
        this.engine.renderer,
        viewport,
        this.engine
      );
      this.setupAnnotationEventListeners();

      // Add animation callback for label position updates
      this.engine.addAnimationCallback(() => {
        if (this.annotationRenderer) {
          this.annotationRenderer.updateLabelPositions();
        }
      });

      console.log('Annotation system initialized');
    }

    // Initialize display controls
    this.setupDisplayControls();

    // Setup auto-rotation controls
    this.setupAutoRotation();

    // Setup share button
    this.setupShareButton();

    // Setup legend resize functionality
    this.setupLegendResize();

    // Start stats update loop
    this.startStatsUpdate();

    // Check for URL parameters
    this.handleUrlParams();

    console.log('CoandaView initialized');
  }

  async handleUrlParams() {
    const params = Utils.getUrlParams();
    console.log('URL params:', params);
    console.log('window.location.search:', window.location.search);

    if (params.simulationId) {
      this.connectToSimulator(
        params.url || 'ws://localhost:8765',
        params.simulationId
      );
    } else if (params.session) {
      this.initCollaboration(params.session);
    } else if (params.token) {
      // Token-based share link - resolve token to case path
      console.log('[handleUrlParams] Token-based share link:', params.token);
      this.loadCaseFromToken(params.token);
    } else if (params.shared_case && params.via_session) {
      // Simulation inside a shared folder - user has access via parent session
      // Store the session context for API calls
      this.sharedAccessSession = params.via_session;
      this.loadSharedCase(params.shared_case, params.via_session);
    } else if (params.case) {
      console.log('[handleUrlParams] Has params.case, checking params.name:', params.name);
      // If display name is passed from dashboard, use it directly
      if (params.name) {
        this.caseDisplayName = params.name;
        this.updateCaseNameDisplay(params.name);
        console.log('[handleUrlParams] Set caseDisplayName to:', this.caseDisplayName);
      }
      // Pass case reference directly - backend resolves UUIDs internally
      this.loadCaseWithAutoCollaboration(params.case);
    } else if (params.file) {
      this.loadFromURL(params.file);
    } else {
      // No case/session specified - show empty viewer
      // User can click the logo to go to dashboard
      console.log('No case/session specified. Click logo to go to dashboard.');
    }
  }

  /**
   * Load a case using a public access token
   */
  async loadCaseFromToken(token) {
    const apiBase = window.Auth?.apiBase || '';
    try {
      // Resolve token to case path and session ID
      const response = await fetch(`${apiBase}/api/resolve-token/${token}`);
      if (!response.ok) {
        throw new Error('Invalid or expired share link');
      }
      const data = await response.json();
      console.log('[Token] Resolved to:', data);

      // Load the case using the resolved path and session
      if (data.session_id) {
        this.initCollaboration(data.session_id, data.case_path);
      } else {
        this.loadCaseFromPath(data.case_path);
      }
    } catch (err) {
      console.error('Failed to load from token:', err);
      Utils.notify('Invalid or expired share link', 'error');
    }
  }

  /**
   * Check if a string looks like a UUID-based case reference
   */
  isUuidRef(str) {
    return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i.test(str);
  }

  /**
   * @deprecated Backend now handles UUID resolution directly in all endpoints.
   * Kept for backward compatibility.
   */
  async resolveCaseRef(caseRef) {
    return caseRef;
  }

  /**
   * Load a case that user has access to via a shared folder session
   */
  async loadSharedCase(casePath, viaSessionId) {
    console.log('Loading shared case:', casePath, 'via session:', viaSessionId);
    const loadingOverlay = document.getElementById('loadingOverlay');
    if (loadingOverlay) loadingOverlay.style.display = 'flex';

    try {
      // Store the shared access context for subsequent API calls
      this.sharedCasePath = casePath;
      this.sharedViaSession = viaSessionId;

      // Load the case - the openfoam-loader will include via_session in requests
      await this.loadCaseFromPath(casePath);
    } catch (err) {
      console.error('Failed to load shared case:', err);
      if (loadingOverlay) loadingOverlay.style.display = 'none';
      Utils.notify('Failed to load shared simulation: ' + err.message, 'error');
    }
  }

  /**
   * Load a case and automatically connect to collaboration if a shared session exists.
   * Also handles public link access - if case has public link enabled, any authenticated user can view it.
   * Supports guest users who accessed via public link.
   */
  async loadCaseWithAutoCollaboration(casePath) {
    const apiBase = window.Auth?.apiBase || '';
    const token = window.Auth?.getToken();
    const isGuest = window.Auth?.isGuestUser();

    // For guest users, we already have the session_id from the guest token
    if (isGuest) {
      const userInfo = window.Auth?.getUserInfo();
      console.log('[Collaboration] Guest user detected:', userInfo);

      // Guest users should join collaboration using their session_id
      // The session_id is stored in the Auth user object
      const guestSessionId = window.Auth?.user?.session_id;
      if (guestSessionId) {
        console.log('[Collaboration] Guest joining session:', guestSessionId);
        this.accessRole = userInfo?.role || 'viewer';
        this.isPublicAccess = true;
        this.initCollaboration(guestSessionId, casePath);
        return;
      } else {
        // No session_id in guest token - just load the case
        console.log('[Collaboration] Guest loading case without collaboration');
        this.loadCaseFromPath(casePath);
        return;
      }
    }

    // Check access and existing session for this case path (for authenticated users)
    if (token) {
      try {
        // First check access using the new endpoint that handles public links
        const accessRes = await fetch(`${apiBase}/api/cases/${encodeURIComponent(casePath)}/access`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });

        if (accessRes.ok) {
          const accessData = await accessRes.json();
          console.log('[Collaboration] Access check result:', accessData);

          if (accessData.has_access) {
            // User has access (owner, collaborator, or public link)
            this.accessRole = accessData.role;
            this.isPublicAccess = accessData.is_public;

            // If there's a session_id, join collaboration (for all users including owner)
            if (accessData.session_id) {
              console.log('[Collaboration] Joining session:', accessData.session_id, 'role:', accessData.role, 'is_public:', accessData.is_public);
              this.initCollaboration(accessData.session_id, casePath);
              return;
            }

            // No session_id in access data, check if there's a shared session by path
            const sessionRes = await fetch(`${apiBase}/api/session-by-path?case_path=${encodeURIComponent(casePath)}`, {
              headers: { 'Authorization': `Bearer ${token}` }
            });

            if (sessionRes.ok) {
              const sessionData = await sessionRes.json();
              console.log('[Collaboration] Session-by-path result:', sessionData);
              if (sessionData.session_id && sessionData.is_shared) {
                console.log('[Collaboration] Auto-connecting to shared session:', sessionData.session_id);
                this.initCollaboration(sessionData.session_id, casePath);
                return;
              }
            }

            // Has access but no shared session, just load the case
            console.log('[Collaboration] No shared session, loading case directly');
            this.loadCaseFromPath(casePath);
            return;
          } else {
            // No access - show error
            console.error('Access denied to case:', casePath);
            Utils.notify('You do not have access to this case. Please ask the owner to share it with you.', 'error');
            const loadingOverlay = document.getElementById('loadingOverlay');
            if (loadingOverlay) loadingOverlay.style.display = 'none';
            return;
          }
        }
      } catch (err) {
        console.warn('Could not check access, trying direct load:', err);
      }
    }

    // Fallback: try to load directly (will fail if user doesn't own the case)
    this.loadCaseFromPath(casePath);
  }

  async loadCaseFromPath(casePath) {
    console.log('Loading case from dashboard:', casePath);

    // Store the case path so download button works (also set by session loading)
    if (!this.sessionDataPath) {
      this.sessionDataPath = casePath;
    }

    // Try to resolve the case to get display name from database
    const apiBase = window.Auth?.apiBase || '';
    const token = window.Auth?.getToken() || localStorage.getItem('coandacloud_token');
    try {
      const resolveRes = await fetch(`${apiBase}/api/resolve-case?case_ref=${encodeURIComponent(casePath)}`, {
        headers: token ? { 'Authorization': `Bearer ${token}` } : {}
      });
      if (resolveRes.ok) {
        const resolved = await resolveRes.json();
        console.log('[loadCaseFromPath] Resolved case:', resolved);
        if (resolved.display_name) {
          this.caseDisplayName = resolved.display_name;
          this.updateCaseNameDisplay(resolved.display_name);
          console.log('[loadCaseFromPath] Using display_name from database:', resolved.display_name);
        } else if (resolved.case_path) {
          // No display_name set - try to extract a friendly name from the case_path
          // e.g., "/user_id/fluent_example/case.cas" -> "fluent_example"
          const pathParts = resolved.case_path.split('/').filter(p => p);
          // Skip the user_id (first part if it's a UUID)
          const uuidPattern = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
          let friendlyName = null;
          for (let i = 0; i < pathParts.length; i++) {
            if (!uuidPattern.test(pathParts[i]) && !pathParts[i].endsWith('.cas') && !pathParts[i].endsWith('.case')) {
              friendlyName = pathParts[i];
              break;
            }
          }
          if (friendlyName) {
            this.caseDisplayName = friendlyName;
            this.updateCaseNameDisplay(friendlyName);
            console.log('[loadCaseFromPath] Using extracted name from path:', friendlyName);
          } else {
            console.log('[loadCaseFromPath] No display_name in database and could not extract from path');
          }
        }
      }
    } catch (err) {
      console.log('[loadCaseFromPath] Could not resolve case display name:', err);
    }

    // Show early progress feedback — the case-info detection call can be slow
    // when the backend needs to download files from cloud storage on first load.
    // Format-specific loaders will replace this with their own staged progress.
    window.loadingProgress.show('Connecting to server...');
    window.loadingProgress.updateProgress(5, 'Detecting case type...');

    try {
      // Detect case type based on path extension first
      const isEnSightFile = casePath.endsWith('.case');
      const isFluentFile = casePath.endsWith('.cas') || casePath.endsWith('.cas.gz');
      const lowerPath = casePath.toLowerCase();
      const isGeometryFile = lowerPath.endsWith('.stl') || lowerPath.endsWith('.obj') ||
                             lowerPath.endsWith('.vtk') || lowerPath.endsWith('.vtu') ||
                             lowerPath.endsWith('.vtp') || lowerPath.endsWith('.ply') ||
                             lowerPath.endsWith('.step') || lowerPath.endsWith('.stp');

      if (isGeometryFile) {
        console.log('Detected geometry file:', casePath);
        await this.loadGeometryFile(casePath);
      } else if (isEnSightFile) {
        console.log('Detected EnSight case file');
        await this.loadEnSightWithGeometry(casePath);
      } else if (isFluentFile) {
        console.log('Detected Fluent case file');
        await this.loadFluentCase(casePath);
      } else {
        // It's a directory - query the backend to detect the case type
        console.log('Checking case type for directory:', casePath);
        const apiBase = window.Auth?.apiBase || '';
        const token = window.Auth?.getToken() || localStorage.getItem('coandacloud_token');

        try {
          // Remove leading slash for URL path parameter format
          const encodedPath = casePath.startsWith('/') ? casePath.substring(1) : casePath;
          window.loadingProgress.updateProgress(10, 'Querying case metadata...');
          const infoRes = await fetch(`${apiBase}/api/openfoam/case-info/${encodedPath}`, {
            headers: token ? { 'Authorization': `Bearer ${token}` } : {}
          });

          if (infoRes.ok) {
            const info = await infoRes.json();
            const detectedType = info.case_type || 'openfoam';
            console.log('Backend detected case type:', detectedType, 'format:', info.geometry_format);

            if (detectedType === 'geometry') {
              console.log('Loading geometry file:', casePath, 'format:', info.geometry_format);
              // Pass geometry format so loadGeometryFile knows the actual file type
              await this.loadGeometryFile(casePath, info.geometry_format);
            } else if (detectedType === 'ensight') {
              // EnSight case - pass original casePath (UUID); backend resolves internally
              console.log('Loading EnSight case:', casePath);
              await this.loadEnSightWithGeometry(casePath);
            } else if (detectedType === 'fluent') {
              console.log('Loading Fluent case:', casePath);
              await this.loadFluentCase(casePath);
            } else {
              // OpenFOAM case
              console.log('Loading OpenFOAM case:', casePath);
              await this.loadOpenFOAMWithGeometry(casePath);
            }
          } else {
            // Fallback to OpenFOAM if case-info fails
            console.log('Case info failed, falling back to OpenFOAM');
            await this.loadOpenFOAMWithGeometry(casePath);
          }
        } catch (infoErr) {
          console.warn('Could not detect case type:', infoErr);
          // Fallback to OpenFOAM
          await this.loadOpenFOAMWithGeometry(casePath);
        }
      }
    } catch (err) {
      console.error('Failed to load case:', err);
      window.loadingProgress.error(err.message);
      Utils.notify(`Failed to load case: ${err.message}`, 'error');
    } finally {
      // Ensure overlay is hidden even if format-specific loader didn't hide it
      // (e.g. if an error was caught internally by the format loader)
      setTimeout(() => {
        if (window.loadingProgress.isVisible) {
          console.warn('[loadCaseFromPath] Loading progress still visible after completion, hiding');
          window.loadingProgress.hide();
        }
      }, 1000);
    }
  }

  /**
   * Load a geometry file (STL, OBJ, VTK, PLY) from server path
   * @param {string} filePath - Path or UUID of the file
   * @param {string} geometryFormat - Optional format hint (e.g., 'vtk', 'stl', 'obj')
   */
  async loadGeometryFile(filePath, geometryFormat = null) {
    console.log('Loading geometry file:', filePath, 'format:', geometryFormat);
    window.loadingProgress.show(`Loading ${filePath.split('/').pop()}`);
    window.loadingProgress.updateProgress(10, 'Fetching file...');

    try {
      const apiBase = window.Auth?.apiBase || '';
      const token = window.Auth?.getToken() || localStorage.getItem('coandacloud_token');

      // Extract filename for display - use caseDisplayName if available (handles UUIDs)
      let fileName = this.caseDisplayName || filePath.split('/').pop();

      // Check if this is a VTK file that needs backend processing
      // VTK/VTU/VTP files may contain UNSTRUCTURED_GRID which Three.js doesn't support
      // Check for VTK files using path, display name, OR geometry format hint
      const lowerPath = filePath.toLowerCase();
      const lowerDisplayName = (this.caseDisplayName || '').toLowerCase();
      const lowerFormat = (geometryFormat || '').toLowerCase();
      const isVTKFile = lowerPath.endsWith('.vtk') || lowerPath.endsWith('.vtu') || lowerPath.endsWith('.vtp') ||
                        lowerDisplayName.endsWith('.vtk') || lowerDisplayName.endsWith('.vtu') || lowerDisplayName.endsWith('.vtp') ||
                        lowerFormat === 'vtk' || lowerFormat === 'vtu' || lowerFormat === 'vtp';

      if (isVTKFile) {
        // Use backend rendering for VTK files (supports all VTK data types)
        console.log('Using backend for VTK file:', filePath);
        window.loadingProgress.updateProgress(30, 'Processing VTK file...');

        // First load to get field metadata (without color)
        let geometryResult = await this.renderingEngine.loadGeometryFromBackend({
          casePath: filePath,
          dataSource: 'vtk',
          colorField: null,
          geometryType: 'surface',
          representation: 'surface',
          useBinary: true
        });

        // Capture field metadata from geometry result
        if (geometryResult.field_metadata) {
          this.fieldMetadata = geometryResult.field_metadata;
          console.log('[VTK] Field metadata:', Object.keys(this.fieldMetadata).length, 'fields');
        }

        // Capture available vector fields
        if (geometryResult.available_vector_fields) {
          this.availableVectorFields = geometryResult.available_vector_fields;
          console.log('[VTK] Available vector fields:', this.availableVectorFields);
        }

        // Select default color field - prefer vector fields like U, velocity
        let defaultColorField = null;
        if (this.fieldMetadata && Object.keys(this.fieldMetadata).length > 0) {
          const fieldNames = Object.keys(this.fieldMetadata);
          console.log('[VTK] Available fields:', fieldNames);

          // Prefer common velocity field names
          const preferredFields = ['U', 'velocity', 'Velocity', 'V', 'u', 'v'];
          for (const preferred of preferredFields) {
            if (this.fieldMetadata[preferred]) {
              defaultColorField = preferred;
              console.log('[VTK] Found preferred field:', preferred);
              break;
            }
          }
          // Fall back to first available field if no preferred found
          if (!defaultColorField) {
            // Prefer non-cellID fields over cellID
            defaultColorField = fieldNames.find(f => f !== 'cellID') || fieldNames[0];
            console.log('[VTK] No preferred field found, using:', defaultColorField);
          }
          console.log('[VTK] Selected default color field:', defaultColorField);
        }

        // Store case info for UI population and colormap changes
        this.currentCasePath = filePath;
        this.dataSource = 'vtk';
        this.renderingMode = 'geometry';
        this.geometryParams = {
          casePath: filePath,
          colorField: defaultColorField,
          geometryType: 'surface',
          representation: 'surface',
          dataSource: 'vtk'
        };

        // Build fields list from field_metadata for UI population
        const fields = [];
        if (this.fieldMetadata) {
          for (const [fieldName, meta] of Object.entries(this.fieldMetadata)) {
            fields.push({
              name: fieldName,
              type: meta.type || 'scalar',
              components: meta.components || 1
            });
          }
        }

        // Set availableFields for filters (slice, streamlines, etc.) to use
        this.availableFields = fields.map(f => f.name);
        console.log('[VTK] Set availableFields for filters:', this.availableFields);

        // Initialize currentData for VTK files (needed for reloadGeometry and probe tool)
        this.currentData = {
          type: 'vtk',
          case_path: filePath,
          case_name: fileName,
          metadata: {
            fileName: fileName,
            vertices: geometryResult.vertices || 0,
            triangles: geometryResult.triangles || 0,
            scalarField: defaultColorField,
            format: 'vtk'
          },
          fields: {},
          available_fields: this.availableFields
        };

        // Populate colormap variable select
        const colormapVarSelect = document.getElementById('colormapVariable');
        if (colormapVarSelect && fields.length > 0) {
          colormapVarSelect.innerHTML = '';
          fields.forEach(f => {
            const option = document.createElement('option');
            option.value = f.name;
            option.textContent = f.name;
            if (f.name === defaultColorField) {
              option.selected = true;
            }
            colormapVarSelect.appendChild(option);
          });
          console.log('[VTK] Populated colormap with', fields.length, 'fields');
        }

        // Reload with default color field if available
        if (defaultColorField) {
          window.loadingProgress.updateProgress(60, `Applying field: ${defaultColorField}...`);
          geometryResult = await this.renderingEngine.loadGeometryFromBackend({
            casePath: filePath,
            dataSource: 'vtk',
            colorField: defaultColorField,
            geometryType: 'surface',
            representation: 'surface',
            useBinary: true
          });

          // Update colormap UI with scalar range
          if (this.renderingEngine.lastGeometryScalarRange && this.controls?.elements) {
            const range = this.renderingEngine.lastGeometryScalarRange;
            this.controls.elements.colormapMin.value = range.min.toFixed(3);
            this.controls.elements.colormapMax.value = range.max.toFixed(3);
            this.colormap.min = range.min;
            this.colormap.max = range.max;
            console.log('[VTK] Applied colormap range:', range);
          }

          // Update mesh parts UI with boundaries from backend
          console.log('[VTK] Full geometry result:', {
            boundaries: geometryResult.boundaries,
            boundariesType: typeof geometryResult.boundaries,
            available_parts: geometryResult.available_parts,
            field_metadata: geometryResult.field_metadata
          });

          // Check if boundaries is an object with keys (not an empty array)
          const hasBoundaries = geometryResult.boundaries &&
            typeof geometryResult.boundaries === 'object' &&
            !Array.isArray(geometryResult.boundaries) &&
            Object.keys(geometryResult.boundaries).length > 0;

          if (hasBoundaries) {
            console.log('[VTK] Updating mesh parts with boundaries:', Object.keys(geometryResult.boundaries));
            this.updateMeshPartsUI(geometryResult.boundaries);
          } else {
            // For single-block VTK files, create a default mesh part using filename
            const partName = fileName.replace(/\.(vtk|vtu|vtp)$/i, '');
            const defaultBoundaries = {};
            defaultBoundaries[partName] = {
              name: partName,
              nFaces: geometryResult.num_triangles || 0,
              visible: true
            };
            console.log('[VTK] No boundaries found, using default mesh part:', partName);
            this.updateMeshPartsUI(defaultBoundaries);
          }
        }

        // Populate all filter field selectors (XY Plot, Integration, Engineering Calculations, etc.)
        console.log('[VTK] Populating filter field selectors with:', this.availableFields);
        this.updatePlotFieldSelectors(this.availableFields);

        // Update case name display (use cached display name if available)
        this.updateCaseNameDisplay(this.caseDisplayName || fileName);

        window.loadingProgress.complete('File loaded');
        Utils.notify(`Loaded ${fileName}`, 'success');
        return;
      }

      // For STL, OBJ, PLY - use client-side loading (works well with Three.js)
      // Fetch the file from server
      const response = await fetch(`${apiBase}/api/download-file?path=${encodeURIComponent(filePath)}`, {
        headers: token ? { 'Authorization': `Bearer ${token}` } : {}
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch file: ${response.statusText}`);
      }

      window.loadingProgress.updateProgress(50, 'Parsing geometry...');

      // Get the file as blob and create a File object for the data loader
      const blob = await response.blob();
      // Extract real filename from Content-Disposition header (handles UUIDs)
      const disposition = response.headers.get('Content-Disposition');
      if (disposition) {
        const match = disposition.match(/filename="?([^";\n]+)"?/);
        if (match) fileName = match[1];
      }
      const file = new File([blob], fileName, { type: blob.type });

      // Use data loader to parse the file
      const data = await this.dataLoader.loadFile(file);

      window.loadingProgress.updateProgress(80, 'Rendering...');

      // Display the geometry
      this.displayData(data);

      // Update case name display (use cached display name if available)
      this.updateCaseNameDisplay(this.caseDisplayName || fileName);
      this.currentCasePath = filePath;

      window.loadingProgress.complete('File loaded');
      Utils.notify(`Loaded ${fileName}`, 'success');

    } catch (err) {
      console.error('Failed to load geometry file:', err);
      window.loadingProgress.error(err.message);
      throw err;
    }
  }

  async initCollaboration(sessionId, casePathOverride = null) {
    console.log('Initializing collaboration for session:', sessionId);
    // Store case path override for loadSessionData to use
    this.casePathOverride = casePathOverride;
    const token = window.Auth ? window.Auth.getToken() : localStorage.getItem('coandacloud_token');

    if (!token) {
      console.warn('No auth token found, redirecting to login');
      // window.location.href = 'landing.html'; // Optional: Redirect
      return;
    }

    // Determine WebSocket URL
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    // Use configured API base or current host
    const apiBase = window.Auth?.apiBase || '';
    // Construct WS URL from HTTP URL
    let wsHost = window.location.host;
    if (apiBase.startsWith('http')) {
      const url = new URL(apiBase);
      wsHost = url.host;
    }

    const wsUrl = `${protocol}//${wsHost}/ws/collaboration/${sessionId}?token=${token}`;

    this.websocket = new WebSocket(wsUrl);

    this.websocket.onopen = () => {
      console.log('✅ Collaboration WebSocket connected to session:', sessionId);
      const userInfo = window.Auth?.getUserInfo() || { id: 'unknown', name: 'Anonymous' };
      console.log('✅ User info:', userInfo.id, userInfo.name);

      // Init managers
      if (typeof CollaborativeCursorManager !== 'undefined') {
        this.cursorManager = new CollaborativeCursorManager(this.websocket, userInfo.id);
        console.log('✅ CursorManager initialized');
      } else {
        console.warn('⚠️ CollaborativeCursorManager not defined');
      }
      if (typeof CollaborativeLockManager !== 'undefined') {
        this.lockManager = new CollaborativeLockManager(this.websocket, userInfo.id);
        window.lockManager = this.lockManager; // Expose globally
      }
      if (typeof PresenceManager !== 'undefined') {
        this.presenceManager = new PresenceManager(this.websocket, sessionId, userInfo.id, userInfo.name);
        window.presenceManager = this.presenceManager; // Expose globally for chat mentions
      }
      if (typeof StateSynchronizer !== 'undefined') {
        this.stateSynchronizer = new StateSynchronizer(this.websocket, sessionId);
        window.stateSynchronizer = this.stateSynchronizer;
      }
      // Initialize View Lock Manager for collaborative camera control
      if (typeof ViewLockManager !== 'undefined') {
        this.viewLockManager = new ViewLockManager(this.websocket, userInfo.id, userInfo.name || userInfo.display_name || 'Anonymous');
        this.viewLockManager.setViewer(this);  // Pass viewer reference for full state capture
        window.viewLockManager = this.viewLockManager;
      }

      // Initialize Collaboration Chat
      if (typeof CollaborationChat !== 'undefined') {
        this.chatManager = new CollaborationChat(this.websocket, sessionId, userInfo.id, userInfo.name || userInfo.display_name || 'Anonymous');
        window.chatManager = this.chatManager;
        console.log('✅ CollaborationChat initialized');
      }

      // Initialize Voice Call Manager (WebRTC P2P)
      if (typeof VoiceCallManager !== 'undefined' && window.voiceCallManager) {
        window.voiceCallManager.initialize(this.websocket, sessionId, userInfo.id, userInfo.name || userInfo.display_name || 'Anonymous');
        this.voiceCallManager = window.voiceCallManager;
        console.log('✅ VoiceCallManager initialized');
      }

      // Hook into controls
      this.setupCollaborationHooks();

      // Also load the session data (geometry/fields)
      // Assume backend endpoint /api/sessions/{id} returns details to load
      this.loadSessionData(sessionId);
    };

    this.websocket.onmessage = (event) => {
      try {
        const message = JSON.parse(event.data);
        this.handleCollaborationMessage(message);
      } catch (e) {
        console.error('Failed to parse collaboration message:', e);
      }
    };

    this.websocket.onclose = (e) => {
      console.log('Collaboration WebSocket closed', e.code, e.reason);
      if (e.code === 4003) {
        Utils.notify('Access denied to this session', 'error');
      } else {
        Utils.notify('Connection to collaboration session lost', 'warning');
      }
    };
  }

  /**
   * Handle incoming collaboration WebSocket messages
   * Routes messages to appropriate managers
   */
  handleCollaborationMessage(message) {
    const { type, data, user_id, user_name } = message;

    // Don't process our own messages (except lock-related, chat, and voice call messages that affect local state)
    const currentUserId = window.Auth?.getUserInfo()?.id;
    const selfAllowedMessages = [
      'lock_granted', 'lock_denied', 'lock_released',
      'lock_auth_request', 'lock_auth_response', 'lock_auth_cancelled',
      'follower_joined', 'follower_left',  // User who joined needs to update their own state
      'chat_message', 'chat_history', 'chat_typing', 'chat_error',
      'voice_participants', 'voice_offer', 'voice_answer', 'voice_ice_candidate'
    ];
    if (user_id === currentUserId && !selfAllowedMessages.includes(type)) {
      return;
    }

    // Also skip if backend marked this message to exclude us
    if (message._exclude_user_id === currentUserId) {
      return;
    }

    switch (type) {
      // Keepalive ping - respond with pong
      case 'ping':
        if (this.websocket && this.websocket.readyState === WebSocket.OPEN) {
          this.websocket.send(JSON.stringify({ type: 'pong', timestamp: message.timestamp }));
        }
        break;

      // Cursor sharing
      case 'cursor_move':
        if (this.cursorManager) {
          this.cursorManager.onRemoteCursorMove(user_id, user_name, message.x, message.y);
        }
        break;

      case 'cursor_leave':
        if (this.cursorManager) {
          this.cursorManager.onRemoteCursorLeave(user_id);
        }
        break;

      // Lock management
      case 'lock_granted':
        if (this.lockManager) {
          this.lockManager.onLockGranted(data.section, data.holder_id, data.holder_name);
        }
        // Also notify ViewLockManager
        if (this.viewLockManager) {
          this.viewLockManager.onLockGranted(data);
        }
        break;

      case 'lock_denied':
        if (this.lockManager) {
          this.lockManager.onLockDenied(data.section, data.reason);
        }
        // Also notify ViewLockManager
        if (this.viewLockManager) {
          this.viewLockManager.onLockDenied(data);
        }
        break;

      case 'lock_released':
        if (this.lockManager) {
          this.lockManager.onLockReleased(data.section, user_id);
        }
        // Also notify ViewLockManager
        if (this.viewLockManager) {
          this.viewLockManager.onLockReleased(user_id, user_name, data);
        }
        break;

      case 'lock_taken':
        // Another user took a lock
        if (this.lockManager) {
          this.lockManager.onLockTaken(data.section, user_id, user_name);
        }
        // Also notify ViewLockManager
        if (this.viewLockManager) {
          this.viewLockManager.onLockTaken(user_id, user_name, data);
        }
        break;

      case 'follower_joined':
        // Someone started following the controller
        if (this.viewLockManager) {
          this.viewLockManager.onFollowerJoined(user_id, user_name, data);
        }
        break;

      case 'follower_left':
        // Someone stopped following the controller
        if (this.viewLockManager) {
          this.viewLockManager.onFollowerLeft(user_id, user_name, data);
        }
        break;

      // Lock authorization messages (multi-user)
      case 'lock_auth_request':
        // Someone is requesting control - check if we're in the target list
        if (this.viewLockManager) {
          const targetUsers = message.target_users || [];
          // If target_users includes us, we need to respond
          if (targetUsers.includes(currentUserId)) {
            this.viewLockManager.onAuthorizationRequest({
              user_id: user_id,
              user_name: user_name,
              request_id: message.request_id,
              section_id: message.section_id
            });
          }
        }
        break;

      case 'lock_auth_response':
        // Response to an authorization request - route to requester
        if (this.viewLockManager) {
          // If the response is for our request (we are target_user_id)
          if (message.target_user_id === currentUserId) {
            this.viewLockManager.onAuthorizationResponse({
              approved: message.approved,
              user_id: user_id,
              user_name: user_name,
              request_id: message.request_id
            });
          }
        }
        break;

      case 'lock_auth_cancelled':
        // Requester cancelled their request
        if (this.viewLockManager) {
          this.viewLockManager.onAuthorizationCancelled({
            user_id: user_id,
            user_name: user_name,
            request_id: message.request_id
          });
        }
        break;

      // Camera sync for view lock
      case 'camera_sync':
        if (this.viewLockManager && message.camera) {
          this.viewLockManager.onCameraSync(user_id, message.camera);
        }
        break;

      // Full view state sync (streamlines, slices, visibility, etc.)
      case 'view_state_sync':
        if (this.viewLockManager && message.state) {
          this.viewLockManager.onViewStateSync(user_id, message.state, message.sequence);
        }
        break;

      // State synchronization (camera, filters, etc.)
      case 'state_update':
        if (this.stateSynchronizer && data) {
          // Only apply if we're in follow mode or don't have the lock
          const hasLock = this.lockManager?.hasLock('camera');
          if (!hasLock) {
            this.applyRemoteStateUpdate(data);
          }
        }
        break;

      // Presence management
      case 'user_joined':
        if (this.presenceManager) {
          this.presenceManager.onUserJoined(user_id, user_name);
        }
        // Also notify ViewLockManager to track session users
        if (this.viewLockManager) {
          this.viewLockManager.onUserJoined(user_id, user_name);
        }
        Utils.notify(`${user_name || 'Someone'} joined the session`, 'info');
        break;

      case 'user_left':
        if (this.presenceManager) {
          this.presenceManager.onUserLeft(user_id);
        }
        if (this.cursorManager) {
          this.cursorManager.onRemoteCursorLeave(user_id);
        }
        if (this.viewLockManager) {
          this.viewLockManager.onUserLeft(user_id);
        }
        Utils.notify(`${user_name || 'Someone'} left the session`, 'info');
        break;

      case 'presence_list':
        // Initial list of users in session
        // Note: users array is at message.users, not data.users
        if (this.presenceManager && message.users) {
          this.presenceManager.updateUserList(message.users);
        }
        // Also set presence list for ViewLockManager
        if (this.viewLockManager && message.users) {
          this.viewLockManager.setPresenceList(message.users);
        }
        break;

      case 'locks_list':
        // Initial list of active locks in session
        // Note: locks object is at message.locks, not data.locks
        if (this.lockManager && message.locks) {
          for (const [sectionId, lockInfo] of Object.entries(message.locks)) {
            this.lockManager.onLockTaken(sectionId, lockInfo.user_id, lockInfo.user_name);
          }
        }
        // Also notify ViewLockManager of initial locks
        if (this.viewLockManager && message.locks) {
          this.viewLockManager.onLocksList(message.locks);
        }
        break;

      // Follow mode
      case 'follow_request':
        this.handleFollowRequest(user_id, user_name);
        break;

      case 'follow_accepted':
        if (this.followingUserId === user_id) {
          Utils.notify(`Now following ${user_name}'s view`, 'success');
        }
        break;

      // Chat messages
      case 'chat_message':
      case 'chat_history':
      case 'chat_typing':
      case 'chat_error':
        if (this.chatManager) {
          this.chatManager.handleMessage(message);
        }
        break;

      // Voice call signaling (WebRTC P2P)
      case 'voice_user_joined':
      case 'voice_user_left':
      case 'voice_participants':
      case 'voice_offer':
      case 'voice_answer':
      case 'voice_ice_candidate':
      case 'voice_mute':
        if (this.voiceCallManager) {
          this.voiceCallManager.handleSignalingMessage(message);
        }
        break;

      // New computed field notification
      case 'field_computed':
        this.onFieldComputed(message.field_name, message.expression, user_name);
        break;

      default:
        console.log('Unknown collaboration message type:', type);
    }
  }

  /**
   * Handle notification that another user computed a new field
   * Updates available fields list and refreshes dropdowns
   */
  onFieldComputed(fieldName, expression, userName) {
    console.log(`[Viewer] Field '${fieldName}' computed by ${userName}: ${expression}`);

    // Add to available fields if not already present
    if (this.availableFields && !this.availableFields.includes(fieldName)) {
      this.availableFields.push(fieldName);

      // Refresh all filter dropdowns
      if (window.filters) {
        window.filters.refreshAllFieldDropdowns();
      }

      Utils.notify(`New field "${fieldName}" computed by ${userName}`, 'info');
    }
  }

  /**
   * Apply remote state updates (camera position, filters, etc.)
   */
  applyRemoteStateUpdate(data) {
    if (data.camera) {
      // Apply camera position
      if (data.camera.position && this.engine?.camera) {
        this.engine.camera.position.set(
          data.camera.position.x,
          data.camera.position.y,
          data.camera.position.z
        );
      }
      if (data.camera.target && this.controls?.controls) {
        this.controls.controls.target.set(
          data.camera.target.x,
          data.camera.target.y,
          data.camera.target.z
        );
        this.controls.controls.update();
      }
    }

    // Could also sync filters, visibility, etc.
    if (data.filters) {
      // TODO: Apply filter state
    }
  }

  /**
   * Handle follow request from another user
   */
  handleFollowRequest(userId, userName) {
    // Auto-accept follow requests for now
    // Could show a confirmation dialog
    if (this.websocket && this.websocket.readyState === WebSocket.OPEN) {
      this.websocket.send(JSON.stringify({
        type: 'follow_accepted',
        data: { follower_id: userId }
      }));
    }
  }

  async loadSessionData(sessionId) {
    try {
      const apiBase = window.Auth?.apiBase || '';
      const token = window.Auth?.getToken();
      const res = await fetch(`${apiBase}/api/visualization-sessions/${sessionId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!res.ok) throw new Error('Failed to load session data');
      const data = await res.json();

      // Store session data_path for browsing shared directories
      if (data.data_path) {
        this.sessionDataPath = data.data_path;
      }

      // Use casePathOverride if provided (from auto-connect), otherwise use session's data_path
      const casePath = this.casePathOverride || data.data_path;
      if (!casePath) {
        console.warn('[loadSessionData] No case path available');
        return;
      }

      const browsePath = casePath.startsWith('/') ? casePath : '/' + casePath;
      console.log('[loadSessionData] Loading case path:', browsePath,
                  'override:', !!this.casePathOverride);

      // Clear the override after use
      this.casePathOverride = null;

      // Check if this is a single geometry file - load directly without browsing
      const lowerPath = browsePath.toLowerCase();
      const isGeometryFile = lowerPath.endsWith('.stl') || lowerPath.endsWith('.obj') ||
                             lowerPath.endsWith('.vtk') || lowerPath.endsWith('.vtu') ||
                             lowerPath.endsWith('.vtp') || lowerPath.endsWith('.ply') ||
                             lowerPath.endsWith('.step') || lowerPath.endsWith('.stp');

      if (isGeometryFile) {
        console.log('[loadSessionData] Detected geometry file, loading directly:', browsePath);
        await this.loadGeometryFile(browsePath);
        return;
      }

      // UUID paths are known cases from the DB — load directly without browsing
      const uuidPattern = /^\/?[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}(\/|$)/i;
      if (uuidPattern.test(browsePath)) {
        console.log('[loadSessionData] UUID path detected, loading case directly:', browsePath);
        await this.loadCaseFromPath(browsePath);
        return;
      }

      // First, check with backend if this is a loadable case or just a folder
      try {
        const browseRes = await fetch(`${apiBase}/api/browse`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { 'Authorization': `Bearer ${token}` } : {})
          },
          body: JSON.stringify({ path: browsePath })
        });

        if (browseRes.ok) {
          const browseData = await browseRes.json();

          // Check if this directory IS a case (OpenFOAM, EnSight, Fluent)
          // Note: backend returns snake_case (is_dir), not camelCase (isDir)
          const hasSystemFolder = browseData.items?.some(item => item.name === 'system' && item.is_dir);
          const hasCaseFile = browseData.items?.some(item => item.name.endsWith('.case'));
          const hasCasFile = browseData.items?.some(item => item.name.endsWith('.cas') || item.name.endsWith('.cas.gz'));
          // Also check if any sub-item is itself a case (nested case structure)
          const hasNestedCase = browseData.items?.some(item => item.is_case === true);
          const isDirectoryWithContents = browseData.items && browseData.items.length > 0;
          const isCase = hasSystemFolder || hasCaseFile || hasCasFile || hasNestedCase;

          if (isCase) {
            // It's a loadable case - load it via loadCaseFromPath which handles type detection
            console.log('[loadSessionData] Path is a case, loading directly:', browsePath);
            await this.loadCaseFromPath(browsePath);
          } else if (isDirectoryWithContents) {
            // It's a folder with contents - open file browser so user can navigate
            console.log('[loadSessionData] Path is a folder, opening file browser:', browsePath);
            Utils.notify('Navigate to the simulation and double-click to load', 'info');
            if (this.controls) {
              await this.controls.showFileBrowser(browsePath);
            }
          } else {
            // Empty or inaccessible - try loading anyway as fallback
            console.log('[loadSessionData] Unknown path type, attempting load:', browsePath);
            await this.loadCaseFromPath(browsePath);
          }
        } else {
          // Browse failed - try loading directly as fallback
          console.log('[loadSessionData] Browse failed, attempting direct load');
          await this.loadCaseFromPath(browsePath);
        }
      } catch (browseError) {
        console.error('[loadSessionData] Error checking path:', browseError);
        // Fallback: try loading directly
        await this.loadCaseFromPath(browsePath);
      }

      // Apply saved state (camera, filters provided in data)
      if (data.camera_position && this.stateSynchronizer) {
        this.stateSynchronizer.applyStateUpdate('camera.position', data.camera_position.position);
        this.stateSynchronizer.applyStateUpdate('camera.target', data.camera_position.target);
      }
    } catch (e) {
      console.error('Failed to load session:', e);
    }
  }

  setupCollaborationHooks() {
    // Hook camera controls
    if (this.controls && this.controls.controls) {
      this.controls.controls.addEventListener('change', () => {
        if (this.stateSynchronizer) {
          const pos = this.engine.camera.position;
          const target = this.controls.controls.target;

          this.stateSynchronizer.onLocalStateChange('camera.position', { x: pos.x, y: pos.y, z: pos.z });
          this.stateSynchronizer.onLocalStateChange('camera.target', { x: target.x, y: target.y, z: target.z });
        }
      });
    }

    // Clear view button highlight when user manually manipulates camera
    if (this.engine && this.engine.controls) {
      this.engine.controls.addEventListener('start', () => {
        // User started interacting with camera (mouse down on viewport)
        if (this.controls && this.controls.clearActiveViewButton) {
          this.controls.clearActiveViewButton();
        }
      });
    }

    // Wire up ViewLockManager to camera controls
    if (this.viewLockManager && this.engine) {
      // Use the actual OrbitControls from the rendering engine
      const orbitControls = this.engine.controls;
      const camera = this.engine.camera;

      if (orbitControls && camera) {
        this.viewLockManager.setCameraControls(orbitControls, camera);
        console.log('✅ ViewLockManager connected to camera controls');
      }
    }

    // Add Take Control Button logic
    const btnTakeControl = document.getElementById('btnTakeControl');
    if (btnTakeControl && this.lockManager) {
      btnTakeControl.addEventListener('click', () => {
        if (this.lockManager.myLocks.has('camera')) {
          this.lockManager.releaseLock('camera');
        } else {
          this.lockManager.requestLock('camera', 'Camera Control');
        }
      });
    }
  }

  // File loading
  async loadFile(file) {
    try {
      window.loadingProgress.show(`Loading ${file.name}`);
      window.loadingProgress.updateProgress(10, 'Reading file...');

      const data = await this.dataLoader.loadFile(file);

      // Check if this file needs to be uploaded to server first
      if (data.needsUpload) {
        window.loadingProgress.updateMessage('Uploading file to server...');
        Utils.notify(`Uploading ${file.name} to server...`, 'info');

        try {
          // Upload file to server
          const uploadResult = await this.openfoamLoader.uploadSingleFile(data.file);

          window.loadingProgress.complete('Upload complete');

          // Get current storage info for success modal
          let storageInfo = null;
          if (this.controls && this.controls.getStorageInfo) {
            storageInfo = await this.controls.getStorageInfo();
          }

          // Show success modal
          FolderUploader.showUploadSuccess({
            caseName: file.name,
            fileCount: 1,
            isOpenFOAM: false,
            storageUsed: storageInfo ? storageInfo.used_bytes : 0,
            storageTotal: storageInfo ? storageInfo.total_bytes : 0
          });

          // Update storage display
          if (this.controls && this.controls.updateStorageDisplay) {
            await this.controls.updateStorageDisplay();
          }

          // Open file browser to show the uploaded file
          if (this.controls && this.controls.showFileBrowser) {
            await this.controls.showFileBrowser(null);
          }

        } catch (err) {
          console.error('Failed to upload file:', err);
          window.loadingProgress.error(err.message);

          // Check if this is a storage quota error
          if (err.isQuotaExceeded) {
            FolderUploader.showUpgradePrompt(err.message);
          } else {
            Utils.notify(`Failed to upload file: ${err.message}`, 'error');
          }
        }
        return;
      }

      window.loadingProgress.updateProgress(70, 'Processing data...');
      this.displayData(data);

      Utils.notify(`Loaded ${file.name} successfully`, 'success');
      this.updateInfo(this.currentData?.metadata || data.metadata || {});

      window.loadingProgress.complete('File loaded');
    } catch (err) {
      console.error('Failed to load file:', err);
      window.loadingProgress.error(err.message);
      Utils.notify(`Failed to load file: ${err.message}`, 'error');
    }
  }

  async loadFromURL(url) {
    const loadingOverlay = document.getElementById('loadingOverlay');
    loadingOverlay.style.display = 'flex';

    try {
      // Check if this looks like a case path (no file extension, likely a directory)
      // If so, try to load it as a case first
      const hasExtension = url.includes('.') && !url.endsWith('/');
      if (!hasExtension) {
        console.log('URL appears to be a case directory, trying loadCaseFromPath...');
        try {
          await this.loadOpenFOAMWithGeometry(url);
          Utils.notify('Case loaded successfully', 'success');
          return;
        } catch (caseErr) {
          console.log('Not a valid case, falling back to file loader:', caseErr.message);
        }
      }

      const data = await this.dataLoader.loadFromURL(url);
      this.displayData(data);
      Utils.notify('Loaded data successfully', 'success');
    } catch (err) {
      console.error('Failed to load from URL:', err);
      Utils.notify(`Failed to load: ${err.message}`, 'error');
    } finally {
      loadingOverlay.style.display = 'none';
    }
  }

  async loadOpenFOAMWithGeometry(casePath) {
    try {
      console.log('Loading OpenFOAM case (geometry-based rendering):', casePath);
      this.currentCasePath = casePath;

      // Initialize progress with stages
      window.loadingProgress.show('Loading OpenFOAM Case');
      window.loadingProgress.initStages([
        'Reading case metadata...',
        'Extracting geometry on backend...',
        'Rendering in browser...',
        'Finalizing...'
      ]);

      // Step 1: Get case info (metadata only, no full mesh data)
      window.loadingProgress.updateProgress(20, 'Getting case info');
      const caseInfo = await this.openfoamLoader.getCaseInfo(casePath);
      console.log('Case info loaded:', caseInfo);

      // Check if backend detected a different case type (EnSight or Fluent)
      if (caseInfo.case_type === 'ensight' && caseInfo.case_file) {
        console.log('[Viewer] Detected EnSight case, redirecting to EnSight loader:', caseInfo.case_file);
        window.loadingProgress.hide();
        return await this.loadEnSightWithGeometry(caseInfo.case_file);
      } else if (caseInfo.case_type === 'fluent' && caseInfo.case_file) {
        console.log('[Viewer] Detected Fluent case, redirecting to Fluent loader:', caseInfo.case_file);
        window.loadingProgress.hide();
        return await this.loadFluentCase(caseInfo.case_file);
      }

      // Store available fields and times for geometry-based rendering
      this.availableFields = caseInfo.fields || [];
      this.availableTimes = caseInfo.times || [];
      this.fieldMetadata = caseInfo.field_metadata || {};  // Store component info
      this.caseDataCacheKey = caseInfo.cache_key || null;  // For cache invalidation
      console.log('[Viewer] Field metadata received:', this.fieldMetadata);
      console.log('[Viewer] Number of fields with metadata:', Object.keys(this.fieldMetadata).length);
      console.log('[Viewer] Cache key:', this.caseDataCacheKey);
      this.renderingMode = 'geometry';

      // Initialize rendering parameters
      this.geometryParams = {
        casePath: casePath,
        dataSource: 'openfoam',  // Store data source for reload operations
        time: this.availableTimes.length > 0 ? this.availableTimes[this.availableTimes.length - 1] : null,
        colorField: this.availableFields.includes('U') ? 'U' : (this.availableFields[0] || 'p'),
        geometryType: 'surface',
        representation: 'surface',
        selectedParts: null,
        cacheKey: this.caseDataCacheKey  // For persistent cache validation
      };

      // Step 2: Check persistent cache first, then load from backend if needed
      const initialPreset = this.controls?.elements?.colormapPreset?.value || this.colormap.preset || 'jet';
      const cacheKey = { time: this.geometryParams.time, colorField: this.geometryParams.colorField };

      let geometryResult;
      const cachedData = await this.getCachedGeometryAsync(cacheKey);

      if (cachedData) {
        // Use cached geometry from IndexedDB - much faster!
        console.log('[OpenFOAM] Using cached geometry from IndexedDB');
        window.loadingProgress.updateProgress(40, 'Loading from cache...');

        geometryResult = await this.engine.renderCachedGeometry(cachedData, {
          colormapSettings: {
            min: cachedData.scalarRange?.min,
            max: cachedData.scalarRange?.max,
            preset: initialPreset
          },
          preserveCamera: false
        });

        // Restore scalar range for colormap UI
        if (cachedData.scalarRange) {
          this.engine.lastGeometryScalarRange = cachedData.scalarRange;
        }
        if (cachedData.scalars) {
          this.engine.lastGeometryScalars = cachedData.scalars;
        }
      } else {
        // Fetch from backend (VTK processing on server)
        window.loadingProgress.updateProgress(40, 'Backend extracting geometry with VTK');

        geometryResult = await this.engine.loadGeometryFromBackend({
          ...this.geometryParams,
          colormapSettings: { preset: initialPreset } // Backend will use its scalar range for min/max
        });

        // Cache the result for future sessions using raw backend data
        if (this.engine.lastGeometryRawData) {
          this.cacheGeometry(cacheKey, {
            geometryData: this.engine.lastGeometryRawData,
            scalars: this.engine.lastGeometryScalars,
            scalarRange: this.engine.lastGeometryScalarRange,
            boundaries: geometryResult.boundaries,
            available_vector_fields: geometryResult.available_vector_fields,
            field_metadata: geometryResult.field_metadata  // Cache field metadata too
          });
          console.log('[OpenFOAM] Geometry cached for future sessions');
        }
      }

      console.log('Geometry loaded:', geometryResult, cachedData ? '(from cache)' : '(from backend)');

      // Store available vector fields from geometry response or cached data (for velocity vectors dropdown)
      const vectorFields = geometryResult.available_vector_fields || cachedData?.available_vector_fields;
      if (vectorFields && vectorFields.length > 0) {
        this.availableVectorFields = vectorFields;
        console.log('[Backend Load] Available vector fields:', this.availableVectorFields);
      } else {
        console.log('[Backend Load] No available_vector_fields in geometry response');
        this.availableVectorFields = [];
      }

      // Merge field metadata from geometry response (actual component counts from numpy shapes)
      const geometryFieldMetadata = geometryResult.field_metadata || cachedData?.field_metadata;
      if (geometryFieldMetadata) {
        this.fieldMetadata = { ...this.fieldMetadata, ...geometryFieldMetadata };
        console.log('[Backend Load] Updated field metadata:', Object.keys(this.fieldMetadata).length, 'fields');
      }

      // Step 3: Create minimal data structure for UI
      window.loadingProgress.updateProgress(80, 'Setting up controls');
      const data = {
        type: 'simulation',
        source: 'OpenFOAM',  // Required for dynamic endpoint routing
        case_path: casePath,
        timesteps: caseInfo.times || [],
        times: caseInfo.times || [],
        fields: this.createFieldsStructure(caseInfo.fields || []),
        metadata: {
          fileName: casePath.split('/').pop(),
          n_times: caseInfo.n_times || 0,
          rendering_mode: 'geometry',
          format: 'OpenFOAM',  // Also set in metadata for consistency
          vertices: geometryResult.vertices,
          triangles: geometryResult.triangles,
          scalarField: geometryResult.scalarField
        }
      };

      this.currentData = data;
      this.totalTimesteps = data.times.length || 1;
      this.currentTimestep = Math.max(this.totalTimesteps - 1, 0);

      // Update UI with metadata
      this.updateInfo(data.metadata);
      if (this.controls?.updateTimeControls) {
        this.controls.updateTimeControls(this.currentTimestep, data.times.length, data.times);
      }
      this.updateFieldControls(data.fields, { force: true });

      // Set colormap variable to match what was loaded
      if (this.controls?.elements?.colormapVariable) {
        this.controls.elements.colormapVariable.value = this.geometryParams.colorField;
      }

      // Set initial colormap range from backend scalar range
      if (this.engine.lastGeometryScalarRange && this.controls?.elements) {
        const range = this.engine.lastGeometryScalarRange;
        this.controls.elements.colormapMin.value = range.min.toFixed(3);
        this.controls.elements.colormapMax.value = range.max.toFixed(3);

        // Store in colormap state
        this.colormap.min = range.min;
        this.colormap.max = range.max;
        this.colormap.preset = initialPreset;
      }

      // Update mesh parts UI with boundaries data from backend
      if (geometryResult.boundaries && Object.keys(geometryResult.boundaries).length > 0) {
        console.log('[Backend Load] Updating mesh parts with boundaries from backend:', geometryResult.boundaries);
        this.updateMeshPartsUI(geometryResult.boundaries);
      }

      // Store field data for probe tool - scalar data from backend
      if (this.engine.lastGeometryScalars && this.geometryParams?.colorField) {
        const currentField = this.geometryParams.colorField;
        // Store the scalar data for the current field (data.fields was created above)
        data.fields[currentField] = this.engine.lastGeometryScalars;
        console.log('[Backend Load] Stored field data for probe:', currentField, 'values:', this.engine.lastGeometryScalars.length);
      }

      const casePathLabel = casePath.split('/').pop();
      Utils.notify(`Loaded OpenFOAM case: ${casePathLabel} (${geometryResult.vertices} vertices)`, 'success');

      // Update case name in toolbar (use cached display name if available)
      this.updateCaseNameDisplay(this.caseDisplayName || casePathLabel);

      // Debug summary
      console.log('='.repeat(80));
      console.log('[Backend Load Complete] Summary:');
      console.log('  Rendering Mode:', this.renderingMode);
      console.log('  Field:', this.geometryParams.colorField);
      console.log('  Vertices:', geometryResult.vertices);
      console.log('  Scalar Range:', this.engine.lastGeometryScalarRange);
      console.log('  Colormap:', {
        preset: this.colormap.preset,
        min: this.colormap.min,
        max: this.colormap.max
      });
      console.log('  Has Scalar Data:', !!this.engine.lastGeometryScalars);
      console.log('  Num Scalars:', this.engine.lastGeometryScalars?.length);
      console.log('  Boundaries:', geometryResult.boundaries ? Object.keys(geometryResult.boundaries).length : 0);
      console.log('='.repeat(80));

      window.loadingProgress.complete('Case loaded successfully');

      // Apply any pending view state from collaboration (for late joiners)
      if (this.viewLockManager && this.viewLockManager.applyPendingViewState) {
        this.viewLockManager.applyPendingViewState();
      }

      // Start preloading other timesteps in the background
      if (this.availableTimes && this.availableTimes.length > 1) {
        // Small delay to let the UI settle before starting background preloads
        setTimeout(() => {
          console.log('[Viewer] Starting timestep preloading...');
          this.startTimestepPreloading();
        }, 1000);
      }
    } catch (err) {
      console.error('Failed to load OpenFOAM case:', err);
      window.loadingProgress.error(err.message);
      Utils.notify(`Failed to load OpenFOAM case: ${err.message}`, 'error');
      throw err; // Re-throw so caller knows loading failed
    }
  }

  createFieldsStructure(fieldNames) {
    const fields = {};
    fieldNames.forEach(name => {
      fields[name] = []; // Empty array - actual data processed on backend
    });
    return fields;
  }

  /**
   * Reload geometry with updated parameters (for geometry-based rendering)
   * Used when field, time, or representation changes
   */
  async reloadGeometry(updates = {}) {
    if (this.renderingMode !== 'geometry' || !this.geometryParams) {
      console.warn('[Viewer] Not in geometry rendering mode, cannot reload');
      return;
    }

    // Bump reload generation — any in-flight reload with a stale generation will be discarded
    this._reloadGeneration = (this._reloadGeneration || 0) + 1;
    const myGeneration = this._reloadGeneration;

    // Wait for any in-flight reload to finish before starting this one
    if (this._reloadGeometryPromise) {
      try {
        await this._reloadGeometryPromise;
      } catch (_) {
        // Previous reload failed, continue with this one
      }
    }

    // If a newer reload was queued while we waited, bail out — it supersedes us
    if (myGeneration !== this._reloadGeneration) {
      console.log('[Viewer] Skipping stale reload (generation', myGeneration, 'vs', this._reloadGeneration + ')');
      return;
    }

    // Wrap this reload in a tracked promise
    const doReload = async () => {

    // Keep preloading paused for the entire duration of this reload (not just 2 seconds)
    if (this.geometryCache.preloadResumeTimeout) {
      clearTimeout(this.geometryCache.preloadResumeTimeout);
    }
    this.geometryCache.preloadPaused = true;

    try {
      // Update geometry parameters
      this.geometryParams = {
        ...this.geometryParams,
        ...updates
      };

      console.log('[Viewer] Reloading geometry with params:', this.geometryParams);

      // Determine what's being updated and show appropriate loading message
      let loadingMessage = 'Reloading Visualization';
      let actionDetails = [];

      if (updates.colorField) {
        actionDetails.push(`Loading field: ${updates.colorField}`);
        loadingMessage = `Loading Field: ${updates.colorField}`;
      }
      if (updates.time !== undefined) {
        actionDetails.push(`Time: ${updates.time}`);
        if (!updates.colorField) {
          loadingMessage = `Loading Time: ${updates.time}`;
        }
      }
      if (updates.representation) {
        actionDetails.push(`Representation: ${updates.representation}`);
        if (!updates.colorField && updates.time === undefined) {
          loadingMessage = `Changing Representation`;
        }
      }
      if (updates.selectedParts) {
        actionDetails.push(`Selected parts changed`);
        if (!updates.colorField && updates.time === undefined && !updates.representation) {
          loadingMessage = `Updating Mesh Parts`;
        }
      }

      // Show loading indicator - use inline spinner for time-only changes
      const isTimeOnlyChange = updates.time !== undefined && !updates.colorField && !updates.representation && !updates.selectedParts;
      const timeSpinner = document.getElementById('timeLoadingSpinner');

      if (isTimeOnlyChange && timeSpinner) {
        timeSpinner.classList.add('active');
      } else {
        window.loadingProgress.show(loadingMessage);
        window.loadingProgress.updateProgress(30, 'Backend processing with VTK...');
      }

      // Save current mesh parts visibility BEFORE reload (will be restored after)
      const savedVisibleParts = this.meshParts?.visibleParts
        ? new Set(this.meshParts.visibleParts)
        : null;
      console.log('[reloadGeometry] Saving visible parts:', savedVisibleParts ? Array.from(savedVisibleParts) : 'none');

      // Get colormap settings - but if field changed OR (time changed AND autoRange enabled), let backend auto-range
      let colormapSettings;
      let useAutoRange = false;
      if (updates.colorField) {
        // Field changed - only send preset, let backend auto-range the new field
        const preset = this.controls?.elements?.colormapPreset?.value || this.colormap.preset || 'jet';
        colormapSettings = { preset };
        useAutoRange = true;
        console.log('[reloadGeometry] Field changed - using auto-range, preset:', preset);
      } else if (updates.time !== undefined && this.colormap.autoRange) {
        // Time changed AND autoRange is enabled - let backend auto-range for new timestep
        const preset = this.controls?.elements?.colormapPreset?.value || this.colormap.preset || 'jet';
        colormapSettings = { preset };
        useAutoRange = true;
        console.log('[reloadGeometry] Time changed with autoRange - using auto-range, preset:', preset);
      } else {
        // Field and time unchanged (or autoRange disabled) - use current min/max from UI
        colormapSettings = this.getColormapSettings();
        console.log('[reloadGeometry] Using current range:', colormapSettings);
      }

      // Check cache for timestep changes (memory first, then IndexedDB)
      let geometryResult;
      const cacheKey = { time: this.geometryParams.time, colorField: this.geometryParams.colorField };
      const cachedData = await this.getCachedGeometryAsync(cacheKey);

      if (cachedData) {
        // Use cached geometry - much faster!
        console.log('[Viewer] Using cached geometry for timestep');
        if (!isTimeOnlyChange) window.loadingProgress.updateProgress(50, 'Loading from cache...');

        // If autoRange is enabled, use the cached scalar range for colormap
        if (useAutoRange && cachedData.scalarRange) {
          colormapSettings = {
            min: cachedData.scalarRange.min,
            max: cachedData.scalarRange.max,
            preset: colormapSettings.preset
          };
          console.log('[Viewer] Using cached scalar range for autoRange:', colormapSettings);
        }

        geometryResult = await this.engine.renderCachedGeometry(cachedData, {
          colormapSettings,
          preserveCamera: true
        });

        // Update colormap UI with cached scalar range if autoRange
        if (useAutoRange && cachedData.scalarRange && this.controls?.elements) {
          this.controls.elements.colormapMin.value = cachedData.scalarRange.min.toFixed(3);
          this.controls.elements.colormapMax.value = cachedData.scalarRange.max.toFixed(3);
          this.colormap.min = cachedData.scalarRange.min;
          this.colormap.max = cachedData.scalarRange.max;
        }
      } else {
        // Fetch from backend
        geometryResult = await this.engine.loadGeometryFromBackend({
          ...this.geometryParams,
          colormapSettings,
          preserveCamera: true
        });

        // Cache the result for future use using raw backend data
        if (updates.time !== undefined && this.engine.lastGeometryRawData) {
          this.cacheGeometry(cacheKey, {
            geometryData: this.engine.lastGeometryRawData,
            scalars: this.engine.lastGeometryScalars,
            scalarRange: this.engine.lastGeometryScalarRange,
            boundaries: geometryResult.boundaries,
            available_vector_fields: geometryResult.available_vector_fields,
            field_metadata: geometryResult.field_metadata  // Cache field metadata too
          });
        }

        // Update available vector fields if provided
        if (geometryResult.available_vector_fields && geometryResult.available_vector_fields.length > 0) {
          this.availableVectorFields = geometryResult.available_vector_fields;
        }
      }

      console.log('[Viewer] Geometry reloaded:', geometryResult);

      // Handle throttled response (backend was busy)
      if (geometryResult?.throttled) {
        console.warn('[Viewer] Geometry request was throttled, retrying...');
        if (!isTimeOnlyChange) window.loadingProgress.hide();
        // Retry once after a short delay
        await new Promise(resolve => setTimeout(resolve, 1000));
        geometryResult = await this.engine.loadGeometryFromBackend({
          ...this.geometryParams,
          colormapSettings,
          preserveCamera: true
        });
        if (geometryResult?.throttled) {
          Utils.notify('Server busy, please try again', 'warning');
          return geometryResult;
        }
      }

      if (!isTimeOnlyChange) window.loadingProgress.updateProgress(90, 'Rendering...');

      // Update metadata
      if (this.currentData && this.currentData.metadata) {
        this.currentData.metadata.vertices = geometryResult.vertices;
        this.currentData.metadata.triangles = geometryResult.triangles;
        this.currentData.metadata.scalarField = geometryResult.scalarField;
        this.updateInfo(this.currentData.metadata);
      }

      // Update mesh parts UI with fresh boundaries data from backend
      if (geometryResult.boundaries && Object.keys(geometryResult.boundaries).length > 0) {
        console.log('[Viewer] Updating mesh parts with boundaries from backend:', geometryResult.boundaries);
        this.updateMeshPartsUI(geometryResult.boundaries);
      }

      // Update colormap range from backend scalar range (critical for field changes!)
      if (this.engine.lastGeometryScalarRange && this.controls?.elements) {
        const range = this.engine.lastGeometryScalarRange;
        console.log('[Viewer] Updating colormap range from backend:', range);

        this.controls.elements.colormapMin.value = range.min.toFixed(3);
        this.controls.elements.colormapMax.value = range.max.toFixed(3);

        // Store in colormap state
        this.colormap.min = range.min;
        this.colormap.max = range.max;

        console.log('[Viewer] Colormap range updated to match new field');
      }

      // Update field data for probe tool - store scalar data from backend
      if (this.engine.lastGeometryScalars && this.geometryParams?.colorField) {
        const currentField = this.geometryParams.colorField;

        // Initialize currentData and fields object if they don't exist
        if (!this.currentData) {
          this.currentData = { metadata: {}, fields: {} };
        }
        if (!this.currentData.fields) {
          this.currentData.fields = {};
        }

        // Store the scalar data for the current field
        this.currentData.fields[currentField] = this.engine.lastGeometryScalars;
        console.log('[Viewer] Updated field data for probe:', currentField, 'values:', this.engine.lastGeometryScalars.length);
      }

      // Reapply representation setting after geometry reload (backend defaults to surface)
      if (this.displaySettings?.representation && this.displaySettings.representation !== 'surface') {
        console.log('[Viewer] Reapplying representation after reload:', this.displaySettings.representation);
        this.updateRepresentation();
      }

      // Restore mesh parts visibility after reload
      // Note: we restore even if savedVisibleParts is empty (user intentionally hid all parts)
      if (savedVisibleParts) {
        console.log('[Viewer] Restoring mesh parts visibility:', Array.from(savedVisibleParts));
        this.applyMeshPartsVisibility(savedVisibleParts);
      }

      // Update slice widget if enabled (refresh vectors/contours/streamlines for new timestep)
      // Skip during caching/preloading to avoid interfering with manual Apply button clicks
      const isCaching = this.geometryCache?.preloadQueue?.length > 0 || this.geometryCache?.isPreloading;
      if (updates.time !== undefined && this.filters?.sliceWidget?.enabled && !isCaching) {
        console.log('[Viewer] Timestep changed - refreshing slice overlays');
        // Set force flags to regenerate any active overlays
        const sliceWidget = this.filters.sliceWidget;
        const hasContours = document.getElementById('sliceShowContours')?.checked;
        const hasStreamlines = document.getElementById('sliceShowStreamlines')?.checked;
        const hasVectors = document.getElementById('sliceShowVectors')?.checked;

        if (hasContours || hasStreamlines || hasVectors) {
          // Set force flags for active overlays
          if (hasContours) sliceWidget.forceContoursGeneration = true;
          if (hasStreamlines) sliceWidget.forceStreamlinesGeneration = true;
          if (hasVectors) sliceWidget.forceVectorsGeneration = true;

          // Trigger slice update
          sliceWidget.updateSliceBackend().catch(err => {
            console.error('[Viewer] Failed to refresh slice for new timestep:', err);
          });
        }
      } else if (isCaching && updates.time !== undefined) {
        console.log('[Viewer] Skipping slice overlay refresh during caching');
      }

      if (isTimeOnlyChange && timeSpinner) {
        timeSpinner.classList.remove('active');
      } else {
        window.loadingProgress.complete('Reload complete');
      }

      return geometryResult;

    } catch (error) {
      console.error('[Viewer] Failed to reload geometry:', error);
      if (isTimeOnlyChange && timeSpinner) {
        timeSpinner.classList.remove('active');
      } else {
        window.loadingProgress.error(error.message);
      }
      Utils.notify(`Failed to reload geometry: ${error.message}`, 'error');
      throw error;
    }

    }; // end doReload

    this._reloadGeometryPromise = doReload();
    try {
      return await this._reloadGeometryPromise;
    } finally {
      this._reloadGeometryPromise = null;
      // Resume preloading now that the reload is done (if not playing)
      if (!this.isPlaying && !this.geometryCache.preloadAborted) {
        this.geometryCache.preloadPaused = false;
        if (this.geometryCache.preloadQueue?.length > 0) {
          setTimeout(() => this.processPreloadQueue(), 500);
        }
      }
    }
  }

  /**
   * Generate a cache key for geometry based on parameters
   */
  getGeometryCacheKey(params) {
    const { time, colorField } = params;
    return `t${time}_f${colorField}`;
  }

  /**
   * Get cached geometry if available (checks memory first, then IndexedDB)
   */
  getCachedGeometry(params) {
    const key = this.getGeometryCacheKey(params);
    const cached = this.geometryCache.cache.get(key);
    if (cached) {
      console.log('[GeometryCache] Memory cache HIT for:', key);
      return cached;
    }
    console.log('[GeometryCache] Memory cache MISS for:', key);
    return null;
  }

  /**
   * Get cached geometry async (checks memory first, then IndexedDB)
   */
  async getCachedGeometryAsync(params) {
    // Check memory cache first
    const memCached = this.getCachedGeometry(params);
    if (memCached) {
      return memCached;
    }

    // Check persistent IndexedDB cache
    if (window.geometryCache) {
      try {
        const persistentParams = {
          casePath: this.geometryParams?.casePath || this.currentCasePath,
          time: params.time,
          colorField: params.colorField,
          selectedParts: this.geometryParams?.selectedParts,
          cacheKey: this.caseDataCacheKey  // For cache invalidation check
        };
        const cached = await window.geometryCache.get(persistentParams);
        if (cached) {
          console.log('[GeometryCache] IndexedDB cache HIT');
          // Also store in memory cache for faster subsequent access
          this.cacheGeometry(params, cached, false); // false = don't persist again
          return cached;
        }
      } catch (err) {
        console.warn('[GeometryCache] IndexedDB read failed:', err);
      }
    }

    return null;
  }

  /**
   * Store geometry in cache (memory + IndexedDB)
   */
  cacheGeometry(params, data, persist = true) {
    const key = this.getGeometryCacheKey(params);

    // Enforce memory cache size limit (LRU eviction)
    if (this.geometryCache.cache.size >= this.geometryCache.maxSize) {
      // Remove oldest entry from memory only
      const oldestKey = this.geometryCache.cache.keys().next().value;
      this.geometryCache.cache.delete(oldestKey);
      console.log('[GeometryCache] Evicted from memory:', oldestKey);
    }

    this.geometryCache.cache.set(key, {
      ...data,
      cachedAt: Date.now()
    });
    console.log('[GeometryCache] Cached in memory:', key, 'Total:', this.geometryCache.cache.size);

    // Also persist to IndexedDB for cross-session caching
    if (persist && window.geometryCache) {
      const persistentParams = {
        casePath: this.geometryParams?.casePath || this.currentCasePath,
        time: params.time,
        colorField: params.colorField,
        selectedParts: this.geometryParams?.selectedParts,
        cacheKey: this.caseDataCacheKey  // Store for later validation
      };
      window.geometryCache.set(persistentParams, data).catch(err => {
        console.warn('[GeometryCache] IndexedDB write failed:', err);
      });
    }
  }

  /**
   * Clear geometry cache (memory + optionally IndexedDB)
   */
  clearGeometryCache(clearPersistent = false) {
    this.geometryCache.cache.clear();
    this.geometryCache.preloadQueue = [];
    this.geometryCache.preloadAborted = true;
    console.log('[GeometryCache] Memory cache cleared');

    if (clearPersistent && window.geometryCache) {
      const casePath = this.geometryParams?.casePath || this.currentCasePath;
      if (casePath) {
        window.geometryCache.clearCase(casePath).catch(console.warn);
      }
    }
  }

  /**
   * Start preloading timesteps in the background
   * Preloads from current timestep outward (current+1, current-1, current+2, current-2, etc.)
   */
  startTimestepPreloading() {
    if (!this.availableTimes || this.availableTimes.length <= 1) {
      console.log('[GeometryCache] No timesteps to preload');
      return;
    }

    // Build preload queue: prioritize timesteps closest to current
    const currentIdx = this.currentTimestep;
    const totalSteps = this.availableTimes.length;
    const queue = [];

    // Alternate between next and previous timesteps
    for (let offset = 1; offset < totalSteps; offset++) {
      const nextIdx = currentIdx + offset;
      const prevIdx = currentIdx - offset;

      if (nextIdx < totalSteps) {
        queue.push(nextIdx);
      }
      if (prevIdx >= 0) {
        queue.push(prevIdx);
      }
    }

    // Filter out already cached timesteps
    const colorField = this.geometryParams?.colorField || 'p';
    this.geometryCache.preloadQueue = queue.filter(idx => {
      const time = this.availableTimes[idx];
      const key = this.getGeometryCacheKey({ time, colorField });
      return !this.geometryCache.cache.has(key);
    });

    console.log('[GeometryCache] Starting preload, queue:', this.geometryCache.preloadQueue.length, 'timesteps');

    this.geometryCache.preloadAborted = false;
    this.processPreloadQueue();
  }

  /**
   * Process the preload queue one timestep at a time
   */
  async processPreloadQueue() {
    if (this.geometryCache.isPreloading) {
      return; // Already preloading
    }

    if (this.geometryCache.preloadAborted) {
      console.log('[GeometryCache] Preloading aborted');
      return;
    }

    // Check if preloading is paused for priority requests
    if (this.geometryCache.preloadPaused) {
      console.log('[GeometryCache] Preloading paused for priority request');
      return;
    }

    if (this.geometryCache.preloadQueue.length === 0) {
      console.log('[GeometryCache] Preloading complete');
      return;
    }

    // Check if cache is full
    if (this.geometryCache.cache.size >= this.geometryCache.maxSize) {
      console.log('[GeometryCache] Cache full, stopping preload');
      return;
    }

    this.geometryCache.isPreloading = true;
    const timestepIdx = this.geometryCache.preloadQueue.shift();
    const time = this.availableTimes[timestepIdx];
    const colorField = this.geometryParams?.colorField || 'p';

    console.log('[GeometryCache] Preloading timestep:', timestepIdx, 'time:', time);

    try {
      // First check if already in IndexedDB (from previous session)
      const cacheKey = { time, colorField };
      const existingCached = await this.getCachedGeometryAsync(cacheKey);

      if (existingCached) {
        console.log('[GeometryCache] Found in IndexedDB, skipping fetch:', time);
      } else {
        // Fetch geometry from backend without displaying it
        const result = await this.engine.fetchGeometryFromBackend({
          ...this.geometryParams,
          time: time,
          colormapSettings: this.getColormapSettings(),
          priority: 'low'  // Mark as low priority for backend rate limiting
        });

        // Cache the result (memory + IndexedDB)
        if (result && !this.geometryCache.preloadAborted) {
          this.cacheGeometry(cacheKey, {
            geometryData: result.geometryData,
            scalars: result.scalars,
            scalarRange: result.scalarRange,
            boundaries: result.boundaries,
            available_vector_fields: result.available_vector_fields,
            field_metadata: result.field_metadata  // Cache field metadata too
          });
        }
      }
    } catch (error) {
      console.warn('[GeometryCache] Failed to preload timestep:', timestepIdx, error.message);
    }

    this.geometryCache.isPreloading = false;

    // Use configurable delay between preloads (increased when following sync)
    if (!this.geometryCache.preloadAborted && !this.geometryCache.preloadPaused && this.geometryCache.preloadQueue.length > 0) {
      const delay = this.geometryCache.preloadDelay || 250;
      setTimeout(() => this.processPreloadQueue(), delay);
    }
  }

  /**
   * Abort any ongoing preloading
   */
  abortPreloading() {
    this.geometryCache.preloadAborted = true;
    this.geometryCache.preloadQueue = [];
    console.log('[GeometryCache] Preloading aborted');
  }

  /**
   * Pause preloading temporarily for priority requests (e.g., user interactions)
   * Automatically resumes after a delay
   */
  pausePreloadingForPriority() {
    // Clear any pending resume timeout
    if (this.geometryCache.preloadResumeTimeout) {
      clearTimeout(this.geometryCache.preloadResumeTimeout);
    }

    this.geometryCache.preloadPaused = true;

    // Resume preloading after 2 seconds of no priority requests
    this.geometryCache.preloadResumeTimeout = setTimeout(() => {
      this.resumePreloading();
    }, 2000);
  }

  /**
   * Resume preloading after priority requests are done
   */
  resumePreloading() {
    if (this.geometryCache.preloadPaused) {
      this.geometryCache.preloadPaused = false;
      console.log('[GeometryCache] Resuming preloading');

      // Continue processing the queue if there are items left
      if (this.geometryCache.preloadQueue.length > 0 && !this.geometryCache.preloadAborted) {
        setTimeout(() => this.processPreloadQueue(), 500);
      }
    }
  }

  async loadFluentCase(casePath) {
    try {
      console.log('Loading Fluent case (geometry-based rendering):', casePath);
      this.currentCasePath = casePath;

      // Initialize progress with stages
      window.loadingProgress.show('Loading Fluent Case');
      window.loadingProgress.initStages([
        'Reading case metadata...',
        'Extracting geometry on backend...',
        'Rendering in browser...',
        'Finalizing...'
      ]);

      // Step 1: Get case info from Fluent case-info endpoint (lightweight - no data transfer)
      window.loadingProgress.updateProgress(20, 'Getting case info');

      const token = window.Auth?.getToken() || localStorage.getItem('coandacloud_token');
      const apiBase = this.openfoamLoader?.apiBase ||
        (window.location.origin.includes('localhost') ? 'http://localhost:8082' : '');

      // Use lightweight case-info endpoint that only returns metadata
      const encodedPath = encodeURIComponent(casePath);
      const response = await fetch(`${apiBase}/api/fluent/case-info/${encodedPath}`, {
        method: 'GET',
        headers: {
          'Authorization': token ? `Bearer ${token}` : ''
        }
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.detail || `Failed to get Fluent case info: ${response.status}`);
      }

      const caseInfo = await response.json();
      console.log('Fluent case info loaded:', caseInfo);

      // Extract fields and times from case info
      const fields = caseInfo.fields || [];
      const times = caseInfo.times || [];

      // Store available fields and times for geometry-based rendering
      this.availableFields = fields;
      this.availableTimes = times;
      this.fieldMetadata = caseInfo.field_metadata || {};  // Store component info for calculator
      this.caseDataCacheKey = caseInfo.cache_key || null;
      console.log('[Viewer] Fluent cache key:', this.caseDataCacheKey);
      console.log('[Viewer] Fluent field metadata:', this.fieldMetadata);
      console.log('[Viewer] Fluent timesteps:', times.length > 0 ? `${times.length} timesteps (${times[0]} to ${times[times.length-1]})` : 'none');
      this.renderingMode = 'geometry';

      // Use case directory for multi-timestep loading, or case_path for single file
      const caseDir = caseInfo.case_dir || casePath;

      // Initialize rendering parameters
      this.geometryParams = {
        casePath: caseDir,  // Use directory for timestep switching
        dataSource: 'fluent',  // Store data source for reload operations
        time: this.availableTimes.length > 0 ? this.availableTimes[this.availableTimes.length - 1] : null,
        colorField: this.availableFields.includes('PRESSURE') ? 'PRESSURE' :
                    (this.availableFields.includes('pressure') ? 'pressure' : (this.availableFields[0] || 'pressure')),
        geometryType: 'surface',
        representation: 'surface',
        selectedParts: null,
        cacheKey: this.caseDataCacheKey
      };

      // Step 2: Check persistent cache first, then load from backend if needed
      const initialPreset = this.controls?.elements?.colormapPreset?.value || this.colormap.preset || 'jet';
      const cacheKey = { time: this.geometryParams.time, colorField: this.geometryParams.colorField };

      let geometryResult;
      const cachedData = await this.getCachedGeometryAsync(cacheKey);

      if (cachedData) {
        // Use cached geometry from IndexedDB
        console.log('[Fluent] Using cached geometry from IndexedDB');
        window.loadingProgress.updateProgress(40, 'Loading from cache...');

        geometryResult = await this.engine.renderCachedGeometry(cachedData, {
          colormapSettings: {
            min: cachedData.scalarRange?.min,
            max: cachedData.scalarRange?.max,
            preset: initialPreset
          },
          preserveCamera: false
        });

        // Restore scalar range for colormap UI
        if (cachedData.scalarRange) {
          this.engine.lastGeometryScalarRange = cachedData.scalarRange;
        }
        if (cachedData.scalars) {
          this.engine.lastGeometryScalars = cachedData.scalars;
        }
      } else {
        // Fetch from backend (VTK processing on server)
        window.loadingProgress.updateProgress(40, 'Backend extracting geometry with VTK');

        geometryResult = await this.engine.loadGeometryFromBackend({
          ...this.geometryParams,
          dataSource: 'fluent',  // Use Fluent geometry endpoint
          colormapSettings: { preset: initialPreset }
        });

        // Cache the result for future sessions
        if (this.engine.lastGeometryRawData) {
          this.cacheGeometry(cacheKey, {
            geometryData: this.engine.lastGeometryRawData,
            scalars: this.engine.lastGeometryScalars,
            scalarRange: this.engine.lastGeometryScalarRange,
            boundaries: geometryResult.boundaries,
            available_vector_fields: geometryResult.available_vector_fields,
            field_metadata: geometryResult.field_metadata  // Cache field metadata too
          });
          console.log('[Fluent] Geometry cached for future sessions');
        }
      }

      console.log('Geometry loaded:', geometryResult, cachedData ? '(from cache)' : '(from backend)');

      // Store available vector fields
      const vectorFields = geometryResult.available_vector_fields || cachedData?.available_vector_fields;
      if (vectorFields && vectorFields.length > 0) {
        this.availableVectorFields = vectorFields;
        console.log('[Backend Load] Available vector fields:', this.availableVectorFields);
      } else {
        this.availableVectorFields = [];
      }

      // Merge field metadata from geometry response (actual component counts from numpy shapes)
      const geometryFieldMetadata = geometryResult.field_metadata || cachedData?.field_metadata;
      if (geometryFieldMetadata) {
        this.fieldMetadata = { ...this.fieldMetadata, ...geometryFieldMetadata };
        console.log('[Backend Load] Updated field metadata:', Object.keys(this.fieldMetadata).length, 'fields');
      }

      // Step 3: Create minimal data structure for UI
      window.loadingProgress.updateProgress(80, 'Setting up controls');

      // Calculate bounds from scene geometry
      const sceneBounds = this.engine.getSceneBounds?.();
      let gridBounds = null;
      if (sceneBounds) {
        gridBounds = {
          xmin: sceneBounds.min.x,
          xmax: sceneBounds.max.x,
          ymin: sceneBounds.min.y,
          ymax: sceneBounds.max.y,
          zmin: sceneBounds.min.z,
          zmax: sceneBounds.max.z
        };
        console.log('[Fluent] Calculated grid bounds from scene:', gridBounds);
      }

      const data = {
        type: 'simulation',
        source: 'Fluent',  // Required for dynamic endpoint routing
        case_path: casePath,
        timesteps: caseInfo.times || [],
        times: caseInfo.times || [],
        fields: this.createFieldsStructure(caseInfo.fields || []),
        grid: {
          bounds: gridBounds
        },
        metadata: {
          fileName: casePath.split('/').pop(),
          n_times: caseInfo.n_times || 0,
          rendering_mode: 'geometry',
          format: 'Fluent',
          vertices: geometryResult.vertices,
          triangles: geometryResult.triangles,
          scalarField: geometryResult.scalarField
        }
      };

      this.currentData = data;
      this.totalTimesteps = data.times.length || 1;
      this.currentTimestep = Math.max(this.totalTimesteps - 1, 0);

      // Update UI with metadata
      this.updateInfo(data.metadata);
      if (this.controls?.updateTimeControls) {
        this.controls.updateTimeControls(this.currentTimestep, data.times.length, data.times);
      }
      this.updateFieldControls(data.fields, { force: true });

      // Set colormap variable to match what was loaded
      if (this.controls?.elements?.colormapVariable) {
        this.controls.elements.colormapVariable.value = this.geometryParams.colorField;
      }

      // Set initial colormap range from backend scalar range
      if (this.engine.lastGeometryScalarRange && this.controls?.elements) {
        const range = this.engine.lastGeometryScalarRange;
        this.controls.elements.colormapMin.value = range.min.toFixed(3);
        this.controls.elements.colormapMax.value = range.max.toFixed(3);

        // Store in colormap state
        this.colormap.min = range.min;
        this.colormap.max = range.max;
        this.colormap.preset = initialPreset;
      }

      // Update mesh parts UI with boundaries data from backend
      if (geometryResult.boundaries && Object.keys(geometryResult.boundaries).length > 0) {
        console.log('[Backend Load] Updating mesh parts with boundaries from backend:', geometryResult.boundaries);
        this.updateMeshPartsUI(geometryResult.boundaries);
      }

      // Store field data for probe tool
      if (this.engine.lastGeometryScalars && this.geometryParams?.colorField) {
        const currentField = this.geometryParams.colorField;
        data.fields[currentField] = this.engine.lastGeometryScalars;
        console.log('[Backend Load] Stored field data for probe:', currentField, 'values:', this.engine.lastGeometryScalars.length);
      }

      const casePathLabel = casePath.split('/').pop();
      Utils.notify(`Loaded Fluent case: ${casePathLabel} (${geometryResult.vertices} vertices)`, 'success');

      // Update case name in toolbar (use cached display name if available)
      this.updateCaseNameDisplay(this.caseDisplayName || casePathLabel);

      // Debug summary
      console.log('='.repeat(80));
      console.log('[Backend Load Complete] Summary:');
      console.log('  Rendering Mode:', this.renderingMode);
      console.log('  Field:', this.geometryParams.colorField);
      console.log('  Vertices:', geometryResult.vertices);
      console.log('  Scalar Range:', this.engine.lastGeometryScalarRange);
      console.log('  Colormap:', {
        preset: this.colormap.preset,
        min: this.colormap.min,
        max: this.colormap.max
      });
      console.log('  Has Scalar Data:', !!this.engine.lastGeometryScalars);
      console.log('  Num Scalars:', this.engine.lastGeometryScalars?.length);
      console.log('  Boundaries:', geometryResult.boundaries ? Object.keys(geometryResult.boundaries).length : 0);
      console.log('='.repeat(80));

      window.loadingProgress.complete('Case loaded successfully');

      // Apply any pending view state from collaboration
      if (this.viewLockManager && this.viewLockManager.applyPendingViewState) {
        this.viewLockManager.applyPendingViewState();
      }
    } catch (err) {
      console.error('Failed to load Fluent case:', err);
      window.loadingProgress.error(err.message);
      Utils.notify(`Failed to load Fluent case: ${err.message}`, 'error');
      throw err; // Re-throw so caller knows loading failed
    }
  }

  async loadEnSightWithGeometry(casePath) {
    try {
      console.log('Loading EnSight case (geometry-based rendering):', casePath);
      this.currentCasePath = casePath;

      // Initialize progress with stages
      window.loadingProgress.show('Loading EnSight Case');
      window.loadingProgress.initStages([
        'Reading case metadata...',
        'Extracting geometry on backend...',
        'Rendering in browser...',
        'Finalizing...'
      ]);

      // Step 1: Get case info from EnSight case-info endpoint (lightweight - no data transfer)
      window.loadingProgress.updateProgress(20, 'Getting case info');

      const token = window.Auth?.getToken() || localStorage.getItem('coandacloud_token');
      const apiBase = this.openfoamLoader?.apiBase ||
        (window.location.origin.includes('localhost') ? 'http://localhost:8082' : '');

      // Use lightweight case-info endpoint that only returns metadata
      const encodedPath = encodeURIComponent(casePath);
      const response = await fetch(`${apiBase}/api/ensight/case-info/${encodedPath}`, {
        method: 'GET',
        headers: {
          'Authorization': token ? `Bearer ${token}` : ''
        }
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.detail || `Failed to get EnSight case info: ${response.status}`);
      }

      const caseInfo = await response.json();
      console.log('EnSight case info loaded:', caseInfo);

      // Extract fields and times from case info
      const fields = caseInfo.fields || [];
      const times = caseInfo.times || [];

      // Store available fields and times for geometry-based rendering
      this.availableFields = fields;
      this.availableTimes = times;
      this.fieldMetadata = caseInfo.field_metadata || {};  // Store component info for calculator
      this.caseDataCacheKey = caseInfo.cache_key || null;  // For cache invalidation
      console.log('[Viewer] EnSight cache key:', this.caseDataCacheKey);
      console.log('[Viewer] EnSight field metadata:', this.fieldMetadata);
      this.renderingMode = 'geometry';

      // Initialize rendering parameters
      this.geometryParams = {
        casePath: casePath,
        dataSource: 'ensight',  // Store data source for reload operations
        time: this.availableTimes.length > 0 ? this.availableTimes[this.availableTimes.length - 1] : null,
        colorField: this.availableFields.includes('p') ? 'p' : (this.availableFields[0] || 'U'),
        geometryType: 'surface',
        representation: 'surface',
        selectedParts: null,
        cacheKey: this.caseDataCacheKey  // For persistent cache validation
      };

      // Step 2: Check persistent cache first, then load from backend if needed
      const initialPreset = this.controls?.elements?.colormapPreset?.value || this.colormap.preset || 'jet';
      const cacheKey = { time: this.geometryParams.time, colorField: this.geometryParams.colorField };

      let geometryResult;
      const cachedData = await this.getCachedGeometryAsync(cacheKey);

      if (cachedData) {
        // Use cached geometry from IndexedDB - much faster!
        console.log('[EnSight] Using cached geometry from IndexedDB');
        window.loadingProgress.updateProgress(40, 'Loading from cache...');

        geometryResult = await this.engine.renderCachedGeometry(cachedData, {
          colormapSettings: {
            min: cachedData.scalarRange?.min,
            max: cachedData.scalarRange?.max,
            preset: initialPreset
          },
          preserveCamera: false
        });

        // Restore scalar range for colormap UI
        if (cachedData.scalarRange) {
          this.engine.lastGeometryScalarRange = cachedData.scalarRange;
        }
        if (cachedData.scalars) {
          this.engine.lastGeometryScalars = cachedData.scalars;
        }
      } else {
        // Fetch from backend (VTK processing on server)
        window.loadingProgress.updateProgress(40, 'Backend extracting geometry with VTK');

        geometryResult = await this.engine.loadGeometryFromBackend({
          ...this.geometryParams,
          dataSource: 'ensight',  // Use EnSight geometry endpoint
          colormapSettings: { preset: initialPreset } // Backend will use its scalar range for min/max
        });

        // Cache the result for future sessions using raw backend data
        if (this.engine.lastGeometryRawData) {
          this.cacheGeometry(cacheKey, {
            geometryData: this.engine.lastGeometryRawData,
            scalars: this.engine.lastGeometryScalars,
            scalarRange: this.engine.lastGeometryScalarRange,
            boundaries: geometryResult.boundaries,
            available_vector_fields: geometryResult.available_vector_fields,
            field_metadata: geometryResult.field_metadata  // Cache field metadata too
          });
          console.log('[EnSight] Geometry cached for future sessions');
        }
      }

      console.log('Geometry loaded:', geometryResult, cachedData ? '(from cache)' : '(from backend)');

      // Store available vector fields from geometry response or cached data (for velocity vectors dropdown)
      const vectorFields = geometryResult.available_vector_fields || cachedData?.available_vector_fields;
      if (vectorFields && vectorFields.length > 0) {
        this.availableVectorFields = vectorFields;
        console.log('[Backend Load] Available vector fields:', this.availableVectorFields);
      } else {
        console.log('[Backend Load] No available_vector_fields in geometry response');
        this.availableVectorFields = [];
      }

      // Merge field metadata from geometry response (actual component counts from numpy shapes)
      const geometryFieldMetadata = geometryResult.field_metadata || cachedData?.field_metadata;
      if (geometryFieldMetadata) {
        this.fieldMetadata = { ...this.fieldMetadata, ...geometryFieldMetadata };
        console.log('[Backend Load] Updated field metadata:', Object.keys(this.fieldMetadata).length, 'fields');
      }

      // Step 3: Create minimal data structure for UI
      window.loadingProgress.updateProgress(80, 'Setting up controls');

      // Calculate bounds from scene geometry
      const sceneBounds = this.engine.getSceneBounds?.();
      let gridBounds = null;
      if (sceneBounds) {
        gridBounds = {
          xmin: sceneBounds.min.x,
          xmax: sceneBounds.max.x,
          ymin: sceneBounds.min.y,
          ymax: sceneBounds.max.y,
          zmin: sceneBounds.min.z,
          zmax: sceneBounds.max.z
        };
        console.log('[EnSight] Calculated grid bounds from scene:', gridBounds);
      }

      const data = {
        type: 'simulation',
        source: 'EnSight',  // Required for dynamic endpoint routing
        case_path: casePath,
        timesteps: caseInfo.times || [],
        times: caseInfo.times || [],
        fields: this.createFieldsStructure(caseInfo.fields || []),
        grid: {
          bounds: gridBounds
        },
        metadata: {
          fileName: casePath.split('/').pop(),
          n_times: caseInfo.n_times || 0,
          rendering_mode: 'geometry',
          format: 'EnSight',  // Also set in metadata for consistency
          vertices: geometryResult.vertices,
          triangles: geometryResult.triangles,
          scalarField: geometryResult.scalarField
        }
      };

      this.currentData = data;
      this.totalTimesteps = data.times.length || 1;
      this.currentTimestep = Math.max(this.totalTimesteps - 1, 0);

      // Update UI with metadata
      this.updateInfo(data.metadata);
      if (this.controls?.updateTimeControls) {
        this.controls.updateTimeControls(this.currentTimestep, data.times.length, data.times);
      }
      this.updateFieldControls(data.fields, { force: true });

      // Set colormap variable to match what was loaded
      if (this.controls?.elements?.colormapVariable) {
        this.controls.elements.colormapVariable.value = this.geometryParams.colorField;
      }

      // Set initial colormap range from backend scalar range
      if (this.engine.lastGeometryScalarRange && this.controls?.elements) {
        const range = this.engine.lastGeometryScalarRange;
        this.controls.elements.colormapMin.value = range.min.toFixed(3);
        this.controls.elements.colormapMax.value = range.max.toFixed(3);

        // Store in colormap state
        this.colormap.min = range.min;
        this.colormap.max = range.max;
        this.colormap.preset = initialPreset;
      }

      // Update mesh parts UI with boundaries data from backend
      if (geometryResult.boundaries && Object.keys(geometryResult.boundaries).length > 0) {
        console.log('[Backend Load] Updating mesh parts with boundaries from backend:', geometryResult.boundaries);
        this.updateMeshPartsUI(geometryResult.boundaries);
      }

      // Store field data for probe tool - scalar data from backend
      if (this.engine.lastGeometryScalars && this.geometryParams?.colorField) {
        const currentField = this.geometryParams.colorField;
        // Store the scalar data for the current field (data.fields was created above)
        data.fields[currentField] = this.engine.lastGeometryScalars;
        console.log('[Backend Load] Stored field data for probe:', currentField, 'values:', this.engine.lastGeometryScalars.length);
      }

      const casePathLabel = casePath.split('/').pop();
      Utils.notify(`Loaded EnSight case: ${casePathLabel} (${geometryResult.vertices} vertices)`, 'success');

      // Update case name in toolbar (use cached display name if available)
      this.updateCaseNameDisplay(this.caseDisplayName || casePathLabel);

      // Debug summary
      console.log('='.repeat(80));
      console.log('[Backend Load Complete] Summary:');
      console.log('  Rendering Mode:', this.renderingMode);
      console.log('  Field:', this.geometryParams.colorField);
      console.log('  Vertices:', geometryResult.vertices);
      console.log('  Scalar Range:', this.engine.lastGeometryScalarRange);
      console.log('  Colormap:', {
        preset: this.colormap.preset,
        min: this.colormap.min,
        max: this.colormap.max
      });
      console.log('  Has Scalar Data:', !!this.engine.lastGeometryScalars);
      console.log('  Num Scalars:', this.engine.lastGeometryScalars?.length);
      console.log('  Boundaries:', geometryResult.boundaries ? Object.keys(geometryResult.boundaries).length : 0);
      console.log('='.repeat(80));

      window.loadingProgress.complete('Case loaded successfully');

      // Apply any pending view state from collaboration (for late joiners)
      if (this.viewLockManager && this.viewLockManager.applyPendingViewState) {
        this.viewLockManager.applyPendingViewState();
      }
    } catch (err) {
      console.error('Failed to load EnSight case:', err);
      window.loadingProgress.error(err.message);
      Utils.notify(`Failed to load EnSight case: ${err.message}`, 'error');
      throw err; // Re-throw so caller knows loading failed
    }
  }

  normalizeSimulationData(data) {
    if (!data || typeof data !== 'object') {
      return data;
    }

    if (data.type === 'simulation-data') {
      const rawTimeSeries = Array.isArray(data.time_series)
        ? data.time_series
        : Array.isArray(data.timeSeries)
          ? data.timeSeries
          : [];

      const timeSeries = rawTimeSeries.map(frame => {
        const timeValue = typeof frame.time === 'number'
          ? frame.time
          : parseFloat(frame.time);
        return {
          time: Number.isFinite(timeValue) ? timeValue : 0,
          fields: frame.fields || {}
        };
      });

      let times = Array.isArray(data.times)
        ? data.times
        : Array.isArray(data.timesteps)
          ? data.timesteps
          : [];

      if (Array.isArray(times)) {
        times = times.map(t => {
          const value = typeof t === 'number' ? t : parseFloat(t);
          return Number.isFinite(value) ? value : 0;
        });
      }

      if ((!times || times.length === 0) && timeSeries.length > 0) {
        times = timeSeries.map(frame => frame.time);
      }

      const timesteps = times.length > 0 ? times : timeSeries.map(frame => frame.time);

      let currentIndex = typeof data.currentTimestep === 'number'
        ? data.currentTimestep
        : (timeSeries.length > 0 ? timeSeries.length - 1 : 0);
      if (timeSeries.length > 0) {
        currentIndex = Math.min(Math.max(currentIndex, 0), timeSeries.length - 1);
      } else {
        currentIndex = 0;
      }

      const currentFields = timeSeries.length > 0
        ? (timeSeries[currentIndex].fields || {})
        : (data.fields || {});

      const availableFields = Array.isArray(data.available_fields)
        ? data.available_fields
        : Object.keys(currentFields);

      const metadataDefaults = {
        format: data.source || 'Simulation',
        fileName: data.case_path || data.metadata?.fileName || 'Simulation',
        timeSteps: timesteps.length,
        pointCount: data.grid?.points?.length || 0,
        faceCount: data.grid?.faces?.length || 0,
        fields: availableFields
      };

      return {
        ...data,
        type: 'simulation',
        times,
        timesteps,
        timeSeries,
        currentTimestep: currentIndex,
        currentTimeValue: timeSeries.length > 0 ? timeSeries[currentIndex].time : (timesteps[currentIndex] || 0),
        fields: currentFields,
        available_fields: availableFields,
        boundaries: data.grid?.boundaries || data.boundaries || {},
        metadata: { ...metadataDefaults, ...(data.metadata || {}) }
      };
    }

    return data;
  }

  displayData(data) {
    console.log('[displayData] Input data:', data);
    const normalizedData = this.normalizeSimulationData(data);
    console.log('[displayData] normalizedData:', normalizedData);
    console.log('[displayData] normalizedData.fields:', normalizedData?.fields);
    console.log('[displayData] normalizedData.available_fields:', normalizedData?.available_fields);
    this.stopPlayback();
    this.currentData = normalizedData;
    this.needsCameraFit = true;
    this.fieldControlsInitialized = false;
    this.colormap.autoRange = true;
    this.cancelStreamlinePick();

    if (this.filters && normalizedData?.fields) {
      console.log('[displayData] Calling updateAvailableFields with:', normalizedData.fields);
      this.filters.updateAvailableFields(normalizedData.fields);

      // Check if streamline plane widget should be enabled now that data is loaded
      setTimeout(() => {
        if (this.filters.streamlinePlaneWidget) {
          const seedTypeSelect = document.getElementById('streamlineSeedType');
          const showPlaneWidget = document.getElementById('streamlinePlaneShowWidget');
          if (seedTypeSelect?.value === 'plane' && showPlaneWidget?.checked) {
            console.log('[displayData] Auto-enabling streamline plane widget (seed type is plane and checkbox is checked)');
            this.filters.streamlinePlaneWidget.enable();
          }
        }
      }, 100);
    } else {
      console.log('[displayData] NOT calling updateAvailableFields. filters:', this.filters, 'normalizedData?.fields:', normalizedData?.fields);
    }

    if (normalizedData && normalizedData.type === 'simulation') {
      this.totalTimesteps = Array.isArray(normalizedData.timesteps)
        ? normalizedData.timesteps.length
        : (Array.isArray(normalizedData.timeSeries) ? normalizedData.timeSeries.length : 1);
      this.currentTimestep = typeof normalizedData.currentTimestep === 'number'
        ? normalizedData.currentTimestep
        : 0;
      this.playbackIntervalMs = this.calculatePlaybackInterval(normalizedData.timesteps || []);
    } else {
      this.totalTimesteps = 1;
      this.currentTimestep = 0;
    }

    // Clear existing objects and filters
    if (this.filters) {
      this.filters.clearAll();
    }
    this.engine.clearAll();
    this.clearStreamlineGizmos();

    if (!normalizedData) {
      this.currentData = null;
      this.selectedPipelineId = 'data-root';
      this.refreshPipelineList();
      if (this.controls?.updatePropertiesPanel) {
        this.controls.updatePropertiesPanel(null);
      }
      if (this.controls?.updateTimeControls) {
        this.controls.updateTimeControls(0, 0, []);
      }
      this.updateInfo({});
      this.updateStats();
      return;
    }

    if (normalizedData.type === 'mesh') {
      let object3D;

      if (normalizedData.parts && normalizedData.parts.length > 1) {
        // Multi-part mesh (e.g. OBJ with multiple groups, STEP assemblies)
        // Create a THREE.Group with individual meshes per part
        const rootGroup = new THREE.Group();
        rootGroup.name = 'main-mesh';

        normalizedData.parts.forEach(part => {
          const matOpts = {
            side: THREE.DoubleSide,
            flatShading: false
          };
          // Apply per-part color from STEP files
          if (part.color) {
            matOpts.color = new THREE.Color(part.color[0], part.color[1], part.color[2]);
          }
          const partMaterial = new THREE.MeshPhongMaterial(matOpts);
          const partMesh = new THREE.Mesh(part.geometry, partMaterial);
          partMesh.name = part.name;
          partMesh.userData.partName = part.name;
          rootGroup.add(partMesh);
        });

        object3D = rootGroup;
      } else {
        // Single mesh (STL, single-group OBJ, PLY, VTK, etc.)
        const material = new THREE.MeshPhongMaterial({
          vertexColors: normalizedData.colors ? true : false,
          side: THREE.DoubleSide,
          flatShading: false
        });
        object3D = new THREE.Mesh(normalizedData.geometry, material);
      }

      this.engine.addObject('main-mesh', object3D, {
        type: 'dataset',
        kind: 'mesh',
        name: normalizedData.metadata?.fileName || 'Mesh Dataset',
        icon: '🧊',
        removable: false
      });

      // Track the active mesh object ID
      this.meshParts.activeMeshObjectId = 'main-mesh';

      this.engine.fitCameraToObject(object3D);

      // Update mesh parts UI if boundaries are available
      const boundaries = normalizedData.boundaries || normalizedData.grid?.boundaries;
      if (boundaries && Object.keys(boundaries).length > 0) {
        this.updateMeshPartsUI(boundaries);
      }
    } else if (normalizedData.type === 'points') {
      const points = this.engine.createPointsFromData(
        normalizedData.vertices,
        normalizedData.colors,
        0.05
      );
      this.engine.addObject('main-points', points, {
        type: 'dataset',
        kind: 'points',
        name: normalizedData.metadata?.fileName || 'Point Dataset',
        icon: '🔹',
        removable: false
      });
      this.engine.fitCameraToObject(points);
    } else if (normalizedData.type === 'simulation') {
      this.displaySimulationData(normalizedData);
    }

    this.updateStats();
    this.selectedPipelineId = 'data-root';
    this.refreshPipelineList();
    this.selectPipelineItem('data-root');

    // Apply any pending view state from collaboration (for late joiners)
    if (this.viewLockManager && this.viewLockManager.applyPendingViewState) {
      this.viewLockManager.applyPendingViewState();
    }
  }

  displaySimulationData(data) {
    const hasFields = data.fields && Object.keys(data.fields).length > 0;
    const timeSeries = Array.isArray(data.timeSeries) ? data.timeSeries : [];
    const times = Array.isArray(data.timesteps)
      ? data.timesteps
      : (Array.isArray(data.times) ? data.times : timeSeries.map(frame => frame.time));

    // Update mesh parts UI if boundaries are available
    const boundaries = data.boundaries || data.grid?.boundaries;
    if (boundaries && Object.keys(boundaries).length > 0) {
      console.log('displaySimulationData: Updating mesh parts with', Object.keys(boundaries).length, 'boundaries');
      this.updateMeshPartsUI(boundaries);
    } else {
      console.warn('displaySimulationData: No boundaries found');
    }

    if (data.grid && data.grid.points && !hasFields) {
      const points = this.engine.createPointsFromData(data.grid.points, null, 0.04);
      this.engine.addObject('grid-points', points, this.getGridPointsMetadata());

      if (this.needsCameraFit) {
        this.engine.fitCameraToObject(points);
        this.needsCameraFit = false;
      }
    }

    if (hasFields && !this.fieldControlsInitialized) {
      this.updateFieldControls(data.fields, { force: true });
    }

    if (timeSeries.length > 0) {
      this.totalTimesteps = timeSeries.length;
      this.currentTimestep = Math.min(
        Math.max(typeof data.currentTimestep === 'number' ? data.currentTimestep : timeSeries.length - 1, 0),
        timeSeries.length - 1
      );
      this.playbackIntervalMs = this.calculatePlaybackInterval(times);
      this.setCurrentTimestep(this.currentTimestep, {
        updateControls: true,
        refreshVisualization: true,
        forceFieldControls: !this.fieldControlsInitialized
      });
    } else {
      this.totalTimesteps = hasFields ? 1 : 0;
      this.currentTimestep = 0;
      if (hasFields) {
        this.applyColormapToData();
      }
      this.playbackIntervalMs = this.calculatePlaybackInterval(times);
      this.controls.updateTimeControls(this.currentTimestep, this.totalTimesteps, times);
    }

    this.refreshPipelineList();
  }

  updateFieldControls(fields, options = {}) {
    if (!this.controls || !this.controls.elements) return;

    const { force = false } = options;
    const select = this.controls.elements.colormapVariable;
    const minInput = this.controls.elements.colormapMin;
    const maxInput = this.controls.elements.colormapMax;
    if (!select || !fields) return;

    const fieldNames = Object.keys(fields);
    const existingOptions = Array.from(select.options).map(opt => opt.value);
    const optionsMatch = existingOptions.length === fieldNames.length
      && fieldNames.every((name, idx) => existingOptions[idx] === name);

    if (force || !this.fieldControlsInitialized || !optionsMatch) {
      const previousSelection = select.value;
      select.innerHTML = '';

      fieldNames.forEach(name => {
        const option = document.createElement('option');
        option.value = name;
        option.textContent = this.formatFieldLabel(name);
        select.appendChild(option);
      });

      this.fieldControlsInitialized = true;

      if (previousSelection && fieldNames.includes(previousSelection)) {
        select.value = previousSelection;
      } else if (fieldNames.length > 0) {
        select.value = fieldNames[0];
      }

      // Also populate XY Plot and Integration field selectors
      this.updatePlotFieldSelectors(fieldNames);
    }

    const targetField = select.value || fieldNames[0];
    if (targetField && this.colormap.autoRange !== false) {
      const scalars = fields[targetField];
      // Only calculate range if we have actual scalar data (not empty array)
      if (scalars && Array.isArray(scalars) && scalars.length > 0 && minInput && maxInput) {
        const range = Colormap.calculateAutoRange(scalars);
        minInput.value = range.min.toFixed(3);
        maxInput.value = range.max.toFixed(3);
        this.colormap.variable = targetField;
        this.colormap.min = range.min;
        this.colormap.max = range.max;
        this.colormap.autoRange = true;
      }
    } else if (targetField) {
      this.colormap.variable = targetField;
    }
  }

  formatFieldLabel(name) {
    return name.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
  }

  /**
   * Update XY Plot and Integration field selectors
   * @param {Array<string>} fieldNames - Available field names
   */
  updatePlotFieldSelectors(fieldNames) {
    // Update XY Plot field selector
    const xyPlotField = document.getElementById('xyPlotField');
    if (xyPlotField) {
      const previousValue = xyPlotField.value;
      xyPlotField.innerHTML = '<option value="">Select field...</option>';
      fieldNames.forEach(name => {
        const option = document.createElement('option');
        option.value = name;
        option.textContent = this.formatFieldLabel(name);
        xyPlotField.appendChild(option);
      });
      // Restore previous selection if still valid
      if (previousValue && fieldNames.includes(previousValue)) {
        xyPlotField.value = previousValue;
      }
    }

    // Update Integration field selector
    const integrationField = document.getElementById('integrationField');
    if (integrationField) {
      const previousValue = integrationField.value;
      integrationField.innerHTML = '<option value="">Select field...</option>';
      fieldNames.forEach(name => {
        const option = document.createElement('option');
        option.value = name;
        option.textContent = this.formatFieldLabel(name);
        integrationField.appendChild(option);
      });
      // Restore previous selection if still valid
      if (previousValue && fieldNames.includes(previousValue)) {
        integrationField.value = previousValue;
      }
    }

    // Update Engineering Calculations field dropdowns (Mass Flow, Force Coefficients, Heat Transfer)
    if (this.filters?.refreshEngineeringDropdowns) {
      const vectorFields = this.availableVectorFields || [];
      this.filters.refreshEngineeringDropdowns(fieldNames, vectorFields);
    }

    // Update Gradient/Derivatives field selector
    const gradientField = document.getElementById('gradientField');
    if (gradientField) {
      const previousValue = gradientField.value;
      gradientField.innerHTML = '<option value="">Select field...</option>';
      fieldNames.forEach(name => {
        const option = document.createElement('option');
        option.value = name;
        option.textContent = this.formatFieldLabel(name);
        gradientField.appendChild(option);
      });
      // Restore previous selection if still valid
      if (previousValue && fieldNames.includes(previousValue)) {
        gradientField.value = previousValue;
      }
    }

    // Update Contour field selector (only scalar fields - contours don't work on vectors)
    const contourField = document.getElementById('contourField');
    if (contourField) {
      const previousValue = contourField.value;
      contourField.innerHTML = '<option value="">Select field...</option>';
      console.log('[Contour Filter] fieldMetadata available:', !!this.fieldMetadata, 'keys:', this.fieldMetadata ? Object.keys(this.fieldMetadata) : 'none');
      console.log('[Contour Filter] fieldNames:', fieldNames);
      // Filter to only scalar fields using actual component count from backend
      const scalarFields = fieldNames.filter(name => {
        // Use field metadata if available
        if (this.fieldMetadata && this.fieldMetadata[name]) {
          const metadata = this.fieldMetadata[name];
          console.log(`[Contour Filter] ${name}: ${metadata.components} components (${metadata.type})`);
          // Only include fields with 1 component (scalars)
          return metadata.components === 1;
        }
        // Fallback: if no metadata, include all fields (let backend validate)
        console.log(`[Contour Filter] ${name}: no metadata, including by default`);
        return true;
      });
      console.log('[Contour Filter] Scalar fields selected:', scalarFields);
      scalarFields.forEach(name => {
        const option = document.createElement('option');
        option.value = name;
        option.textContent = this.formatFieldLabel(name);
        contourField.appendChild(option);
      });
      // Restore previous selection if still valid
      if (previousValue && scalarFields.includes(previousValue)) {
        contourField.value = previousValue;
      }
    }

    // Update Volume Rendering field selector (only scalar fields - volume rendering doesn't work on vectors)
    const volumeField = document.getElementById('volumeField');
    if (volumeField) {
      const previousValue = volumeField.value;
      volumeField.innerHTML = '<option value="">Select field...</option>';
      // Filter to only scalar fields using actual component count from backend
      const scalarFields = fieldNames.filter(name => {
        // Use field metadata if available
        if (this.fieldMetadata && this.fieldMetadata[name]) {
          const metadata = this.fieldMetadata[name];
          // Only include fields with 1 component (scalars)
          return metadata.components === 1;
        }
        // Fallback: if no metadata, include all fields (let backend validate)
        return true;
      });
      scalarFields.forEach(name => {
        const option = document.createElement('option');
        option.value = name;
        option.textContent = this.formatFieldLabel(name);
        volumeField.appendChild(option);
      });
      // Restore previous selection if still valid
      if (previousValue && scalarFields.includes(previousValue)) {
        volumeField.value = previousValue;
      }
    }

    // Update Glyph vector field selector (prefer vector fields, but allow all)
    const glyphVectorField = document.getElementById('glyphVectorField');
    if (glyphVectorField) {
      const previousValue = glyphVectorField.value;
      glyphVectorField.innerHTML = '<option value="">Select field...</option>';
      // Separate vector and scalar fields using actual component count
      const isVectorField = (name) => {
        // Use field metadata if available
        if (this.fieldMetadata && this.fieldMetadata[name]) {
          const metadata = this.fieldMetadata[name];
          // Vector fields have 3 components
          return metadata.components === 3;
        }
        // Fallback: assume it's not a vector
        return false;
      };
      const vectors = fieldNames.filter(isVectorField);
      const others = fieldNames.filter(name => !isVectorField(name));

      // Add vector fields first (preferred)
      if (vectors.length > 0) {
        const vectorGroup = document.createElement('optgroup');
        vectorGroup.label = 'Vector Fields';
        vectors.forEach(name => {
          const option = document.createElement('option');
          option.value = name;
          option.textContent = this.formatFieldLabel(name);
          vectorGroup.appendChild(option);
        });
        glyphVectorField.appendChild(vectorGroup);
      }

      // Add other fields
      if (others.length > 0) {
        const otherGroup = document.createElement('optgroup');
        otherGroup.label = 'Other Fields';
        others.forEach(name => {
          const option = document.createElement('option');
          option.value = name;
          option.textContent = this.formatFieldLabel(name);
          otherGroup.appendChild(option);
        });
        glyphVectorField.appendChild(otherGroup);
      }

      // Restore previous selection if still valid
      if (previousValue && fieldNames.includes(previousValue)) {
        glyphVectorField.value = previousValue;
      }
    }

    // Update Calculator field reference selector
    const calculatorFieldReference = document.getElementById('calculatorFieldReference');
    if (calculatorFieldReference) {
      const previousValue = calculatorFieldReference.value;
      calculatorFieldReference.innerHTML = '<option value="">Available fields...</option>';
      fieldNames.forEach(name => {
        const option = document.createElement('option');
        option.value = name;
        option.textContent = this.formatFieldLabel(name);
        calculatorFieldReference.appendChild(option);
      });
      // Restore previous selection if still valid
      if (previousValue && fieldNames.includes(previousValue)) {
        calculatorFieldReference.value = previousValue;
      }
    }
  }

  setCurrentTimestep(index, options = {}) {
    if (!this.currentData || this.currentData.type !== 'simulation') {
      return;
    }

    // Pause preloading to give priority to this user-initiated request
    this.pausePreloadingForPriority();

    const {
      updateControls = true,
      refreshVisualization = true,
      forceFieldControls = false
    } = options;

    const timeSeries = Array.isArray(this.currentData.timeSeries) ? this.currentData.timeSeries : [];
    const times = Array.isArray(this.currentData.timesteps)
      ? this.currentData.timesteps
      : timeSeries.map(frame => frame.time);

    if (timeSeries.length === 0) {
      this.totalTimesteps = times.length > 0 ? times.length : 1;
      if (times.length > 1) {
        this.playbackIntervalMs = this.calculatePlaybackInterval(times);
      }
      this.currentTimestep = Math.min(Math.max(index, 0), this.totalTimesteps - 1);
      this.currentData.currentTimestep = this.currentTimestep;
      this.currentData.currentTimeValue = times.length > 0 ? times[this.currentTimestep] : 0;
      if (this.currentData.metadata) {
        this.currentData.metadata.currentTime = this.currentData.currentTimeValue;
      }

      if (forceFieldControls || !this.fieldControlsInitialized) {
        this.updateFieldControls(this.currentData.fields || {}, { force: true });
      }

      if (updateControls && this.controls?.updateTimeControls) {
        this.controls.updateTimeControls(
          this.currentTimestep,
          this.totalTimesteps || 0,
          times
        );
      }

      if (refreshVisualization) {
        // For geometry-based rendering, reload with new time
        if (this.renderingMode === 'geometry' && this.availableTimes) {
          const newTime = this.availableTimes[this.currentTimestep];
          if (newTime !== undefined && newTime !== this.geometryParams?.time) {
            console.log('[Viewer] Time changed to:', newTime);
            this.reloadGeometry({ time: newTime }).catch(err => {
              console.error('[Viewer] Failed to reload geometry for new time:', err);
            });
          }
        } else {
          // For traditional rendering
          this.applyColormapToData();
          this.scheduleStreamlineRecompute();
        }

        // Notify view lock manager of timestep change
        if (this.viewLockManager) {
          this.viewLockManager.onVisualizationChange();
        }
      }
      return;
    }

    const clamped = Utils.clamp
      ? Utils.clamp(index, 0, timeSeries.length - 1)
      : Math.min(Math.max(index, 0), timeSeries.length - 1);

    this.totalTimesteps = timeSeries.length;
    this.currentTimestep = clamped;
    this.currentData.currentTimestep = clamped;
    if (times.length > 1) {
      this.playbackIntervalMs = this.calculatePlaybackInterval(times);
    }

    const frame = timeSeries[clamped];
    this.currentData.currentTimeValue = frame.time;
    this.currentData.fields = frame.fields || {};
    if (this.currentData.metadata) {
      this.currentData.metadata.currentTime = frame.time;
    }

    if (forceFieldControls || !this.fieldControlsInitialized) {
      this.updateFieldControls(this.currentData.fields, { force: true });
    }

    if (this.colormap.autoRange !== false && this.controls?.elements) {
      const variable = this.controls.elements.colormapVariable.value;
      const scalars = this.currentData.fields[variable];
      // Only calculate range if we have actual scalar data
      if (scalars && Array.isArray(scalars) && scalars.length > 0) {
        const range = Colormap.calculateAutoRange(scalars);
        this.controls.elements.colormapMin.value = range.min.toFixed(3);
        this.controls.elements.colormapMax.value = range.max.toFixed(3);
        this.colormap.min = range.min;
        this.colormap.max = range.max;
      }
    }

    if (updateControls && this.controls?.updateTimeControls) {
      this.controls.updateTimeControls(
        clamped,
        timeSeries.length,
        times
      );
    }

    if (refreshVisualization) {
      // For geometry-based rendering, reload with new time
      if (this.renderingMode === 'geometry' && this.availableTimes) {
        const newTime = this.availableTimes[this.currentTimestep];
        if (newTime !== undefined && newTime !== this.geometryParams?.time) {
          console.log('[Viewer] Time changed to:', newTime);
          this.reloadGeometry({ time: newTime }).catch(err => {
            console.error('[Viewer] Failed to reload geometry for new time:', err);
          });
        }
      } else {
        // For traditional rendering
        this.applyColormapToData();
        this.scheduleStreamlineRecompute();
      }

      // Notify view lock manager of timestep change
      if (this.viewLockManager) {
        this.viewLockManager.onVisualizationChange();
      }
    }
  }

  scheduleStreamlineRecompute() {
    if (!this.filters || typeof this.filters.hasActiveStreamlines !== 'function' || !this.filters.hasActiveStreamlines()) {
      return;
    }

    if (this.streamlineRecomputeTimeout) {
      clearTimeout(this.streamlineRecomputeTimeout);
    }

    this.streamlineRecomputeTimeout = setTimeout(async () => {
      this.streamlineRecomputeTimeout = null;
      if (!this.currentData || !this.filters || typeof this.filters.recomputeStreamlinesForData !== 'function') {
        return;
      }
      try {
        await this.filters.recomputeStreamlinesForData(this.currentData);
      } catch (error) {
        console.error('Streamline recompute failed:', error);
      }
    }, 150);
  }

  // WebSocket connection
  async connectToSimulator(url, simulationId = null) {
    return new Promise((resolve, reject) => {
      this.controls.updateConnectionStatus('connecting');

      try {
        const wsUrl = simulationId ? `${url}/${simulationId}` : url;
        this.websocket = new WebSocket(wsUrl);

        this.websocket.onopen = () => {
          console.log('Connected to simulator');
          this.controls.updateConnectionStatus('connected');
          resolve();
        };

        this.websocket.onmessage = (event) => {
          this.handleWebSocketMessage(event.data);
        };

        this.websocket.onerror = (error) => {
          console.error('WebSocket error:', error);
          this.controls.updateConnectionStatus('disconnected');
          reject(error);
        };

        this.websocket.onclose = () => {
          console.log('Disconnected from simulator');
          this.controls.updateConnectionStatus('disconnected');
        };
      } catch (err) {
        this.controls.updateConnectionStatus('disconnected');
        reject(err);
      }
    });
  }

  handleWebSocketMessage(message) {
    try {
      const data = this.dataLoader.parseWebSocketData(message);
      this.displayData(data);
    } catch (err) {
      console.error('Failed to parse WebSocket message:', err);
    }
  }

  disconnectFromSimulator() {
    if (this.websocket) {
      this.websocket.close();
      this.websocket = null;
    }
  }

  // Camera controls
  resetCamera() {
    let targetObject = null;

    const priorityIds = ['grid-surface', 'main-mesh', 'grid-points', 'main-points'];
    for (const id of priorityIds) {
      const entry = this.engine.objects.get(id);
      if (entry?.object) {
        targetObject = entry.object;
        break;
      }
    }

    if (!targetObject) {
      for (const entry of this.engine.objects.values()) {
        if (entry?.object) {
          targetObject = entry.object;
          break;
        }
      }
    }

    if (targetObject) {
      this.engine.fitCameraToObject(targetObject);
    } else {
      this.engine.resetCamera();
    }
    // Notify view lock manager of camera change
    if (this.viewLockManager) {
      this.viewLockManager.onVisualizationChange();
    }
  }

  setCameraView(view) {
    this.engine.setCameraView(view);
    // Notify view lock manager of camera change
    if (this.viewLockManager) {
      this.viewLockManager.onVisualizationChange();
    }
  }

  // Display controls
  toggleWireframe() {
    this.engine.toggleWireframe();
  }

  toggleAxes() {
    this.engine.toggleAxes();
  }

  toggleGrid() {
    this.engine.toggleGrid();
  }

  setBackgroundColor(color) {
    this.engine.setBackgroundColor(color);
  }

  // Colormap controls

  /**
   * Get current colormap settings from UI
   */
  getColormapSettings() {
    if (!this.controls || !this.controls.elements) {
      return { min: 0, max: 1, preset: 'jet' };
    }

    const preset = this.controls.elements.colormapPreset?.value || 'jet';
    const min = parseFloat(this.controls.elements.colormapMin?.value || 0);
    const max = parseFloat(this.controls.elements.colormapMax?.value || 1);

    return { min, max, preset };
  }

  updateColormap() {
    if (!this.currentData || !this.currentData.fields) {
      console.log('[updateColormap] No data available');
      return;
    }

    // Pause preloading to give priority to colormap updates
    this.pausePreloadingForPriority();

    const variable = this.controls.elements.colormapVariable.value;
    const preset = this.controls.elements.colormapPreset.value;
    const min = parseFloat(this.controls.elements.colormapMin.value);
    const max = parseFloat(this.controls.elements.colormapMax.value);

    console.log('[updateColormap] Called:', {
      renderingMode: this.renderingMode,
      variable, preset, min, max
    });

    this.colormap = {
      ...this.colormap,
      variable,
      preset,
      min,
      max
    };

    // For geometry-based rendering, recolor without reloading
    if (this.renderingMode === 'geometry') {
      console.log('[updateColormap] Using geometry mode - recoloring');
      const colormapSettings = this.getColormapSettings();
      const success = this.engine.recolorGeometry(colormapSettings);
      console.log('[updateColormap] Recolor result:', success);
      return;
    }

    // Update visualization with new colormap (traditional frontend rendering)
    console.log('[updateColormap] Using frontend mode - applying colormap to data');
    this.applyColormapToData();
  }

  setManualColormapRange() {
    this.colormap.autoRange = false;
  }

  enableAutoColormapRange() {
    this.colormap.autoRange = true;
  }

  async onColormapVariableChange() {
    // Pause preloading to give priority to field change
    this.pausePreloadingForPriority();

    const newField = this.controls.elements.colormapVariable.value;
    console.log('[onColormapVariableChange] Called:', {
      renderingMode: this.renderingMode,
      newField,
      currentField: this.geometryParams?.colorField
    });

    // For geometry-based rendering, reload with new field
    if (this.renderingMode === 'geometry') {
      if (newField && newField !== this.geometryParams?.colorField) {
        console.log('[onColormapVariableChange] Field changed, reloading geometry with:', newField);
        // Clear cache since cached data is field-specific
        this.clearGeometryCache();
        await this.reloadGeometry({ colorField: newField });
        // Restart preloading for the new field
        this.startTimestepPreloading();
      } else {
        console.log('[onColormapVariableChange] Field unchanged, skipping reload');
      }
      return;
    }

    // For traditional rendering, use old approach
    console.log('[onColormapVariableChange] Frontend mode - auto ranging');
    this.enableAutoColormapRange();
    this.autoRangeColormap();
  }

  applyColormapToData() {
    if (!this.currentData || !this.currentData.fields) return;

    const variable = this.colormap.variable;
    const scalars = this.currentData.fields[variable];
    if (!Array.isArray(scalars) || scalars.length === 0) return;

    let min = Number(this.colormap.min);
    let max = Number(this.colormap.max);

    const needsAutoRange = !Number.isFinite(min) || !Number.isFinite(max) || min === max;
    if (needsAutoRange) {
      const range = Colormap.calculateAutoRange(scalars);
      min = range.min;
      max = range.max;

      if (min === max) {
        const epsilon = Math.max(Math.abs(min) * 1e-3, 1e-6);
        min -= epsilon;
        max += epsilon;
      }

      if (this.controls && this.controls.elements) {
        this.controls.elements.colormapMin.value = min.toFixed(3);
        this.controls.elements.colormapMax.value = max.toFixed(3);
      }
    }

    this.colormap.min = min;
    this.colormap.max = max;

    const colors = Colormap.mapDataToColors(
      scalars,
      min,
      max,
      this.colormap.preset
    ).map(c => [c[0] / 255, c[1] / 255, c[2] / 255]);

    let pointsObject = null;
    if (this.currentData.grid && Array.isArray(this.currentData.grid.points) && this.currentData.grid.points.length > 0) {
      pointsObject = this.engine.createPointsFromData(
        this.currentData.grid.points,
        colors,
        0.04
      );

      this.engine.removeObject('grid-points');
      this.engine.addObject('grid-points', pointsObject, {
        ...this.getGridPointsMetadata(),
        options: {
          variable,
          min,
          max,
          preset: this.colormap.preset
        }
      });

      // Hide points by default, show surface instead
      if (pointsObject) {
        pointsObject.visible = false;
      }
    } else {
      this.engine.removeObject('grid-points');
    }

    // Pass visible parts filter to createSurfaceMesh if available
    const visibleParts = (this.meshParts.boundaries && Object.keys(this.meshParts.boundaries).length > 0)
      ? this.meshParts.visibleParts
      : null;
    const boundaries = this.meshParts.boundaries || null;


    const surfaceObject = this.createSurfaceMesh(this.currentData.grid, colors, visibleParts, boundaries);

    // Check if user intentionally hid all parts (empty visibleParts set)
    const userHidAll = visibleParts && visibleParts.size === 0;

    if (surfaceObject) {
      this.engine.removeObject('grid-surface');
      this.engine.addObject('grid-surface', surfaceObject, {
        type: 'visualization',
        kind: 'surface',
        name: 'Surface Representation',
        icon: '🟦',
        removable: false,
        options: {
          variable,
          min,
          max,
          preset: this.colormap.preset
        }
      });

      // Track the active mesh object ID
      this.meshParts.activeMeshObjectId = 'grid-surface';

      // Apply current display settings (representation and opacity)
      this.updateRepresentation();
      this.updateOpacity();
    } else if (userHidAll) {
      // User clicked "Hide All" - remove the mesh entirely
      this.engine.removeObject('grid-surface');
    } else if (this.currentData.grid && this.currentData.grid.vertices && this.currentData.grid.vertices.length > 0) {
      // No faces available - create point cloud visualization
      console.log('No surface mesh available, creating point cloud with', this.currentData.grid.vertices.length, 'points');
      console.log('Colors array:', colors ? colors.length : 'undefined', 'colors');
      console.log('Scalars:', scalars ? scalars.length : 'undefined', 'values');
      this.engine.removeObject('grid-surface');

      // Create colored points from vertices
      const pointCloud = this.createColoredPointCloud(this.currentData.grid.vertices, colors);
      console.log('Point cloud created:', pointCloud);
      if (pointCloud) {
        this.engine.addObject('grid-surface', pointCloud, {
          type: 'visualization',
          kind: 'points',
          name: 'Point Cloud Representation',
          icon: '⚫',
          removable: false,
          options: {
            variable,
            min,
            max,
            preset: this.colormap.preset
          }
        });
        console.log('Point cloud added to engine as grid-surface');
      } else {
        console.error('Failed to create point cloud!');
      }
    } else {
      this.engine.removeObject('grid-surface');
    }

    if (this.needsCameraFit) {
      // Get the grid-surface object (could be mesh or points)
      const gridSurfaceEntry = this.engine.objects.get('grid-surface');
      const target = gridSurfaceEntry ? gridSurfaceEntry.object : (surfaceObject || null);
      console.log('Camera fitting - needsCameraFit:', this.needsCameraFit, 'target:', target);
      if (target) {
        console.log('Fitting camera to object...');
        this.engine.fitCameraToObject(target);
        this.needsCameraFit = false;
        console.log('Camera fitted');
      } else {
        console.error('No target for camera fitting!');
      }
    }

    this.updateStats();
    if (this.selectedPipelineId && ['grid-points', 'grid-surface'].includes(this.selectedPipelineId)) {
      const entry = this.engine.objects.get(this.selectedPipelineId);
      if (entry && this.controls?.updatePropertiesPanel) {
        this.controls.updatePropertiesPanel(this.buildObjectProperties(entry, this.selectedPipelineId));
      }
    }
    this.refreshPipelineList();
  }

  autoRangeColormap() {
    // For geometry-based rendering, use backend scalar range
    if (this.renderingMode === 'geometry' && this.engine.lastGeometryScalarRange) {
      const range = this.engine.lastGeometryScalarRange;
      this.controls.elements.colormapMin.value = range.min.toFixed(3);
      this.controls.elements.colormapMax.value = range.max.toFixed(3);
      this.colormap.autoRange = true;
      this.updateColormap(); // This will trigger recoloring
      return;
    }

    // For traditional rendering, calculate from field data
    if (!this.currentData || !this.currentData.fields) return;

    const variable = this.controls.elements.colormapVariable.value;
    const scalars = this.currentData.fields[variable];

    // Only calculate range if we have actual scalar data
    if (scalars && Array.isArray(scalars) && scalars.length > 0) {
      const range = Colormap.calculateAutoRange(scalars);
      this.controls.elements.colormapMin.value = range.min.toFixed(3);
      this.controls.elements.colormapMax.value = range.max.toFixed(3);
      this.colormap.autoRange = true;
      this.updateColormap();
    }
  }

  calculatePlaybackInterval(times) {
    if (!Array.isArray(times) || times.length < 2) {
      return 400;
    }

    let totalDelta = 0;
    let count = 0;

    for (let i = 1; i < times.length; i++) {
      const delta = Math.abs(times[i] - times[i - 1]);
      if (Number.isFinite(delta) && delta > 0) {
        totalDelta += delta;
        count++;
      }
    }

    if (count === 0) {
      return 400;
    }

    const averageSeconds = totalDelta / count;
    const intervalMs = averageSeconds * 1000;
    return Math.min(Math.max(intervalMs, 60), 2000);
  }

  createSurfaceMesh(grid, colorsLUT, visibleParts = null, boundaries = null) {
    if (!grid || !Array.isArray(grid.vertices) || !Array.isArray(grid.faces) || !Array.isArray(grid.owner)) {
      return null;
    }

    // If we have boundaries, create separate meshes for each part
    if (boundaries && Object.keys(boundaries).length > 0) {
      return this.createSurfaceMeshByParts(grid, colorsLUT, visibleParts, boundaries);
    }

    // Fallback to single mesh (for non-OpenFOAM cases or cases without boundaries)
    return this.createSingleSurfaceMesh(grid, colorsLUT, 0, grid.faces.length);
  }

  createSurfaceMeshByParts(grid, colorsLUT, visibleParts, boundaries) {
    const group = new THREE.Group();
    group.name = 'mesh-parts-group';

    const vertices = grid.vertices;
    const faces = grid.faces;
    const boundaryStart = this.getBoundaryStartIndex(grid);

    // Create internal mesh if we have internal faces
    if (boundaryStart > 0) {
      const internalMesh = this.createSingleSurfaceMesh(grid, colorsLUT, 0, boundaryStart);
      if (internalMesh) {
        internalMesh.name = 'internal';
        internalMesh.userData.partName = 'internal';
        internalMesh.visible = visibleParts ? visibleParts.has('internal') : true;
        group.add(internalMesh);
        console.log(`Created internal mesh with ${boundaryStart} faces, visible: ${internalMesh.visible}`);
      }
    }

    // Create mesh for each boundary patch
    for (const [patchName, patchData] of Object.entries(boundaries)) {
      const startFace = patchData.startFace;
      const endFace = startFace + patchData.nFaces;

      const patchMesh = this.createSingleSurfaceMesh(grid, colorsLUT, startFace, endFace);
      if (patchMesh) {
        patchMesh.name = patchName;
        patchMesh.userData.partName = patchName;
        patchMesh.visible = visibleParts ? visibleParts.has(patchName) : true;
        group.add(patchMesh);
        console.log(`Created boundary mesh '${patchName}' with ${patchData.nFaces} faces, visible: ${patchMesh.visible}`);
      }
    }

    console.log(`createSurfaceMeshByParts: Created group with ${group.children.length} mesh parts`);
    return group.children.length > 0 ? group : null;
  }

  createSingleSurfaceMesh(grid, colorsLUT, startFaceIndex, endFaceIndex) {
    const vertices = grid.vertices;
    const faces = grid.faces;
    const owner = grid.owner;

    const positions = [];
    const colors = [];
    const cellIndices = [];
    const verticesCount = vertices.length;
    const usePerVertexColors = Array.isArray(colorsLUT)
      && verticesCount > 0
      && colorsLUT.length === verticesCount;

    for (let faceIndex = startFaceIndex; faceIndex < endFaceIndex && faceIndex < faces.length; faceIndex++) {
      const face = faces[faceIndex];
      if (!Array.isArray(face) || face.length < 3) continue;

      const baseVertex = vertices[face[0]];
      if (!baseVertex) continue;

      if (usePerVertexColors) {
        const colorA = colorsLUT[face[0]] || [0.5, 0.5, 0.5];

        for (let i = 1; i < face.length - 1; i++) {
          const v1 = vertices[face[i]];
          const v2 = vertices[face[i + 1]];
          if (!v1 || !v2) continue;

          const colorB = colorsLUT[face[i]] || [0.5, 0.5, 0.5];
          const colorC = colorsLUT[face[i + 1]] || [0.5, 0.5, 0.5];

          positions.push(
            baseVertex[0], baseVertex[1], baseVertex[2],
            v1[0], v1[1], v1[2],
            v2[0], v2[1], v2[2]
          );

          colors.push(
            colorA[0], colorA[1], colorA[2],
            colorB[0], colorB[1], colorB[2],
            colorC[0], colorC[1], colorC[2]
          );

          cellIndices.push(face[0], face[i], face[i + 1]);
        }
      } else {
        const cellIndex = owner[faceIndex];
        if (cellIndex == null) continue;

        const hasValidField = cellIndex >= 0 && cellIndex < colorsLUT.length;
        const faceColor = hasValidField
          ? (colorsLUT[cellIndex] || [0.5, 0.5, 0.5])
          : [0.5, 0.5, 0.5];

        for (let i = 1; i < face.length - 1; i++) {
          const v1 = vertices[face[i]];
          const v2 = vertices[face[i + 1]];
          if (!v1 || !v2) continue;

          positions.push(
            baseVertex[0], baseVertex[1], baseVertex[2],
            v1[0], v1[1], v1[2],
            v2[0], v2[1], v2[2]
          );

          colors.push(
            faceColor[0], faceColor[1], faceColor[2],
            faceColor[0], faceColor[1], faceColor[2],
            faceColor[0], faceColor[1], faceColor[2]
          );

          const cellAttrValue = hasValidField ? cellIndex : -1;
          cellIndices.push(cellAttrValue, cellAttrValue, cellAttrValue);
        }
      }
    }

    if (positions.length === 0) {
      return null;
    }

    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
    geometry.setAttribute('color', new THREE.Float32BufferAttribute(colors, 3));
    geometry.setAttribute('cellIndex', new THREE.Float32BufferAttribute(cellIndices, 1));
    geometry.computeVertexNormals();

    const material = new THREE.MeshPhongMaterial({
      vertexColors: true,
      side: THREE.DoubleSide,
      shininess: 12,
      polygonOffset: true,
      polygonOffsetFactor: 1,
      polygonOffsetUnits: 1
    });

    return new THREE.Mesh(geometry, material);
  }

  createColoredPointCloud(vertices, colorsLUT) {
    if (!vertices || vertices.length === 0) {
      return null;
    }

    const positions = [];
    const colors = [];

    // Create points with colors from the LUT
    for (let i = 0; i < vertices.length; i++) {
      const vertex = vertices[i];
      const color = colorsLUT && colorsLUT[i] ? colorsLUT[i] : [0.5, 0.5, 0.5];

      positions.push(vertex[0], vertex[1], vertex[2]);
      colors.push(color[0], color[1], color[2]);
    }

    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
    geometry.setAttribute('color', new THREE.Float32BufferAttribute(colors, 3));

    // Compute bounding box/sphere for camera fitting
    geometry.computeBoundingBox();
    geometry.computeBoundingSphere();

    const material = new THREE.PointsMaterial({
      size: 3.0,  // Increased from 0.5 to make points more visible
      vertexColors: true,
      sizeAttenuation: true
    });

    const points = new THREE.Points(geometry, material);
    console.log('Created point cloud with', vertices.length, 'points');
    console.log('Bounding box:', geometry.boundingBox);
    console.log('Bounding sphere:', geometry.boundingSphere);

    return points;
  }

  getBoundaryStartIndex(grid) {
    if (!grid) return 0;
    if (Number.isInteger(grid.nInternalFaces)) {
      return grid.nInternalFaces;
    }
    if (Array.isArray(grid.neighbour)) {
      return grid.neighbour.length;
    }
    return 0;
  }

  // Time controls
  goToFrame(frame) {
    this.setCurrentTimestep(frame);
  }

  previousFrame() {
    if (this.totalTimesteps <= 0) return;
    const target = this.currentTimestep <= 0
      ? this.totalTimesteps - 1
      : this.currentTimestep - 1;
    this.goToFrame(target);
  }

  nextFrame() {
    if (this.totalTimesteps <= 0) return;
    const target = this.currentTimestep >= this.totalTimesteps - 1
      ? 0
      : this.currentTimestep + 1;
    this.goToFrame(target);
  }

  goToLastFrame() {
    if (this.totalTimesteps <= 0) return;
    this.goToFrame(this.totalTimesteps - 1);
  }

  togglePlayback() {
    if (this.isPlaying) {
      this.stopPlayback();
    } else {
      this.startPlayback();
    }
  }

  startPlayback() {
    if (!this.currentData || this.totalTimesteps <= 1) {
      return;
    }

    this.isPlaying = true;
    // Bump generation so any previous loop instance exits
    this._playbackGeneration = (this._playbackGeneration || 0) + 1;

    const btn = this.controls.elements.btnPlayPause;
    const icon = btn.querySelector('img') || document.getElementById('playPauseIcon');
    if (icon) {
      icon.src = 'static/assets/icons/pause.svg';
      icon.alt = 'Pause';
    }

    // Read user FPS from the speed slider
    const fpsSlider = document.getElementById('playbackFps');
    if (fpsSlider) {
      this.playbackTargetFps = parseInt(fpsSlider.value, 10);
    }

    // Stop preloading entirely during playback (not just pause)
    this.abortPreloading();

    // Async playback loop: waits for each frame to finish before advancing
    this._playbackLoop(this._playbackGeneration);
  }

  async _playbackLoop(generation) {
    while (this.isPlaying && generation === this._playbackGeneration) {
      if (!this.currentData || this.totalTimesteps <= 0) {
        break;
      }

      const frameStart = performance.now();

      const nextIndex = this.currentTimestep >= this.totalTimesteps - 1
        ? 0
        : this.currentTimestep + 1;

      // Advance the timestep and wait for geometry to load
      if (this.renderingMode === 'geometry' && this.availableTimes) {
        const newTime = this.availableTimes[nextIndex];
        if (newTime !== undefined) {
          this.currentTimestep = nextIndex;
          this.currentData.currentTimestep = this.currentTimestep;
          if (this.currentData.timesteps) {
            this.currentData.currentTimeValue = this.currentData.timesteps[this.currentTimestep];
          }
          if (this.controls?.updateTimeControls) {
            const times = this.currentData.timesteps || this.availableTimes;
            this.controls.updateTimeControls(this.currentTimestep, this.totalTimesteps, times);
          }
          try {
            await this.reloadGeometry({ time: newTime });
          } catch (err) {
            console.error('[Playback] Failed to load frame:', err);
          }
        }
      } else {
        this.setCurrentTimestep(nextIndex);
      }

      // Check again after await — stop/pause or new loop may have started
      if (!this.isPlaying || generation !== this._playbackGeneration) break;

      // Enforce target FPS: wait remaining time to hit the target frame interval
      if (this.playbackTargetFps > 0) {
        const targetMs = 1000 / this.playbackTargetFps;
        const elapsed = performance.now() - frameStart;
        const waitMs = Math.max(0, targetMs - elapsed);
        if (waitMs > 0) {
          await new Promise(resolve => setTimeout(resolve, waitMs));
        }
      }
      // fps=0 (Max): no extra wait, advance as fast as frames load
    }
  }

  stopPlayback() {
    this.isPlaying = false;
    // Bump generation to kill any in-flight loop
    this._playbackGeneration = (this._playbackGeneration || 0) + 1;
    const btn = this.controls.elements.btnPlayPause;
    const icon = btn.querySelector('img') || document.getElementById('playPauseIcon');
    if (icon) {
      icon.src = 'static/assets/icons/play.svg';
      icon.alt = 'Play';
    }

    if (this.playbackInterval) {
      clearInterval(this.playbackInterval);
      this.playbackInterval = null;
    }
  }

  // Filters
  async applyFilter(filterType) {
    if (!this.currentData) {
      Utils.notify('No data loaded', 'warning');
      return;
    }

    // Pause preloading to give priority to filter requests
    this.pausePreloadingForPriority();

    try {
      let filterId = null;
      switch (filterType) {
        case 'contour':
          filterId = this.filters.createContour(this.currentData, {
            variable: this.colormap.variable,
            value: (this.colormap.min + this.colormap.max) / 2,
            colormap: this.colormap.preset
          });
          break;
        case 'streamlines':
          filterId = await this.filters.createStreamlines(this.currentData, {
            colormap: this.colormap.preset
          });
          break;
        case 'slice':
          filterId = this.filters.createSlice(this.currentData, {
            plane: 'xy',
            position: 0,
            variable: this.colormap.variable,
            colormap: this.colormap.preset
          });
          break;
        case 'vectors':
          filterId = this.filters.createVectorField(this.currentData, {
            variable: 'velocity',
            scale: 0.5,
            skipFactor: 3,
            colormap: this.colormap.preset
          });
          break;
        case 'threshold':
          filterId = this.filters.createThreshold(this.currentData, {
            variable: this.colormap.variable,
            min: this.colormap.min,
            max: this.colormap.max,
            colormap: this.colormap.preset
          });
          break;
        default:
          Utils.notify(`Filter "${filterType}" not yet implemented`, 'info');
      }

      if (filterId) {
        this.selectPipelineItem(filterId);
        Utils.notify('Filter added', 'success');
      }
    } catch (err) {
      console.error('Failed to apply filter:', err);
      Utils.notify(`Filter error: ${err.message}`, 'error');
    }
  }

  // Screenshot
  // Screenshot
  refreshPipelineList() {
    if (!this.controls || typeof this.controls.updatePipelineTree !== 'function') {
      return;
    }

    const items = [];
    const hasData = !!this.currentData;
    const rootLabel = hasData
      ? (this.currentData.metadata?.fileName || this.currentData.case_path || 'Loaded Data')
      : 'No Data Loaded';

    items.push({
      id: 'data-root',
      name: rootLabel,
      icon: hasData ? '📦' : '📂',
      removable: hasData
    });

    if (this.engine?.objects instanceof Map) {
      this.engine.objects.forEach((entry, id) => {
        if (!entry) return;
        const metadata = entry.metadata || {};
        const name = metadata.name || this.formatLabel(metadata.kind || metadata.type || id);
        const icon = metadata.icon || this.getIconForKind(metadata.kind || metadata.type);
        const removable = metadata.removable !== false;

        items.push({
          id,
          name,
          icon,
          removable
        });
      });
    }

    this.controls.updatePipelineTree(items, this.selectedPipelineId);
  }

  selectPipelineItem(id) {
    this.selectedPipelineId = id;

    if (id === 'data-root') {
      if (this.currentData) {
        this.controls.updatePropertiesPanel(this.buildDataProperties());
      } else {
        this.controls.updatePropertiesPanel(null);
      }
      this.refreshPipelineList();
      return;
    }

    const entry = this.engine.objects.get(id);
    if (entry) {
      this.controls.updatePropertiesPanel(this.buildObjectProperties(entry, id));
    } else {
      this.controls.updatePropertiesPanel(null);
    }
    this.refreshPipelineList();
  }

  removePipelineItem(id) {
    if (id === 'data-root') {
      if (!this.currentData) return;
      this.clearCurrentData();
      Utils.notify('Data removed', 'info');
      return;
    }

    let removed = false;
    if (this.filters?.hasFilter(id)) {
      const filterInfo = this.filters.getFilter(id);
      removed = this.filters.removeFilter(id);
      if (removed) {
        Utils.notify(`${this.formatLabel(filterInfo?.type || 'Filter')} removed`, 'info');
      }
    } else {
      this.engine.removeObject(id);
      removed = true;
    }

    if (!removed) {
      return;
    }

    if (this.selectedPipelineId === id) {
      this.selectedPipelineId = 'data-root';
      if (this.currentData) {
        this.controls.updatePropertiesPanel(this.buildDataProperties());
      } else {
        this.controls.updatePropertiesPanel(null);
      }
    }

    this.refreshPipelineList();
    this.updateStats();
  }

  clearCurrentData() {
    this.stopPlayback();
    this.currentData = null;
    this.totalTimesteps = 0;
    this.currentTimestep = 0;
    this.fieldControlsInitialized = false;
    if (this.filters) {
      this.filters.clearAll();
    }
    if (this.streamlinePreviews.size > 0) {
      Array.from(this.streamlinePreviews.keys()).forEach(id => this.detachStreamlinePreview(id));
      this.streamlinePreviews.clear();
    }
    this.engine.clearAll();
    if (this.controls?.updateTimeControls) {
      this.controls.updateTimeControls(0, 0, []);
    }
    if (this.controls?.updatePropertiesPanel) {
      this.controls.updatePropertiesPanel(null);
    }
    this.selectedPipelineId = 'data-root';
    this.updateInfo({});
    this.refreshPipelineList();
    this.updateStats();
  }

  async saveScreenshot() {
    // Show resolution picker modal
    this.showResolutionModal('screenshot');
  }

  showResolutionModal(mode = 'screenshot') {
    const modal = document.getElementById('resolutionModal');
    const title = document.getElementById('resolutionModalTitle');
    const confirmText = document.getElementById('confirmResolutionText');
    const presetSelect = document.getElementById('resolutionPreset');
    const customFields = document.getElementById('customResolutionFields');

    // Update modal title based on mode
    title.textContent = mode === 'screenshot' ? 'Screenshot Resolution' : 'Video Resolution';
    confirmText.textContent = mode === 'screenshot' ? 'Save Screenshot' : 'Start Recording';

    // Show/hide custom fields based on selection
    const handlePresetChange = () => {
      customFields.style.display = presetSelect.value === 'custom' ? 'block' : 'none';
    };
    presetSelect.removeEventListener('change', handlePresetChange);
    presetSelect.addEventListener('change', handlePresetChange);

    // Show modal
    modal.style.display = 'flex';

    // Handle close
    const closeModal = () => {
      modal.style.display = 'none';
    };

    document.getElementById('btnCloseResolution').onclick = closeModal;
    document.getElementById('btnCancelResolution').onclick = closeModal;

    // Handle confirm
    document.getElementById('btnConfirmResolution').onclick = async () => {
      const preset = presetSelect.value;
      let width, height;

      if (preset === 'current') {
        width = this.engine.renderer.domElement.width;
        height = this.engine.renderer.domElement.height;
      } else if (preset === 'custom') {
        width = parseInt(document.getElementById('customWidth').value);
        height = parseInt(document.getElementById('customHeight').value);
      } else {
        [width, height] = preset.split('x').map(Number);
      }

      // Get SVG size limit
      const svgSizeLimitSelect = document.getElementById('svgSizeLimit');
      const svgSizeLimit = svgSizeLimitSelect.value === 'unlimited' ? null : parseInt(svgSizeLimitSelect.value);

      // Get selected export formats
      const exportFormats = {
        png: document.getElementById('exportPNG')?.checked === true,
        svg: document.getElementById('exportSVG')?.checked === true,
        pdf: document.getElementById('exportPDF')?.checked === true
      };

      // Check at least one format is selected
      if (!exportFormats.png && !exportFormats.svg && !exportFormats.pdf) {
        Utils.notify('Please select at least one export format', 'error');
        return;
      }

      closeModal();

      if (mode === 'screenshot') {
        await this.captureHighResScreenshot(width, height, svgSizeLimit, exportFormats);
      } else {
        this.recordingWidth = width;
        this.recordingHeight = height;
        await this.toggleVideoRecording();
      }
    };
  }

  async captureHighResScreenshot(width, height, svgSizeLimitMB = null, formats = { png: true, svg: true, pdf: true }) {
    try {
      const timestamp = Date.now();
      const savedFormats = [];

      // Create an offscreen renderer for high-res capture (doesn't affect main viewport)
      const offscreenRenderer = new THREE.WebGLRenderer({
        antialias: true,
        alpha: true,
        preserveDrawingBuffer: true
      });
      offscreenRenderer.setSize(width, height);
      offscreenRenderer.setPixelRatio(1);
      offscreenRenderer.shadowMap.enabled = this.engine.renderer.shadowMap.enabled;
      offscreenRenderer.shadowMap.type = this.engine.renderer.shadowMap.type;

      // Clone the camera for offscreen rendering with correct aspect ratio
      const offscreenCamera = this.engine.camera.clone();
      offscreenCamera.aspect = width / height;
      offscreenCamera.updateProjectionMatrix();

      // Render to offscreen canvas
      offscreenRenderer.render(this.engine.scene, offscreenCamera);

      // Capture PNG at high resolution (also needed for PDF)
      let pngDataURL = null;
      if (formats.png || formats.pdf) {
        pngDataURL = offscreenRenderer.domElement.toDataURL('image/png');
        if (formats.png) {
          const pngFilename = `coandacloud-${timestamp}-${width}x${height}.png`;
          await Utils.downloadDataURL(pngDataURL, pngFilename);
          savedFormats.push('PNG');
        }
      }

      // Generate and save SVG (using high-res dimensions and size limit)
      if (formats.svg) {
        const svgData = this.generateSVG(width, height, svgSizeLimitMB);
        const svgFilename = `coandacloud-${timestamp}-${width}x${height}.svg`;
        await Utils.downloadDataURL(svgData, svgFilename);
        savedFormats.push('SVG');
      }

      // Generate and save PDF (using PNG data)
      if (formats.pdf) {
        try {
          // If we didn't capture PNG earlier, capture it now for PDF
          if (!pngDataURL) {
            pngDataURL = offscreenRenderer.domElement.toDataURL('image/png');
          }
          const pdfData = await this.generatePDF(pngDataURL, width, height);
          const pdfFilename = `coandacloud-${timestamp}-${width}x${height}.pdf`;
          await Utils.downloadDataURL(pdfData, pdfFilename);
          savedFormats.push('PDF');
        } catch (pdfErr) {
          console.error('[PDF Export] Failed to generate PDF:', pdfErr);
          // Don't fail the whole screenshot if PDF generation fails
        }
      }

      // Dispose offscreen renderer to free GPU memory
      offscreenRenderer.dispose();

      // Capture legend if visible (only in selected formats)
      await this.captureLegend(timestamp, formats);

      const formatsStr = savedFormats.join(', ');
      const sizeInfo = formats.svg && svgSizeLimitMB ? ` (SVG limited to ${svgSizeLimitMB}MB)` : '';
      Utils.notify(`Screenshot saved at ${width}×${height} (${formatsStr})${sizeInfo}`, 'success');
    } catch (err) {
      console.error('Failed to save screenshot:', err);
      Utils.notify(`Failed to save screenshot: ${err.message}`, 'error');
    }
  }

  async captureLegend(timestamp, formats = { png: true, svg: true, pdf: true }) {
    // Check all possible legend elements
    const legendConfigs = [
      { id: 'streamlineLegend', name: 'streamline' },
      { id: 'sliceLegend', name: 'slice' },
      { id: 'isolinesLegend', name: 'isolines' },
      { id: 'glyphLegend', name: 'glyph' }
    ];

    let capturedAny = false;

    for (const config of legendConfigs) {
      const legend = document.getElementById(config.id);

      // Check if legend is visible
      if (!legend || legend.style.display === 'none') {
        continue;
      }

      console.log(`[Legend Capture] Capturing ${config.name} legend`);

      try {
        // Get legend elements dynamically based on legend ID
        const titleEl = document.getElementById(`${config.id}Title`);
        const gradientEl = document.getElementById(`${config.id}Gradient`);
        const ticksEl = document.getElementById(`${config.id}Ticks`);
        const metaEl = document.getElementById(`${config.id}Meta`);

        const legendRect = legend.getBoundingClientRect();
        const colormapName = legend.dataset.colormap || 'viridis';
        const colors = this.getColormapColors(colormapName);

        const savedFormats = [];

        // Generate SVG only if SVG format is selected
        if (formats.svg) {
          await this.captureLegendSVG(timestamp, legendRect, titleEl, gradientEl, ticksEl, metaEl, colors, colormapName, config.name);
          savedFormats.push('SVG');
        }

        // Generate high-res PNG only if PNG format is selected
        if (formats.png) {
          await this.captureLegendPNG(timestamp, legendRect, titleEl, gradientEl, ticksEl, metaEl, colors, colormapName, config.name);
          savedFormats.push('PNG');
        }

        if (savedFormats.length > 0) {
          console.log(`[Legend Capture] ${config.name} legend saved successfully (${savedFormats.join(', ')})`);
          capturedAny = true;
        }
      } catch (err) {
        console.error(`[Legend Capture] Failed to capture ${config.name} legend:`, err);
        // Don't throw - legend capture failure shouldn't stop screenshot
      }
    }

    if (!capturedAny) {
      console.log('[Legend Capture] No visible legends to capture');
    }
  }

  async captureLegendSVG(timestamp, legendRect, titleEl, gradientEl, ticksEl, metaEl, colors, colormapName, legendName = 'legend') {
    // Make the legend wider to fit tick numbers properly - minimum 350px width
    const width = Math.max(350, legendRect.width * 2);
    const height = 100; // Fixed height for consistent layout
    const borderRadius = 8; // Match CSS --border-radius: 8px
    const padding = 25;

    let svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}" viewBox="0 0 ${width} ${height}">`;

    // Background with rounded corners (matching the visualization legend)
    svg += `<rect width="${width}" height="${height}" rx="${borderRadius}" ry="${borderRadius}" fill="rgba(0,0,0,0.75)" stroke="rgba(51,51,51,1)" stroke-width="1"/>`;

    let yOffset = 12;

    // Title
    if (titleEl && titleEl.textContent) {
      svg += `<text x="${width / 2}" y="${yOffset + 14}" text-anchor="middle" fill="#fff" font-family="system-ui, -apple-system, sans-serif" font-size="14" font-weight="600">${this.escapeXml(titleEl.textContent)}</text>`;
      yOffset += 28;
    }

    // Gradient bar with rounded corners (matching CSS --border-radius: 8px)
    if (gradientEl) {
      const gradientX = padding;
      const gradientY = yOffset;
      const gradientWidth = width - (padding * 2);
      const gradientHeight = 20;
      const gradientRadius = 8; // Match CSS --border-radius

      // Define linear gradient with unique ID for this legend
      const gradientId = `legendGradient_${legendName}`;
      svg += `<defs><linearGradient id="${gradientId}" x1="0%" y1="0%" x2="100%" y2="0%">`;
      for (let i = 0; i < colors.length; i++) {
        const offset = (i / (colors.length - 1)) * 100;
        svg += `<stop offset="${offset}%" stop-color="${colors[i]}"/>`;
      }
      svg += `</linearGradient></defs>`;

      // Draw gradient rectangle with rounded corners
      svg += `<rect x="${gradientX}" y="${gradientY}" width="${gradientWidth}" height="${gradientHeight}" rx="${gradientRadius}" ry="${gradientRadius}" fill="url(#${gradientId})"/>`;

      yOffset += gradientHeight + 8;
    }

    // Ticks - horizontally distributed with proper spacing
    if (ticksEl) {
      const ticks = ticksEl.querySelectorAll('span');
      if (ticks.length > 0) {
        const tickAreaWidth = width - (padding * 2);
        const tickSpacing = tickAreaWidth / (ticks.length - 1);
        ticks.forEach((tick, i) => {
          const x = padding + (i * tickSpacing);
          svg += `<text x="${x}" y="${yOffset + 12}" text-anchor="middle" fill="#ccc" font-family="system-ui, -apple-system, sans-serif" font-size="12">${this.escapeXml(tick.textContent)}</text>`;
        });
        yOffset += 18;
      }
    }

    // Meta information
    if (metaEl && metaEl.textContent) {
      svg += `<text x="${width / 2}" y="${yOffset + 10}" text-anchor="middle" fill="#888" font-family="system-ui, -apple-system, sans-serif" font-size="10">${this.escapeXml(metaEl.textContent)}</text>`;
    }

    svg += `</svg>`;

    // Download SVG
    const blob = new Blob([svg], { type: 'image/svg+xml' });
    const url = URL.createObjectURL(blob);
    await Utils.downloadDataURL(url, `coandacloud-${legendName}-legend-${timestamp}.svg`);
    URL.revokeObjectURL(url);
  }

  async captureLegendPNG(timestamp, legendRect, titleEl, gradientEl, ticksEl, metaEl, colors, colormapName, legendName = 'legend') {
    // Create a larger canvas for higher resolution (4x scale)
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const scale = 4;

    // Make the legend wider to fit tick numbers properly - minimum 500px width
    const baseWidth = Math.max(500, legendRect.width * 3);
    const baseHeight = 160; // Fixed height for consistent layout
    const borderRadius = 8; // Match CSS --border-radius: 8px
    const padding = 40; // Extra padding for tick numbers

    canvas.width = baseWidth * scale;
    canvas.height = baseHeight * scale;
    ctx.scale(scale, scale);

    // Helper function to draw rounded rectangle
    const roundRect = (x, y, w, h, r) => {
      ctx.beginPath();
      ctx.moveTo(x + r, y);
      ctx.lineTo(x + w - r, y);
      ctx.quadraticCurveTo(x + w, y, x + w, y + r);
      ctx.lineTo(x + w, y + h - r);
      ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
      ctx.lineTo(x + r, y + h);
      ctx.quadraticCurveTo(x, y + h, x, y + h - r);
      ctx.lineTo(x, y + r);
      ctx.quadraticCurveTo(x, y, x + r, y);
      ctx.closePath();
    };

    // Draw background with rounded corners (matching CSS --border-radius: 8px)
    roundRect(0, 0, baseWidth, baseHeight, borderRadius);
    ctx.fillStyle = 'rgba(0, 0, 0, 0.75)'; // Match CSS legend background
    ctx.fill();

    // Draw border with rounded corners (matching CSS --border-color: #333)
    roundRect(0, 0, baseWidth, baseHeight, borderRadius);
    ctx.strokeStyle = '#333';
    ctx.lineWidth = 2;
    ctx.stroke();

    let yOffset = 20;

    // Draw title
    if (titleEl && titleEl.textContent) {
      ctx.fillStyle = '#fff';
      ctx.font = '600 26px system-ui, -apple-system, sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText(titleEl.textContent, baseWidth / 2, yOffset + 22);
      yOffset += 45;
    }

    // Draw gradient bar with rounded corners (matching CSS --border-radius: 8px)
    if (gradientEl) {
      const gradientX = padding;
      const gradientY = yOffset;
      const gradientWidth = baseWidth - (padding * 2);
      const gradientHeight = 32;
      const gradientRadius = 8; // Match CSS --border-radius: 8px

      // Create gradient
      const gradient = ctx.createLinearGradient(gradientX, gradientY, gradientX + gradientWidth, gradientY);
      for (let i = 0; i < colors.length; i++) {
        gradient.addColorStop(i / (colors.length - 1), colors[i]);
      }

      // Draw gradient with rounded corners
      roundRect(gradientX, gradientY, gradientWidth, gradientHeight, gradientRadius);
      ctx.fillStyle = gradient;
      ctx.fill();

      yOffset += gradientHeight + 12;
    }

    // Draw ticks horizontally with proper spacing
    if (ticksEl) {
      const ticks = ticksEl.querySelectorAll('span');
      if (ticks.length > 0) {
        ctx.fillStyle = '#ccc';
        ctx.font = '22px system-ui, -apple-system, sans-serif';
        ctx.textAlign = 'center';

        const tickAreaWidth = baseWidth - (padding * 2);
        const tickSpacing = tickAreaWidth / (ticks.length - 1);

        ticks.forEach((tick, i) => {
          const x = padding + (i * tickSpacing);
          ctx.fillText(tick.textContent, x, yOffset + 20);
        });

        yOffset += 32;
      }
    }

    // Draw meta information
    if (metaEl && metaEl.textContent) {
      ctx.fillStyle = '#888';
      ctx.font = '18px system-ui, -apple-system, sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText(metaEl.textContent, baseWidth / 2, yOffset + 10);
    }

    // Convert canvas to PNG and download
    const dataURL = canvas.toDataURL('image/png');
    const filename = `coandacloud-${legendName}-legend-${timestamp}.png`;
    await Utils.downloadDataURL(dataURL, filename);
  }

  escapeXml(text) {
    return text.replace(/[<>&'"]/g, (c) => {
      switch (c) {
        case '<': return '&lt;';
        case '>': return '&gt;';
        case '&': return '&amp;';
        case "'": return '&apos;';
        case '"': return '&quot;';
      }
    });
  }

  getColormapColors(colormapName) {
    // Use centralized Colormap to get color stops
    if (window.Colormap && window.Colormap.presets && window.Colormap.presets[colormapName]) {
      // Convert preset format [[pos, [r,g,b]], ...] to hex array
      return window.Colormap.presets[colormapName].map(([pos, rgb]) => {
        return '#' + rgb.map(c => c.toString(16).padStart(2, '0')).join('');
      });
    }

    // Handle solid color - use the main mesh colormap's solid color
    if (window.Colormap && window.Colormap.isSolidColor(colormapName)) {
      const solidHex = window.Colormap.getSolidColor('colormapPreset');
      return [solidHex, solidHex];
    }

    // Fallback to local colormaps if Colormap not available
    const colormaps = {
      viridis: ['#440154', '#31688e', '#35b779', '#fde724'],
      plasma: ['#0d0887', '#7e03a8', '#cc4778', '#f89540', '#f0f921'],
      jet: ['#00007f', '#0000ff', '#00ffff', '#ffff00', '#ff0000', '#7f0000'],
      rainbow: ['#ff0000', '#ff7f00', '#ffff00', '#00ff00', '#0000ff', '#4b0082', '#9400d3'],
      coolwarm: ['#3b4cc0', '#7396f5', '#b4d7f8', '#f6bfa1', '#e8745f', '#b40426'],
      turbo: ['#30123b', '#4777ef', '#1ac9c6', '#a0da39', '#fca50a', '#d02c2c']
    };

    return colormaps[colormapName] || colormaps.viridis;
  }

  generateSVG(width = null, height = null, sizeLimitMB = null) {
    // Get canvas dimensions
    width = width || this.engine.renderer.domElement.width;
    height = height || this.engine.renderer.domElement.height;
    const camera = this.engine.camera;

    // Calculate target size in bytes (if limit specified)
    const targetSizeBytes = sizeLimitMB ? sizeLimitMB * 1024 * 1024 : null;

    // First pass: count geometry to estimate size and calculate sampling
    let totalTriangles = 0;
    let totalLineVertices = 0;

    if (targetSizeBytes) {
      this.engine.scene.traverse((object) => {
        if (!object.visible) return;
        const geometry = object.geometry;
        if (!geometry || !geometry.attributes.position) return;

        if (object.type === 'Mesh') {
          if (geometry.index) {
            totalTriangles += geometry.index.count / 3;
          } else {
            totalTriangles += geometry.attributes.position.count / 3;
          }
        } else if (object.type === 'Line' || object.type === 'LineSegments' || object.type === 'LineLoop') {
          totalLineVertices += geometry.attributes.position.count;
        }
      });

      // Estimate bytes per element (approximate)
      // Meshes are now exported as edges (not filled triangles)
      // For closed meshes: each edge is shared by 2 triangles, each triangle has 3 edges
      // So unique edges ≈ (triangles * 3) / 2 = triangles * 1.5
      const estimatedEdges = totalTriangles * 1.5;
      const bytesPerEdge = 80; // ~80 bytes per SVG line element
      const bytesPerLineVertex = 30; // ~30 bytes per line vertex

      const estimatedSize = (estimatedEdges * bytesPerEdge) + (totalLineVertices * bytesPerLineVertex);

      console.log(`[SVG Export] Estimated size: ${(estimatedSize / (1024 * 1024)).toFixed(2)} MB`);
      console.log(`[SVG Export] Total triangles: ${totalTriangles}, Estimated edges: ${estimatedEdges.toFixed(0)}, Total line vertices: ${totalLineVertices}`);

      // Calculate sampling rates
      if (estimatedSize > targetSizeBytes) {
        const ratio = targetSizeBytes / estimatedSize;
        this.svgSamplingRate = Math.max(0.01, ratio); // Minimum 1% sampling
        console.log(`[SVG Export] Applying ${(this.svgSamplingRate * 100).toFixed(1)}% sampling to fit ${sizeLimitMB}MB limit`);
      } else {
        this.svgSamplingRate = 1.0; // No sampling needed
        console.log(`[SVG Export] No sampling needed, estimated size within limit`);
      }
    } else {
      this.svgSamplingRate = 1.0; // No limit, export everything
      console.log(`[SVG Export] No size limit, exporting all geometry`);
    }

    // Create SVG header
    let svg = `<?xml version="1.0" encoding="UTF-8"?>
<svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink"
     width="${width}" height="${height}" viewBox="0 0 ${width} ${height}">
  <defs>
    <style>
      .mesh-line { stroke: #4a9eff; stroke-width: 1; fill: none; }
      .mesh-face { fill: #2a5f9f; fill-opacity: 0.7; stroke: #1a3f6f; stroke-width: 0.5; }
      .streamline { stroke-width: 2; fill: none; stroke-linecap: round; }
      .axis-x { stroke: #ff0000; stroke-width: 2; }
      .axis-y { stroke: #00ff00; stroke-width: 2; }
      .axis-z { stroke: #0000ff; stroke-width: 2; }
    </style>
  </defs>
  <rect width="100%" height="100%" fill="${this.engine.scene.background ?
        '#' + this.engine.scene.background.getHexString() : '#1a1a1a'}"/>
`;

    // Helper function to project 3D point to 2D screen space
    const project = (point) => {
      const vector = point.clone();
      vector.project(camera);
      return {
        x: (vector.x + 1) * width / 2,
        y: (-vector.y + 1) * height / 2
      };
    };

    // Count objects for debugging
    let meshCount = 0;
    let lineCount = 0;
    let skippedObjects = 0;
    const objectTypes = {};

    console.log('[SVG Export] Starting scene traversal...');

    // Export scene objects
    this.engine.scene.traverse((object) => {
      // Track all object types for debugging
      objectTypes[object.type] = (objectTypes[object.type] || 0) + 1;

      if (!object.visible) {
        skippedObjects++;
        return; // Skip invisible objects
      }

      if (object.type === 'Mesh' || object.type === 'Line' || object.type === 'LineSegments' || object.type === 'LineLoop') {
        const geometry = object.geometry;
        if (!geometry || !geometry.attributes.position) {
          console.log(`[SVG Export] Skipping ${object.type} - no geometry or position attribute`, object.name);
          return;
        }

        const positions = geometry.attributes.position;
        const worldMatrix = object.matrixWorld;

        if (object.type === 'Line' || object.type === 'LineSegments' || object.type === 'LineLoop') {
          lineCount++;
          console.log(`[SVG Export] Processing ${object.type}: ${object.name || 'unnamed'}, vertices: ${positions.count}`);
          // Export lines (streamlines, axes, etc.)
          let pathData = '';
          const isStreamline = object.userData?.type === 'streamline';
          const color = object.material?.color ?
            '#' + object.material.color.getHexString() : '#4a9eff';

          // Apply sampling for lines
          const lineStep = Math.max(1, Math.round(1 / this.svgSamplingRate));

          for (let i = 0; i < positions.count - 1; i += lineStep) {
            const v1 = new THREE.Vector3(
              positions.getX(i),
              positions.getY(i),
              positions.getZ(i)
            ).applyMatrix4(worldMatrix);

            const v2 = new THREE.Vector3(
              positions.getX(i + 1),
              positions.getY(i + 1),
              positions.getZ(i + 1)
            ).applyMatrix4(worldMatrix);

            const p1 = project(v1);
            const p2 = project(v2);

            if (i === 0) {
              pathData += `M ${p1.x.toFixed(2)} ${p1.y.toFixed(2)} `;
            }
            pathData += `L ${p2.x.toFixed(2)} ${p2.y.toFixed(2)} `;
          }

          if (pathData) {
            const className = isStreamline ? 'streamline' : 'mesh-line';
            svg += `  <path class="${className}" d="${pathData}" stroke="${color}"/>\n`;
          }
        } else if (object.type === 'Mesh') {
          meshCount++;
          console.log(`[SVG Export] Processing Mesh: ${object.name || 'unnamed'}, triangles: ${geometry.index ? geometry.index.count / 3 : 0}`);

          // Get material color for edges
          let edgeColor = '#4a9eff';
          if (object.material) {
            if (object.material.color) {
              edgeColor = '#' + object.material.color.getHexString();
            } else if (Array.isArray(object.material)) {
              edgeColor = object.material[0]?.color ? '#' + object.material[0].color.getHexString() : edgeColor;
            }
          }

          // Extract unique edges from mesh (wireframe representation)
          // This creates a cleaner SVG by showing only edges instead of filled triangles
          const edgeSet = new Set();
          const edgeKey = (i1, i2) => {
            // Create consistent key regardless of vertex order
            const [a, b] = i1 < i2 ? [i1, i2] : [i2, i1];
            return `${a}-${b}`;
          };

          if (geometry.index) {
            // Indexed geometry - extract edges from triangles
            const indices = geometry.index.array;
            const totalTriangles = indices.length / 3;
            const triangleStep = Math.max(1, Math.round(1 / this.svgSamplingRate));

            console.log(`[SVG Export] Extracting edges from ${totalTriangles} triangles (sampling: ${triangleStep})`);

            for (let i = 0; i < indices.length; i += 3 * triangleStep) {
              const idx0 = indices[i];
              const idx1 = indices[i + 1];
              const idx2 = indices[i + 2];

              // Add three edges of the triangle
              edgeSet.add(edgeKey(idx0, idx1));
              edgeSet.add(edgeKey(idx1, idx2));
              edgeSet.add(edgeKey(idx2, idx0));
            }
          } else {
            // Non-indexed geometry
            const totalTriangles = positions.count / 3;
            const triangleStep = Math.max(1, Math.round(1 / this.svgSamplingRate));

            console.log(`[SVG Export] Extracting edges from ${totalTriangles} non-indexed triangles (sampling: ${triangleStep})`);

            for (let i = 0; i < positions.count; i += 3 * triangleStep) {
              edgeSet.add(edgeKey(i, i + 1));
              edgeSet.add(edgeKey(i + 1, i + 2));
              edgeSet.add(edgeKey(i + 2, i));
            }
          }

          console.log(`[SVG Export] Found ${edgeSet.size} unique edges`);

          // Render unique edges as lines
          for (const edgeKeyStr of edgeSet) {
            const [idx0, idx1] = edgeKeyStr.split('-').map(Number);

            // Get vertices in world space
            const v0 = new THREE.Vector3(
              positions.getX(idx0),
              positions.getY(idx0),
              positions.getZ(idx0)
            ).applyMatrix4(worldMatrix);

            const v1 = new THREE.Vector3(
              positions.getX(idx1),
              positions.getY(idx1),
              positions.getZ(idx1)
            ).applyMatrix4(worldMatrix);

            // Project to screen space
            const p0 = project(v0);
            const p1 = project(v1);

            // Draw edge as line
            svg += `  <line x1="${p0.x.toFixed(2)}" y1="${p0.y.toFixed(2)}" ` +
              `x2="${p1.x.toFixed(2)}" y2="${p1.y.toFixed(2)}" ` +
              `stroke="${edgeColor}" stroke-width="1" stroke-opacity="0.8"/>\n`;
          }
        }
      }
    });

    console.log(`[SVG Export] Scene object types:`, objectTypes);
    console.log(`[SVG Export] Exported ${lineCount} lines and ${meshCount} meshes`);
    console.log(`[SVG Export] Skipped ${skippedObjects} invisible objects`);
    console.log(`[SVG Export] SVG size: ${svg.length} characters`);

    // Add metadata
    svg += `  <text x="10" y="${height - 10}" font-family="Arial" font-size="12" fill="#888">
    CoandaCloud - ${new Date().toISOString()} - ${lineCount} lines, ${meshCount} meshes
  </text>
</svg>`;

    return 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(svg);
  }

  async generatePDF(pngDataURL, width, height) {
    try {
      // Get jsPDF from window (loaded via CDN)
      const { jsPDF } = window.jspdf;

      if (!jsPDF) {
        console.error('[PDF Export] jsPDF library not loaded');
        throw new Error('jsPDF library not available');
      }

      // Calculate PDF dimensions (A4 landscape by default, or custom size)
      // Convert pixels to mm (assuming 96 DPI: 1 inch = 25.4mm, 96 pixels = 25.4mm)
      const pixelsToMm = 25.4 / 96;
      const widthMm = width * pixelsToMm;
      const heightMm = height * pixelsToMm;

      // Determine orientation
      const orientation = width > height ? 'landscape' : 'portrait';

      // Create PDF with custom size to match image aspect ratio
      const doc = new jsPDF({
        orientation: orientation,
        unit: 'mm',
        format: [widthMm, heightMm]
      });

      // Load PNG image and add to PDF
      return new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => {
          // Add image to fill entire PDF page
          doc.addImage(img, 'PNG', 0, 0, widthMm, heightMm);

          // Add metadata
          doc.setProperties({
            title: 'CoandaCloud Export',
            subject: 'CFD Visualization',
            author: 'CoandaCloud',
            keywords: 'CFD, visualization, OpenFOAM',
            creator: 'CoandaCloud'
          });

          // Generate PDF as data URL
          const pdfData = doc.output('dataurlstring');
          resolve(pdfData);
        };
        img.onerror = (err) => {
          console.error('[PDF Export] Failed to load PNG image', err);
          reject(new Error('Failed to load PNG image for PDF'));
        };
        img.src = pngDataURL;
      });
    } catch (err) {
      console.error('[PDF Export] Error generating PDF:', err);
      throw err;
    }
  }

  async toggleVideoRecording() {
    if (this.isRecording) {
      await this.stopVideoRecording();
    } else {
      await this.startVideoRecording();
    }
  }

  async startVideoRecording() {
    if (this.isRecording) return;

    const canvas = this.engine?.renderer?.domElement;
    if (!canvas || typeof canvas.captureStream !== 'function') {
      Utils.notify('Video recording is not supported in this browser.', 'error');
      return;
    }

    const mimeType = this.getSupportedRecordingMimeType();
    if (!mimeType) {
      Utils.notify('No supported video format available for recording.', 'error');
      return;
    }

    try {
      const stream = canvas.captureStream(60);
      this.recordedChunks = [];
      this.mediaRecorder = new MediaRecorder(stream, { mimeType });
      this.recordingMimeType = mimeType;
      this.mediaRecorder.ondataavailable = (event) => {
        if (event.data && event.data.size > 0) {
          this.recordedChunks.push(event.data);
        }
      };
      this.mediaRecorder.onstop = () => this.handleRecordingStop();
      this.mediaRecorder.start();
      this.isRecording = true;
      this.controls?.setRecordingState?.(true);
      Utils.notify('Recording started.', 'info');
    } catch (err) {
      console.error('Failed to start recording:', err);
      Utils.notify(`Failed to start recording: ${err.message}`, 'error');
      this.mediaRecorder = null;
      this.recordedChunks = [];
      this.recordingMimeType = null;
      this.controls?.setRecordingState?.(false);
    }
  }

  async stopVideoRecording() {
    if (!this.isRecording || !this.mediaRecorder) return;

    try {
      if (this.mediaRecorder.state !== 'inactive') {
        this.mediaRecorder.stop();
      }
      Utils.notify('Finalizing recording...', 'info');
    } catch (err) {
      console.error('Failed to stop recording:', err);
      Utils.notify(`Failed to stop recording: ${err.message}`, 'error');
    } finally {
      this.isRecording = false;
      this.controls?.setRecordingState?.(false);
    }
  }

  async handleRecordingStop() {
    try {
      if (!this.recordedChunks || this.recordedChunks.length === 0) {
        Utils.notify('No video data captured.', 'warning');
        return;
      }

      const timestamp = Date.now();
      const blob = new Blob(this.recordedChunks, {
        type: this.recordingMimeType || 'video/webm'
      });
      const filename = `coandacloud-${timestamp}.webm`;
      Utils.downloadBlob(blob, filename);

      // Capture legend with same timestamp as video
      await this.captureLegend(timestamp);

      Utils.notify('Video and legend saved', 'success');
    } catch (err) {
      console.error('Failed to save recording:', err);
      Utils.notify(`Failed to save recording: ${err.message}`, 'error');
    } finally {
      this.mediaRecorder = null;
      this.recordedChunks = [];
      this.recordingMimeType = null;
    }
  }

  getSupportedRecordingMimeType() {
    if (typeof MediaRecorder === 'undefined') {
      return null;
    }

    if (typeof MediaRecorder.isTypeSupported !== 'function') {
      return 'video/webm';
    }

    const candidates = [
      'video/webm;codecs=vp9',
      'video/webm;codecs=vp8',
      'video/webm'
    ];

    return candidates.find(type => MediaRecorder.isTypeSupported(type)) || null;
  }

  // Stats update
  startStatsUpdate() {
    setInterval(() => {
      this.updateStats();
    }, 1000);
  }

  updateStats() {
    const stats = this.engine.getStats();
    this.controls.updateStats(stats);
  }

  buildDataProperties() {
    if (!this.currentData) return null;

    const metadata = this.currentData.metadata || {};
    const props = {};

    props['Type'] = this.formatLabel(this.currentData.type || 'unknown');
    if (metadata.fileName) {
      // Extract just the filename without the path
      const fileName = metadata.fileName.split('/').pop().split('\\').pop();
      props['File Name'] = fileName;
    }
    if (metadata.format) props['Format'] = metadata.format;
    if (metadata.fileSize) props['File Size'] = Utils.formatBytes(metadata.fileSize);
    if (metadata.pointCount || metadata.vertexCount) {
      const points = metadata.pointCount || metadata.vertexCount;
      props['Points'] = points.toLocaleString();
    }
    if (metadata.faceCount) {
      props['Faces'] = metadata.faceCount.toLocaleString();
    }
    if (typeof metadata.timeSteps === 'number') {
      props['Timesteps'] = metadata.timeSteps;
    }
    if (metadata.currentTime != null) {
      props['Current Time'] = this.formatNumber(metadata.currentTime);
    }
    if (Array.isArray(this.currentData.available_fields) && this.currentData.available_fields.length > 0) {
      props['Fields'] = this.currentData.available_fields.join(', ');
    }
    if (this.currentData.case_path) {
      // Extract just the directory name without the full path
      const casePath = this.currentData.case_path.split('/').pop().split('\\').pop();
      props['Case Path'] = casePath;
    }
    if (Array.isArray(this.currentData.timesteps) && this.currentData.timesteps.length > 1) {
      const first = this.currentData.timesteps[0];
      const last = this.currentData.timesteps[this.currentData.timesteps.length - 1];
      props['Time Range'] = `${this.formatNumber(first)} → ${this.formatNumber(last)}`;
    }

    return {
      type: 'info',
      title: 'Dataset',
      items: props
    };
  }

  buildObjectProperties(entry, id) {
    if (!entry) return null;
    const metadata = entry.metadata || {};
    if (metadata.type === 'filter' && this.filters?.hasFilter(id)) {
      return this.buildFilterProperties(id, metadata);
    }

    const props = {
      'Object ID': id,
      'Type': this.formatLabel(metadata.kind || metadata.type || 'object')
    };

    if (metadata.options && typeof metadata.options === 'object') {
      Object.entries(metadata.options).forEach(([key, value]) => {
        props[this.formatLabel(key)] = this.formatOptionValue(value);
      });
    }

    const object = entry.object;
    const geometry = object?.geometry;
    if (geometry?.attributes?.position) {
      props['Vertices'] = geometry.attributes.position.count.toLocaleString();
    }
    if (geometry?.index) {
      props['Triangles'] = Math.round(geometry.index.count / 3).toLocaleString();
    }
    if (object instanceof THREE.Group) {
      props['Children'] = object.children.length;
    }

    return {
      type: 'info',
      title: metadata.name || this.formatLabel(metadata.kind || metadata.type || 'object'),
      items: props
    };
  }

  buildFilterProperties(id, metadata) {
    const filterEntry = this.filters?.getFilter(id);
    if (!filterEntry) {
      return {
        type: 'info',
        title: metadata.name || 'Filter',
        items: { Status: 'Unavailable' }
      };
    }

    const schema = this.getFilterSchema(filterEntry.type, filterEntry.options);
    const info = {
      'Filter Type': this.formatLabel(filterEntry.type)
    };

    if (filterEntry.options?.variable) {
      info.Variable = this.formatLabel(filterEntry.options.variable);
    }
    if (filterEntry.options?.seedType) {
      info['Seed Type'] = this.formatLabel(filterEntry.options.seedType);
    }
    if (filterEntry.options?.seedCount != null) {
      info['Seed Points'] = filterEntry.options.seedCount.toLocaleString();
    }

    return {
      type: 'filter',
      id,
      kind: filterEntry.type,
      title: metadata.name || this.formatLabel(filterEntry.type),
      info,
      schema,
      values: filterEntry.options
    };
  }

  getFilterSchema(type, options = {}) {
    const colormapChoices = this.getColormapChoices();

    switch (type) {
      case 'contour': {
        let fieldChoices = this.getFieldChoices('scalar');
        if (fieldChoices.length === 0) {
          fieldChoices = this.getFieldChoices();
        }
        const variable = options.variable || (fieldChoices[0]?.value || '');
        const range = this.getFieldRange(variable);
        const step = range && range.span > 0 ? range.span / 200 : 0.01;
        return [
          {
            name: 'variable',
            label: 'Variable',
            type: 'select',
            choices: fieldChoices,
            value: variable
          },
          {
            name: 'value',
            label: 'Iso Value',
            type: 'number',
            value: options.value ?? (range ? (range.min + range.max) / 2 : 0),
            min: range?.min,
            max: range?.max,
            step,
            valueType: 'float'
          },
          {
            name: 'colormap',
            label: 'Colormap',
            type: 'select',
            choices: colormapChoices,
            value: options.colormap || 'viridis'
          }
        ];
      }

      case 'slice': {
        let fieldChoices = this.getFieldChoices('scalar');
        if (fieldChoices.length === 0) {
          fieldChoices = this.getFieldChoices();
        }
        const plane = options.plane || 'xy';
        const axisBounds = this.getAxisBounds(plane);
        const variable = options.variable || (fieldChoices[0]?.value || '');
        return [
          {
            name: 'plane',
            label: 'Plane',
            type: 'select',
            choices: [
              { value: 'xy', label: 'XY (Z constant)' },
              { value: 'xz', label: 'XZ (Y constant)' },
              { value: 'yz', label: 'YZ (X constant)' }
            ],
            value: plane
          },
          {
            name: 'position',
            label: 'Position',
            type: 'number',
            value: options.position ?? axisBounds.center,
            min: axisBounds.min,
            max: axisBounds.max,
            step: axisBounds.span / 200 || 0.01,
            valueType: 'float'
          },
          {
            name: 'variable',
            label: 'Scalar Field',
            type: 'select',
            choices: fieldChoices,
            value: variable
          },
          {
            name: 'colormap',
            label: 'Colormap',
            type: 'select',
            choices: colormapChoices,
            value: options.colormap || 'viridis'
          }
        ];
      }

      case 'streamlines': {
        const defaults = this.filters?.getStreamlineDefaultParams(this.currentData) || this.getFallbackStreamlineDefaults();
        const seedType = options.seedType || 'point';
        const colormap = options.colormap || 'jet';
        const valueOr = (value, fallback) => (value != null ? value : fallback);

        let vectorChoices = this.getFieldChoices('vector');
        if (vectorChoices.length === 0) {
          vectorChoices = this.getFieldChoices();
        }
        const vectorField = options.variable || (vectorChoices[0]?.value || 'velocity');

        const pointDefaults = defaults.pointCenter || [0, 0, 0];
        const lineStartDefaults = defaults.lineStart || [-1, 0, 0];
        const lineEndDefaults = defaults.lineEnd || [1, 0, 0];
        const planeOriginDefaults = defaults.planeOrigin || [0, 0, 0];
        const planeNormalDefaults = defaults.planeNormal || [0, 0, 1];

        return [
          {
            name: 'seedType',
            label: 'Seed Type',
            type: 'select',
            choices: [
              { value: 'point', label: 'Point Cloud' },
              { value: 'line', label: 'Line' },
              { value: 'plane', label: 'Plane' }
            ],
            value: seedType
          },
          {
            name: 'variable',
            label: 'Vector Field',
            type: 'select',
            choices: vectorChoices,
            value: vectorField
          },
          {
            name: 'maxLength',
            label: 'Max Length',
            type: 'number',
            value: valueOr(options.maxLength, defaults.maxLength),
            min: 1,
            step: 1,
            valueType: 'float'
          },
          {
            name: 'stepSize',
            label: 'Step Size',
            type: 'number',
            value: valueOr(options.stepSize, defaults.stepSize),
            min: 0.00005,
            step: defaults.stepSize / 4 || 0.0005,
            valueType: 'float'
          },
          {
            name: 'previewEnabled',
            label: 'Preview Particles',
            type: 'select',
            choices: [
              { value: 'true', label: 'Enabled' },
              { value: 'false', label: 'Disabled' }
            ],
            value: valueOr(String(options.previewEnabled !== false), 'true')
          },
          {
            name: 'previewDotScale',
            label: 'Preview Dot Scale',
            type: 'number',
            value: valueOr(options.previewDotScale, defaults.previewDotScale),
            min: 0.0001,
            step: (defaults.previewDotScale || 0.004) / 4 || 0.0005,
            valueType: 'float'
          },
          {
            name: 'previewSpeed',
            label: 'Preview Speed',
            type: 'number',
            value: valueOr(options.previewSpeed, defaults.previewSpeed),
            min: 0,
            step: (defaults.previewSpeed || 1) / 4 || 0.1,
            valueType: 'float'
          },
          {
            name: 'colormap',
            label: 'Colormap',
            type: 'select',
            choices: colormapChoices,
            value: colormap
          },
          {
            name: 'pointCount',
            label: 'Number of Seeds',
            type: 'number',
            group: 'seed-point',
            value: Math.max(1, Math.round(valueOr(options.pointCount, defaults.pointCount))),
            min: 1,
            step: 1,
            valueType: 'int'
          },
          {
            name: 'pointRadius',
            label: 'Radius',
            type: 'number',
            group: 'seed-point',
            value: Math.max(valueOr(options.pointRadius, defaults.pointRadius), 0.001),
            min: 0.001,
            step: 0.05,
            valueType: 'float'
          },
          {
            name: 'pointCenterX',
            label: 'Center X',
            type: 'number',
            group: 'seed-point',
            pickGroup: 'point-center',
            pickLabel: 'Pick Center',
            value: valueOr(options.pointCenterX, pointDefaults[0]),
            step: 0.1,
            valueType: 'float'
          },
          {
            name: 'pointCenterY',
            label: 'Center Y',
            type: 'number',
            group: 'seed-point',
            pickGroup: 'point-center',
            value: valueOr(options.pointCenterY, pointDefaults[1]),
            step: 0.1,
            valueType: 'float'
          },
          {
            name: 'pointCenterZ',
            label: 'Center Z',
            type: 'number',
            group: 'seed-point',
            pickGroup: 'point-center',
            value: valueOr(options.pointCenterZ, pointDefaults[2]),
            step: 0.1,
            valueType: 'float'
          },
          {
            name: 'lineResolution',
            label: 'Samples',
            type: 'number',
            group: 'seed-line',
            value: Math.max(2, Math.round(valueOr(options.lineResolution, defaults.lineResolution))),
            min: 2,
            step: 1,
            valueType: 'int'
          },
          {
            name: 'lineStartX',
            label: 'Start X',
            type: 'number',
            group: 'seed-line',
            pickGroup: 'line-start',
            pickLabel: 'Pick Start',
            value: valueOr(options.lineStartX, lineStartDefaults[0]),
            step: 0.1,
            valueType: 'float'
          },
          {
            name: 'lineStartY',
            label: 'Start Y',
            type: 'number',
            group: 'seed-line',
            pickGroup: 'line-start',
            value: valueOr(options.lineStartY, lineStartDefaults[1]),
            step: 0.1,
            valueType: 'float'
          },
          {
            name: 'lineStartZ',
            label: 'Start Z',
            type: 'number',
            group: 'seed-line',
            pickGroup: 'line-start',
            value: valueOr(options.lineStartZ, lineStartDefaults[2]),
            step: 0.1,
            valueType: 'float'
          },
          {
            name: 'lineEndX',
            label: 'End X',
            type: 'number',
            group: 'seed-line',
            pickGroup: 'line-end',
            pickLabel: 'Pick End',
            value: valueOr(options.lineEndX, lineEndDefaults[0]),
            step: 0.1,
            valueType: 'float'
          },
          {
            name: 'lineEndY',
            label: 'End Y',
            type: 'number',
            group: 'seed-line',
            pickGroup: 'line-end',
            value: valueOr(options.lineEndY, lineEndDefaults[1]),
            step: 0.1,
            valueType: 'float'
          },
          {
            name: 'lineEndZ',
            label: 'End Z',
            type: 'number',
            group: 'seed-line',
            pickGroup: 'line-end',
            value: valueOr(options.lineEndZ, lineEndDefaults[2]),
            step: 0.1,
            valueType: 'float'
          },
          {
            name: 'planeWidth',
            label: 'Width',
            type: 'number',
            group: 'seed-plane',
            value: Math.max(valueOr(options.planeWidth, defaults.planeWidth), 0.01),
            min: 0.01,
            step: 0.1,
            valueType: 'float'
          },
          {
            name: 'planeHeight',
            label: 'Height',
            type: 'number',
            group: 'seed-plane',
            value: Math.max(valueOr(options.planeHeight, defaults.planeHeight), 0.01),
            min: 0.01,
            step: 0.1,
            valueType: 'float'
          },
          {
            name: 'planeResolutionU',
            label: 'Resolution U',
            type: 'number',
            group: 'seed-plane',
            value: Math.max(2, Math.round(valueOr(options.planeResolutionU, defaults.planeResolutionU))),
            min: 2,
            step: 1,
            valueType: 'int'
          },
          {
            name: 'planeResolutionV',
            label: 'Resolution V',
            type: 'number',
            group: 'seed-plane',
            value: Math.max(2, Math.round(valueOr(options.planeResolutionV, defaults.planeResolutionV))),
            min: 2,
            step: 1,
            valueType: 'int'
          },
          {
            name: 'planeOriginX',
            label: 'Origin X',
            type: 'number',
            group: 'seed-plane',
            pickGroup: 'plane-origin',
            pickLabel: 'Pick Origin',
            value: valueOr(options.planeOriginX, planeOriginDefaults[0]),
            step: 0.1,
            valueType: 'float'
          },
          {
            name: 'planeOriginY',
            label: 'Origin Y',
            type: 'number',
            group: 'seed-plane',
            pickGroup: 'plane-origin',
            value: valueOr(options.planeOriginY, planeOriginDefaults[1]),
            step: 0.1,
            valueType: 'float'
          },
          {
            name: 'planeOriginZ',
            label: 'Origin Z',
            type: 'number',
            group: 'seed-plane',
            pickGroup: 'plane-origin',
            value: valueOr(options.planeOriginZ, planeOriginDefaults[2]),
            step: 0.1,
            valueType: 'float'
          },
          {
            name: 'planeNormalX',
            label: 'Normal X',
            type: 'number',
            group: 'seed-plane',
            pickGroup: 'plane-normal',
            pickLabel: 'Pick Normal',
            value: valueOr(options.planeNormalX, planeNormalDefaults[0]),
            step: 0.1,
            valueType: 'float'
          },
          {
            name: 'planeNormalY',
            label: 'Normal Y',
            type: 'number',
            group: 'seed-plane',
            pickGroup: 'plane-normal',
            value: valueOr(options.planeNormalY, planeNormalDefaults[1]),
            step: 0.1,
            valueType: 'float'
          },
          {
            name: 'planeNormalZ',
            label: 'Normal Z',
            type: 'number',
            group: 'seed-plane',
            pickGroup: 'plane-normal',
            value: valueOr(options.planeNormalZ, planeNormalDefaults[2]),
            step: 0.1,
            valueType: 'float'
          }
        ];
      }

      case 'threshold': {
        let fieldChoices = this.getFieldChoices('scalar');
        if (fieldChoices.length === 0) {
          fieldChoices = this.getFieldChoices();
        }
        const variable = options.variable || (fieldChoices[0]?.value || '');
        const range = this.getFieldRange(variable);
        const defaults = range || { min: 0, max: 1, span: 1 };
        return [
          {
            name: 'variable',
            label: 'Variable',
            type: 'select',
            choices: fieldChoices,
            value: variable
          },
          {
            name: 'min',
            label: 'Minimum',
            type: 'number',
            value: options.min ?? defaults.min,
            min: defaults.min,
            max: defaults.max,
            step: defaults.span / 200 || 0.01,
            valueType: 'float'
          },
          {
            name: 'max',
            label: 'Maximum',
            type: 'number',
            value: options.max ?? defaults.max,
            min: defaults.min,
            max: defaults.max,
            step: defaults.span / 200 || 0.01,
            valueType: 'float'
          },
          {
            name: 'colormap',
            label: 'Colormap',
            type: 'select',
            choices: colormapChoices,
            value: options.colormap || 'viridis'
          }
        ];
      }

      case 'vectors': {
        let fieldChoices = this.getFieldChoices('vector');
        if (fieldChoices.length === 0) {
          fieldChoices = this.getFieldChoices();
        }
        const variable = options.variable || (fieldChoices[0]?.value || '');
        return [
          {
            name: 'variable',
            label: 'Vector Field',
            type: 'select',
            choices: fieldChoices,
            value: variable
          },
          {
            name: 'scale',
            label: 'Scale',
            type: 'number',
            value: options.scale ?? 1,
            min: 0.01,
            step: 0.1,
            valueType: 'float'
          },
          {
            name: 'skipFactor',
            label: 'Skip Factor',
            type: 'number',
            value: options.skipFactor ?? 2,
            min: 1,
            max: 100,
            step: 1,
            valueType: 'int'
          },
          {
            name: 'colormap',
            label: 'Colormap',
            type: 'select',
            choices: colormapChoices,
            value: options.colormap || 'viridis'
          }
        ];
      }

      default:
        return [];
    }
  }

  getFieldChoices(kind = 'any') {
    if (!this.currentData) {
      return [];
    }

    const fieldMap = this.currentData.fields || {};
    const names = Array.isArray(this.currentData.available_fields) && this.currentData.available_fields.length > 0
      ? this.currentData.available_fields.slice()
      : Object.keys(fieldMap);

    const choices = [];
    names.forEach(name => {
      const field = fieldMap[name];
      if (!field) {
        if (kind === 'any') {
          choices.push({ value: name, label: this.formatLabel(name) });
        }
        return;
      }
      const sample = field[0];
      const isScalar = Number.isFinite(sample);
      const isVector = Array.isArray(sample);

      if (kind === 'scalar' && !isScalar) return;
      if (kind === 'vector' && !isVector) return;

      choices.push({
        value: name,
        label: this.formatLabel(name)
      });
    });

    return choices;
  }

  getColormapChoices() {
    return Object.keys(Colormap.presets).map(key => ({
      value: key,
      label: this.formatLabel(key)
    }));
  }

  getFieldRange(variable) {
    if (!variable || !this.currentData?.fields) {
      return null;
    }
    const scalars = this.currentData.fields[variable];
    if (!Array.isArray(scalars) && !(scalars instanceof Float32Array) && !(scalars instanceof Float64Array)) {
      return null;
    }

    let min = Infinity;
    let max = -Infinity;
    for (let i = 0; i < scalars.length; i++) {
      const value = scalars[i];
      if (!Number.isFinite(value)) continue;
      if (value < min) min = value;
      if (value > max) max = value;
    }

    if (min === Infinity || max === -Infinity) {
      return null;
    }

    return {
      min,
      max,
      span: max - min
    };
  }

  getAxisBounds(plane) {
    const bounds = this.currentData?.grid?.bounds || {
      xmin: -1, xmax: 1,
      ymin: -1, ymax: 1,
      zmin: -1, zmax: 1
    };

    switch (plane) {
      case 'xy':
        return {
          min: bounds.zmin ?? -1,
          max: bounds.zmax ?? 1,
          span: (bounds.zmax ?? 1) - (bounds.zmin ?? -1),
          center: ((bounds.zmax ?? 1) + (bounds.zmin ?? -1)) / 2
        };
      case 'xz':
        return {
          min: bounds.ymin ?? -1,
          max: bounds.ymax ?? 1,
          span: (bounds.ymax ?? 1) - (bounds.ymin ?? -1),
          center: ((bounds.ymax ?? 1) + (bounds.ymin ?? -1)) / 2
        };
      case 'yz':
      default:
        return {
          min: bounds.xmin ?? -1,
          max: bounds.xmax ?? 1,
          span: (bounds.xmax ?? 1) - (bounds.xmin ?? -1),
          center: ((bounds.xmax ?? 1) + (bounds.xmin ?? -1)) / 2
        };
    }
  }

  getFallbackStreamlineDefaults() {
    return {
      pointCenter: [0, 0, 0],
      pointRadius: 1,
      pointCount: 200,
      lineStart: [-1, 0, 0],
      lineEnd: [1, 0, 0],
      lineResolution: 100,
      planeOrigin: [0, 0, 0],
      planeNormal: [0, 0, 1],
      planeWidth: 2,
      planeHeight: 2,
      planeResolutionU: 20,
      planeResolutionV: 20
    };
  }

  async updateFilterOptions(id, updates) {
    if (!this.filters || !this.currentData) {
      return;
    }
    const result = await this.filters.updateFilter(id, this.currentData, updates);
    if (result) {
      this.selectPipelineItem(id);
      this.updateStats();
      Utils.notify('Filter updated', 'success');
    } else {
      Utils.notify('Unable to update filter with the provided parameters', 'warning');
    }
  }

  formatLabel(text) {
    if (!text) return '';
    return String(text)
      .replace(/[_-]+/g, ' ')
      .replace(/\b\w/g, c => c.toUpperCase());
  }

  getIconForKind(kind = '') {
    const map = {
      contour: '📈',
      streamlines: '<img src="static/assets/icons/streamlines.svg" width="16" height="16" style="filter: brightness(0) invert(1); vertical-align: middle;">',
      slice: '✂️',
      vectors: '➡️',
      threshold: '📊',
      mesh: '🧊',
      points: '🔹',
      surface: '🟦'
    };
    return map[kind] || '🧩';
  }

  formatNumber(value) {
    if (!Number.isFinite(value)) {
      return String(value);
    }
    const abs = Math.abs(value);
    if ((abs !== 0 && abs < 0.001) || abs >= 10000) {
      return value.toExponential(2);
    }
    return value.toFixed(3);
  }

  formatOptionValue(value) {
    if (Array.isArray(value)) {
      if (value.length === 0) return '[]';
      if (Array.isArray(value[0])) {
        return `${value.length} items`;
      }
      if (value.every(v => typeof v === 'number')) {
        return value.map(v => this.formatNumber(v)).join(', ');
      }
      return value.join(', ');
    }
    if (typeof value === 'number') {
      return this.formatNumber(value);
    }
    if (typeof value === 'boolean') {
      return value ? 'Yes' : 'No';
    }
    if (value == null) {
      return '—';
    }
    if (typeof value === 'object') {
      try {
        return JSON.stringify(value);
      } catch {
        return String(value);
      }
    }
    return String(value);
  }

  getGridPointsMetadata() {
    const metadata = {
      type: 'visualization',
      kind: 'points',
      name: 'Point Representation',
      icon: '🔹',
      removable: false
    };

    if (this.colormap && Number.isFinite(this.colormap.min) && Number.isFinite(this.colormap.max)) {
      metadata.options = {
        variable: this.colormap.variable,
        min: this.colormap.min,
        max: this.colormap.max,
        preset: this.colormap.preset
      };
    }

    return metadata;
  }

  updateInfo(metadata) {
    const infoPanel = document.getElementById('infoPanel');
    // Info panel was removed - skip if not present
    if (!infoPanel) return;

    if (!metadata || Object.keys(metadata).length === 0) {
      infoPanel.innerHTML = `
        <p><strong>CoandaCloud v1.0</strong></p>
        <p>Browser-based scientific visualization tool</p>
        <hr>
        <p class="text-muted">Load a data file or connect to a simulation to begin.</p>
      `;
      return;
    }
    // Extract just the filename without the path
    const fileName = metadata.fileName
      ? metadata.fileName.split('/').pop().split('\\').pop()
      : 'Unknown File';

    infoPanel.innerHTML = `
      <p><strong>${metadata.format || 'Unknown Format'}</strong></p>
      <p>${fileName}</p>
      <hr>
      <p>Size: ${Utils.formatBytes(metadata.fileSize || 0)}</p>
      <p>Points: ${(metadata.vertexCount || metadata.pointCount || 0).toLocaleString()}</p>
      ${metadata.timeSteps ? `<p>Timesteps: ${metadata.timeSteps}</p>` : ''}
      ${Number.isFinite(metadata.time_start) && Number.isFinite(metadata.time_end)
        ? `<p>Time Range: ${Number(metadata.time_start).toFixed(3)}s → ${Number(metadata.time_end).toFixed(3)}s</p>`
        : ''}
      ${metadata.faceCount ? `<p>Faces: ${metadata.faceCount.toLocaleString()}</p>` : ''}
    `;
  }

  updateCaseNameDisplay(caseName) {
    const caseNameDisplay = document.getElementById('caseNameDisplay');
    const caseNameToolbar = document.getElementById('caseNameToolbar');

    console.log('[updateCaseNameDisplay] Called with:', caseName);
    console.log('[updateCaseNameDisplay] Elements found:', {
      caseNameDisplay: !!caseNameDisplay,
      caseNameToolbar: !!caseNameToolbar
    });

    if (caseNameDisplay && caseNameToolbar) {
      // Extract just the folder name from the path
      let displayName = caseName ? caseName.split('/').pop().split('\\').pop() : '-';

      // If the display name looks like a UUID, show a friendlier name
      const uuidPattern = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
      if (uuidPattern.test(displayName)) {
        displayName = 'Simulation';
      }

      // Clean up uploaded_case_YYYYMMDD_HHMMSS_hash format to be more readable
      const uploadedCasePattern = /^uploaded_case_(\d{4})(\d{2})(\d{2})_(\d{2})(\d{2})(\d{2})_[a-f0-9]+$/i;
      const match = displayName.match(uploadedCasePattern);
      if (match) {
        const [, year, month, day, hour, min, sec] = match;
        displayName = `Upload ${day}/${month}/${year} ${hour}:${min}`;
      }

      caseNameDisplay.textContent = displayName;
      caseNameToolbar.style.display = caseName ? 'flex' : 'none';
      console.log('[updateCaseNameDisplay] Updated to:', displayName, 'display:', caseNameToolbar.style.display);
    } else {
      console.warn('[updateCaseNameDisplay] Elements not found in DOM');
    }
  }

  // ============ MESH PARTS METHODS ============

  updateMeshPartsUI(boundaries) {
    console.log('updateMeshPartsUI called with boundaries:', boundaries);
    const meshPartsPanel = document.getElementById('meshPartsPanel');
    const meshPartsList = document.getElementById('meshPartsList');
    const boundaryPatchesList = document.getElementById('boundaryPatchesList');

    // Restore internal mesh checkbox visibility (may have been hidden by multi-part OBJ)
    const internalCheckbox = document.getElementById('partInternalMesh');
    if (internalCheckbox) {
      internalCheckbox.closest('.mesh-part-item').style.display = '';
    }

    if (!boundaries || Object.keys(boundaries).length === 0) {
      console.warn('No boundaries data available');
      meshPartsPanel.querySelector('.text-muted').style.display = 'block';
      meshPartsList.style.display = 'none';
      return;
    }

    console.log(`Found ${Object.keys(boundaries).length} boundary patches`);
    const sampleEntry = Object.entries(boundaries)[0];
    console.log('Sample boundary:', sampleEntry[0], '=', sampleEntry[1]);

    meshPartsPanel.querySelector('.text-muted').style.display = 'none';
    meshPartsList.style.display = 'block';

    // Store boundaries
    this.meshParts.boundaries = boundaries;

    // Dynamically detect the internal mesh part name from the actual Group
    this.detectInternalMeshPartName();

    // Show/hide internal mesh checkbox based on whether one was detected
    const internalItem = internalCheckbox ? internalCheckbox.closest('.mesh-part-item') : null;
    if (internalItem) {
      internalItem.style.display = this.meshParts.internalMeshPartName ? '' : 'none';
    }

    // Initialize visibleParts with all actual part names (internal + boundaries)
    this.meshParts.visibleParts = new Set([
      ...(this.meshParts.internalMeshPartName ? [this.meshParts.internalMeshPartName] : []),
      ...Object.keys(boundaries)
    ]);

    console.log('Initialized visibleParts:', Array.from(this.meshParts.visibleParts));
    console.log('Internal mesh part name:', this.meshParts.internalMeshPartName);

    // Clear and populate boundary patches list
    boundaryPatchesList.innerHTML = '';

    // Group patches by common prefix
    const groups = this.groupPatchesByPrefix(boundaries);

    groups.forEach(group => {
      if (group.patches.length === 1) {
        // Single patch - no group needed
        const [patchName, patchData] = group.patches[0];
        const patchItem = this.createPatchItem(patchName, patchData);
        boundaryPatchesList.appendChild(patchItem);
      } else {
        // Multiple patches - create collapsible group
        const groupElement = this.createPatchGroup(group);
        boundaryPatchesList.appendChild(groupElement);
      }
    });

    // Setup control buttons
    const btnShowAll = document.getElementById('btnShowAllParts');
    const btnHideAll = document.getElementById('btnHideAllParts');

    btnShowAll.onclick = () => this.showAllMeshParts();
    btnHideAll.onclick = () => this.hideAllMeshParts();

    // Internal mesh checkbox - use dynamically detected part name
    if (internalCheckbox) {
      internalCheckbox.onchange = (e) => {
        if (this.meshParts.internalMeshPartName) {
          this.toggleMeshPart(this.meshParts.internalMeshPartName, e.target.checked);
        }
      };
    }

    // Refresh solidify filter part selector if the solidify panel is open
    this.updateSolidifyPartsList();
  }

  detectInternalMeshPartName() {
    // Detect the internal mesh part name by finding the part that's NOT in boundaries
    const meshObjectId = this.meshParts.activeMeshObjectId || 'grid-surface';
    const meshObject = this.engine.objects.get(meshObjectId) || this.engine.objects.get('main-mesh');

    if (!meshObject || !meshObject.object) {
      console.warn('[detectInternalMeshPartName] No mesh object found');
      this.meshParts.internalMeshPartName = null;
      return;
    }

    const obj = meshObject.object;

    if (obj.type === 'Group') {
      // Recursively get all meshes and find the one that's NOT in boundaries
      const allMeshes = this.getAllMeshesInGroup(obj);
      const boundaryNames = new Set(Object.keys(this.meshParts.boundaries));

      for (const mesh of allMeshes) {
        const partName = mesh.userData.partName;
        if (partName && !boundaryNames.has(partName)) {
          // This is the internal mesh (not a boundary)
          this.meshParts.internalMeshPartName = partName;
          console.log(`[detectInternalMeshPartName] Found internal mesh: '${partName}'`);
          return;
        }
      }

      console.warn('[detectInternalMeshPartName] No internal mesh found in Group');
      this.meshParts.internalMeshPartName = null;
    } else {
      // Single mesh - use its part name or default
      this.meshParts.internalMeshPartName = obj.userData.partName || 'mesh';
      console.log(`[detectInternalMeshPartName] Single mesh: '${this.meshParts.internalMeshPartName}'`);
    }
  }

  groupPatchesByPrefix(boundaries) {
    // Group patches using boundary type metadata from the solver.
    // Falls back to prefix-based grouping when type info is unavailable.

    const patches = Object.entries(boundaries);

    // Check if type metadata is available (OpenFOAM boundary file provides it)
    const hasTypeMetadata = patches.some(([_, data]) => data && data.type);

    if (hasTypeMetadata) {
      return this._groupPatchesByType(patches);
    }
    return this._groupPatchesByNamePrefix(patches);
  }

  _groupPatchesByType(patches) {
    // Group patches by their solver-defined type (wall, patch, cyclicAMI, etc.)
    // Then create subgroups within each type based on name prefixes
    const typeGroups = new Map();

    patches.forEach(([name, data]) => {
      const patchType = (data && data.type) || 'other';
      if (!typeGroups.has(patchType)) {
        typeGroups.set(patchType, []);
      }
      typeGroups.get(patchType).push([name, data]);
    });

    const groups = [];

    // Display-friendly names for common OpenFOAM boundary types
    const typeLabels = {
      'wall': 'Wall',
      'patch': 'Patch',
      'cyclicAMI': 'Cyclic AMI',
      'cyclic': 'Cyclic',
      'symmetryPlane': 'Symmetry Plane',
      'symmetry': 'Symmetry',
      'wedge': 'Wedge',
      'empty': 'Empty',
      'processor': 'Processor',
      'overset': 'Overset',
      'other': 'Other'
    };

    typeGroups.forEach((groupPatches, patchType) => {
      const label = typeLabels[patchType] || patchType;
      if (groupPatches.length === 1) {
        // Single patch in this type — no group needed
        groups.push({
          name: groupPatches[0][0],
          patches: [groupPatches[0]]
        });
      } else {
        // Multiple patches — create subgroups by name prefix
        const subgroups = this._createSubgroupsByPrefix(groupPatches);
        groups.push({
          name: `${label} (${patchType})`,
          patches: groupPatches,
          subgroups: subgroups
        });
      }
    });

    groups.sort((a, b) => a.name.localeCompare(b.name));
    return groups;
  }

  _createSubgroupsByPrefix(patches) {
    // Group patches by common name prefix for nested display
    const MIN_PREFIX_LENGTH = 3;
    const MIN_GROUP_SIZE = 2;

    const prefixGroups = new Map();

    patches.forEach(([name, data]) => {
      // Split on delimiters; use first segment as prefix
      const firstSegment = name.split(/[_:%-]/)[0];
      let prefix = firstSegment.length >= MIN_PREFIX_LENGTH
        ? firstSegment
        : name.substring(0, Math.min(MIN_PREFIX_LENGTH, name.length));

      // Strip trailing digits so AMI1/AMI2 share prefix "AMI"
      const alphaPrefix = prefix.replace(/\d+$/, '');
      if (alphaPrefix.length >= MIN_PREFIX_LENGTH) {
        prefix = alphaPrefix;
      }

      if (!prefixGroups.has(prefix)) {
        prefixGroups.set(prefix, []);
      }
      prefixGroups.get(prefix).push([name, data]);
    });

    const subgroups = [];

    prefixGroups.forEach((groupPatches, prefix) => {
      if (groupPatches.length >= MIN_GROUP_SIZE) {
        // Create a subgroup for patches with common prefix
        subgroups.push({
          name: prefix,
          patches: groupPatches
        });
      }
      // Single patches will be shown directly (no subgroup)
    });

    // Only return subgroups if we have meaningful groupings
    // (at least one subgroup with 2+ patches)
    if (subgroups.length > 0 && subgroups.some(sg => sg.patches.length >= MIN_GROUP_SIZE)) {
      return subgroups;
    }
    return null;
  }

  _groupPatchesByNamePrefix(patches) {
    // Fallback: group by common name prefix (for EnSight, Fluent, etc.)
    const MIN_PREFIX_LENGTH = 3;
    const MIN_GROUP_SIZE = 2;

    const numericPatches = [];
    const namedPatches = [];

    patches.forEach(([name, data]) => {
      if (/^\d+$/.test(name)) {
        numericPatches.push([name, data]);
      } else {
        namedPatches.push([name, data]);
      }
    });

    const groups = [];

    // Handle numeric patches
    if (numericPatches.length > 3) {
      groups.push({
        name: 'Numbered Patches',
        patches: numericPatches.sort((a, b) => parseInt(a[0]) - parseInt(b[0]))
      });
    } else {
      numericPatches.forEach(([name, data]) => {
        groups.push({ name, patches: [[name, data]] });
      });
    }

    // Build prefix groups for named patches
    const prefixGroups = new Map();

    namedPatches.forEach(([name, data]) => {
      // Split on delimiters; use first segment as prefix
      const firstSegment = name.split(/[_:%-]/)[0];
      let prefix = firstSegment.length >= MIN_PREFIX_LENGTH
        ? firstSegment
        : name.substring(0, MIN_PREFIX_LENGTH);

      // Strip trailing digits so AMI1/AMI2 share prefix "AMI"
      const alphaPrefix = prefix.replace(/\d+$/, '');
      if (alphaPrefix.length >= MIN_PREFIX_LENGTH) {
        prefix = alphaPrefix;
      }

      if (!prefixGroups.has(prefix)) {
        prefixGroups.set(prefix, []);
      }
      prefixGroups.get(prefix).push([name, data]);
    });

    prefixGroups.forEach((groupPatches, prefix) => {
      if (groupPatches.length < MIN_GROUP_SIZE) {
        groupPatches.forEach(([name, data]) => {
          groups.push({ name, patches: [[name, data]] });
        });
      } else {
        groups.push({ name: prefix, patches: groupPatches });
      }
    });

    groups.sort((a, b) => a.name.localeCompare(b.name));
    return groups;
  }

  createPatchItem(patchName, patchData) {
    const patchItem = document.createElement('div');
    patchItem.className = 'mesh-part-item';
    patchItem.innerHTML = `
      <input type="checkbox" id="part${patchName}" data-patch="${patchName}" checked>
      <label for="part${patchName}">${patchName} (${patchData.nFaces} faces)</label>
    `;

    const checkbox = patchItem.querySelector('input');
    checkbox.addEventListener('change', (e) => {
      this.toggleMeshPart(patchName, e.target.checked);

      // Update parent group checkbox if this is part of a group
      this.updateGroupCheckboxState(patchName);
    });

    return patchItem;
  }

  updateGroupCheckboxState(patchName) {
    // Find if this patch belongs to a group
    const groups = document.querySelectorAll('.mesh-part-group');
    groups.forEach(groupDiv => {
      try {
        const patches = JSON.parse(groupDiv.dataset.patches || '[]');
        if (patches.includes(patchName)) {
          // This patch belongs to this group
          const groupCheckbox = groupDiv.querySelector('.mesh-part-group-header input[type="checkbox"]');
          if (groupCheckbox) {
            // Check if all children are checked
            const allChecked = patches.every(name => {
              const cb = document.getElementById(`part${name}`);
              return cb && cb.checked;
            });
            groupCheckbox.checked = allChecked;
          }
        }
      } catch (e) {
        console.warn('Error updating group checkbox:', e);
      }
    });
  }

  createPatchGroup(group) {
    const groupDiv = document.createElement('div');
    groupDiv.className = 'mesh-part-group';

    // Calculate total faces
    const totalFaces = group.patches.reduce((sum, [_, data]) => sum + data.nFaces, 0);

    // Create group header (collapsed by default)
    const header = document.createElement('div');
    header.className = 'mesh-part-group-header collapsed';
    header.innerHTML = `
      <span class="expand-icon">▶</span>
      <input type="checkbox" id="group${group.name}" data-group="${group.name}" checked>
      <span class="group-title">${group.name} (${group.patches.length} parts, ${totalFaces} faces)</span>
    `;

    // Create children container (collapsed by default)
    const children = document.createElement('div');
    children.className = 'mesh-part-group-children collapsed';

    // Check if we have subgroups (nested grouping by name prefix)
    if (group.subgroups && group.subgroups.length > 0) {
      // Build a set of patches that belong to subgroups
      const subgroupedPatches = new Set();
      group.subgroups.forEach(sg => {
        sg.patches.forEach(([name]) => subgroupedPatches.add(name));
      });

      // Add subgroups first
      group.subgroups.forEach(subgroup => {
        const subgroupElement = this.createSubgroup(subgroup);
        children.appendChild(subgroupElement);
      });

      // Add ungrouped patches (those not in any subgroup)
      group.patches.forEach(([patchName, patchData]) => {
        if (!subgroupedPatches.has(patchName)) {
          const patchItem = this.createPatchItem(patchName, patchData);
          children.appendChild(patchItem);
        }
      });
    } else {
      // No subgroups - add individual patches directly
      group.patches.forEach(([patchName, patchData]) => {
        const patchItem = this.createPatchItem(patchName, patchData);
        children.appendChild(patchItem);
      });
    }

    groupDiv.appendChild(header);
    groupDiv.appendChild(children);

    // Toggle expand/collapse on header click (but not checkbox)
    header.addEventListener('click', (e) => {
      if (e.target.tagName !== 'INPUT') {
        const isCollapsed = header.classList.toggle('collapsed');
        children.classList.toggle('collapsed');
        // Update expand icon
        const icon = header.querySelector('.expand-icon');
        if (icon) {
          icon.textContent = isCollapsed ? '▶' : '▼';
        }
      }
    });

    // Group checkbox - toggle all children (BATCH UPDATE)
    const groupCheckbox = header.querySelector('input[type="checkbox"]');
    groupCheckbox.addEventListener('change', (e) => {
      e.stopPropagation();
      const checked = e.target.checked;

      // Batch update: modify visibleParts directly for all patches
      group.patches.forEach(([patchName]) => {
        // Use data-patch attribute instead of ID (avoids CSS escaping issues with special chars)
        const childCheckbox = children.querySelector(`input[data-patch="${patchName}"]`);
        if (childCheckbox) {
          childCheckbox.checked = checked;
        }
        // Update visibleParts
        if (checked) {
          this.meshParts.visibleParts.add(patchName);
        } else {
          this.meshParts.visibleParts.delete(patchName);
        }
      });

      // Toggle visibility in the 3D scene (supports nested groups)
      const meshObjectId = this.meshParts.activeMeshObjectId || 'grid-surface';
      const meshObject = this.engine.objects.get(meshObjectId) || this.engine.objects.get('main-mesh');

      if (meshObject && meshObject.object && meshObject.object.type === 'Group') {
        group.patches.forEach(([patchName]) => {
          // Recursively find the mesh in nested groups
          const partMesh = this.findMeshInGroup(meshObject.object, patchName);
          if (partMesh) {
            partMesh.visible = checked;
          }
        });
      }

      this._syncSolidifyVisibility();

      // Notify view lock manager of visibility change
      if (this.viewLockManager) {
        this.viewLockManager.onVisualizationChange();
      }
    });

    // Store group info for later use
    groupDiv.dataset.groupName = group.name;
    groupDiv.dataset.patches = JSON.stringify(group.patches.map(([name]) => name));

    return groupDiv;
  }

  createSubgroup(subgroup) {
    // Create a nested subgroup within a type group
    const subgroupDiv = document.createElement('div');
    subgroupDiv.className = 'mesh-part-subgroup';

    // Calculate total faces
    const totalFaces = subgroup.patches.reduce((sum, [_, data]) => sum + data.nFaces, 0);

    // Create subgroup header (collapsed by default)
    const header = document.createElement('div');
    header.className = 'mesh-part-subgroup-header collapsed';
    header.innerHTML = `
      <span class="expand-icon">▶</span>
      <input type="checkbox" data-subgroup="${subgroup.name}" checked>
      <span class="subgroup-title">${subgroup.name} (${subgroup.patches.length}, ${totalFaces} faces)</span>
    `;

    // Create children container (collapsed by default)
    const children = document.createElement('div');
    children.className = 'mesh-part-subgroup-children collapsed';

    // Add individual patches
    subgroup.patches.forEach(([patchName, patchData]) => {
      const patchItem = this.createPatchItem(patchName, patchData);
      children.appendChild(patchItem);
    });

    subgroupDiv.appendChild(header);
    subgroupDiv.appendChild(children);

    // Toggle expand/collapse on header click
    header.addEventListener('click', (e) => {
      if (e.target.tagName !== 'INPUT') {
        const isCollapsed = header.classList.toggle('collapsed');
        children.classList.toggle('collapsed');
        const icon = header.querySelector('.expand-icon');
        if (icon) {
          icon.textContent = isCollapsed ? '▶' : '▼';
        }
      }
    });

    // Subgroup checkbox - toggle all children
    const subgroupCheckbox = header.querySelector('input[type="checkbox"]');
    subgroupCheckbox.addEventListener('change', (e) => {
      e.stopPropagation();
      const checked = e.target.checked;

      // Update all patches in this subgroup
      subgroup.patches.forEach(([patchName]) => {
        const childCheckbox = children.querySelector(`input[data-patch="${patchName}"]`);
        if (childCheckbox) {
          childCheckbox.checked = checked;
        }
        if (checked) {
          this.meshParts.visibleParts.add(patchName);
        } else {
          this.meshParts.visibleParts.delete(patchName);
        }
      });

      // Toggle visibility in the 3D scene
      const meshObjectId = this.meshParts.activeMeshObjectId || 'grid-surface';
      const meshObject = this.engine.objects.get(meshObjectId) || this.engine.objects.get('main-mesh');

      if (meshObject && meshObject.object && meshObject.object.type === 'Group') {
        subgroup.patches.forEach(([patchName]) => {
          const partMesh = this.findMeshInGroup(meshObject.object, patchName);
          if (partMesh) {
            partMesh.visible = checked;
          }
        });
      }

      this._syncSolidifyVisibility();

      // Update parent group checkbox state
      this.updateParentGroupCheckbox(subgroupDiv);

      if (this.viewLockManager) {
        this.viewLockManager.onVisualizationChange();
      }
    });

    // Store subgroup info
    subgroupDiv.dataset.subgroupName = subgroup.name;
    subgroupDiv.dataset.patches = JSON.stringify(subgroup.patches.map(([name]) => name));

    return subgroupDiv;
  }

  updateParentGroupCheckbox(subgroupDiv) {
    // Find parent group and update its checkbox based on all children states
    const parentGroup = subgroupDiv.closest('.mesh-part-group');
    if (!parentGroup) return;

    const groupCheckbox = parentGroup.querySelector('.mesh-part-group-header input[type="checkbox"]');
    if (!groupCheckbox) return;

    // Check all patch checkboxes within this group
    const allPatches = parentGroup.querySelectorAll('.mesh-part-group-children input[data-patch]');
    const allChecked = Array.from(allPatches).every(cb => cb.checked);
    groupCheckbox.checked = allChecked;
  }

  // Helper: Recursively find a mesh by partName in nested groups
  findMeshInGroup(group, partName) {
    for (const child of group.children) {
      if (child.userData.partName === partName) {
        return child;
      }
      if (child instanceof THREE.Group) {
        const found = this.findMeshInGroup(child, partName);
        if (found) return found;
      }
    }
    return null;
  }

  // Helper: Recursively get all meshes from nested groups
  getAllMeshesInGroup(group, meshes = []) {
    for (const child of group.children) {
      if (child.userData.partName && !(child instanceof THREE.Group)) {
        meshes.push(child);
      }
      if (child instanceof THREE.Group) {
        this.getAllMeshesInGroup(child, meshes);
      }
    }
    return meshes;
  }

  // Helper: Check if an object and all its parents are visible
  isObjectFullyVisible(object) {
    let current = object;
    while (current) {
      if (!current.visible) {
        return false;
      }
      current = current.parent;
    }
    return true;
  }

  // Helper: Get all raycastable objects (including Groups with nested meshes)
  getRaycastableObjects() {
    const raycastables = [];
    this.engine.objects.forEach((entry, id) => {
      if (entry.object && entry.object.visible) {
        // Include meshes, points, and groups (groups will be recursively traversed by raycaster)
        if (entry.object.isMesh || entry.object.isPoints || entry.object.type === 'Group') {
          raycastables.push(entry.object);
        }
      }
    });
    return raycastables;
  }

  toggleMeshPart(partName, visible) {
    if (visible) {
      this.meshParts.visibleParts.add(partName);
    } else {
      this.meshParts.visibleParts.delete(partName);
    }

    // Use dynamic mesh object ID, with fallback to legacy names
    const meshObjectId = this.meshParts.activeMeshObjectId || 'grid-surface';
    const meshObject = this.engine.objects.get(meshObjectId) || this.engine.objects.get('main-mesh');
    if (meshObject && meshObject.object) {
      const obj = meshObject.object;

      console.log(`[toggleMeshPart] Looking for part '${partName}'`);
      console.log(`[toggleMeshPart] Object type: ${obj.type}`);

      // Handle both single mesh and group of meshes (including nested groups)
      if (obj.type === 'Group') {
        // Recursively find the mesh with this part name
        const partMesh = this.findMeshInGroup(obj, partName);
        if (partMesh) {
          partMesh.visible = visible;
          console.log(`[toggleMeshPart] Found and toggled '${partName}' to ${visible}`);
        } else {
          console.warn(`[toggleMeshPart] Could not find part '${partName}' in group hierarchy!`);
        }
      } else {
        // Single mesh - toggle entire mesh visibility
        console.log(`[toggleMeshPart] Single mesh, toggling entire mesh`);
        obj.visible = visible;
      }
      // No need to call render() - the animation loop handles it automatically
      // Notify view lock manager of visibility change
      if (this.viewLockManager) {
        this.viewLockManager.onVisualizationChange();
      }
    }
    this._syncSolidifyVisibility();
  }

  /**
   * Sync solidified mesh children visibility to match visibleParts.
   * Called after any part visibility change so the solidify filter stays in sync.
   */
  _syncSolidifyVisibility() {
    if (!this.filters?.meshSolidify) return;
    const solidEntry = this.engine.objects.get('solidify-filter');
    if (!solidEntry) return;
    solidEntry.object.traverse((child) => {
      if (child.isMesh && child.name) {
        child.visible = this.meshParts.visibleParts.has(child.name);
      }
    });
  }

  /**
   * Apply a set of visible parts - used to restore visibility after geometry reload
   * @param {Set} visibleParts - Set of part names that should be visible
   */
  applyMeshPartsVisibility(visibleParts) {
    if (!visibleParts) return;

    // Update the internal visibleParts set
    this.meshParts.visibleParts = new Set(visibleParts);

    // Get the mesh object
    const meshObjectId = this.meshParts.activeMeshObjectId || 'grid-surface';
    const meshObject = this.engine.objects.get(meshObjectId) || this.engine.objects.get('main-mesh');

    if (!meshObject || !meshObject.object) {
      console.warn('[applyMeshPartsVisibility] No mesh object found');
      return;
    }

    const obj = meshObject.object;

    // Apply visibility to all parts in the mesh
    if (obj.type === 'Group') {
      obj.traverse((child) => {
        if (child.isMesh) {
          // Check both mesh.name and userData.partName for part identification
          const partName = child.userData.partName || child.name;
          if (partName) {
            const shouldBeVisible = visibleParts.has(partName);
            child.visible = shouldBeVisible;
          }
        }
      });
    }

    // Update UI checkboxes to match - checkboxes use data-patch attribute and id="part{name}"
    // First, get all boundary checkboxes
    const allPatchCheckboxes = document.querySelectorAll('input[data-patch]');
    allPatchCheckboxes.forEach(checkbox => {
      const patchName = checkbox.getAttribute('data-patch');
      checkbox.checked = visibleParts.has(patchName);
    });

    // Also try by ID pattern (part{name})
    visibleParts.forEach(partName => {
      const checkbox = document.getElementById(`part${partName}`);
      if (checkbox) checkbox.checked = true;
    });

    // Handle internal mesh checkbox
    const internalMeshCheckbox = document.getElementById('partInternalMesh');
    if (internalMeshCheckbox && this.meshParts.internalMeshPartName) {
      internalMeshCheckbox.checked = visibleParts.has(this.meshParts.internalMeshPartName);
    }

    // Update group checkboxes state based on their children
    const groups = document.querySelectorAll('.mesh-part-group');
    groups.forEach(groupDiv => {
      try {
        const patches = JSON.parse(groupDiv.dataset.patches || '[]');
        const groupCheckbox = groupDiv.querySelector('.mesh-part-group-header input[type="checkbox"]');
        if (groupCheckbox && patches.length > 0) {
          const allChecked = patches.every(name => visibleParts.has(name));
          const someChecked = patches.some(name => visibleParts.has(name));
          groupCheckbox.checked = allChecked;
          groupCheckbox.indeterminate = someChecked && !allChecked;
        }
      } catch (e) {
        console.warn('Error updating group checkbox:', e);
      }
    });

    this._syncSolidifyVisibility();
    console.log('[applyMeshPartsVisibility] Applied visibility for', visibleParts.size, 'parts');
  }

  showAllMeshParts() {
    // Use dynamic mesh object ID, with fallback to legacy names
    const meshObjectId = this.meshParts.activeMeshObjectId || 'grid-surface';
    const meshObject = this.engine.objects.get(meshObjectId) || this.engine.objects.get('main-mesh');

    if (!meshObject || !meshObject.object) {
      console.warn('[showAllMeshParts] No mesh object found');
      return;
    }

    const obj = meshObject.object;

    // Clear and rebuild visibleParts set with ACTUAL part names from the mesh
    this.meshParts.visibleParts.clear();

    if (obj.type === 'Group') {
      // Recursively get all meshes from nested groups
      const allMeshes = this.getAllMeshesInGroup(obj);

      allMeshes.forEach(mesh => {
        const partName = mesh.userData.partName;
        if (partName) {
          this.meshParts.visibleParts.add(partName);
          mesh.visible = true;
          console.log(`[showAllMeshParts] Showing part: ${partName}`);

          // Update checkbox for this part
          const checkbox = document.getElementById(`part${partName}`);
          if (checkbox) checkbox.checked = true;
        }
      });
      console.log(`[showAllMeshParts] Showed all ${allMeshes.length} mesh parts`);
    } else {
      // Single mesh - just show it and add to visible parts
      obj.visible = true;
      const partName = obj.userData.partName || 'mesh';
      this.meshParts.visibleParts.add(partName);
    }

    // Update all group checkboxes
    document.querySelectorAll('.mesh-part-group-header input[type="checkbox"]').forEach(cb => {
      cb.checked = true;
    });
    this._syncSolidifyVisibility();
    // No need to call render() - the animation loop handles it automatically
    // Notify view lock manager of visibility change
    if (this.viewLockManager) {
      this.viewLockManager.onVisualizationChange();
    }
  }

  hideAllMeshParts() {
    // Use dynamic mesh object ID, with fallback to legacy names
    const meshObjectId = this.meshParts.activeMeshObjectId || 'grid-surface';
    const meshObject = this.engine.objects.get(meshObjectId) || this.engine.objects.get('main-mesh');

    if (!meshObject || !meshObject.object) {
      console.warn('[hideAllMeshParts] No mesh object found');
      return;
    }

    const obj = meshObject.object;

    // Clear the visibleParts set
    this.meshParts.visibleParts.clear();

    if (obj.type === 'Group') {
      // Recursively get all meshes from nested groups
      const allMeshes = this.getAllMeshesInGroup(obj);

      allMeshes.forEach(mesh => {
        const partName = mesh.userData.partName;
        if (partName) {
          mesh.visible = false;
          console.log(`[hideAllMeshParts] Hiding part: ${partName}`);

          // Update checkbox for this part
          const checkbox = document.getElementById(`part${partName}`);
          if (checkbox) checkbox.checked = false;
        }
      });
      console.log(`[hideAllMeshParts] Hid all ${allMeshes.length} mesh parts`);
    } else {
      // Single mesh
      obj.visible = false;
    }

    // Update all group checkboxes
    document.querySelectorAll('.mesh-part-group-header input[type="checkbox"]').forEach(cb => {
      cb.checked = false;
    });
    this._syncSolidifyVisibility();
    // No need to call render() - the animation loop handles it automatically
    // Notify view lock manager of visibility change
    if (this.viewLockManager) {
      this.viewLockManager.onVisualizationChange();
    }
  }

  updateMeshVisibility() {
    // This function is now deprecated - mesh parts are shown/hidden using visibility flags
    // Kept as no-op to avoid breaking any legacy code that might call it
    console.log('updateMeshVisibility: Using visibility toggle instead of geometry rebuild');
  }

  buildFilteredGeometry(data, visibleParts, boundaries) {
    console.log('buildFilteredGeometry called');
    console.log('  data:', data ? 'exists' : 'null');
    console.log('  data.vertices:', data?.vertices ? data.vertices.length : 'none');
    console.log('  data.faces:', data?.faces ? data.faces.length : 'none');
    console.log('  visibleParts:', Array.from(visibleParts));
    console.log('  boundaries:', Object.keys(boundaries).length);

    if (!data || !data.vertices || !data.faces) {
      console.error('buildFilteredGeometry: missing data');
      return null;
    }

    // If no parts are visible, return empty geometry to hide mesh
    if (!visibleParts || visibleParts.size === 0) {
      console.log('buildFilteredGeometry: no visible parts, creating empty geometry');
      const emptyGeometry = new THREE.BufferGeometry();
      emptyGeometry.setAttribute('position', new THREE.Float32BufferAttribute([], 3));
      emptyGeometry.setIndex([]);
      return emptyGeometry;
    }

    const vertices = data.vertices;
    const faces = data.faces;
    const nInternalFaces = data.nInternalFaces || faces.length;

    console.log(`buildFilteredGeometry: vertices=${vertices.length}, faces=${faces.length}, nInternalFaces=${nInternalFaces}`);

    const filteredFaces = [];

    // Add internal faces if visible
    if (visibleParts.has('internal')) {
      for (let i = 0; i < nInternalFaces; i++) {
        filteredFaces.push(faces[i]);
      }
    }

    // Add boundary faces if visible
    Object.entries(boundaries).forEach(([patchName, patchData]) => {
      if (visibleParts.has(patchName)) {
        const startFace = patchData.startFace;
        const endFace = startFace + patchData.nFaces;
        for (let i = startFace; i < endFace && i < faces.length; i++) {
          filteredFaces.push(faces[i]);
        }
      }
    });

    // Build THREE.js geometry from filtered faces
    const geometry = new THREE.BufferGeometry();
    const positions = [];
    const indices = [];

    // Create vertex map to reindex
    const vertexMap = new Map();
    let vertexIndex = 0;

    filteredFaces.forEach(face => {
      const faceIndices = [];
      face.forEach(vIdx => {
        if (!vertexMap.has(vIdx)) {
          vertexMap.set(vIdx, vertexIndex);
          positions.push(vertices[vIdx * 3], vertices[vIdx * 3 + 1], vertices[vIdx * 3 + 2]);
          vertexIndex++;
        }
        faceIndices.push(vertexMap.get(vIdx));
      });

      // Triangulate face
      for (let i = 1; i < faceIndices.length - 1; i++) {
        indices.push(faceIndices[0], faceIndices[i], faceIndices[i + 1]);
      }
    });

    geometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
    geometry.setIndex(indices);
    geometry.computeVertexNormals();

    return geometry;
  }

  // ============ STREAMLINE PICKING & GIZMOS ============

  setupPicking() {
    if (this.overlayGroup) return;

    this.overlayGroup = new THREE.Group();
    this.overlayGroup.name = 'streamline-overlays';
    this.engine.scene.add(this.overlayGroup);

    const canvas = this.engine.renderer.domElement;
    canvas.addEventListener('pointerdown', (event) => this.onViewportPointerDown(event));
    window.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        this.cancelStreamlinePick();
      }
    });
  }

  beginStreamlinePick(filterId, target, options = {}) {
    if (!target) return;
    if (this.streamlinePickState) {
      this.cancelStreamlinePick();
    }
    this.streamlinePickState = {
      filterId,
      target,
      callback: typeof options.callback === 'function' ? options.callback : null
    };
    const canvas = this.engine.renderer.domElement;
    canvas.classList.add('picking');
    if (this.engine.controls) {
      this.previousControlState = {
        enableZoom: this.engine.controls.enableZoom,
        enableRotate: this.engine.controls.enableRotate,
        enablePan: this.engine.controls.enablePan
      };
      this.engine.controls.enableZoom = false;
      this.engine.controls.enableRotate = false;
      this.engine.controls.enablePan = false;
    }
    Utils.notify('Click in the viewport to set the streamline parameter, or press Esc to cancel.', 'info');
  }

  pickStreamlineSeedCenter(callback) {
    this.beginStreamlinePick(null, 'point-center', { callback });
  }

  cancelStreamlinePick(notify = false) {
    if (!this.streamlinePickState) return;
    this.streamlinePickState = null;
    const canvas = this.engine.renderer.domElement;
    canvas.classList.remove('picking');
    if (this.engine.controls) {
      if (this.previousControlState) {
        this.engine.controls.enableZoom = this.previousControlState.enableZoom;
        this.engine.controls.enableRotate = this.previousControlState.enableRotate;
        this.engine.controls.enablePan = this.previousControlState.enablePan;
      } else {
        this.engine.controls.enableZoom = true;
        this.engine.controls.enableRotate = true;
        this.engine.controls.enablePan = true;
      }
    }
    this.previousControlState = null;
    if (notify) {
      const message = this.pendingStreamlineMessage || 'Streamline parameter updated.';
      Utils.notify(message, 'success');
    }
    this.pendingStreamlineMessage = null;
  }

  async onViewportPointerDown(event) {
    if (!this.streamlinePickState) return;
    if (event.button !== 0) return;

    event.preventDefault();

    const canvas = this.engine.renderer.domElement;
    const rect = canvas.getBoundingClientRect();
    this.pickPointer.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
    this.pickPointer.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;

    const pickables = this.getPickableObjects();
    if (pickables.length === 0) {
      Utils.notify('No geometry available for picking.', 'warning');
      this.cancelStreamlinePick();
      return;
    }

    this.raycaster.setFromCamera(this.pickPointer, this.engine.camera);
    const intersects = this.raycaster.intersectObjects(pickables, true);

    // Filter to only fully visible objects
    const visibleIntersects = intersects.filter(hit => this.isObjectFullyVisible(hit.object));

    if (visibleIntersects.length === 0) {
      Utils.notify('Click intersected nothing. Try again.', 'warning');
      return;
    }

    const point = visibleIntersects[0].point;
    await this.applyStreamlinePick(point);
  }

  async applyStreamlinePick(point) {
    if (!this.streamlinePickState) return;
    const { filterId, target, callback } = this.streamlinePickState;
    const precision = (value) => Number(Number(value).toFixed(4));

    if (filterId) {
      const entry = this.filters?.getFilter(filterId);
      if (!entry) {
        this.cancelStreamlinePick();
        return;
      }

      const updates = {};

      switch (target) {
        case 'point-center':
          updates.pointCenterX = precision(point.x);
          updates.pointCenterY = precision(point.y);
          updates.pointCenterZ = precision(point.z);
          break;
        case 'line-start':
          updates.lineStartX = precision(point.x);
          updates.lineStartY = precision(point.y);
          updates.lineStartZ = precision(point.z);
          break;
        case 'line-end':
          updates.lineEndX = precision(point.x);
          updates.lineEndY = precision(point.y);
          updates.lineEndZ = precision(point.z);
          break;
        case 'plane-origin':
          updates.planeOriginX = precision(point.x);
          updates.planeOriginY = precision(point.y);
          updates.planeOriginZ = precision(point.z);
          break;
        case 'plane-normal': {
          const origin = new THREE.Vector3(
            entry.options?.planeOriginX ?? 0,
            entry.options?.planeOriginY ?? 0,
            entry.options?.planeOriginZ ?? 0
          );
          const normal = new THREE.Vector3(point.x, point.y, point.z).sub(origin);
          if (normal.lengthSq() < 1e-8) {
            Utils.notify('Choose a point away from the plane origin to define a normal.', 'warning');
            return;
          }
          normal.normalize();
          updates.planeNormalX = precision(normal.x);
          updates.planeNormalY = precision(normal.y);
          updates.planeNormalZ = precision(normal.z);
          break;
        }
        default:
          Utils.notify('Unsupported pick target.', 'warning');
          this.cancelStreamlinePick();
          return;
      }

      await this.updateFilterOptions(filterId, updates);
      this.pendingStreamlineMessage = 'Streamline parameters updated.';
      this.cancelStreamlinePick(true);
      return;
    }

    if (callback) {
      callback([point.x, point.y, point.z]);
      this.pendingStreamlineMessage = 'Streamline seed center set.';
      this.cancelStreamlinePick(true);
      return;
    }

    Utils.notify('Unable to apply pick without an active streamline filter.', 'warning');
    this.cancelStreamlinePick();
  }

  getPickableObjects() {
    const pickables = [];
    if (!this.engine?.objects) return pickables;

    this.engine.objects.forEach(entry => {
      if (!entry || !entry.object) return;
      if (entry.metadata?.type === 'filter' && entry.metadata.kind === 'streamlines') {
        return;
      }
      pickables.push(entry.object);
    });

    return pickables;
  }

  onStreamlineFilterUpdated(filterId, options) {
    if (!filterId || !options) return;
    this.updateStreamlineGizmos(filterId, options);
    this.updateStreamlineLegend(filterId, options);
  }

  onStreamlineFilterRemoved(filterId) {
    if (!filterId) return;
    this.detachStreamlinePreview(filterId);
    const helper = this.streamlineHelpers[filterId];
    if (helper && this.overlayGroup) {
      this.overlayGroup.remove(helper);
      helper.traverse(obj => {
        if (obj.geometry) obj.geometry.dispose();
        if (obj.material) {
          if (Array.isArray(obj.material)) {
            obj.material.forEach(mat => mat.dispose());
          } else {
            obj.material.dispose();
          }
        }
      });
    }
    delete this.streamlineHelpers[filterId];
    if (this.streamlinePickState?.filterId === filterId) {
      this.cancelStreamlinePick();
    }

    const activeFilters = typeof this.filters?.getActiveFilters === 'function'
      ? this.filters.getActiveFilters()
      : [];
    const nextStreamline = Array.isArray(activeFilters)
      ? activeFilters.find(([, entry]) => entry?.type === 'streamlines')
      : null;

    if (nextStreamline && nextStreamline[1]?.options) {
      this.updateStreamlineLegend(nextStreamline[0], nextStreamline[1].options);
    } else {
      this.hideStreamlineLegend();
    }
  }

  updateStreamlineGizmos(filterId) {
    // Seed gizmo feature removed - always clean up any existing helper
    if (!this.overlayGroup) return;

    let helper = this.streamlineHelpers[filterId];
    if (helper) {
      this.overlayGroup.remove(helper);
      helper.traverse(obj => {
        if (obj.geometry) obj.geometry.dispose();
        if (obj.material) {
          if (Array.isArray(obj.material)) {
            obj.material.forEach(mat => mat.dispose());
          } else {
            obj.material.dispose();
          }
        }
      });
      delete this.streamlineHelpers[filterId];
    }
  }

  updateStreamlineLegend(filterId, options) {
    console.log('[Legend Update] Updating streamline legend with options:', {
      filterId,
      scalarRange: options?.scalarRange,
      scalarField: options?.scalarField,
      scalarMetric: options?.scalarMetric,
      variable: options?.variable,
      colormap: options?.colormap,
      colorBy: options?.colorBy
    });

    // Hide legend if no color mapping is enabled
    if (options?.colorBy === 'none') {
      console.log('[Legend Update] No color mapping enabled, hiding legend');
      this.hideStreamlineLegend();
      return;
    }

    const legend = document.getElementById('streamlineLegend');
    const gradientEl = document.getElementById('streamlineLegendGradient');
    const ticksEl = document.getElementById('streamlineLegendTicks');
    const titleEl = document.getElementById('streamlineLegendTitle');
    const metaEl = document.getElementById('streamlineLegendMeta');

    if (!legend || !gradientEl || !ticksEl || !titleEl || !metaEl) {
      console.warn('[Legend Update] Missing legend elements');
      return;
    }

    const range = options?.scalarRange;
    const hasRange = range
      && Number.isFinite(range.min)
      && Number.isFinite(range.max)
      && range.max > range.min;

    if (!hasRange) {
      console.warn('[Legend Update] No valid scalar range, hiding legend');
      this.hideStreamlineLegend();
      return;
    }

    const preset = options.colormap || 'jet';
    gradientEl.style.background = Colormap.createGradientCSS(preset, 'to right', 'streamlineColormap');

    const ticks = Colormap.createLegend(range.min, range.max, 4)
      .map(tick => `<span>${this.formatNumber(tick.value)}</span>`);
    ticksEl.innerHTML = ticks.join('');

    const fieldLabel = this.formatLabel(options.scalarField || options.variable || 'Streamline');
    const metricLabel = this.formatLabel(options.scalarMetric || 'Magnitude');
    const presetLabel = this.formatLabel(preset);

    titleEl.textContent = `Streamline ${fieldLabel}`;
    metaEl.textContent = `${metricLabel} • ${presetLabel}`;

    legend.dataset.filterId = filterId || '';
    legend.dataset.colormap = preset; // Store colormap name for legend capture
    legend.style.display = 'flex';
  }

  hideStreamlineLegend() {
    const legend = document.getElementById('streamlineLegend');
    if (!legend) return;
    legend.style.display = 'none';
    delete legend.dataset.filterId;
  }

  updateSliceLegend(filterId, options = {}) {
    if (!options || !options.colorField) {
      console.warn('[Slice Legend] No options provided, hiding legend');
      this.hideSliceLegend();
      return;
    }

    const legend = document.getElementById('sliceLegend');
    const gradientEl = document.getElementById('sliceLegendGradient');
    const ticksEl = document.getElementById('sliceLegendTicks');
    const titleEl = document.getElementById('sliceLegendTitle');
    const metaEl = document.getElementById('sliceLegendMeta');

    if (!legend || !gradientEl || !ticksEl || !titleEl || !metaEl) {
      console.warn('[Slice Legend] Missing legend elements');
      return;
    }

    const range = options?.scalarRange;
    const hasRange = range
      && Number.isFinite(range.min)
      && Number.isFinite(range.max)
      && range.max > range.min;

    if (!hasRange) {
      console.warn('[Slice Legend] No valid scalar range, hiding legend');
      this.hideSliceLegend();
      return;
    }

    const preset = options.colormap || 'jet';
    gradientEl.style.background = Colormap.createGradientCSS(preset, 'to right', 'sliceContourColormap');

    const ticks = Colormap.createLegend(range.min, range.max, 4)
      .map(tick => `<span>${this.formatNumber(tick.value)}</span>`);
    ticksEl.innerHTML = ticks.join('');

    const fieldLabel = this.formatLabel(options.colorField || 'Field');
    const presetLabel = this.formatLabel(preset);

    titleEl.textContent = `Slice ${fieldLabel}`;
    metaEl.textContent = `Contour • ${presetLabel}`;

    legend.dataset.filterId = filterId || '';
    legend.dataset.colormap = preset;
    legend.style.display = 'flex';

    console.log('[Slice Legend] Updated:', { field: fieldLabel, range, colormap: preset });
  }

  hideSliceLegend() {
    const legend = document.getElementById('sliceLegend');
    if (!legend) return;
    legend.style.display = 'none';
    delete legend.dataset.filterId;
  }

  updateGlyphLegend(filterId, options = {}) {
    console.log('[Glyph Legend] Updating with options:', {
      filterId,
      colorField: options?.colorField,
      scalarRange: options?.scalarRange,
      colormap: options?.colormap
    });

    const legend = document.getElementById('glyphLegend');
    const gradientEl = document.getElementById('glyphLegendGradient');
    const ticksEl = document.getElementById('glyphLegendTicks');
    const titleEl = document.getElementById('glyphLegendTitle');
    const metaEl = document.getElementById('glyphLegendMeta');

    if (!legend || !gradientEl || !ticksEl || !titleEl || !metaEl) {
      console.warn('[Glyph Legend] Missing legend elements');
      return;
    }

    const range = options?.scalarRange;
    const hasRange = range
      && Number.isFinite(range.min)
      && Number.isFinite(range.max)
      && range.max > range.min;

    if (!hasRange) {
      console.warn('[Glyph Legend] No valid scalar range, hiding legend');
      this.hideGlyphLegend();
      return;
    }

    const preset = options.colormap || 'jet';
    gradientEl.style.background = Colormap.createGradientCSS(preset, 'to right', 'glyphColormap');

    const ticks = Colormap.createLegend(range.min, range.max, 4)
      .map(tick => `<span>${this.formatNumber(tick.value)}</span>`);
    ticksEl.innerHTML = ticks.join('');

    // Format colorField for display
    const colorFieldLabel = options.colorField
      ? this.formatLabel(options.colorField)
      : 'Velocity Magnitude';
    const presetLabel = this.formatLabel(preset);

    titleEl.textContent = `Glyphs: ${colorFieldLabel}`;
    metaEl.textContent = presetLabel;

    legend.dataset.filterId = filterId || 'glyph-filter';
    legend.dataset.colormap = preset;
    legend.style.display = 'flex';
  }

  hideGlyphLegend() {
    const legend = document.getElementById('glyphLegend');
    if (!legend) return;
    legend.style.display = 'none';
    delete legend.dataset.filterId;
  }

  updateIsolinesLegend(filterId, options = {}) {
    console.log('[Isolines Legend] Updating with options:', {
      filterId,
      field: options?.field,
      scalarRange: options?.scalarRange,
      colormap: options?.colormap,
      numIsolines: options?.numIsolines
    });

    const legend = document.getElementById('isolinesLegend');
    const gradientEl = document.getElementById('isolinesLegendGradient');
    const ticksEl = document.getElementById('isolinesLegendTicks');
    const titleEl = document.getElementById('isolinesLegendTitle');
    const metaEl = document.getElementById('isolinesLegendMeta');

    if (!legend || !gradientEl || !ticksEl || !titleEl || !metaEl) {
      console.warn('[Isolines Legend] Missing legend elements');
      return;
    }

    const range = options?.scalarRange;
    const hasRange = range
      && Number.isFinite(range.min)
      && Number.isFinite(range.max)
      && range.max > range.min;

    if (!hasRange) {
      console.warn('[Isolines Legend] No valid scalar range, hiding legend');
      this.hideIsolinesLegend();
      return;
    }

    const preset = options.colormap || 'jet';
    gradientEl.style.background = Colormap.createGradientCSS(preset, 'to right', 'sliceIsolinesColormap');

    const ticks = Colormap.createLegend(range.min, range.max, 4)
      .map(tick => `<span>${this.formatNumber(tick.value)}</span>`);
    ticksEl.innerHTML = ticks.join('');

    const fieldLabel = this.formatLabel(options.field || 'Field');
    const presetLabel = this.formatLabel(preset);
    const numLines = options.numIsolines || 10;

    titleEl.textContent = `Contour Lines: ${fieldLabel}`;
    metaEl.textContent = `${numLines} lines • ${presetLabel}`;

    legend.dataset.filterId = filterId || '';
    legend.dataset.colormap = preset;
    legend.style.display = 'flex';

    console.log('[Isolines Legend] Updated:', { field: fieldLabel, range, colormap: preset, numLines });
  }

  hideIsolinesLegend() {
    const legend = document.getElementById('isolinesLegend');
    if (!legend) return;
    legend.style.display = 'none';
    delete legend.dataset.filterId;
  }

  setupLegendResize() {
    // Setup resize for streamline legend
    this.setupSingleLegendResize('streamlineLegend', 'legendResizeHandle', 'legendResizeCorner');

    // Setup resize for slice legend
    this.setupSingleLegendResize('sliceLegend', 'sliceLegendResizeHandle', 'sliceLegendResizeCorner');

    // Setup resize for glyph legend
    this.setupSingleLegendResize('glyphLegend', 'glyphLegendResizeHandle', 'glyphLegendResizeCorner');

    // Setup resize for isolines legend
    this.setupSingleLegendResize('isolinesLegend', 'isolinesLegendResizeHandle', 'isolinesLegendResizeCorner');

    // Setup drag for all legends
    this.setupLegendDrag('streamlineLegend', 'streamlineLegendTitle');
    this.setupLegendDrag('sliceLegend', 'sliceLegendTitle');
    this.setupLegendDrag('glyphLegend', 'glyphLegendTitle');
    this.setupLegendDrag('isolinesLegend', 'isolinesLegendTitle');
  }

  setupLegendDrag(legendId, titleId) {
    const legend = document.getElementById(legendId);
    const title = document.getElementById(titleId);

    if (!legend || !title) return;

    let isDragging = false;
    let startX = 0;
    let startY = 0;
    let currentTranslateX = 0;
    let currentTranslateY = 0;

    const startDrag = (e) => {
      e.preventDefault();
      e.stopPropagation();

      isDragging = true;
      startX = e.clientX;
      startY = e.clientY;

      // Get current transform values if any
      const transform = legend.style.transform;
      if (transform && transform.includes('translate')) {
        const match = transform.match(/translate\(([^,]+)px,\s*([^)]+)px\)/);
        if (match) {
          currentTranslateX = parseFloat(match[1]) || 0;
          currentTranslateY = parseFloat(match[2]) || 0;
        }
      }

      title.style.cursor = 'grabbing';
      document.body.style.userSelect = 'none';
    };

    const doDrag = (e) => {
      if (!isDragging) return;
      e.preventDefault();

      const deltaX = e.clientX - startX;
      const deltaY = e.clientY - startY;

      const newTranslateX = currentTranslateX + deltaX;
      const newTranslateY = currentTranslateY + deltaY;

      // Use transform instead of changing position properties (no layout shift)
      legend.style.transform = `translate(${newTranslateX}px, ${newTranslateY}px)`;
    };

    const stopDrag = () => {
      if (!isDragging) return;
      isDragging = false;

      // Update current translate values for next drag
      const transform = legend.style.transform;
      if (transform && transform.includes('translate')) {
        const match = transform.match(/translate\(([^,]+)px,\s*([^)]+)px\)/);
        if (match) {
          currentTranslateX = parseFloat(match[1]) || 0;
          currentTranslateY = parseFloat(match[2]) || 0;
        }
      }

      title.style.cursor = 'grab';
      document.body.style.userSelect = '';
    };

    // Make title draggable
    title.style.cursor = 'grab';
    title.addEventListener('mousedown', startDrag);

    // Global move and up events
    document.addEventListener('mousemove', doDrag);
    document.addEventListener('mouseup', stopDrag);
  }

  setupSingleLegendResize(legendId, handleId, cornerId) {
    const legend = document.getElementById(legendId);
    const resizeHandle = document.getElementById(handleId);
    const resizeCorner = document.getElementById(cornerId);

    if (!legend || (!resizeHandle && !resizeCorner)) return;

    let isResizing = false;
    let startX = 0;
    let startWidth = 0;

    const startResize = (e) => {
      e.preventDefault();
      e.stopPropagation();
      isResizing = true;
      startX = e.clientX;
      startWidth = legend.offsetWidth;
      legend.classList.add('resizing');
      document.body.style.cursor = 'ew-resize';
    };

    const doResize = (e) => {
      if (!isResizing) return;
      e.preventDefault();

      // Calculate new width (subtract because legend is positioned from the right)
      const deltaX = startX - e.clientX;
      const newWidth = Math.max(200, Math.min(600, startWidth + deltaX));
      legend.style.width = newWidth + 'px';
    };

    const stopResize = () => {
      if (!isResizing) return;
      isResizing = false;
      legend.classList.remove('resizing');
      document.body.style.cursor = '';
    };

    // Attach events to both handle and corner
    if (resizeHandle) {
      resizeHandle.addEventListener('mousedown', startResize);
    }
    if (resizeCorner) {
      resizeCorner.addEventListener('mousedown', startResize);
    }

    // Global move and up events
    document.addEventListener('mousemove', doResize);
    document.addEventListener('mouseup', stopResize);
  }

  clearStreamlineGizmos() {
    if (!this.overlayGroup) return;
    Object.keys(this.streamlineHelpers).forEach(id => this.onStreamlineFilterRemoved(id));
  }

  computePlaneAxes(normal) {
    const candidate = Math.abs(normal.dot(new THREE.Vector3(0, 1, 0))) > 0.9
      ? new THREE.Vector3(1, 0, 0)
      : new THREE.Vector3(0, 1, 0);
    const axisU = new THREE.Vector3().crossVectors(normal, candidate).normalize();
    if (axisU.lengthSq() < 1e-6) {
      axisU.set(1, 0, 0);
    }
    const axisV = new THREE.Vector3().crossVectors(normal, axisU).normalize();
    return { axisU, axisV };
  }

  attachStreamlinePreview(filterId, streamlines, options = {}) {
    console.log('[Particle Preview] attachStreamlinePreview called:', {
      filterId,
      streamlineCount: streamlines?.length,
      previewEnabled: options?.previewEnabled,
      previewSpeed: options?.previewSpeed,
      previewDotScale: options?.previewDotScale
    });

    if (!filterId || !Array.isArray(streamlines) || streamlines.length === 0) {
      console.log('[Particle Preview] Early return - invalid parameters');
      return;
    }

    if (options && options.previewEnabled === false) {
      console.log('[Particle Preview] Preview disabled, detaching');
      this.detachStreamlinePreview(filterId);
      return;
    }

    this.detachStreamlinePreview(filterId);

    const animations = [];
    const previewGroup = new THREE.Group();
    previewGroup.name = `${filterId}-preview`;

    const bounds = this.getStreamlineBounds();
    const span = Math.max(
      bounds.xmax - bounds.xmin,
      bounds.ymax - bounds.ymin,
      bounds.zmax - bounds.zmin,
      1
    );
    const dotScale = Number.isFinite(options.previewDotScale) ? Math.max(options.previewDotScale, 0.0001) : 0.004;
    const markerRadius = Math.max(span * dotScale, 0.0005);

    const baseGeometry = new THREE.SphereGeometry(markerRadius, 12, 10);

    const colormapName = options.colormap || 'jet';
    const scalarRange = options.scalarRange;
    const hasScalarRange = scalarRange && Number.isFinite(scalarRange.min) && Number.isFinite(scalarRange.max) && scalarRange.max > scalarRange.min;

    // Get solid color for streamline colormap dropdown if using solid colormap
    const solidColorHex = Colormap.isSolidColor(colormapName) ? Colormap.getSolidColor('streamlineColormap') : null;

    const normalizeScalar = (value) => {
      if (!hasScalarRange || !Number.isFinite(value)) {
        return null;
      }
      const normalized = (value - scalarRange.min) / (scalarRange.max - scalarRange.min);
      return Math.max(0, Math.min(1, normalized));
    };

    streamlines.forEach((entry, index) => {
      const points = Array.isArray(entry)
        ? entry
        : (Array.isArray(entry?.points) ? entry.points : null);
      if (!Array.isArray(points) || points.length < 2) return;

      const scalars = Array.isArray(entry?.scalars) ? entry.scalars : null;
      const path = this.buildStreamlinePath(points, scalars);
      if (!path || path.totalLength <= 0) return;

      const t = streamlines.length > 1 ? index / (streamlines.length - 1) : 0;
      const fallbackColor = Colormap.getColorThree(t, colormapName, solidColorHex);

      // Determine speed direction (forward = positive, backward = negative)
      const speed = Number.isFinite(options.previewSpeed) ? options.previewSpeed : span * 0.2;

      // Simple approach: Start at 0% for forward, 100% for backward
      // This ensures all particles start at the same relative position on their streamlines
      // Forward (→): 0% → 100% (flows along the streamline path)
      // Backward (←): 100% → 0% (flows backward along the path)
      const initialDistance = speed >= 0 ? 0 : path.totalLength;
      const initialSample = this.sampleStreamlinePath(path, initialDistance);

      // Debug: Log first few streamlines' start positions
      if (index < 3) {
        console.log(`[Particle Preview] Streamline ${index} debug:`, {
          totalPoints: points.length,
          pathTotalLength: path.totalLength,
          speedDirection: speed >= 0 ? 'forward (→)' : 'backward (←)',
          particleStartDistance: initialDistance,
          particleStartPercent: ((initialDistance / path.totalLength) * 100).toFixed(1) + '%',
          particleStartPosition: initialSample.position,
          firstPoint: points[0],
          lastPoint: points[points.length - 1]
        });
      }

      const initialNormalized = normalizeScalar(initialSample.scalar);
      const color = initialNormalized != null
        ? Colormap.getColorThree(initialNormalized, colormapName, solidColorHex)
        : fallbackColor.clone();

      const material = new THREE.MeshBasicMaterial({
        color: color.clone(),
        transparent: true,
        opacity: 0.9,
        depthWrite: false
      });

      const marker = new THREE.Mesh(baseGeometry, material);
      const initial = initialSample.position;
      marker.position.set(initial[0], initial[1], initial[2]);
      previewGroup.add(marker);

      animations.push({
        marker,
        path,
        distance: initialDistance,
        seedDistance: initialDistance,  // Remember where particle started (min or max coord)
        speed: speed,  // Already calculated above based on direction
        scalarRange: hasScalarRange ? scalarRange : null,
        colormap: colormapName,
        solidColorHex: solidColorHex,  // Per-dropdown solid color
        fallbackColor
      });
    });

    console.log('[Particle Preview] Created', animations.length, 'particle animations');

    if (animations.length === 0) {
      console.log('[Particle Preview] No animations created, returning');
      previewGroup.traverse(obj => {
        if (obj.geometry) obj.geometry.dispose();
        if (obj.material) obj.material.dispose();
      });
      return;
    }

    const callback = (delta) => {
      animations.forEach(data => {
        // Update distance
        data.distance += data.speed * delta;

        // Reset to seed position when reaching the end (pulse effect from seed)
        // For forward direction (speed > 0): reset when >= totalLength
        // For backward direction (speed < 0): reset when <= 0
        if (data.speed > 0 && data.distance >= data.path.totalLength) {
          data.distance = data.seedDistance;  // Reset to seed
        } else if (data.speed < 0 && data.distance <= 0) {
          data.distance = data.seedDistance;  // Reset to seed
        }

        // Clamp distance to valid range
        data.distance = Math.max(0, Math.min(data.distance, data.path.totalLength));

        const sample = this.sampleStreamlinePath(data.path, data.distance);
        data.marker.position.set(sample.position[0], sample.position[1], sample.position[2]);

        if (data.scalarRange && Number.isFinite(sample.scalar)) {
          const normalized = Math.max(0, Math.min(1, (sample.scalar - data.scalarRange.min) / (data.scalarRange.max - data.scalarRange.min)));
          const color = Colormap.getColorThree(normalized, data.colormap, data.solidColorHex);
          data.marker.material.color.copy(color);
        } else if (data.fallbackColor) {
          data.marker.material.color.copy(data.fallbackColor);
        }
      });
    };

    this.engine.addAnimationCallback(callback);

    const entry = this.engine.objects.get(filterId);
    let standalone = false;
    if (entry?.object) {
      entry.object.add(previewGroup);
    } else {
      standalone = true;
      this.engine.addObject(`${filterId}-preview`, previewGroup, {
        type: 'helper',
        kind: 'streamline-preview',
        removable: false
      });
    }

    this.streamlinePreviews.set(filterId, {
      group: previewGroup,
      callback,
      animations,
      standalone
    });

    console.log('[Particle Preview] Preview attached successfully:', {
      filterId,
      animationCount: animations.length,
      standalone,
      groupParent: standalone ? 'engine' : 'streamline-object'
    });
  }

  detachStreamlinePreview(filterId) {
    const preview = this.streamlinePreviews.get(filterId);
    if (!preview) return;

    this.engine.removeAnimationCallback(preview.callback);

    if (preview.standalone) {
      this.engine.removeObject(`${filterId}-preview`);
    } else if (preview.group?.parent) {
      preview.group.parent.remove(preview.group);
    }

    preview.group?.traverse(obj => {
      if (obj.geometry) obj.geometry.dispose();
      if (obj.material) {
        if (Array.isArray(obj.material)) {
          obj.material.forEach(mat => mat.dispose());
        } else {
          obj.material.dispose();
        }
      }
    });

    this.streamlinePreviews.delete(filterId);
  }

  buildStreamlinePath(points, scalars = null) {
    if (!Array.isArray(points) || points.length < 2) {
      return null;
    }

    const segments = [];
    let totalLength = 0;

    for (let i = 1; i < points.length; i++) {
      const a = points[i - 1];
      const b = points[i];
      const dx = b[0] - a[0];
      const dy = b[1] - a[1];
      const dz = b[2] - a[2];
      const length = Math.sqrt(dx * dx + dy * dy + dz * dz);
      if (length <= 0) continue;
      segments.push({
        start: a,
        end: b,
        length,
        scalarStart: Array.isArray(scalars) ? scalars[i - 1] : null,
        scalarEnd: Array.isArray(scalars) ? scalars[i] : null
      });
      totalLength += length;
    }

    if (segments.length === 0) {
      return null;
    }

    return {
      segments,
      totalLength,
      scalars: Array.isArray(scalars) ? scalars : null
    };
  }

  getPointAlongPath(path, distance) {
    const sample = this.sampleStreamlinePath(path, distance);
    return sample.position;
  }

  sampleStreamlinePath(path, distance) {
    if (!path || path.totalLength <= 0) {
      return {
        position: [0, 0, 0],
        scalar: null
      };
    }

    let remaining = distance % path.totalLength;

    for (let i = 0; i < path.segments.length; i++) {
      const segment = path.segments[i];
      if (remaining <= segment.length) {
        const t = remaining / segment.length;
        const position = [
          segment.start[0] + (segment.end[0] - segment.start[0]) * t,
          segment.start[1] + (segment.end[1] - segment.start[1]) * t,
          segment.start[2] + (segment.end[2] - segment.start[2]) * t
        ];
        let scalar = null;
        const startScalar = segment.scalarStart;
        const endScalar = segment.scalarEnd;
        if (Number.isFinite(startScalar) && Number.isFinite(endScalar)) {
          scalar = startScalar + (endScalar - startScalar) * t;
        } else if (Number.isFinite(startScalar)) {
          scalar = startScalar;
        } else if (Number.isFinite(endScalar)) {
          scalar = endScalar;
        }
        return {
          position,
          scalar
        };
      }
      remaining -= segment.length;
    }

    const last = path.segments[path.segments.length - 1];
    if (!last) {
      return {
        position: [0, 0, 0],
        scalar: null
      };
    }

    let scalar = null;
    if (Number.isFinite(last.scalarEnd)) {
      scalar = last.scalarEnd;
    } else if (Number.isFinite(last.scalarStart)) {
      scalar = last.scalarStart;
    }

    return {
      position: last.end.slice(),
      scalar
    };
  }

  getStreamlineBounds() {
    const bounds = this.currentData?.volume?.bounds || this.currentData?.grid?.bounds;
    if (!bounds) {
      return {
        xmin: -1,
        xmax: 1,
        ymin: -1,
        ymax: 1,
        zmin: -1,
        zmax: 1
      };
    }
    return bounds;
  }

  // ============ PROBE TOOL METHODS ============

  setupProbeEventListeners() {
    const viewport = document.getElementById('viewport');

    viewport.addEventListener('mousemove', (event) => {
      if (!this.probeEnabled) return;

      const rect = viewport.getBoundingClientRect();
      this.mouse.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
      this.mouse.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;

      // Store screen coordinates for probe display positioning
      this.mouseScreenX = event.clientX;
      this.mouseScreenY = event.clientY;

      this.updateProbe();
    });

    viewport.addEventListener('mouseleave', () => {
      this.hideProbe();
    });
  }

  enableProbe(enabled) {
    this.probeEnabled = enabled;
    const probeDisplay = document.getElementById('probeDisplay');

    if (!enabled) {
      probeDisplay.style.display = 'none';
    }
  }

  updateProbe() {
    if (!this.probeEnabled || !this.currentData) return;

    const probeDisplay = document.getElementById('probeDisplay');

    // Get the mesh object - try grid-surface first, then main-mesh
    let meshEntry = this.engine.objects.get('grid-surface') || this.engine.objects.get('main-mesh');

    if (!meshEntry || !meshEntry.object) {
      probeDisplay.style.display = 'none';
      return;
    }

    const mainMesh = meshEntry.object;

    // Cast ray
    this.raycaster.setFromCamera(this.mouse, this.engine.camera);
    const intersects = this.raycaster.intersectObject(mainMesh, true);

    // Filter to only fully visible objects
    const visibleIntersects = intersects.filter(hit => this.isObjectFullyVisible(hit.object));

    if (visibleIntersects.length > 0) {
      const intersection = visibleIntersects[0];
      const point = intersection.point;

      // Get field value at this point
      // In backend mode, use geometryParams.colorField; in frontend mode, use colormap.variable
      const fieldName = (this.renderingMode === 'geometry' && this.geometryParams?.colorField)
        ? this.geometryParams.colorField
        : (this.colormap.variable || 'velocity');
      const fieldValue = this.getFieldValueAtPoint(point, fieldName, intersection);

      document.getElementById('probePosition').textContent =
        `(${point.x.toFixed(3)}, ${point.y.toFixed(3)}, ${point.z.toFixed(3)})`;
      document.getElementById('probeField').textContent = fieldName;
      document.getElementById('probeValue').textContent = fieldValue;

      // Position the probe display next to the cursor
      const offsetX = 15; // Offset from cursor
      const offsetY = 15;
      probeDisplay.style.left = `${this.mouseScreenX + offsetX}px`;
      probeDisplay.style.top = `${this.mouseScreenY + offsetY}px`;
      probeDisplay.style.right = 'auto'; // Remove fixed right positioning

      probeDisplay.style.display = 'block';
    } else {
      probeDisplay.style.display = 'none';
    }
  }

  getFieldValueAtPoint(point, fieldName, intersection) {
    // Check if we have data and fields
    if (!this.currentData) {
      return 'No data loaded';
    }

    if (!this.currentData.fields) {
      return 'No fields';
    }

    if (!this.currentData.fields[fieldName]) {
      return 'Field not found';
    }

    const fieldData = this.currentData.fields[fieldName];
    if (!Array.isArray(fieldData) || fieldData.length === 0) {
      return 'No field data';
    }

    // Get the value from intersection
    if (intersection && intersection.object && intersection.face) {
      const mesh = intersection.object;
      const geometry = mesh.geometry;
      const cellIndexAttr = geometry.getAttribute('cellIndex');

      let valueIndex = -1;

      if (cellIndexAttr) {
        // Frontend mode: use cellIndex attribute
        const vertexIdx = intersection.face.a;
        valueIndex = Math.round(cellIndexAttr.getX(vertexIdx));
        console.log('[Probe] Frontend mode - Cell index:', valueIndex);
      } else {
        // Backend mode: scalars map directly to vertices
        // Use the vertex index from the intersection
        valueIndex = intersection.face.a;
        console.log('[Probe] Backend mode - Vertex index:', valueIndex, 'Field data length:', fieldData.length);
      }

      if (valueIndex >= 0 && valueIndex < fieldData.length) {
        const value = fieldData[valueIndex];
        console.log('[Probe] Field value at index', valueIndex, ':', value);

        const unit = this.getFieldUnit(fieldName);

        if (typeof value === 'number') {
          return `${value.toFixed(4)} ${unit}`;
        } else if (Array.isArray(value) && value.length === 3) {
          // Vector field - calculate magnitude and show both
          const mag = Math.sqrt(value[0] ** 2 + value[1] ** 2 + value[2] ** 2);
          return `${mag.toFixed(4)} ${unit} [${value.map(v => v.toFixed(3)).join(', ')}]`;
        } else if (Array.isArray(value)) {
          return `[${value.map(v => v.toFixed(3)).join(', ')}] ${unit}`;
        } else {
          return String(value);
        }
      } else {
        console.log('[Probe] Index out of bounds:', valueIndex, 'max:', fieldData.length);
      }
    }

    return 'No value';
  }

  getFieldUnit(fieldName) {
    // Check if we have dimension info from the data
    if (this.currentData && this.currentData.fieldDimensions && this.currentData.fieldDimensions[fieldName]) {
      return this.currentData.fieldDimensions[fieldName];
    }

    // Fallback to common OpenFOAM field units based on typical dimensions
    // Note: Pressure units depend on solver (incompressible vs compressible)
    const units = {
      // Velocity - [0 1 -1 0 0 0 0]
      'U': 'm/s',
      'velocity': 'm/s',

      // Pressure - DEPENDS ON SOLVER:
      // Incompressible: [0 2 -2 0 0 0 0] = m²/s² (kinematic pressure)
      // Compressible: [1 -1 -2 0 0 0 0] = Pa (absolute pressure)
      'p': 'm²/s²',  // Most common is incompressible
      'p_rgh': 'm²/s²',
      'pressure': 'm²/s²',

      // Temperature - [0 0 0 1 0 0 0]
      'T': 'K',
      'temperature': 'K',

      // Turbulence
      'k': 'm²/s²',
      'epsilon': 'm²/s³',
      'omega': '1/s',
      'nut': 'm²/s',
      'nuTilda': 'm²/s',

      // Other
      'rho': 'kg/m³',
      'density': 'kg/m³',
      'nu': 'm²/s',
      'alpha': '-',
      'mag(U)': 'm/s',
      'vorticity': '1/s'
    };

    return units[fieldName] || '';
  }

  hideProbe() {
    const probeDisplay = document.getElementById('probeDisplay');
    probeDisplay.style.display = 'none';
  }

  // ============ DISPLAY CONTROLS METHODS ============

  setupDisplayControls() {
    const representationSelect = document.getElementById('representationType');
    const opacitySlider = document.getElementById('opacitySlider');
    const opacityValue = document.getElementById('opacityValue');

    if (representationSelect) {
      representationSelect.addEventListener('change', (e) => {
        this.displaySettings.representation = e.target.value;
        this.updateRepresentation();
        // Notify view lock manager of visualization change
        if (this.viewLockManager) {
          this.viewLockManager.onVisualizationChange();
        }
      });
    }

    if (opacitySlider) {
      opacitySlider.addEventListener('input', (e) => {
        const opacity = parseInt(e.target.value) / 100;
        this.displaySettings.opacity = opacity;
        opacityValue.textContent = `${e.target.value}%`;
        this.updateOpacity();
        // Notify view lock manager of visualization change
        if (this.viewLockManager) {
          this.viewLockManager.onVisualizationChange();
        }
      });
    }

    // Light Level slider
    const lightLevelSlider = document.getElementById('lightLevelSlider');
    const lightLevelValue = document.getElementById('lightLevelValue');
    if (lightLevelSlider) {
      lightLevelSlider.addEventListener('input', (e) => {
        const level = parseInt(e.target.value) / 100; // 0–2
        this.displaySettings.lightLevel = level;
        if (lightLevelValue) lightLevelValue.textContent = `${e.target.value}%`;
        this.updateLighting();
        if (this.viewLockManager) {
          this.viewLockManager.onVisualizationChange();
        }
      });
    }

    // Shadow Level slider
    const shadowLevelSlider = document.getElementById('shadowLevelSlider');
    const shadowLevelValue = document.getElementById('shadowLevelValue');
    if (shadowLevelSlider) {
      shadowLevelSlider.addEventListener('input', (e) => {
        const level = parseInt(e.target.value) / 100; // 0–1
        this.displaySettings.shadowLevel = level;
        if (shadowLevelValue) shadowLevelValue.textContent = `${e.target.value}%`;
        this.updateLighting();
        if (this.viewLockManager) {
          this.viewLockManager.onVisualizationChange();
        }
      });
    }

    // XY Plot Controls
    this.setupXYPlotControls();

    // Measurement Tool Controls
    this.setupMeasurementControls();

    // Integration Controls
    this.setupIntegrationControls();

    // Engineering Calculations Controls (Mass Flow, Force Coefficients, Heat Transfer)
    this.setupEngineeringCalculationsControls();

    // Mesh Cutter Controls
    this.setupMeshCutterControls();

    // Mesh Eraser Controls
    this.setupMeshEraserControls();

    // Mesh Solidify Controls
    this.setupMeshSolidifyControls();
  }

  // ============ AUTO-ROTATION METHODS ============

  setupAutoRotation() {
    const btnRotateView = document.getElementById('btnRotateView');
    const rotateModal = document.getElementById('rotateViewModal');
    const btnCloseRotateModal = document.getElementById('btnCloseRotateModal');
    const speedSlider = document.getElementById('rotateSpeedSlider');
    const speedValue = document.getElementById('rotateSpeedValue');
    const durationSlider = document.getElementById('rotateDurationSlider');
    const durationValue = document.getElementById('rotateDurationValue');
    const continuousToggle = document.getElementById('rotateContinuousToggle');
    const toggleKnob = document.getElementById('rotateToggleKnob');
    const btnStartRotation = document.getElementById('btnStartRotation');

    if (!btnRotateView || !rotateModal) {
      console.warn('Auto-rotation UI elements not found');
      return;
    }

    // Toggle modal visibility
    btnRotateView.addEventListener('click', () => {
      const isVisible = rotateModal.style.display !== 'none';
      rotateModal.style.display = isVisible ? 'none' : 'block';
    });

    // Close modal
    btnCloseRotateModal.addEventListener('click', () => {
      rotateModal.style.display = 'none';
    });

    // Close on click outside
    document.addEventListener('click', (e) => {
      if (rotateModal.style.display === 'block' &&
          !rotateModal.contains(e.target) &&
          e.target !== btnRotateView &&
          !btnRotateView.contains(e.target)) {
        rotateModal.style.display = 'none';
      }
    });

    // Speed slider
    speedSlider.addEventListener('input', () => {
      this.autoRotation.speed = parseInt(speedSlider.value);
      speedValue.textContent = `${speedSlider.value} deg/s`;
    });

    // Duration slider
    durationSlider.addEventListener('input', () => {
      this.autoRotation.duration = parseInt(durationSlider.value);
      durationValue.textContent = `${durationSlider.value} s`;
    });

    // Continuous toggle with visual feedback
    const toggleTrack = document.getElementById('rotateToggleTrack');
    continuousToggle.addEventListener('change', () => {
      this.autoRotation.continuous = continuousToggle.checked;
      // Update toggle knob position and track color
      toggleKnob.style.transform = continuousToggle.checked ? 'translateX(18px)' : 'translateX(0)';
      if (toggleTrack) {
        toggleTrack.style.backgroundColor = continuousToggle.checked ? 'var(--apple-accent, #30d158)' : 'var(--bg-darker, #0a0a0a)';
      }
      // Disable duration slider if continuous
      durationSlider.disabled = continuousToggle.checked;
      durationSlider.style.opacity = continuousToggle.checked ? '0.5' : '1';
    });

    // Start/Stop button
    btnStartRotation.addEventListener('click', () => {
      if (this.autoRotation.isActive) {
        this.stopAutoRotation();
      } else {
        this.startAutoRotation();
      }
    });
  }

  /**
   * Setup share button click handler
   */
  setupShareButton() {
    const btnShare = document.getElementById('btnShare');
    if (!btnShare) return;

    btnShare.addEventListener('click', () => {
      // Get current case info
      if (!this.currentData?.case_path) {
        Utils.notify('No simulation loaded to share', 'error');
        return;
      }

      // Extract case info for sharing
      const casePath = this.currentData.case_path;

      // Get the display name - prefer the database display name, then header, then fallback
      const caseNameDisplay = document.getElementById('caseNameDisplay');
      console.log('[Share] Debug - caseDisplayName:', this.caseDisplayName);
      console.log('[Share] Debug - caseNameDisplay.textContent:', caseNameDisplay?.textContent);
      console.log('[Share] Debug - currentData.case_name:', this.currentData?.case_name);
      const caseName = this.caseDisplayName || caseNameDisplay?.textContent || this.currentData.case_name || 'Simulation';
      console.log('[Share] Using caseName:', caseName);

      // The file_id is the UUID if using S3, otherwise use the path
      // Check if casePath looks like a UUID
      const isUUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i.test(casePath);
      const fileId = isUUID ? casePath.split('/')[0] : null;
      const subPath = isUUID && casePath.includes('/') ? casePath.substring(casePath.indexOf('/') + 1) : '';

      // Open the share modal
      if (window.shareModal) {
        window.shareModal.open(casePath, 'case', caseName, fileId, subPath);
      } else {
        Utils.notify('Share modal not available', 'error');
      }
    });
  }

  startAutoRotation() {
    if (this.autoRotation.isActive) return;

    const camera = this.engine.camera;
    const controls = this.engine.controls;
    if (!camera || !controls) {
      console.warn('Camera or controls not available');
      return;
    }

    // Calculate mesh center (use controls target or scene center)
    this.autoRotation.meshCenter = controls.target.clone();

    // Calculate rotation axis: perpendicular to view direction (up vector of camera)
    // This creates an axis parallel to the screen that passes through the mesh center
    this.autoRotation.rotationAxis = camera.up.clone().normalize();

    this.autoRotation.isActive = true;
    this.autoRotation.startTime = performance.now();

    // Update UI
    const btnStartRotation = document.getElementById('btnStartRotation');
    btnStartRotation.innerHTML = `
      <svg width="16" height="16" viewBox="0 0 20 20" fill="currentColor">
        <rect x="4" y="4" width="12" height="12" rx="2"/>
      </svg>
      Stop Rotation
    `;
    btnStartRotation.style.background = 'var(--apple-danger, #ff453a)';

    // Highlight the rotate button
    const btnRotateView = document.getElementById('btnRotateView');
    if (btnRotateView) {
      btnRotateView.style.background = 'rgba(48, 209, 88, 0.3)';
    }

    // Start animation loop
    this.animateRotation();

    // Trigger collaborative sync
    if (this.viewLockManager) {
      this.viewLockManager.onVisualizationChange();
    }
  }

  stopAutoRotation() {
    if (!this.autoRotation.isActive) return;

    this.autoRotation.isActive = false;
    if (this.autoRotation.animationId) {
      cancelAnimationFrame(this.autoRotation.animationId);
      this.autoRotation.animationId = null;
    }

    // Update UI
    const btnStartRotation = document.getElementById('btnStartRotation');
    btnStartRotation.innerHTML = `
      <svg width="16" height="16" viewBox="0 0 20 20" fill="currentColor">
        <polygon points="5,3 17,10 5,17"/>
      </svg>
      Start Rotation
    `;
    btnStartRotation.style.background = 'var(--apple-accent, #30d158)';

    // Remove highlight from rotate button
    const btnRotateView = document.getElementById('btnRotateView');
    if (btnRotateView) {
      btnRotateView.style.background = '';
    }

    // Trigger collaborative sync
    if (this.viewLockManager) {
      this.viewLockManager.onVisualizationChange();
    }
  }

  animateRotation(lastTime) {
    if (!this.autoRotation.isActive) return;

    const now = performance.now();
    const elapsed = (now - this.autoRotation.startTime) / 1000; // seconds

    // Check if duration exceeded (unless continuous)
    if (!this.autoRotation.continuous && elapsed >= this.autoRotation.duration) {
      this.stopAutoRotation();
      return;
    }

    const camera = this.engine.camera;
    const controls = this.engine.controls;
    const center = this.autoRotation.meshCenter;
    const axis = this.autoRotation.rotationAxis;

    // Calculate actual delta time for smooth animation
    const deltaTime = lastTime ? (now - lastTime) / 1000 : 1 / 60;

    // Calculate rotation angle for this frame
    // speed is in degrees per second, convert to radians
    const angleRadians = (this.autoRotation.speed * Math.PI / 180) * deltaTime;

    // Rotate camera position around the axis
    const offset = camera.position.clone().sub(center);

    // Create rotation quaternion
    const quaternion = new THREE.Quaternion();
    quaternion.setFromAxisAngle(axis, angleRadians);

    // Apply rotation to offset
    offset.applyQuaternion(quaternion);

    // Update camera position
    camera.position.copy(center.clone().add(offset));

    // Keep camera looking at center
    camera.lookAt(center);

    // Update controls target
    controls.target.copy(center);

    // Sync camera position to followers (throttled to ~30fps)
    if (this.viewLockManager) {
      if (!this.autoRotation.lastSyncTime || (now - this.autoRotation.lastSyncTime) > 33) {
        this.viewLockManager.onVisualizationChange();
        this.autoRotation.lastSyncTime = now;
      }
    }

    // Request next frame with current time
    this.autoRotation.animationId = requestAnimationFrame(() => this.animateRotation(now));
  }

  /**
   * Reload the current case
   */
  async reloadCurrentCase() {
    if (!this.currentData?.case_path) {
      console.warn('[Reload] No current case to reload');
      return;
    }

    const casePath = this.currentData.case_path;
    const caseSource = this.currentData.source || this.currentData.metadata?.format || 'OpenFOAM';
    console.log(`[Reload] Reloading ${caseSource} case: ${casePath}`);

    try {
      if (caseSource === 'EnSight') {
        await this.loadEnSightWithGeometry(casePath);
      } else if (caseSource === 'Fluent') {
        await this.loadFluentCase(casePath);
      } else {
        await this.loadOpenFOAMWithGeometry(casePath);
      }
      Utils.notify('Case reloaded', 'success');
    } catch (err) {
      console.error('[Reload] Failed to reload case:', err);
      Utils.notify(`Failed to reload: ${err.message}`, 'error');
    }
  }

  /**
   * Setup XY Plot event handlers
   */
  setupXYPlotControls() {
    const xyPlotEnabled = document.getElementById('xyPlotEnabled');
    const xyPlotControls = document.getElementById('xyPlotControls');
    const btnExtractPlotData = document.getElementById('btnExtractPlotData');
    const btnPickLineStart = document.getElementById('btnPickLineStart');
    const btnPickLineEnd = document.getElementById('btnPickLineEnd');
    const probeTypeSelect = document.getElementById('xyPlotProbeType');
    const btnAddProbePoint = document.getElementById('btnAddProbePoint');
    const btnClearProbePoints = document.getElementById('btnClearProbePoints');

    // Initialize probe points array
    this.probePoints = [];

    // Toggle controls visibility
    if (xyPlotEnabled) {
      xyPlotEnabled.addEventListener('change', (e) => {
        xyPlotControls.style.display = e.target.checked ? 'block' : 'none';

        // Remove visualizations when disabled
        if (!e.target.checked) {
          this.clearLineProbe();
          this.clearProbePoints();
          // Disable slice widget by unchecking the checkbox
          const slicePlaneShow = document.getElementById('slicePlaneShow');
          if (slicePlaneShow && slicePlaneShow.checked) {
            slicePlaneShow.checked = false;
            slicePlaneShow.dispatchEvent(new Event('change'));
          }
          // Clear plot data and notify sync
          window.currentPlotData = null;
          if (this.viewLockManager) {
            this.viewLockManager.onVisualizationChange();
          }
        }
      });
    }

    // Probe type change handler
    if (probeTypeSelect) {
      probeTypeSelect.addEventListener('change', (e) => {
        const probeType = e.target.value;
        const lineProbeTool = document.getElementById('lineProbeTool');
        const pointCloudProbeTool = document.getElementById('pointCloudProbeTool');
        const slicePlaneTool = document.getElementById('slicePlaneTool');
        const lineProbeInfo = document.getElementById('lineProbeInfo');
        const pointCloudProbeInfo = document.getElementById('pointCloudProbeInfo');
        const slicePlaneProbeInfo = document.getElementById('slicePlaneProbeInfo');

        // Clear plot data when changing probe type (but not when applying synced state)
        if (!this.viewLockManager?.isApplyingState) {
          window.currentPlotData = null;
        }

        if (probeType === 'line') {
          lineProbeTool.style.display = 'block';
          pointCloudProbeTool.style.display = 'none';
          slicePlaneTool.style.display = 'none';
          lineProbeInfo.style.display = 'block';
          pointCloudProbeInfo.style.display = 'none';
          slicePlaneProbeInfo.style.display = 'none';
          this.clearProbePoints();
          // Disable slice widget
          const slicePlaneShow1 = document.getElementById('slicePlaneShow');
          if (slicePlaneShow1 && slicePlaneShow1.checked) {
            slicePlaneShow1.checked = false;
            slicePlaneShow1.dispatchEvent(new Event('change'));
          }
        } else if (probeType === 'points') {
          lineProbeTool.style.display = 'none';
          pointCloudProbeTool.style.display = 'block';
          slicePlaneTool.style.display = 'none';
          lineProbeInfo.style.display = 'none';
          pointCloudProbeInfo.style.display = 'block';
          slicePlaneProbeInfo.style.display = 'none';
          this.clearLineProbe();
          // Disable slice widget
          const slicePlaneShow2 = document.getElementById('slicePlaneShow');
          if (slicePlaneShow2 && slicePlaneShow2.checked) {
            slicePlaneShow2.checked = false;
            slicePlaneShow2.dispatchEvent(new Event('change'));
          }
        } else if (probeType === 'slice') {
          lineProbeTool.style.display = 'none';
          pointCloudProbeTool.style.display = 'none';
          slicePlaneTool.style.display = 'block';
          lineProbeInfo.style.display = 'none';
          pointCloudProbeInfo.style.display = 'none';
          slicePlaneProbeInfo.style.display = 'block';
          this.clearLineProbe();
          this.clearProbePoints();

          // Set default origin to mesh center if not already set
          // Skip auto-enabling slice widget when applying synced state (controlled user just needs the plot)
          if (!this.viewLockManager?.isApplyingState) {
            this.initializeSlicePlaneDefaults();
          }
        }

        // Notify sync of probe type change
        if (this.viewLockManager) {
          this.viewLockManager.onVisualizationChange();
        }
      });
    }

    // Pick line start point
    if (btnPickLineStart) {
      btnPickLineStart.addEventListener('click', () => {
        this.startLineProbe('start');
      });
    }

    // Pick line end point
    if (btnPickLineEnd) {
      btnPickLineEnd.addEventListener('click', () => {
        this.startLineProbe('end');
      });
    }

    // Add probe point
    if (btnAddProbePoint) {
      console.log('Add Probe Point button found, attaching event listener');
      btnAddProbePoint.addEventListener('click', () => {
        console.log('Add Probe Point button clicked!');
        this.startProbePointPicking();
      });
    } else {
      console.error('Add Probe Point button NOT found!');
    }

    // Clear all probe points
    if (btnClearProbePoints) {
      console.log('Clear Probe Points button found, attaching event listener');
      btnClearProbePoints.addEventListener('click', () => {
        console.log('Clear Probe Points button clicked!');
        this.clearProbePoints();
        // Notify sync
        if (this.viewLockManager) {
          this.viewLockManager.onVisualizationChange();
        }
      });
    } else {
      console.error('Clear Probe Points button NOT found!');
    }

    // Slice plane controls
    const btnPickSlicePlaneOrigin = document.getElementById('btnPickSlicePlaneOrigin');
    const btnSlicePlaneX = document.getElementById('btnSlicePlaneX');
    const btnSlicePlaneY = document.getElementById('btnSlicePlaneY');
    const btnSlicePlaneZ = document.getElementById('btnSlicePlaneZ');
    const slicePlaneShow = document.getElementById('slicePlaneShow');

    // Quick plane buttons - set orientation and auto-enable widget
    if (btnSlicePlaneZ) {
      btnSlicePlaneZ.addEventListener('click', () => {
        this.setSlicePlaneOrientation(0, 0, 1);
      });
    }

    if (btnSlicePlaneY) {
      btnSlicePlaneY.addEventListener('click', () => {
        this.setSlicePlaneOrientation(0, 1, 0);
      });
    }

    if (btnSlicePlaneX) {
      btnSlicePlaneX.addEventListener('click', () => {
        this.setSlicePlaneOrientation(1, 0, 0);
      });
    }

    // Pick origin button
    if (btnPickSlicePlaneOrigin) {
      btnPickSlicePlaneOrigin.addEventListener('click', () => {
        this.startSlicePlaneOriginPick();
      });
    }

    // Note: The slicePlaneShow checkbox is now handled by the InteractiveSliceWidget
    // in slice-widget.js, so we don't need a separate event listener here

    // Extract plot data (will auto-open modal)
    if (btnExtractPlotData) {
      btnExtractPlotData.addEventListener('click', async () => {
        await this.filters.applyWithProgressBar('btnExtractPlotData', async () => {
          await this.filters.applyXYPlotFromUI();
        }, {
          startMessage: 'Extracting plot data...',
          successMessage: 'Plot data extracted successfully',
          errorPrefix: 'Plot extraction error'
        });
      });
    }
  }

  /**
   * Setup Measurement Tool event handlers
   */
  setupMeasurementControls() {
    const measurementEnabled = document.getElementById('measurementEnabled');
    const measurementControls = document.getElementById('measurementControls');
    const measurementTypeSelect = document.getElementById('measurementType');
    const measurementUnitSelect = document.getElementById('measurementUnit');
    const btnPickMeasureStart = document.getElementById('btnPickMeasureStart');
    const btnPickMeasureEnd = document.getElementById('btnPickMeasureEnd');
    const btnClearMeasurement = document.getElementById('btnClearMeasurement');
    const measurementShow = document.getElementById('measurementShow');

    // Control containers
    const distanceMeasurementControls = document.getElementById('distanceMeasurementControls');
    const angleMeasurementControls = document.getElementById('angleMeasurementControls');
    const areaMeasurementControls = document.getElementById('areaMeasurementControls');
    const volumeMeasurementControls = document.getElementById('volumeMeasurementControls');

    // Initialize measurement state
    this.measurementStart = null;
    this.measurementEnd = null;
    this.measurementMode = null;  // 'start' or 'end'
    this.measurements = [];  // Array to store multiple measurements
    this.measurementIdCounter = 0;  // Counter for unique IDs

    // Angle measurement state
    this.anglePoint1 = null;
    this.angleVertex = null;
    this.anglePoint2 = null;
    this.angleMode = null; // 'point1', 'vertex', or 'point2'
    this.angleTempMarkers = []; // Store temp markers for all 3 points

    // Area measurement state
    this.areaPoints = [];
    this.areaPickingActive = false;
    this.areaTempMarkers = []; // Store temp markers for all polygon points

    // Volume measurement state
    this.volumePoints = [];
    this.volumePickingActive = false;
    this.volumeTempMarkers = []; // Store temp markers for all volume points

    // Measurement unit state
    this.measurementUnit = 'm'; // Default to meters

    // Toggle controls visibility
    if (measurementEnabled) {
      measurementEnabled.addEventListener('change', (e) => {
        measurementControls.style.display = e.target.checked ? 'block' : 'none';

        // Clear all measurements when disabled
        if (!e.target.checked) {
          this.clearAllMeasurements();
          this.clearMeasurementTempMarker();
          this.measurementStart = null;
          this.measurementEnd = null;
          document.getElementById('measureStartDisplay').textContent = 'Start: Not set';
          document.getElementById('measureEndDisplay').textContent = 'End: Not set';
          this.updateMeasurementsList();
        }
      });
    }

    // Measurement type selector
    if (measurementTypeSelect) {
      measurementTypeSelect.addEventListener('change', (e) => {
        const type = e.target.value;

        // Hide all control panels
        distanceMeasurementControls.style.display = 'none';
        angleMeasurementControls.style.display = 'none';
        areaMeasurementControls.style.display = 'none';
        volumeMeasurementControls.style.display = 'none';

        // Show selected control panel
        if (type === 'distance') {
          distanceMeasurementControls.style.display = 'block';
        } else if (type === 'angle') {
          angleMeasurementControls.style.display = 'block';
        } else if (type === 'area') {
          areaMeasurementControls.style.display = 'block';
        } else if (type === 'volume') {
          volumeMeasurementControls.style.display = 'block';
        }
      });
    }

    // Measurement unit selector
    if (measurementUnitSelect) {
      measurementUnitSelect.addEventListener('change', (e) => {
        this.measurementUnit = e.target.value;
        // Update all existing measurements to show in new units
        this.updateMeasurementsList();
      });
    }

    // Show/hide checkbox
    if (measurementShow) {
      measurementShow.addEventListener('change', (e) => {
        if (e.target.checked) {
          // Show all measurements
          this.measurements.forEach(m => {
            if (m.visualGroup) {
              this.engine.scene.add(m.visualGroup);
            }
          });
        } else {
          // Hide all measurements
          this.measurements.forEach(m => {
            if (m.visualGroup) {
              this.engine.scene.remove(m.visualGroup);
            }
          });
        }
      });
    }

    // Pick measurement start point
    if (btnPickMeasureStart) {
      btnPickMeasureStart.addEventListener('click', () => {
        this.startMeasurementPicking('start');
      });
    }

    // Pick measurement end point
    if (btnPickMeasureEnd) {
      btnPickMeasureEnd.addEventListener('click', () => {
        this.startMeasurementPicking('end');
      });
    }

    // Remove individual distance measurement points
    const btnRemoveMeasureStart = document.getElementById('btnRemoveMeasureStart');
    const btnRemoveMeasureEnd = document.getElementById('btnRemoveMeasureEnd');

    if (btnRemoveMeasureStart) {
      btnRemoveMeasureStart.addEventListener('click', () => {
        this.removeMeasurementPoint('start');
      });
    }

    if (btnRemoveMeasureEnd) {
      btnRemoveMeasureEnd.addEventListener('click', () => {
        this.removeMeasurementPoint('end');
      });
    }

    // Clear all measurements
    if (btnClearMeasurement) {
      btnClearMeasurement.textContent = 'Clear All Measurements';
      btnClearMeasurement.addEventListener('click', () => {
        this.clearAllMeasurements();
        this.clearMeasurementTempMarker();
        this.measurementStart = null;
        this.measurementEnd = null;
        this.anglePoint1 = null;
        this.angleVertex = null;
        this.anglePoint2 = null;
        this.areaPoints = [];
        this.volumePoints = [];
        document.getElementById('measureStartDisplay').textContent = 'Start: Not set';
        document.getElementById('measureEndDisplay').textContent = 'End: Not set';
        this.updateMeasurementsList();
        this.updateAreaPointsList();
        this.updateVolumePointsList();
      });
    }

    // Angle measurement buttons
    const btnPickAnglePoint1 = document.getElementById('btnPickAnglePoint1');
    const btnPickAngleVertex = document.getElementById('btnPickAngleVertex');
    const btnPickAnglePoint2 = document.getElementById('btnPickAnglePoint2');
    const btnCreateAngle = document.getElementById('btnCreateAngle');

    if (btnPickAnglePoint1) {
      btnPickAnglePoint1.addEventListener('click', () => {
        this.startAngleMeasurementPicking('point1');
      });
    }

    if (btnPickAngleVertex) {
      btnPickAngleVertex.addEventListener('click', () => {
        this.startAngleMeasurementPicking('vertex');
      });
    }

    if (btnPickAnglePoint2) {
      btnPickAnglePoint2.addEventListener('click', () => {
        this.startAngleMeasurementPicking('point2');
      });
    }

    if (btnCreateAngle) {
      btnCreateAngle.addEventListener('click', () => {
        this.createAngleMeasurement();
      });
    }

    // Remove individual angle measurement points
    const btnRemoveAnglePoint1 = document.getElementById('btnRemoveAnglePoint1');
    const btnRemoveAngleVertex = document.getElementById('btnRemoveAngleVertex');
    const btnRemoveAnglePoint2 = document.getElementById('btnRemoveAnglePoint2');

    if (btnRemoveAnglePoint1) {
      btnRemoveAnglePoint1.addEventListener('click', () => {
        this.removeAnglePoint('point1');
      });
    }

    if (btnRemoveAngleVertex) {
      btnRemoveAngleVertex.addEventListener('click', () => {
        this.removeAnglePoint('vertex');
      });
    }

    if (btnRemoveAnglePoint2) {
      btnRemoveAnglePoint2.addEventListener('click', () => {
        this.removeAnglePoint('point2');
      });
    }

    // Area measurement buttons
    const btnAddAreaPoint = document.getElementById('btnAddAreaPoint');
    const btnCreateArea = document.getElementById('btnCreateArea');
    const btnClearAreaPoints = document.getElementById('btnClearAreaPoints');

    if (btnAddAreaPoint) {
      btnAddAreaPoint.addEventListener('click', () => {
        this.startAreaPointPicking();
      });
    }

    if (btnCreateArea) {
      btnCreateArea.addEventListener('click', () => {
        this.createAreaMeasurement();
      });
    }

    if (btnClearAreaPoints) {
      btnClearAreaPoints.addEventListener('click', () => {
        this.areaPoints = [];
        this.clearPersistentTempMarkers(this.areaTempMarkers);
        this.updateAreaPointsList();
      });
    }

    // Volume measurement buttons
    const btnAddVolumePoint = document.getElementById('btnAddVolumePoint');
    const btnCreateVolume = document.getElementById('btnCreateVolume');
    const btnClearVolumePoints = document.getElementById('btnClearVolumePoints');

    if (btnAddVolumePoint) {
      btnAddVolumePoint.addEventListener('click', () => {
        this.startVolumePointPicking();
      });
    }

    if (btnCreateVolume) {
      btnCreateVolume.addEventListener('click', () => {
        this.createVolumeMeasurement();
      });
    }

    if (btnClearVolumePoints) {
      btnClearVolumePoints.addEventListener('click', () => {
        this.volumePoints = [];
        this.clearPersistentTempMarkers(this.volumeTempMarkers);
        this.updateVolumePointsList();
      });
    }
  }

  /**
   * Setup Annotation Tool event handlers
   */
  setupAnnotationEventListeners() {
    const annotationsEnabled = document.getElementById('annotationsEnabled');
    const annotationsControls = document.getElementById('annotationsControls');
    const annotationType = document.getElementById('annotationType');
    const btnAddAnnotation = document.getElementById('btnAddAnnotation');
    const btnClearAllAnnotations = document.getElementById('btnClearAllAnnotations');

    // Annotation type control containers
    const textAnnotationControls = document.getElementById('textAnnotationControls');
    const arrowAnnotationControls = document.getElementById('arrowAnnotationControls');
    const scaleBarControls = document.getElementById('scaleBarControls');
    const axesControls = document.getElementById('axesControls');

    // Enable/disable annotations
    if (annotationsEnabled) {
      annotationsEnabled.addEventListener('change', (e) => {
        annotationsControls.style.display = e.target.checked ? 'block' : 'none';

        // Clear all annotations when disabled
        if (!e.target.checked && this.annotationRenderer) {
          this.annotationRenderer.clearAll();
          this.updateAnnotationsList();
        }
      });
    }

    // Annotation type selection - show/hide relevant controls
    if (annotationType) {
      annotationType.addEventListener('change', (e) => {
        const type = e.target.value;

        // Hide all controls first
        textAnnotationControls.style.display = 'none';
        arrowAnnotationControls.style.display = 'none';
        scaleBarControls.style.display = 'none';
        axesControls.style.display = 'none';

        // Show selected control
        if (type === 'text') {
          textAnnotationControls.style.display = 'block';
        } else if (type === 'arrow') {
          arrowAnnotationControls.style.display = 'block';
        } else if (type === 'scale_bar') {
          scaleBarControls.style.display = 'block';

          // Auto-calculate a meaningful scale bar length based on scene size
          const sceneBounds = this.calculateSceneBounds();
          if (sceneBounds) {
            const sceneSize = sceneBounds.max.distanceTo(sceneBounds.min);
            const scaleBarLength = this.calculateNiceScaleBarLength(sceneSize);

            const scaleBarLengthInput = document.getElementById('scaleBarLength');
            const scaleBarUnitInput = document.getElementById('scaleBarUnit');

            if (scaleBarLengthInput && parseFloat(scaleBarLengthInput.value) === 1.0) {
              // Only update if still at default value
              scaleBarLengthInput.value = scaleBarLength.displayValue;
              if (scaleBarUnitInput) {
                scaleBarUnitInput.value = scaleBarLength.unit;
              }
            }
          }
        } else if (type === 'axes') {
          axesControls.style.display = 'block';

          // Set a reasonable default axes length based on scene bounds
          const sceneBounds = this.calculateSceneBounds();
          if (sceneBounds) {
            const sceneSize = sceneBounds.max.distanceTo(sceneBounds.min);
            const suggestedLength = sceneSize * 0.3; // 30% of scene size
            const axesLengthInput = document.getElementById('axesLength');
            if (axesLengthInput && parseFloat(axesLengthInput.value) === 1.0) {
              // Only update if still at default value
              axesLengthInput.value = suggestedLength.toFixed(3);
            }
          }
        }
      });
    }

    // Add annotation button
    if (btnAddAnnotation) {
      btnAddAnnotation.addEventListener('click', () => {
        this.addAnnotationFromUI();
      });
    }

    // Clear all annotations button
    if (btnClearAllAnnotations) {
      btnClearAllAnnotations.addEventListener('click', () => {
        if (this.annotationRenderer) {
          const count = this.annotationRenderer.clearAll();
          this.updateAnnotationsList();
          Utils.notify(`Cleared ${count} annotation(s)`, 'success');
        }
      });
    }

    // Annotation point picking buttons
    const btnPickTextPosition = document.getElementById('btnPickTextPosition');
    const btnPickArrowStart = document.getElementById('btnPickArrowStart');
    const btnPickArrowEnd = document.getElementById('btnPickArrowEnd');

    if (btnPickTextPosition) {
      btnPickTextPosition.addEventListener('click', () => {
        this.startAnnotationPointPicking('text');
      });
    }

    if (btnPickArrowStart) {
      btnPickArrowStart.addEventListener('click', () => {
        this.startAnnotationPointPicking('arrow_start');
      });
    }

    if (btnPickArrowEnd) {
      btnPickArrowEnd.addEventListener('click', () => {
        this.startAnnotationPointPicking('arrow_end');
      });
    }

    const btnPickAxesOrigin = document.getElementById('btnPickAxesOrigin');
    if (btnPickAxesOrigin) {
      btnPickAxesOrigin.addEventListener('click', () => {
        this.startAnnotationPointPicking('axes_origin');
      });
    }

    const btnPickScaleBarPosition = document.getElementById('btnPickScaleBarPosition');
    if (btnPickScaleBarPosition) {
      btnPickScaleBarPosition.addEventListener('click', () => {
        this.startAnnotationPointPicking('scale_bar_position');
      });
    }
  }

  /**
   * Start annotation point picking
   * @param {string} mode - 'text', 'arrow_start', 'arrow_end', or 'axes_origin'
   */
  startAnnotationPointPicking(mode) {
    this.annotationPickMode = mode;
    this.annotationPickingActive = true;

    // Update UI feedback
    const buttonMap = {
      'text': 'btnPickTextPosition',
      'arrow_start': 'btnPickArrowStart',
      'arrow_end': 'btnPickArrowEnd',
      'axes_origin': 'btnPickAxesOrigin',
      'scale_bar_position': 'btnPickScaleBarPosition'
    };

    const btn = document.getElementById(buttonMap[mode]);
    if (btn) {
      btn.textContent = 'Click on mesh...';
      btn.style.backgroundColor = '#ff6b35';
    }

    // Setup mouse handler for annotation picking
    this.setupAnnotationPointPickingHandler();

    console.log(`Annotation picking mode active: ${mode}`);
  }

  /**
   * Setup mouse handler for annotation point picking
   */
  setupAnnotationPointPickingHandler() {
    const viewport = document.getElementById('viewport');

    if (this.annotationPointClickHandler) {
      return; // Already set up
    }

    const clickHandler = (event) => {
      if (!this.annotationPickingActive || !this.annotationPickMode) return;

      const rect = viewport.getBoundingClientRect();
      const x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
      const y = -((event.clientY - rect.top) / rect.height) * 2 + 1;

      this.mouse.set(x, y);
      this.raycaster.setFromCamera(this.mouse, this.engine.camera);

      // Get all visible raycastable objects (meshes, points, and groups)
      const meshObjects = this.getRaycastableObjects();

      const intersects = this.raycaster.intersectObjects(meshObjects, true);

      // Filter to only fully visible objects
      const visibleIntersects = intersects.filter(hit => this.isObjectFullyVisible(hit.object));

      if (visibleIntersects.length > 0) {
        const point = visibleIntersects[0].point;
        const pointArray = [point.x, point.y, point.z];

        if (this.annotationPickMode === 'text') {
          this.annotationTextPosition = pointArray;
          document.getElementById('textPositionDisplay').textContent =
            `[${point.x.toFixed(3)}, ${point.y.toFixed(3)}, ${point.z.toFixed(3)}]`;

          // Visualize the picked point immediately
          this.visualizeTempStartMarker(pointArray, 'annotation');

          const btn = document.getElementById('btnPickTextPosition');
          btn.textContent = 'Pick Point on Mesh';
          btn.style.backgroundColor = '';

        } else if (this.annotationPickMode === 'arrow_start') {
          this.annotationArrowStart = pointArray;
          document.getElementById('arrowStartDisplay').textContent =
            `[${point.x.toFixed(3)}, ${point.y.toFixed(3)}, ${point.z.toFixed(3)}]`;

          // Visualize the picked point immediately
          this.visualizeTempStartMarker(pointArray, 'annotation');

          const btn = document.getElementById('btnPickArrowStart');
          btn.textContent = 'Pick Start Point';
          btn.style.backgroundColor = '';

        } else if (this.annotationPickMode === 'arrow_end') {
          this.annotationArrowEnd = pointArray;
          document.getElementById('arrowEndDisplay').textContent =
            `[${point.x.toFixed(3)}, ${point.y.toFixed(3)}, ${point.z.toFixed(3)}]`;

          // Visualize the picked point immediately
          this.visualizeTempStartMarker(pointArray, 'annotation');

          const btn = document.getElementById('btnPickArrowEnd');
          btn.textContent = 'Pick End Point';
          btn.style.backgroundColor = '';

        } else if (this.annotationPickMode === 'axes_origin') {
          this.annotationAxesOrigin = pointArray;
          document.getElementById('axesOriginDisplay').textContent =
            `[${point.x.toFixed(3)}, ${point.y.toFixed(3)}, ${point.z.toFixed(3)}]`;

          // Visualize the picked point immediately
          this.visualizeTempStartMarker(pointArray, 'annotation');

          const btn = document.getElementById('btnPickAxesOrigin');
          btn.textContent = 'Pick Origin on Mesh';
          btn.style.backgroundColor = '';

        } else if (this.annotationPickMode === 'scale_bar_position') {
          this.annotationScaleBarPosition = pointArray;
          document.getElementById('scaleBarPositionDisplay').textContent =
            `[${point.x.toFixed(3)}, ${point.y.toFixed(3)}, ${point.z.toFixed(3)}]`;

          // Visualize the picked point immediately
          this.visualizeTempStartMarker(pointArray, 'annotation');

          const btn = document.getElementById('btnPickScaleBarPosition');
          btn.textContent = 'Pick Position on Mesh';
          btn.style.backgroundColor = '';
        }

        this.annotationPickingActive = false;
        this.annotationPickMode = null;

        console.log(`Annotation point set:`, point);
      }
    };

    viewport.addEventListener('click', clickHandler);
    this.annotationPointClickHandler = true;
  }

  /**
   * Setup Integration event handlers
   */
  setupIntegrationControls() {
    const integrationEnabled = document.getElementById('integrationEnabled');
    const integrationControls = document.getElementById('integrationControls');
    const btnComputeIntegration = document.getElementById('btnComputeIntegration');
    const btnExportIntegrationCSV = document.getElementById('btnExportIntegrationCSV');

    // Toggle controls visibility
    if (integrationEnabled) {
      integrationEnabled.addEventListener('change', (e) => {
        integrationControls.style.display = e.target.checked ? 'block' : 'none';
      });
    }

    // Compute integration
    if (btnComputeIntegration) {
      btnComputeIntegration.addEventListener('click', async () => {
        await this.filters.applyWithProgressBar('btnComputeIntegration', async () => {
          await this.filters.applyIntegrationFromUI();
        }, {
          startMessage: 'Computing integral...',
          successMessage: 'Integral computed successfully',
          errorPrefix: 'Integration error'
        });
      });
    }

    // Export integration results
    if (btnExportIntegrationCSV) {
      btnExportIntegrationCSV.addEventListener('click', () => {
        this.filters.exportIntegrationToCSV();
      });
    }
  }

  /**
   * Setup Engineering Calculations event handlers (Mass Flow, Force Coefficients, Heat Transfer)
   */
  setupEngineeringCalculationsControls() {
    // Mass Flow Rate
    const massFlowEnabled = document.getElementById('massFlowEnabled');
    const massFlowControls = document.getElementById('massFlowControls');
    const btnComputeMassFlow = document.getElementById('btnComputeMassFlow');
    const massFlowDensityMode = document.getElementById('massFlowDensityMode');

    if (massFlowEnabled) {
      massFlowEnabled.addEventListener('change', (e) => {
        massFlowControls.style.display = e.target.checked ? 'block' : 'none';
      });
    }

    if (massFlowDensityMode) {
      massFlowDensityMode.addEventListener('change', (e) => {
        document.getElementById('massFlowDensityConstant').style.display =
          e.target.value === 'constant' ? 'block' : 'none';
        document.getElementById('massFlowDensityField').style.display =
          e.target.value === 'field' ? 'block' : 'none';
      });
    }

    if (btnComputeMassFlow) {
      btnComputeMassFlow.addEventListener('click', async () => {
        await this.filters.applyWithProgressBar('btnComputeMassFlow', async () => {
          await this.filters.applyMassFlowFromUI();
        }, {
          startMessage: 'Computing mass flow rate...',
          successMessage: 'Mass flow rate computed',
          errorPrefix: 'Mass flow error'
        });
      });
    }

    // Force Coefficients
    const forceCoeffEnabled = document.getElementById('forceCoeffEnabled');
    const forceCoeffControls = document.getElementById('forceCoeffControls');
    const btnComputeForceCoeff = document.getElementById('btnComputeForceCoeff');

    if (forceCoeffEnabled) {
      forceCoeffEnabled.addEventListener('change', (e) => {
        forceCoeffControls.style.display = e.target.checked ? 'block' : 'none';
      });
    }

    if (btnComputeForceCoeff) {
      btnComputeForceCoeff.addEventListener('click', async () => {
        await this.filters.applyWithProgressBar('btnComputeForceCoeff', async () => {
          await this.filters.applyForceCoeffFromUI();
        }, {
          startMessage: 'Computing force coefficients...',
          successMessage: 'Force coefficients computed',
          errorPrefix: 'Force coefficients error'
        });
      });
    }

    // Heat Transfer
    const heatTransferEnabled = document.getElementById('heatTransferEnabled');
    const heatTransferControls = document.getElementById('heatTransferControls');
    const btnComputeHeatTransfer = document.getElementById('btnComputeHeatTransfer');

    if (heatTransferEnabled) {
      heatTransferEnabled.addEventListener('change', (e) => {
        heatTransferControls.style.display = e.target.checked ? 'block' : 'none';
      });
    }

    if (btnComputeHeatTransfer) {
      btnComputeHeatTransfer.addEventListener('click', async () => {
        await this.filters.applyWithProgressBar('btnComputeHeatTransfer', async () => {
          await this.filters.applyHeatTransferFromUI();
        }, {
          startMessage: 'Computing heat transfer...',
          successMessage: 'Heat transfer computed',
          errorPrefix: 'Heat transfer error'
        });
      });
    }
  }

  /**
   * Setup Mesh Cutter event handlers
   */
  setupMeshCutterControls() {
    const meshCutterEnabled = document.getElementById('meshCutterEnabled');
    const meshCutterControls = document.getElementById('meshCutterControls');
    const btnAddCuttingPlane = document.getElementById('btnAddCuttingPlane');
    const btnApplyMeshCutter = document.getElementById('btnApplyMeshCutter');
    const btnClearCuttingPlanes = document.getElementById('btnClearCuttingPlanes');
    const btnPickCutOrigin = document.getElementById('btnPickCutOrigin');
    const btnPickCutNormal = document.getElementById('btnPickCutNormal');

    // Toggle mesh cutter controls visibility
    if (meshCutterEnabled) {
      meshCutterEnabled.addEventListener('change', (e) => {
        meshCutterControls.style.display = e.target.checked ? 'block' : 'none';
        if (e.target.checked) {
          this.filters.initMeshCutter();
        }
      });
    }

    // Add cutting plane
    if (btnAddCuttingPlane) {
      btnAddCuttingPlane.addEventListener('click', () => {
        this.filters.addCuttingPlane();
        // Broadcast state change for collaboration
        if (this.viewLockManager) {
          this.viewLockManager.onVisualizationChange();
        }
      });
    }

    // Apply mesh cutter
    if (btnApplyMeshCutter) {
      btnApplyMeshCutter.addEventListener('click', async () => {
        await this.filters.applyWithProgressBar('btnApplyMeshCutter', async () => {
          await this.filters.applyMeshCutter();
        }, {
          startMessage: 'Applying mesh cut...',
          successMessage: 'Mesh cut applied',
          errorPrefix: 'Mesh cut error'
        });
        // Broadcast state change for collaboration
        if (this.viewLockManager) {
          this.viewLockManager.onVisualizationChange();
        }
      });
    }

    // Clear all cutting planes
    if (btnClearCuttingPlanes) {
      btnClearCuttingPlanes.addEventListener('click', () => {
        this.filters.clearCuttingPlanes();
        // Broadcast state change for collaboration
        if (this.viewLockManager) {
          this.viewLockManager.onVisualizationChange(true);  // Force immediate sync
        }
      });
    }

    // Export cut mesh as STL
    const btnExportCutMeshSTL = document.getElementById('btnExportCutMeshSTL');
    if (btnExportCutMeshSTL) {
      btnExportCutMeshSTL.addEventListener('click', async () => {
        await this.filters.applyWithProgressBar('btnExportCutMeshSTL', async () => {
          await this.exportCutMeshAsSTL();
        }, {
          startMessage: 'Clipping and exporting mesh...',
          successMessage: 'STL exported successfully',
          errorPrefix: 'STL export error'
        });
      });
    }

    // Pick origin from mesh
    if (btnPickCutOrigin) {
      btnPickCutOrigin.addEventListener('click', () => {
        this.startCutPlaneOriginPick();
      });
    }

    // Pick normal from mesh surface
    if (btnPickCutNormal) {
      btnPickCutNormal.addEventListener('click', () => {
        this.startCutPlaneNormalPick();
      });
    }
  }

  /**
   * Setup Mesh Eraser event handlers
   */
  setupMeshEraserControls() {
    const meshEraserEnabled = document.getElementById('meshEraserEnabled');
    const meshEraserControls = document.getElementById('meshEraserControls');
    const btnStartErasing = document.getElementById('btnStartErasing');
    const btnClearAllEraserSequences = document.getElementById('btnClearAllEraserSequences');
    const btnExportErasedMeshSTL = document.getElementById('btnExportErasedMeshSTL');
    const eraserBrushRadius = document.getElementById('eraserBrushRadius');
    const eraserBrushRadiusValue = document.getElementById('eraserBrushRadiusValue');

    // Toggle mesh eraser controls visibility
    if (meshEraserEnabled) {
      meshEraserEnabled.addEventListener('change', (e) => {
        meshEraserControls.style.display = e.target.checked ? 'block' : 'none';
        if (e.target.checked) {
          if (this.filters.meshEraser) this.filters.meshEraser.enable();
        } else {
          if (this.filters.meshEraser) this.filters.meshEraser.disable();
        }
      });
    }

    // Brush radius slider → input (bidirectional)
    if (eraserBrushRadius && eraserBrushRadiusValue) {
      eraserBrushRadius.addEventListener('input', () => {
        const val = parseInt(eraserBrushRadius.value, 10);
        eraserBrushRadiusValue.value = val;
        if (this.filters.meshEraser) this.filters.meshEraser.setBrushRadius(val);
      });

      eraserBrushRadiusValue.addEventListener('change', () => {
        let val = parseFloat(eraserBrushRadiusValue.value.replace(',', '.'));
        if (isNaN(val) || val < 0.1) val = 1;
        if (val > 100) val = 100;
        eraserBrushRadiusValue.value = val;
        eraserBrushRadius.value = Math.min(Math.max(Math.round(val), 1), 50);
        if (this.filters.meshEraser) this.filters.meshEraser.setBrushRadius(val);
      });

      eraserBrushRadiusValue.addEventListener('keydown', (e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          eraserBrushRadiusValue.blur();
        }
      });
    }

    // Brush shape dropdown
    const eraserBrushShape = document.getElementById('eraserBrushShape');
    const eraserBrushSizeLabel = document.getElementById('eraserBrushSizeLabel');
    if (eraserBrushShape) {
      eraserBrushShape.addEventListener('change', () => {
        this.filters.meshEraser.setBrushShape(eraserBrushShape.value);
        if (eraserBrushSizeLabel) {
          eraserBrushSizeLabel.textContent = eraserBrushShape.value === 'cube' ? 'Brush Side' : 'Brush Radius';
        }
      });
    }

    // Start/Stop erasing toggle
    if (btnStartErasing) {
      btnStartErasing.addEventListener('click', () => {
        const eraser = this.filters.meshEraser;
        if (!eraser) return;

        if (eraser.erasing) {
          eraser.stopErasing();
          btnStartErasing.textContent = 'Start Erasing';
          btnStartErasing.style.background = '#c0392b';
        } else {
          eraser.startErasing();
          btnStartErasing.textContent = 'Stop Erasing';
          btnStartErasing.style.background = '#e74c3c';
        }
      });
    }

    // Clear all sequences
    if (btnClearAllEraserSequences) {
      btnClearAllEraserSequences.addEventListener('click', () => {
        if (this.filters.meshEraser) {
          this.filters.meshEraser.clearAllSequences();
          // Reset button if currently erasing
          if (btnStartErasing) {
            btnStartErasing.textContent = 'Start Erasing';
            btnStartErasing.style.background = '#c0392b';
          }
        }
      });
    }

    // Export erased mesh as STL
    if (btnExportErasedMeshSTL) {
      btnExportErasedMeshSTL.addEventListener('click', async () => {
        await this.filters.applyWithProgressBar('btnExportErasedMeshSTL', async () => {
          await this.exportErasedMeshAsSTL();
        }, {
          startMessage: 'Exporting erased mesh...',
          successMessage: 'STL exported successfully',
          errorPrefix: 'STL export error'
        });
      });
    }
  }

  /**
   * Setup Mesh Solidify event handlers
   */
  setupMeshSolidifyControls() {
    const solidifyEnabled = document.getElementById('meshSolidifyEnabled');
    const solidifyControls = document.getElementById('meshSolidifyControls');
    const solidifySlider = document.getElementById('solidifyThicknessSlider');
    const solidifyValue = document.getElementById('solidifyThicknessValue');
    const solidifyDirection = document.getElementById('solidifyDirection');
    const btnApply = document.getElementById('btnApplySolidify');
    const btnSelectAll = document.getElementById('btnSolidifySelectAll');
    const btnSelectNone = document.getElementById('btnSolidifySelectNone');

    // Toggle controls visibility
    if (solidifyEnabled && solidifyControls) {
      solidifyEnabled.addEventListener('change', (e) => {
        solidifyControls.style.display = e.target.checked ? 'block' : 'none';
        if (e.target.checked) {
          this.updateSolidifyPartsList();
        }
        if (!e.target.checked && this.filters.meshSolidify) {
          this.filters.meshSolidify.remove();
          this._clearSolidifyHighlights();
          const results = document.getElementById('solidifyResults');
          if (results) results.style.display = 'none';
          const dl = document.getElementById('btnDownloadSolidify');
          if (dl) dl.disabled = true;
          // Broadcast solidify removal to collaboration followers
          const vlm = this.viewLockManager || window.viewLockManager;
          if (vlm && vlm.amIController) {
            vlm.onVisualizationChange();
          }
        }
      });
    }

    // Select All / Select None buttons
    if (btnSelectAll) {
      btnSelectAll.addEventListener('click', () => {
        const cbs = document.querySelectorAll('#solidifyPartsList input[type="checkbox"]');
        cbs.forEach(cb => {
          cb.checked = true;
          this._setSolidifyPartHighlight(cb.dataset.partName, true);
        });
      });
    }
    if (btnSelectNone) {
      btnSelectNone.addEventListener('click', () => {
        const cbs = document.querySelectorAll('#solidifyPartsList input[type="checkbox"]');
        cbs.forEach(cb => { cb.checked = false; });
        this._clearSolidifyHighlights();
      });
    }

    // Sync slider ↔ text input
    if (solidifySlider && solidifyValue) {
      solidifySlider.addEventListener('input', () => {
        solidifyValue.value = solidifySlider.value;
      });

      solidifyValue.addEventListener('change', () => {
        let val = parseFloat(solidifyValue.value.replace(',', '.'));
        if (isNaN(val) || val < 0.01) val = 0.1;
        if (val > 50) val = 50;
        solidifyValue.value = val;
        solidifySlider.value = Math.min(Math.max(val, 0.1), 20);
      });

      solidifyValue.addEventListener('keydown', (e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          solidifyValue.blur();
        }
      });
    }

    // Apply button
    const btnDownload = document.getElementById('btnDownloadSolidify');
    if (btnApply) {
      btnApply.addEventListener('click', async () => {
        if (!this.filters.meshSolidify) return;

        await this.filters.applyWithProgressBar('btnApplySolidify', async () => {
          // Double-rAF: first rAF fires before paint, second fires after paint completes
          await new Promise(r => requestAnimationFrame(() => requestAnimationFrame(r)));

          const thickness = parseFloat(solidifyValue?.value || '1');
          const direction = solidifyDirection?.value || 'both';

          // Collect selected parts from the part selector checkboxes
          const selectedParts = this._getSolidifySelectedParts();

          const result = this.filters.meshSolidify.apply(thickness, direction, selectedParts);

          // Re-apply highlights to the newly created solidified mesh for checked parts
          if (selectedParts && selectedParts.size > 0) {
            for (const partName of selectedParts) {
              this._setSolidifyPartHighlight(partName, true);
            }
          }

          // Update results display
          const resultsDiv = document.getElementById('solidifyResults');
          if (resultsDiv && result) {
            document.getElementById('solidifyPartCount').textContent = result.parts;
            document.getElementById('solidifyTriCount').textContent = result.triangles.toLocaleString();
            resultsDiv.style.display = 'block';
          }

          // Enable download button if solidify produced geometry
          if (btnDownload && result && result.parts > 0) {
            btnDownload.disabled = false;
          }
          // Broadcast solidify state to collaboration followers
          const vlm = this.viewLockManager || window.viewLockManager;
          if (vlm && vlm.amIController) {
            vlm.onVisualizationChange();
          }
        }, {
          startMessage: 'Applying solidify filter...',
          successMessage: 'Solidify filter applied',
          errorPrefix: 'Solidify error'
        });
      });
    }

    // Download button — exports only the currently checked parts from the solidified mesh
    if (btnDownload) {
      btnDownload.addEventListener('click', async () => {
        if (!this.filters.meshSolidify) return;

        await this.filters.applyWithProgressBar('btnDownloadSolidify', async () => {
          // Double-rAF: first rAF fires before paint, second fires after paint completes
          await new Promise(r => requestAnimationFrame(() => requestAnimationFrame(r)));

          const selectedParts = this._getSolidifySelectedParts();
          this.filters.meshSolidify.downloadOBJ(selectedParts);
        }, {
          startMessage: 'Generating OBJ file...',
          successMessage: 'OBJ file downloaded',
          errorPrefix: 'Export error'
        });
      });
    }
  }

  /**
   * Populate the solidify filter's part selector with checkboxes for each mesh part.
   * Called when the solidify filter is enabled and when new mesh data is loaded.
   */
  updateSolidifyPartsList() {
    const container = document.getElementById('solidifyPartsList');
    if (!container || !this.filters.meshSolidify) return;

    // Clear any previous highlights before rebuilding the list
    this._clearSolidifyHighlights();

    const parts = this.filters.meshSolidify.getAvailableParts();

    if (parts.length === 0) {
      container.innerHTML = '<div style="font-size: 10px; color: #888; padding: 4px;">No parts available (single mesh or no mesh loaded)</div>';
      return;
    }

    container.innerHTML = '';
    const visibleParts = this.meshParts.visibleParts;

    for (const partName of parts) {
      const item = document.createElement('div');
      item.style.cssText = 'display: flex; align-items: center; gap: 4px; padding: 2px 4px;';

      const cb = document.createElement('input');
      cb.type = 'checkbox';
      cb.id = `solidifyPart_${partName}`;
      cb.dataset.partName = partName;
      // Default: checked if the part is currently visible in the main mesh parts panel
      cb.checked = visibleParts.has(partName);

      const label = document.createElement('label');
      label.htmlFor = cb.id;
      label.textContent = partName;
      label.style.cssText = 'font-size: 10px; cursor: pointer; user-select: none;';

      // Highlight / unhighlight on checkbox change — only when the solidify filter is active
      cb.addEventListener('change', (e) => {
        if (document.getElementById('meshSolidifyEnabled')?.checked) {
          this._setSolidifyPartHighlight(e.target.dataset.partName, e.target.checked);
        }
      });

      item.appendChild(cb);
      item.appendChild(label);
      container.appendChild(item);

      // Apply initial highlight for checked parts — only when the solidify filter is active
      const solidifyActive = document.getElementById('meshSolidifyEnabled')?.checked;
      if (cb.checked && solidifyActive) {
        this._setSolidifyPartHighlight(partName, true);
      }
    }
  }

  /**
   * Read the solidify part selector checkboxes and return a Set of selected part names.
   * Returns null if no checkboxes exist (single mesh — solidify everything).
   */
  _getSolidifySelectedParts() {
    const cbs = document.querySelectorAll('#solidifyPartsList input[type="checkbox"]');
    if (cbs.length === 0) return null; // no part selector → solidify all visible

    const selected = new Set();
    cbs.forEach(cb => {
      if (cb.checked) selected.add(cb.dataset.partName);
    });
    return selected;
  }

  /**
   * Highlight or unhighlight a mesh part in the 3D scene when its solidify checkbox changes.
   * Uses emissive color to create a visible highlight without changing base appearance.
   */
  _setSolidifyPartHighlight(partName, highlight) {
    // Apply highlight to both the original mesh and the solidified mesh
    // (the solidified mesh overlaps the original, so both must be updated)
    const roots = [];
    const meshObjectId = this.meshParts.activeMeshObjectId || 'main-mesh';
    const meshEntry = this.engine.objects.get(meshObjectId)
                   || this.engine.objects.get('main-mesh');
    if (meshEntry && meshEntry.object.type === 'Group') roots.push(meshEntry.object);
    const solidEntry = this.engine.objects.get('solidify-filter');
    if (solidEntry) roots.push(solidEntry.object);
    if (roots.length === 0) return;

    for (const rootObj of roots) {
      rootObj.traverse((child) => {
        if (!child.isMesh || !child.material) return;
        const name = child.userData.partName || child.name;
        if (name !== partName) return;

        if (highlight) {
          if (child.userData._origEmissiveHex == null) {
            child.userData._origEmissiveHex = child.material.emissive
              ? child.material.emissive.getHex() : 0x000000;
          }
          child.material.emissive.setHex(0x1a3a5c);
        } else {
          const orig = child.userData._origEmissiveHex;
          if (orig != null) {
            child.material.emissive.setHex(orig);
            delete child.userData._origEmissiveHex;
          }
        }
      });
    }

    if (this.engine.requestRender) this.engine.requestRender();
  }

  /**
   * Remove all solidify highlights from the scene (e.g. when filter is disabled or parts list rebuilt).
   */
  _clearSolidifyHighlights() {
    const roots = [];
    const meshObjectId = this.meshParts.activeMeshObjectId || 'main-mesh';
    const meshEntry = this.engine.objects.get(meshObjectId)
                   || this.engine.objects.get('main-mesh');
    if (meshEntry && meshEntry.object.type === 'Group') roots.push(meshEntry.object);
    const solidEntry = this.engine.objects.get('solidify-filter');
    if (solidEntry) roots.push(solidEntry.object);

    for (const rootObj of roots) {
      rootObj.traverse((child) => {
        if (!child.isMesh || !child.material) return;
        const orig = child.userData._origEmissiveHex;
        if (orig != null) {
          child.material.emissive.setHex(orig);
          delete child.userData._origEmissiveHex;
        }
      });
    }

    if (this.engine.requestRender) this.engine.requestRender();
  }

  /**
   * Start cutting plane origin picking
   */
  startCutPlaneOriginPick() {
    this.cutPlaneOriginPickActive = true;

    const btn = document.getElementById('btnPickCutOrigin');
    const originalText = btn?.textContent || 'Pick';
    if (btn) {
      btn.textContent = 'Click on mesh...';
      btn.style.backgroundColor = '#ff6b35';
    }

    const viewport = document.getElementById('viewport');

    const clickHandler = (event) => {
      if (!this.cutPlaneOriginPickActive) return;

      const rect = viewport.getBoundingClientRect();
      const mouse = new THREE.Vector2(
        ((event.clientX - rect.left) / rect.width) * 2 - 1,
        -((event.clientY - rect.top) / rect.height) * 2 + 1
      );

      const raycaster = new THREE.Raycaster();
      raycaster.setFromCamera(mouse, this.engine.camera);

      const meshes = [];
      this.engine.scene.traverse((obj) => {
        if (obj.isMesh && obj.visible) {
          meshes.push(obj);
        }
      });

      const intersects = raycaster.intersectObjects(meshes, false);

      if (intersects.length > 0) {
        const point = intersects[0].point;
        document.getElementById('cutPlaneOriginX').value = point.x.toFixed(4);
        document.getElementById('cutPlaneOriginY').value = point.y.toFixed(4);
        document.getElementById('cutPlaneOriginZ').value = point.z.toFixed(4);
        console.log('[MeshCutter] Picked origin:', point);
      }

      // Clean up
      this.cutPlaneOriginPickActive = false;
      viewport.removeEventListener('click', clickHandler);
      if (btn) {
        btn.textContent = originalText;
        btn.style.backgroundColor = '';
      }
    };

    viewport.addEventListener('click', clickHandler);
  }

  /**
   * Start cutting plane normal picking (from surface normal)
   */
  startCutPlaneNormalPick() {
    this.cutPlaneNormalPickActive = true;

    const btn = document.getElementById('btnPickCutNormal');
    const originalText = btn?.textContent || 'Pick';
    if (btn) {
      btn.textContent = 'Click on surface...';
      btn.style.backgroundColor = '#ff6b35';
    }

    const viewport = document.getElementById('viewport');

    const clickHandler = (event) => {
      if (!this.cutPlaneNormalPickActive) return;

      const rect = viewport.getBoundingClientRect();
      const mouse = new THREE.Vector2(
        ((event.clientX - rect.left) / rect.width) * 2 - 1,
        -((event.clientY - rect.top) / rect.height) * 2 + 1
      );

      const raycaster = new THREE.Raycaster();
      raycaster.setFromCamera(mouse, this.engine.camera);

      const meshes = [];
      this.engine.scene.traverse((obj) => {
        if (obj.isMesh && obj.visible) {
          meshes.push(obj);
        }
      });

      const intersects = raycaster.intersectObjects(meshes, false);

      if (intersects.length > 0 && intersects[0].face) {
        const normal = intersects[0].face.normal.clone();
        // Transform normal to world space
        const normalMatrix = new THREE.Matrix3().getNormalMatrix(intersects[0].object.matrixWorld);
        normal.applyMatrix3(normalMatrix).normalize();

        document.getElementById('cutPlaneNormalX').value = normal.x.toFixed(4);
        document.getElementById('cutPlaneNormalY').value = normal.y.toFixed(4);
        document.getElementById('cutPlaneNormalZ').value = normal.z.toFixed(4);
        console.log('[MeshCutter] Picked normal:', normal);
      }

      // Clean up
      this.cutPlaneNormalPickActive = false;
      viewport.removeEventListener('click', clickHandler);
      if (btn) {
        btn.textContent = originalText;
        btn.style.backgroundColor = '';
      }
    };

    viewport.addEventListener('click', clickHandler);
  }

  /**
   * Check if a point is inside a bounded cut region (same logic as shader)
   */
  isPointInsideCut(point, cutPlanes) {
    for (const plane of cutPlanes) {
      const dist = plane[0] * point.x + plane[1] * point.y + plane[2] * point.z + plane[3];
      if (dist > 0) return false;
    }
    return true;
  }

  /**
   * Export cut mesh as STL - clips triangles using same logic as GPU shader
   */
  async exportCutMeshAsSTL() {
    // Yield for TWO animation frames to ensure browser actually paints the progress bar
    // (first rAF fires before paint, second fires after paint completes)
    await new Promise(resolve => requestAnimationFrame(() => requestAnimationFrame(resolve)));
    const startTime = Date.now();

    // Get cut regions from bounded clipper (same data as shader uses)
    const cuttingPlaneWidget = this.filters?.cuttingPlaneWidget;
    const boundedClipper = cuttingPlaneWidget?.boundedClipper;
    const cuts = boundedClipper?.cuts || [];

    console.log('[ExportSTL] Active cuts:', cuts.length);

    // Collect and clip triangles
    const keptPositions = [];
    let totalTris = 0, clippedTris = 0;

    this.engine.scene.traverse((obj) => {
      if (!obj.isMesh || !obj.visible || !obj.geometry) return;

      // Skip helpers/widgets
      let p = obj;
      while (p) {
        const n = p.name || '';
        if (n.startsWith('CuttingPlane_') || n.startsWith('SlicePlane_') ||
            n.includes('Helper') || n.includes('Control') || n.includes('Gizmo')) return;
        p = p.parent;
      }
      if (obj.type.includes('Helper') || obj.type === 'LineSegments' ||
          obj.type === 'Line' || obj.type === 'Points') return;
      if (obj.material?.type === 'MeshBasicMaterial') return;

      // Get world-space geometry
      let geom = obj.geometry.clone();
      obj.updateMatrixWorld();
      geom.applyMatrix4(obj.matrixWorld);
      if (geom.index) geom = geom.toNonIndexed();

      const pos = geom.getAttribute('position').array;

      // Process each triangle
      for (let i = 0; i < pos.length; i += 9) {
        totalTris++;

        const v0 = new THREE.Vector3(pos[i], pos[i+1], pos[i+2]);
        const v1 = new THREE.Vector3(pos[i+3], pos[i+4], pos[i+5]);
        const v2 = new THREE.Vector3(pos[i+6], pos[i+7], pos[i+8]);

        // Check centroid against all cuts (same as shader logic)
        const centroid = v0.clone().add(v1).add(v2).multiplyScalar(1/3);

        let insideCut = false;
        for (const cut of cuts) {
          if (this.isPointInsideCut(centroid, cut.planes)) {
            insideCut = true;
            break;
          }
        }

        if (insideCut) {
          clippedTris++;
          continue;
        }

        keptPositions.push(pos[i], pos[i+1], pos[i+2],
                          pos[i+3], pos[i+4], pos[i+5],
                          pos[i+6], pos[i+7], pos[i+8]);
      }
    });

    console.log(`[ExportSTL] ${totalTris} triangles, ${clippedTris} clipped`);

    if (keptPositions.length === 0) {
      throw new Error('No geometry remaining after clipping');
    }

    // Build result geometry
    const resultGeom = new THREE.BufferGeometry();
    resultGeom.setAttribute('position', new THREE.Float32BufferAttribute(keptPositions, 3));
    resultGeom.computeVertexNormals();

    // Export binary STL
    const exporter = new THREE.STLExporter();
    const stlBuffer = exporter.parse(new THREE.Mesh(resultGeom), { binary: true });

    // Download
    const blob = new Blob([stlBuffer], { type: 'application/octet-stream' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `cut_mesh_${Date.now()}.stl`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    const sizeMB = (blob.size / 1024 / 1024).toFixed(2);
    console.log(`[ExportSTL] Exported ${sizeMB} MB, ${clippedTris} triangles removed`);

    // Ensure minimum display time so progress bar is visible
    const elapsed = Date.now() - startTime;
    const minDisplayTime = 500; // ms
    if (elapsed < minDisplayTime) {
      await new Promise(resolve => setTimeout(resolve, minDisplayTime - elapsed));
    }
  }

  /**
   * Export the mesh with erased triangles removed as STL
   */
  async exportErasedMeshAsSTL() {
    await new Promise(resolve => requestAnimationFrame(() => requestAnimationFrame(resolve)));
    const startTime = Date.now();

    const keptPositions = [];
    let totalTris = 0;

    this.engine.scene.traverse((obj) => {
      if (!obj.isMesh || !obj.visible || !obj.geometry) return;

      // Skip helpers/widgets
      let p = obj;
      while (p) {
        const n = p.name || '';
        if (n.startsWith('CuttingPlane_') || n.startsWith('SlicePlane_') ||
            n.includes('Helper') || n.includes('Control') || n.includes('Gizmo') ||
            n === 'EraserBrushCursor') return;
        p = p.parent;
      }
      if (obj.type.includes('Helper') || obj.type === 'LineSegments' ||
          obj.type === 'Line' || obj.type === 'Points') return;
      if (obj.material?.type === 'MeshBasicMaterial') return;

      let geom = obj.geometry.clone();
      obj.updateMatrixWorld();
      geom.applyMatrix4(obj.matrixWorld);
      if (geom.index) geom = geom.toNonIndexed();

      const pos = geom.getAttribute('position').array;
      for (let i = 0; i < pos.length; i += 9) {
        totalTris++;
        keptPositions.push(pos[i], pos[i+1], pos[i+2],
                           pos[i+3], pos[i+4], pos[i+5],
                           pos[i+6], pos[i+7], pos[i+8]);
      }
    });

    if (keptPositions.length === 0) {
      throw new Error('No geometry remaining after erasing');
    }

    const resultGeom = new THREE.BufferGeometry();
    resultGeom.setAttribute('position', new THREE.Float32BufferAttribute(keptPositions, 3));
    resultGeom.computeVertexNormals();

    const exporter = new THREE.STLExporter();
    const stlBuffer = exporter.parse(new THREE.Mesh(resultGeom), { binary: true });

    const blob = new Blob([stlBuffer], { type: 'application/octet-stream' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `erased_mesh_${Date.now()}.stl`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    const sizeMB = (blob.size / 1024 / 1024).toFixed(2);
    console.log(`[ExportSTL] Exported erased mesh: ${sizeMB} MB, ${totalTris} triangles`);

    const elapsed = Date.now() - startTime;
    const minDisplayTime = 500;
    if (elapsed < minDisplayTime) {
      await new Promise(resolve => setTimeout(resolve, minDisplayTime - elapsed));
    }
  }

  /**
   * Initialize slice plane with default values based on mesh center
   */
  initializeSlicePlaneDefaults() {
    // Only set defaults if origin is at (0,0,0) - meaning user hasn't set custom values
    const currentOx = parseFloat(document.getElementById('slicePlaneOriginX')?.value || 0);
    const currentOy = parseFloat(document.getElementById('slicePlaneOriginY')?.value || 0);
    const currentOz = parseFloat(document.getElementById('slicePlaneOriginZ')?.value || 0);

    // Check if we should reset to mesh center
    if (currentOx === 0 && currentOy === 0 && currentOz === 0) {
      // Calculate mesh center from bounds
      const bounds = this.currentData?.bounds || [-10, 10, -10, 10, -10, 10];
      const centerX = (bounds[0] + bounds[1]) / 2;
      const centerY = (bounds[2] + bounds[3]) / 2;
      const centerZ = (bounds[4] + bounds[5]) / 2;

      // Set origin to mesh center
      document.getElementById('slicePlaneOriginX').value = centerX.toFixed(3);
      document.getElementById('slicePlaneOriginY').value = centerY.toFixed(3);
      document.getElementById('slicePlaneOriginZ').value = centerZ.toFixed(3);

      console.log(`Initialized slice plane origin to mesh center: [${centerX.toFixed(3)}, ${centerY.toFixed(3)}, ${centerZ.toFixed(3)}]`);
    }

    // Auto-enable the interactive widget
    const slicePlaneShow = document.getElementById('slicePlaneShow');
    if (slicePlaneShow && !slicePlaneShow.checked) {
      slicePlaneShow.checked = true;
      slicePlaneShow.dispatchEvent(new Event('change'));
    }
  }

  /**
   * Set slice plane orientation (X, Y, or Z normal)
   * If widget is already enabled, updates the orientation. Otherwise enables it.
   */
  setSlicePlaneOrientation(nx, ny, nz) {
    // Set the normal values
    document.getElementById('slicePlaneNormalX').value = nx.toString();
    document.getElementById('slicePlaneNormalY').value = ny.toString();
    document.getElementById('slicePlaneNormalZ').value = nz.toString();

    // Check if widget is already enabled
    const sliceWidget = this.filters?.sliceWidget;
    const isEnabled = sliceWidget && sliceWidget.enabled;

    if (isEnabled) {
      // Widget is already enabled - just update the orientation
      console.log(`[Slice Plane] Updating orientation to [${nx}, ${ny}, ${nz}]`);

      // Sync the values and update the plane
      sliceWidget.syncHeatmapToSlice();
      sliceWidget.updatePlaneFromInputs();
    } else {
      // Widget not enabled - set origin to mesh center and enable
      console.log(`[Slice Plane] Enabling widget with orientation [${nx}, ${ny}, ${nz}]`);

      const bounds = this.calculateSceneBounds();
      if (bounds) {
        const center = new THREE.Vector3().addVectors(bounds.min, bounds.max).multiplyScalar(0.5);
        document.getElementById('slicePlaneOriginX').value = center.x.toFixed(3);
        document.getElementById('slicePlaneOriginY').value = center.y.toFixed(3);
        document.getElementById('slicePlaneOriginZ').value = center.z.toFixed(3);
      }

      // Enable the widget
      const slicePlaneShow = document.getElementById('slicePlaneShow');
      if (slicePlaneShow) {
        slicePlaneShow.checked = true;
        slicePlaneShow.dispatchEvent(new Event('change'));
      }
    }
  }

  /**
   * Start slice plane origin picking
   */
  startSlicePlaneOriginPick() {
    this.slicePlaneOriginPickActive = true;

    // Update UI feedback
    const btn = document.getElementById('btnPickSlicePlaneOrigin');
    const originalText = btn.textContent;
    btn.textContent = 'Click on mesh...';
    btn.style.backgroundColor = '#ff6b35';

    // Setup one-time click handler
    const viewport = document.getElementById('viewport');

    const clickHandler = (event) => {
      if (!this.slicePlaneOriginPickActive) return;

      // Calculate mouse position in normalized device coordinates
      const rect = viewport.getBoundingClientRect();
      const x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
      const y = -((event.clientY - rect.top) / rect.height) * 2 + 1;

      this.mouse.set(x, y);

      // Update raycaster
      this.raycaster.setFromCamera(this.mouse, this.engine.camera);

      // Get all visible raycastable objects (meshes, points, and groups)
      const meshObjects = this.getRaycastableObjects();

      // Find intersections
      const intersects = this.raycaster.intersectObjects(meshObjects, true);

      // Filter to only fully visible objects (object and all parents must be visible)
      const visibleIntersects = intersects.filter(hit => this.isObjectFullyVisible(hit.object));

      if (visibleIntersects.length > 0) {
        const point = visibleIntersects[0].point;

        // Update origin inputs
        document.getElementById('slicePlaneOriginX').value = point.x.toFixed(3);
        document.getElementById('slicePlaneOriginY').value = point.y.toFixed(3);
        document.getElementById('slicePlaneOriginZ').value = point.z.toFixed(3);

        // Reset button
        btn.textContent = originalText;
        btn.style.backgroundColor = '';

        // Update plane visualization if enabled
        if (document.getElementById('slicePlaneShow')?.checked) {
          const nx = parseFloat(document.getElementById('slicePlaneNormalX')?.value || 0);
          const ny = parseFloat(document.getElementById('slicePlaneNormalY')?.value || 0);
          const nz = parseFloat(document.getElementById('slicePlaneNormalZ')?.value || 1);
          this.visualizeSlicePlane([point.x, point.y, point.z], [nx, ny, nz]);
        }

        // Deactivate picking mode
        this.slicePlaneOriginPickActive = false;
        viewport.removeEventListener('click', clickHandler);
      }
    };

    viewport.addEventListener('click', clickHandler);
  }

  /**
   * Start line probe tool
   * @param {string} pointType - 'start' or 'end'
   */
  startLineProbe(pointType) {
    this.lineProbeMode = pointType;
    this.lineProbeActive = true;

    // Update UI feedback
    const btn = document.getElementById(`btnPickLine${pointType === 'start' ? 'Start' : 'End'}`);
    btn.textContent = 'Click on mesh...';
    btn.style.backgroundColor = '#ff6b35';

    // Enable raycaster picking
    this.setupLineProbeMouseHandler();

    console.log(`Line probe mode active: picking ${pointType} point`);
  }

  /**
   * Setup mouse handler for line probe picking
   */
  setupLineProbeMouseHandler() {
    const viewport = document.getElementById('viewport');

    const clickHandler = (event) => {
      if (!this.lineProbeActive) return;

      // Calculate mouse position in normalized device coordinates
      const rect = viewport.getBoundingClientRect();
      const x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
      const y = -((event.clientY - rect.top) / rect.height) * 2 + 1;

      this.mouse.set(x, y);

      // Update raycaster
      this.raycaster.setFromCamera(this.mouse, this.engine.camera);

      // Get all visible raycastable objects (meshes, points, and groups)
      const meshObjects = this.getRaycastableObjects();

      // Find intersections
      const intersects = this.raycaster.intersectObjects(meshObjects, true);

      // Filter to only fully visible objects (object and all parents must be visible)
      const visibleIntersects = intersects.filter(hit => this.isObjectFullyVisible(hit.object));

      if (visibleIntersects.length > 0) {
        const point = visibleIntersects[0].point;

        // Store the point
        if (this.lineProbeMode === 'start') {
          this.lineProbeStart = [point.x, point.y, point.z];
          document.getElementById('lineStartDisplay').textContent =
            `Start: [${point.x.toFixed(3)}, ${point.y.toFixed(3)}, ${point.z.toFixed(3)}]`;

          // Visualize start point immediately
          this.visualizeLineProbeStartPoint(this.lineProbeStart);

          // Reset button
          const btn = document.getElementById('btnPickLineStart');
          btn.textContent = 'Pick Start Point';
          btn.style.backgroundColor = '';
        } else {
          this.lineProbeEnd = [point.x, point.y, point.z];
          document.getElementById('lineEndDisplay').textContent =
            `End: [${point.x.toFixed(3)}, ${point.y.toFixed(3)}, ${point.z.toFixed(3)}]`;

          // Reset button
          const btn = document.getElementById('btnPickLineEnd');
          btn.textContent = 'Pick End Point';
          btn.style.backgroundColor = '';
        }

        // Visualize the line if both points are set
        if (this.lineProbeStart && this.lineProbeEnd) {
          // Clear temporary start point marker
          this.clearLineProbeTempMarker();
          this.visualizeLineProbe();
        }

        // Disable line probe mode
        this.lineProbeActive = false;
        this.lineProbeMode = null;

        // Remove event listener
        viewport.removeEventListener('click', clickHandler);

        console.log(`Line probe point set:`, point);
      }
    };

    viewport.addEventListener('click', clickHandler);
  }

  /**
   * Visualize the line probe in the 3D view (same style as streamlines seed line)
   */
  visualizeLineProbe() {
    // Remove existing line if present
    if (this.lineProbeHelper) {
      this.engine.scene.remove(this.lineProbeHelper);
      this.lineProbeHelper = null;

      // Remove animation callback
      if (this.lineProbeScaleCallback) {
        this.engine.removeAnimationCallback(this.lineProbeScaleCallback);
        this.lineProbeScaleCallback = null;
      }
    }

    if (!this.lineProbeStart || !this.lineProbeEnd) return;

    // Create a group for the line and indicators (same style as streamlines)
    const probeGroup = new THREE.Group();

    const startPoint = new THREE.Vector3(...this.lineProbeStart);
    const endPoint = new THREE.Vector3(...this.lineProbeEnd);

    // Calculate base size based on line length
    const lineLength = startPoint.distanceTo(endPoint);
    const baseSphereRadius = lineLength * 0.04;  // 4% of line length - visible but not huge

    // Create a simple line
    const lineGeometry = new THREE.BufferGeometry().setFromPoints([startPoint, endPoint]);
    const lineMaterial = new THREE.LineBasicMaterial({
      color: 0xffff00,  // Bright yellow
      linewidth: 2,
      depthTest: false,
      depthWrite: false
    });
    const line = new THREE.Line(lineGeometry, lineMaterial);
    line.renderOrder = 999;
    line.frustumCulled = false;
    probeGroup.add(line);

    // Add a green sphere at the start point (HUGE and BRIGHT)
    const startSphereGeometry = new THREE.SphereGeometry(baseSphereRadius, 32, 32);  // Higher quality sphere
    const startSphereMaterial = new THREE.MeshBasicMaterial({
      color: 0x00ff00,  // Bright green for start
      opacity: 1.0,
      transparent: false,  // Fully opaque
      depthTest: false,   // Always render on top
      depthWrite: false
    });
    const startSphere = new THREE.Mesh(startSphereGeometry, startSphereMaterial);
    startSphere.position.copy(startPoint);
    startSphere.renderOrder = 999;
    startSphere.frustumCulled = false;
    probeGroup.add(startSphere);

    // Add a red sphere at the end point (HUGE and BRIGHT)
    const endSphereGeometry = new THREE.SphereGeometry(baseSphereRadius, 32, 32);  // Higher quality sphere
    const endSphereMaterial = new THREE.MeshBasicMaterial({
      color: 0xff0000,  // Bright red for end
      opacity: 1.0,
      transparent: false,  // Fully opaque
      depthTest: false,   // Always render on top
      depthWrite: false
    });
    const endSphere = new THREE.Mesh(endSphereGeometry, endSphereMaterial);
    endSphere.position.copy(endPoint);
    endSphere.renderOrder = 999;
    endSphere.frustumCulled = false;
    probeGroup.add(endSphere);

    console.log('Spheres created - Start (green):', startSphere, 'End (red):', endSphere);

    // Store references for dynamic scaling
    probeGroup.userData.line = line;
    probeGroup.userData.startSphere = startSphere;
    probeGroup.userData.endSphere = endSphere;
    probeGroup.userData.baseSphereRadius = baseSphereRadius;
    probeGroup.userData.lineCenter = new THREE.Vector3().addVectors(startPoint, endPoint).multiplyScalar(0.5);

    // Add the group to scene
    this.lineProbeHelper = probeGroup;
    this.engine.scene.add(this.lineProbeHelper);

    console.log('Probe group added to scene:', {
      probeGroup,
      childrenCount: probeGroup.children.length,
      inScene: this.engine.scene.children.includes(probeGroup),
      visible: probeGroup.visible
    });

    // Register animation callback for dynamic scaling
    this.lineProbeScaleCallback = this.engine.addAnimationCallback(() => {
      this.updateLineProbeScale();
    });

    console.log('Line probe visualized with dynamic scaling - YELLOW line should be visible!');
  }

  /**
   * Clear line probe visualization and reset state
   */
  clearLineProbe() {
    // Remove line visualization from scene
    if (this.lineProbeHelper) {
      this.engine.scene.remove(this.lineProbeHelper);
      this.lineProbeHelper = null;
    }

    // Remove temporary marker
    this.clearLineProbeTempMarker();

    // Remove animation callback
    if (this.lineProbeScaleCallback) {
      this.engine.removeAnimationCallback(this.lineProbeScaleCallback);
      this.lineProbeScaleCallback = null;
    }

    // Clear stored points
    this.lineProbeStart = null;
    this.lineProbeEnd = null;

    // Clear UI display
    const lineStartDisplay = document.getElementById('lineStartDisplay');
    const lineEndDisplay = document.getElementById('lineEndDisplay');
    if (lineStartDisplay) {
      lineStartDisplay.textContent = 'Not set';
    }
    if (lineEndDisplay) {
      lineEndDisplay.textContent = 'Not set';
    }

    console.log('Line probe cleared');
  }

  /**
   * Visualize temporary line probe start point marker
   */
  visualizeLineProbeStartPoint(point) {
    this.visualizeTempStartMarker(point, 'lineProbe');
  }

  /**
   * Clear temporary line probe marker
   */
  clearLineProbeTempMarker() {
    this.clearTempMarker('lineProbe');
  }

  /**
   * Start probe point picking mode
   */
  startProbePointPicking() {
    console.log('startProbePointPicking() called');
    const viewport = document.getElementById('viewport');
    const btn = document.getElementById('btnAddProbePoint');

    console.log('Viewport:', viewport, 'Button:', btn);

    if (!viewport || !btn) {
      console.error('Missing viewport or button!');
      return;
    }

    // Set picking mode active
    btn.textContent = 'Click on mesh...';
    btn.style.backgroundColor = '#5cb85c';
    console.log('Button text updated, waiting for mesh click...');

    // Add click listener
    const clickHandler = (event) => {
      const rect = viewport.getBoundingClientRect();
      const mouse = new THREE.Vector2(
        ((event.clientX - rect.left) / rect.width) * 2 - 1,
        -((event.clientY - rect.top) / rect.height) * 2 + 1
      );

      this.raycaster.setFromCamera(mouse, this.engine.camera);

      // Get all mesh objects
      // Get all visible raycastable objects (meshes, points, and groups)
      const meshObjects = this.getRaycastableObjects();

      // Find intersections
      const intersects = this.raycaster.intersectObjects(meshObjects, true);

      // Filter to only fully visible objects (object and all parents must be visible)
      const visibleIntersects = intersects.filter(hit => this.isObjectFullyVisible(hit.object));

      if (visibleIntersects.length > 0) {
        const point = visibleIntersects[0].point;
        this.addProbePoint([point.x, point.y, point.z]);

        // Reset button
        btn.textContent = 'Add Point';
        btn.style.backgroundColor = '';

        // Remove event listener
        viewport.removeEventListener('click', clickHandler);
      }
    };

    viewport.addEventListener('click', clickHandler);
  }

  /**
   * Add a probe point to the list and visualize it
   * @param {Array} point - [x, y, z] coordinates
   * @param {boolean} skipSync - If true, don't trigger sync (used when applying received state)
   */
  addProbePoint(point, skipSync = false) {
    this.probePoints.push(point);
    this.visualizeProbePoint(point, this.probePoints.length - 1);
    this.updateProbePointsUI();
    console.log('Probe point added:', point);

    // Notify sync (unless applying received state)
    if (!skipSync && this.viewLockManager && !this.viewLockManager.isApplyingState) {
      this.viewLockManager.onVisualizationChange();
    }
  }

  /**
   * Visualize a single probe point with a sphere and numbered label
   */
  visualizeProbePoint(point, index) {
    if (!this.probePointMarkers) {
      this.probePointMarkers = [];
    }

    const pointVector = new THREE.Vector3(...point);
    const baseSphereRadius = 0.005;  // Base size for probe points - very small
    const pointNumber = index + 1;  // 1-based numbering

    // Create sphere
    const sphereGeometry = new THREE.SphereGeometry(baseSphereRadius, 16, 16);
    const sphereMaterial = new THREE.MeshBasicMaterial({
      color: 0x00ffff,  // Cyan color
      opacity: 1.0,
      transparent: false,
      depthTest: false,
      depthWrite: false
    });

    const sphere = new THREE.Mesh(sphereGeometry, sphereMaterial);
    sphere.position.copy(pointVector);
    sphere.renderOrder = 999;
    sphere.frustumCulled = false;

    // Create text label using canvas texture
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');
    canvas.width = 128;
    canvas.height = 128;

    // Draw circle background
    context.fillStyle = 'rgba(0, 255, 255, 0.8)';
    context.beginPath();
    context.arc(64, 64, 60, 0, Math.PI * 2);
    context.fill();

    // Draw text
    context.fillStyle = '#000000';
    context.font = 'bold 80px Arial';
    context.textAlign = 'center';
    context.textBaseline = 'middle';
    context.fillText(pointNumber.toString(), 64, 64);

    const texture = new THREE.CanvasTexture(canvas);
    const spriteMaterial = new THREE.SpriteMaterial({
      map: texture,
      depthTest: false,
      depthWrite: false
    });
    const sprite = new THREE.Sprite(spriteMaterial);
    sprite.position.copy(pointVector);
    sprite.position.y += baseSphereRadius * 3;  // Offset above sphere
    sprite.scale.set(0.1, 0.1, 1);
    sprite.renderOrder = 1000;

    // Group sphere and label together
    const group = new THREE.Group();
    group.add(sphere);
    group.add(sprite);
    group.userData.baseSphereRadius = baseSphereRadius;
    group.userData.pointPosition = pointVector;

    this.engine.scene.add(group);
    this.probePointMarkers.push(group);

    // Setup dynamic scaling if this is the first point
    if (this.probePointMarkers.length === 1 && !this.probePointsScaleCallback) {
      this.probePointsScaleCallback = this.engine.addAnimationCallback(() => {
        this.updateProbePointsScale();
      });
    }
  }

  /**
   * Update the probe points UI display
   */
  updateProbePointsUI() {
    const countDisplay = document.getElementById('probePointsCount');
    const listDisplay = document.getElementById('probePointsList');

    if (countDisplay) {
      countDisplay.textContent = `Points: ${this.probePoints.length}`;
    }

    if (listDisplay) {
      listDisplay.innerHTML = '';
      this.probePoints.forEach((pt, index) => {
        const pointDiv = document.createElement('div');
        pointDiv.style.cssText = 'font-size: 10px; padding: 2px 0; color: #00ffff;';
        pointDiv.textContent = `${index + 1}: [${pt[0].toFixed(2)}, ${pt[1].toFixed(2)}, ${pt[2].toFixed(2)}]`;
        listDisplay.appendChild(pointDiv);
      });
    }
  }

  /**
   * Clear all probe points
   */
  clearProbePoints() {
    // Remove all markers from scene
    if (this.probePointMarkers) {
      this.probePointMarkers.forEach(marker => {
        this.engine.scene.remove(marker);
      });
      this.probePointMarkers = [];
    }

    // Remove animation callback
    if (this.probePointsScaleCallback) {
      this.engine.removeAnimationCallback(this.probePointsScaleCallback);
      this.probePointsScaleCallback = null;
    }

    // Clear points array
    this.probePoints = [];

    // Update UI
    this.updateProbePointsUI();

    console.log('Probe points cleared');
  }

  /**
   * Visualize slice plane in 3D viewport
   */
  visualizeSlicePlane(origin, normal, customWidth = null, customHeight = null) {
    // Remove existing slice plane if any
    this.clearSlicePlane();

    // Calculate plane size based on mesh bounds or use custom dimensions
    const bounds = this.currentData?.bounds || [-10, 10, -10, 10, -10, 10];
    const sizeX = bounds[1] - bounds[0];
    const sizeY = bounds[3] - bounds[2];
    const sizeZ = bounds[5] - bounds[4];
    const maxSize = Math.max(sizeX, sizeY, sizeZ);

    // Read custom dimensions from UI if not provided as parameters
    const widthInput = parseFloat(document.getElementById('slicePlaneWidth')?.value || 0);
    const heightInput = parseFloat(document.getElementById('slicePlaneHeight')?.value || 0);

    // Use custom dimensions if provided (value > 0), otherwise auto from mesh
    const planeWidth = customWidth || (widthInput > 0 ? widthInput : maxSize);
    const planeHeight = customHeight || (heightInput > 0 ? heightInput : maxSize);

    // Normalize normal vector
    const normalVec = new THREE.Vector3(normal[0], normal[1], normal[2]);
    normalVec.normalize();

    // Create plane geometry with custom dimensions
    const planeGeometry = new THREE.PlaneGeometry(planeWidth, planeHeight);

    // Create semi-transparent material
    const planeMaterial = new THREE.MeshBasicMaterial({
      color: 0x00ffff,
      transparent: true,
      opacity: 0.15,
      side: THREE.DoubleSide,
      depthWrite: false
    });

    const planeMesh = new THREE.Mesh(planeGeometry, planeMaterial);

    // Position the plane
    planeMesh.position.set(origin[0], origin[1], origin[2]);

    // Orient the plane to face the normal direction
    // Default plane normal is (0, 0, 1), rotate to match desired normal
    const defaultNormal = new THREE.Vector3(0, 0, 1);
    const quaternion = new THREE.Quaternion();
    quaternion.setFromUnitVectors(defaultNormal, normalVec);
    planeMesh.quaternion.copy(quaternion);

    // Add edge outline for better visibility
    const edgesGeometry = new THREE.EdgesGeometry(planeGeometry);
    const edgesMaterial = new THREE.LineBasicMaterial({ color: 0x00ffff, linewidth: 2 });
    const edges = new THREE.LineSegments(edgesGeometry, edgesMaterial);
    planeMesh.add(edges);

    // Calculate screen-space sizes based on viewport and camera distance
    // Get camera distance to plane origin
    const planeOrigin = new THREE.Vector3(origin[0], origin[1], origin[2]);
    const cameraDistance = this.engine.camera.position.distanceTo(planeOrigin);

    // Calculate size that will appear as approximately 10 pixels on screen
    // Formula: screenSize = (pixels / viewportHeight) * distance * 2 * tan(fov/2)
    const viewport = document.getElementById('viewport');
    const viewportHeight = viewport ? viewport.clientHeight : 800;
    const fov = this.engine.camera.fov * Math.PI / 180; // Convert to radians
    const targetPixels = 10; // Desired size in pixels

    const screenSpaceSize = (targetPixels / viewportHeight) * cameraDistance * 2 * Math.tan(fov / 2);

    // Add origin marker (sphere) - sized for screen space
    const markerSize = screenSpaceSize;
    const originGeometry = new THREE.SphereGeometry(markerSize, 16, 16);
    const originMaterial = new THREE.MeshBasicMaterial({ color: 0xff0000 });
    const originMarker = new THREE.Mesh(originGeometry, originMaterial);
    originMarker.position.set(0, 0, 0); // At plane origin
    planeMesh.add(originMarker);

    // Add normal direction arrow - approximately 50 pixels long
    const arrowTargetPixels = 50;
    const arrowLength = (arrowTargetPixels / viewportHeight) * cameraDistance * 2 * Math.tan(fov / 2);
    const arrowHelper = new THREE.ArrowHelper(
      normalVec,
      new THREE.Vector3(0, 0, 0),
      arrowLength,
      0x00ff00,
      arrowLength * 0.3,
      arrowLength * 0.2
    );
    planeMesh.add(arrowHelper);

    // Store reference and add to scene
    this.slicePlaneMesh = planeMesh;
    this.engine.scene.add(planeMesh);

    console.log('Slice plane visualized at:', origin, 'with normal:', normal);
  }

  /**
   * Clear slice plane visualization
   */
  clearSlicePlane() {
    if (this.slicePlaneMesh) {
      this.engine.scene.remove(this.slicePlaneMesh);
      this.slicePlaneMesh = null;
      console.log('Slice plane cleared');
    }
  }

  /**
   * Visualize measurement line with distance label
   */
  visualizeMeasurement(startPoint, endPoint, providedId = null) {
    // Use provided ID or create unique ID for this measurement
    const measurementId = providedId !== null ? providedId : ++this.measurementIdCounter;
    // Update counter if provided ID is higher (to avoid conflicts)
    if (providedId !== null && providedId > this.measurementIdCounter) {
      this.measurementIdCounter = providedId;
    }

    // Color palette for measurements (distinct, vibrant colors)
    const colors = [
      0xFFFF00, // Yellow
      0x00FFFF, // Cyan
      0xFF00FF, // Magenta
      0x00FF00, // Green
      0xFF8800, // Orange
      0x8800FF, // Purple
      0xFF0088, // Pink
      0x00FF88, // Mint
      0x88FF00, // Lime
      0x0088FF, // Light Blue
      0xFF4444, // Red
      0x44FF44, // Bright Green
    ];

    // Get color for this measurement (cycle through palette)
    const colorHex = colors[(measurementId - 1) % colors.length];
    const colorString = '#' + colorHex.toString(16).padStart(6, '0');

    // Create line geometry
    const points = [
      new THREE.Vector3(startPoint[0], startPoint[1], startPoint[2]),
      new THREE.Vector3(endPoint[0], endPoint[1], endPoint[2])
    ];
    const lineGeometry = new THREE.BufferGeometry().setFromPoints(points);
    const lineMaterial = new THREE.LineBasicMaterial({
      color: colorHex,
      linewidth: 3,
      depthTest: true
    });
    const line = new THREE.Line(lineGeometry, lineMaterial);

    // Calculate distance
    const distance = Math.sqrt(
      Math.pow(endPoint[0] - startPoint[0], 2) +
      Math.pow(endPoint[1] - startPoint[1], 2) +
      Math.pow(endPoint[2] - startPoint[2], 2)
    );

    // Calculate midpoint for label placement
    const midX = (startPoint[0] + endPoint[0]) / 2;
    const midY = (startPoint[1] + endPoint[1]) / 2;
    const midZ = (startPoint[2] + endPoint[2]) / 2;

    // Create text sprite for distance label (with unit conversion)
    const convertedDistance = this.convertDistance(distance);
    const unit = this.getDistanceUnit();
    const decimals = this.measurementUnit === 'um' ? 1 : (this.measurementUnit === 'mm' ? 2 : (this.measurementUnit === 'km' ? 6 : 3));
    const distanceText = `${measurementId}: ${convertedDistance.toFixed(decimals)} ${unit}`;
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');
    canvas.width = 120;
    canvas.height = 28;

    // Rounded rectangle background
    const padding = 4;
    const borderRadius = 3;

    context.fillStyle = 'rgba(0, 0, 0, 0.75)';
    context.beginPath();
    context.roundRect(padding, padding, canvas.width - padding * 2, canvas.height - padding * 2, borderRadius);
    context.fill();

    // Subtle border with measurement color
    context.strokeStyle = colorString;
    context.lineWidth = 1;
    context.beginPath();
    context.roundRect(padding, padding, canvas.width - padding * 2, canvas.height - padding * 2, borderRadius);
    context.stroke();

    // Text with nice font and measurement color
    context.font = '500 11px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Arial, sans-serif';
    context.fillStyle = colorString;
    context.textAlign = 'center';
    context.textBaseline = 'middle';
    context.fillText(distanceText, canvas.width / 2, canvas.height / 2);

    const texture = new THREE.CanvasTexture(canvas);
    const spriteMaterial = new THREE.SpriteMaterial({
      map: texture,
      depthTest: false,
      depthWrite: false
    });
    const sprite = new THREE.Sprite(spriteMaterial);
    sprite.position.set(midX, midY, midZ);

    // Very small, compact scaling
    const scaleFactor = Math.max(0.04, Math.min(0.12, distance * 0.015));
    sprite.scale.set(scaleFactor, scaleFactor * 0.23, 1);

    // Add start and end point markers
    const markerGeometry = new THREE.SphereGeometry(distance * 0.01, 16, 16);
    const markerMaterial = new THREE.MeshBasicMaterial({ color: colorHex });

    const startMarker = new THREE.Mesh(markerGeometry, markerMaterial);
    startMarker.position.set(startPoint[0], startPoint[1], startPoint[2]);

    const endMarker = new THREE.Mesh(markerGeometry, markerMaterial);
    endMarker.position.set(endPoint[0], endPoint[1], endPoint[2]);

    // Create group to hold all measurement elements
    const measurementGroup = new THREE.Group();
    measurementGroup.add(line);
    measurementGroup.add(sprite);
    measurementGroup.add(startMarker);
    measurementGroup.add(endMarker);

    // Store measurement data
    const measurement = {
      id: measurementId,
      type: 'distance',
      startPoint: startPoint,
      endPoint: endPoint,
      distance: distance,
      visualGroup: measurementGroup,
      color: colorString
    };
    this.measurements.push(measurement);

    // Add to scene
    this.engine.scene.add(measurementGroup);

    console.log('Measurement', measurementId, 'visualized:', distance.toFixed(4), 'm');

    return distance;
  }

  /**
   * Clear all measurements
   * @param {boolean} resetCounter - If true, reset the ID counter (used when syncing from controller)
   */
  clearAllMeasurements(resetCounter = false) {
    this.measurements.forEach(m => {
      if (m.visualGroup) {
        this.engine.scene.remove(m.visualGroup);
      }
    });
    this.measurements = [];
    if (resetCounter) {
      this.measurementIdCounter = 0;
    }
    console.log('All measurements cleared', resetCounter ? '(counter reset)' : '');
  }

  /**
   * Clear a specific measurement by ID
   */
  clearMeasurement(measurementId) {
    const index = this.measurements.findIndex(m => m.id === measurementId);
    if (index !== -1) {
      const measurement = this.measurements[index];
      if (measurement.visualGroup) {
        this.engine.scene.remove(measurement.visualGroup);
      }
      this.measurements.splice(index, 1);
      console.log('Measurement', measurementId, 'cleared');
      this.updateMeasurementsList();
    }
  }

  // ============ ANGLE MEASUREMENT METHODS ============

  /**
   * Remove a distance measurement point
   */
  removeMeasurementPoint(pointType) {
    if (pointType === 'start') {
      this.measurementStart = null;
      document.getElementById('measureStartDisplay').textContent = 'Start: Not set';
      document.getElementById('btnRemoveMeasureStart').style.display = 'none';
      this.clearMeasurementTempMarker();
    } else if (pointType === 'end') {
      this.measurementEnd = null;
      document.getElementById('measureEndDisplay').textContent = 'End: Not set';
      document.getElementById('btnRemoveMeasureEnd').style.display = 'none';
    }
    Utils.notify(`${pointType === 'start' ? 'Start' : 'End'} point removed`, 'info');
  }

  /**
   * Remove an angle measurement point
   */
  removeAnglePoint(pointType) {
    if (pointType === 'point1') {
      // Find and remove the temp marker for point1
      if (this.angleTempMarkers.length > 0) {
        const markerData = this.angleTempMarkers.shift(); // Remove first marker
        if (markerData.marker) {
          this.engine.scene.remove(markerData.marker);
        }
        if (markerData.callback) {
          this.engine.removeAnimationCallback(markerData.callback);
        }
      }
      this.anglePoint1 = null;
      document.getElementById('anglePoint1Display').textContent = 'Point 1: Not set';
      document.getElementById('btnRemoveAnglePoint1').style.display = 'none';
    } else if (pointType === 'vertex') {
      // Find and remove the temp marker for vertex (second marker)
      if (this.angleTempMarkers.length > 1) {
        const markerData = this.angleTempMarkers.splice(1, 1)[0]; // Remove second marker
        if (markerData.marker) {
          this.engine.scene.remove(markerData.marker);
        }
        if (markerData.callback) {
          this.engine.removeAnimationCallback(markerData.callback);
        }
      }
      this.angleVertex = null;
      document.getElementById('angleVertexDisplay').textContent = 'Vertex: Not set';
      document.getElementById('btnRemoveAngleVertex').style.display = 'none';
    } else if (pointType === 'point2') {
      // Find and remove the temp marker for point2 (third marker)
      if (this.angleTempMarkers.length > 2) {
        const markerData = this.angleTempMarkers.pop(); // Remove last marker
        if (markerData.marker) {
          this.engine.scene.remove(markerData.marker);
        }
        if (markerData.callback) {
          this.engine.removeAnimationCallback(markerData.callback);
        }
      }
      this.anglePoint2 = null;
      document.getElementById('anglePoint2Display').textContent = 'Point 2: Not set';
      document.getElementById('btnRemoveAnglePoint2').style.display = 'none';
    }
    Utils.notify(`Angle ${pointType} removed`, 'info');
  }

  /**
   * Start angle measurement point picking
   */
  startAngleMeasurementPicking(pointType) {
    this.angleMode = pointType;
    this.measurementPickingActive = true;

    // Update UI feedback
    const btnMap = {
      'point1': 'btnPickAnglePoint1',
      'vertex': 'btnPickAngleVertex',
      'point2': 'btnPickAnglePoint2'
    };

    const btn = document.getElementById(btnMap[pointType]);
    btn.textContent = 'Click on mesh...';
    btn.style.backgroundColor = '#ff6b35';

    // Enable raycaster picking (reuse existing handler)
    this.setupAngleMeasurementMouseHandler();

    console.log(`Angle measurement mode: picking ${pointType}`);
  }

  /**
   * Setup mouse handler for angle measurement point picking
   */
  setupAngleMeasurementMouseHandler() {
    const viewport = document.getElementById('viewport');

    if (this.angleMeasurementClickHandler) {
      return; // Already set up
    }

    const clickHandler = (event) => {
      if (!this.measurementPickingActive || !this.angleMode) return;

      const rect = viewport.getBoundingClientRect();
      const x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
      const y = -((event.clientY - rect.top) / rect.height) * 2 + 1;

      this.mouse.set(x, y);
      this.raycaster.setFromCamera(this.mouse, this.engine.camera);

      // Get all visible raycastable objects (meshes, points, and groups)
      const meshObjects = this.getRaycastableObjects();

      const intersects = this.raycaster.intersectObjects(meshObjects, true);

      // Filter to only fully visible objects
      const visibleIntersects = intersects.filter(hit => this.isObjectFullyVisible(hit.object));

      if (visibleIntersects.length > 0) {
        const point = visibleIntersects[0].point;

        if (this.angleMode === 'point1') {
          this.anglePoint1 = [point.x, point.y, point.z];
          document.getElementById('anglePoint1Display').textContent =
            `Point 1: [${point.x.toFixed(3)}, ${point.y.toFixed(3)}, ${point.z.toFixed(3)}]`;
          this.addPersistentTempMarker(this.anglePoint1, this.angleTempMarkers);

          const btn = document.getElementById('btnPickAnglePoint1');
          btn.textContent = 'Pick Point 1';
          btn.style.backgroundColor = '';

          // Show remove button
          document.getElementById('btnRemoveAnglePoint1').style.display = 'inline-block';

        } else if (this.angleMode === 'vertex') {
          this.angleVertex = [point.x, point.y, point.z];
          document.getElementById('angleVertexDisplay').textContent =
            `Vertex: [${point.x.toFixed(3)}, ${point.y.toFixed(3)}, ${point.z.toFixed(3)}]`;
          this.addPersistentTempMarker(this.angleVertex, this.angleTempMarkers);

          const btn = document.getElementById('btnPickAngleVertex');
          btn.textContent = 'Pick Vertex';
          btn.style.backgroundColor = '';

          // Show remove button
          document.getElementById('btnRemoveAngleVertex').style.display = 'inline-block';

        } else if (this.angleMode === 'point2') {
          this.anglePoint2 = [point.x, point.y, point.z];
          document.getElementById('anglePoint2Display').textContent =
            `Point 2: [${point.x.toFixed(3)}, ${point.y.toFixed(3)}, ${point.z.toFixed(3)}]`;
          this.addPersistentTempMarker(this.anglePoint2, this.angleTempMarkers);

          const btn = document.getElementById('btnPickAnglePoint2');
          btn.textContent = 'Pick Point 2';
          btn.style.backgroundColor = '';

          // Show remove button
          document.getElementById('btnRemoveAnglePoint2').style.display = 'inline-block';
        }

        this.measurementPickingActive = false;
        this.angleMode = null;
      }
    };

    viewport.addEventListener('click', clickHandler);
    this.angleMeasurementClickHandler = true;
  }

  /**
   * Create angle measurement from picked points
   */
  createAngleMeasurement() {
    if (!this.anglePoint1 || !this.angleVertex || !this.anglePoint2) {
      Utils.notify('Please pick all 3 points for angle measurement', 'warning');
      return;
    }

    // Calculate angle
    const p0 = this.anglePoint1;
    const p1 = this.angleVertex; // vertex
    const p2 = this.anglePoint2;

    const v1 = [p0[0] - p1[0], p0[1] - p1[1], p0[2] - p1[2]];
    const v2 = [p2[0] - p1[0], p2[1] - p1[1], p2[2] - p1[2]];

    const dot = v1[0] * v2[0] + v1[1] * v2[1] + v1[2] * v2[2];
    const mag1 = Math.sqrt(v1[0] * v1[0] + v1[1] * v1[1] + v1[2] * v1[2]);
    const mag2 = Math.sqrt(v2[0] * v2[0] + v2[1] * v2[1] + v2[2] * v2[2]);

    const angleRad = Math.acos(dot / (mag1 * mag2));
    const angleDeg = angleRad * (180 / Math.PI);

    // Create measurement ID
    const measurementId = ++this.measurementIdCounter;

    // Visualize the angle
    this.visualizeAngleMeasurement(p0, p1, p2, angleDeg, measurementId);

    // Clear temp markers now that final measurement is created
    this.clearPersistentTempMarkers(this.angleTempMarkers);

    // Reset points
    this.anglePoint1 = null;
    this.angleVertex = null;
    this.anglePoint2 = null;
    document.getElementById('anglePoint1Display').textContent = 'Point 1: Not set';
    document.getElementById('angleVertexDisplay').textContent = 'Vertex: Not set';
    document.getElementById('anglePoint2Display').textContent = 'Point 2: Not set';

    Utils.notify(`Angle: ${angleDeg.toFixed(2)}°`, 'success');
  }

  /**
   * Visualize angle measurement
   */
  visualizeAngleMeasurement(p0, p1, p2, angleDeg, measurementId) {
    const measurementGroup = new THREE.Group();
    measurementGroup.name = `angle-measurement-${measurementId}`;

    // Get color for this measurement
    const colors = [
      0xFFFF00, 0x00FFFF, 0xFF00FF, 0x00FF00, 0xFF8800, 0x8800FF,
      0xFF0088, 0x00FF88, 0x88FF00, 0x0088FF, 0xFF4444, 0x44FF44,
    ];
    const colorHex = colors[(measurementId - 1) % colors.length];
    const colorString = '#' + colorHex.toString(16).padStart(6, '0');

    // Create lines from vertex to both points
    const p0Vec = new THREE.Vector3(...p0);
    const p1Vec = new THREE.Vector3(...p1);
    const p2Vec = new THREE.Vector3(...p2);

    const lineGeom1 = new THREE.BufferGeometry().setFromPoints([p1Vec, p0Vec]);
    const lineGeom2 = new THREE.BufferGeometry().setFromPoints([p1Vec, p2Vec]);
    const lineMaterial = new THREE.LineBasicMaterial({ color: colorHex, linewidth: 2 });

    measurementGroup.add(new THREE.Line(lineGeom1, lineMaterial));
    measurementGroup.add(new THREE.Line(lineGeom2, lineMaterial));

    // Calculate average line length for sphere sizing
    const length1 = p1Vec.distanceTo(p0Vec);
    const length2 = p1Vec.distanceTo(p2Vec);
    const avgLength = (length1 + length2) / 2;

    // Add spheres at points (size proportional to line length, like distance measurement)
    const sphereGeometry = new THREE.SphereGeometry(avgLength * 0.01, 16, 16);
    const sphereMaterial = new THREE.MeshBasicMaterial({ color: colorHex });

    const sphere0 = new THREE.Mesh(sphereGeometry, sphereMaterial);
    const sphere1 = new THREE.Mesh(sphereGeometry, sphereMaterial);
    const sphere2 = new THREE.Mesh(sphereGeometry, sphereMaterial);

    sphere0.position.copy(p0Vec);
    sphere1.position.copy(p1Vec);
    sphere2.position.copy(p2Vec);

    measurementGroup.add(sphere0);
    measurementGroup.add(sphere1);
    measurementGroup.add(sphere2);

    // Create text label for angle
    const angleText = `${measurementId}: ${angleDeg.toFixed(2)}°`;
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');
    canvas.width = 120;
    canvas.height = 28;

    // Rounded rectangle background (matching distance measurement style)
    const padding = 4;
    const borderRadius = 3;

    context.fillStyle = 'rgba(0, 0, 0, 0.75)';
    context.beginPath();
    context.roundRect(padding, padding, canvas.width - padding * 2, canvas.height - padding * 2, borderRadius);
    context.fill();

    // Subtle border with measurement color
    context.strokeStyle = colorString;
    context.lineWidth = 1;
    context.beginPath();
    context.roundRect(padding, padding, canvas.width - padding * 2, canvas.height - padding * 2, borderRadius);
    context.stroke();

    // Text with nice font and measurement color
    context.font = '500 11px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Arial, sans-serif';
    context.fillStyle = colorString;
    context.textAlign = 'center';
    context.textBaseline = 'middle';
    context.fillText(angleText, canvas.width / 2, canvas.height / 2);

    const texture = new THREE.CanvasTexture(canvas);
    const spriteMaterial = new THREE.SpriteMaterial({
      map: texture,
      depthTest: false,
      depthWrite: false
    });
    const sprite = new THREE.Sprite(spriteMaterial);

    // Position label at vertex (offset proportional to measurement size)
    sprite.position.copy(p1Vec);
    sprite.position.y += avgLength * 0.15;

    // Match distance measurement scaling
    const scaleFactor = Math.max(0.04, Math.min(0.12, avgLength * 0.015));
    sprite.scale.set(scaleFactor, scaleFactor * 0.23, 1);

    measurementGroup.add(sprite);

    // Add to scene
    this.engine.scene.add(measurementGroup);

    // Store measurement
    const measurement = {
      id: measurementId,
      type: 'angle',
      point1: p0,
      vertex: p1,
      point2: p2,
      angle: angleDeg,
      visualGroup: measurementGroup,
      color: colorString
    };
    this.measurements.push(measurement);
    this.updateMeasurementsList();
  }

  // ============ AREA MEASUREMENT METHODS ============

  /**
   * Start area point picking
   */
  startAreaPointPicking() {
    this.areaPickingActive = true;
    const btn = document.getElementById('btnAddAreaPoint');
    btn.textContent = 'Click on mesh...';
    btn.style.backgroundColor = '#ff6b35';

    this.setupAreaMeasurementMouseHandler();
  }

  /**
   * Setup mouse handler for area measurement point picking
   */
  setupAreaMeasurementMouseHandler() {
    const viewport = document.getElementById('viewport');

    if (this.areaMeasurementClickHandler) {
      return; // Already set up
    }

    const clickHandler = (event) => {
      if (!this.areaPickingActive) return;

      const rect = viewport.getBoundingClientRect();
      const x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
      const y = -((event.clientY - rect.top) / rect.height) * 2 + 1;

      this.mouse.set(x, y);
      this.raycaster.setFromCamera(this.mouse, this.engine.camera);

      // Get all visible raycastable objects (meshes, points, and groups)
      const meshObjects = this.getRaycastableObjects();

      const intersects = this.raycaster.intersectObjects(meshObjects, true);

      // Filter to only fully visible objects
      const visibleIntersects = intersects.filter(hit => this.isObjectFullyVisible(hit.object));

      if (visibleIntersects.length > 0) {
        const point = visibleIntersects[0].point;
        const pointArray = [point.x, point.y, point.z];
        this.areaPoints.push(pointArray);
        this.updateAreaPointsList();

        // Add persistent temp marker (stays visible until measurement is created)
        this.addPersistentTempMarker(pointArray, this.areaTempMarkers);

        const btn = document.getElementById('btnAddAreaPoint');
        btn.textContent = 'Add Point';
        btn.style.backgroundColor = '';
        this.areaPickingActive = false;

        Utils.notify(`Area point ${this.areaPoints.length} added`, 'success');
      }
    };

    viewport.addEventListener('click', clickHandler);
    this.areaMeasurementClickHandler = true;
  }

  /**
   * Update area points list UI
   */
  updateAreaPointsList() {
    const listElement = document.getElementById('areaPointsList');
    if (!listElement) return;

    if (this.areaPoints.length === 0) {
      listElement.innerHTML = '<div>No points added (need 3+)</div>';
      return;
    }

    let html = '';
    this.areaPoints.forEach((point, index) => {
      html += `
        <div style="display: flex; justify-content: space-between; padding: 2px; font-size: 10px;">
          <div>P${index + 1}: [${point[0].toFixed(2)}, ${point[1].toFixed(2)}, ${point[2].toFixed(2)}]</div>
          <button class="btn-small" style="padding: 1px 4px; font-size: 9px;" onclick="window.viewer.areaPoints.splice(${index}, 1); window.viewer.updateAreaPointsList();">×</button>
        </div>
      `;
    });

    listElement.innerHTML = html;
  }

  /**
   * Create area measurement from picked points
   */
  createAreaMeasurement() {
    if (this.areaPoints.length < 3) {
      Utils.notify('Please pick at least 3 points for area measurement', 'warning');
      return;
    }

    // Calculate area using shoelace formula (projected to best-fit plane)
    const area = this.calculatePolygonArea(this.areaPoints);

    // Create measurement ID
    const measurementId = ++this.measurementIdCounter;

    // Visualize the area
    this.visualizeAreaMeasurement(this.areaPoints, area, measurementId);

    // Clear temp markers now that final measurement is created
    this.clearPersistentTempMarkers(this.areaTempMarkers);

    // Reset points
    this.areaPoints = [];
    this.updateAreaPointsList();

    Utils.notify(`Area: ${area.toFixed(4)} m²`, 'success');
  }

  /**
   * Calculate polygon area (3D polygon projected to best-fit plane)
   */
  calculatePolygonArea(points) {
    if (points.length < 3) return 0;

    // Simple approach: use cross products to get total area
    // This works for convex polygons in 3D
    let totalArea = 0;
    const p0 = points[0];

    for (let i = 1; i < points.length - 1; i++) {
      const p1 = points[i];
      const p2 = points[i + 1];

      // Vectors from p0 to p1 and p0 to p2
      const v1 = [p1[0] - p0[0], p1[1] - p0[1], p1[2] - p0[2]];
      const v2 = [p2[0] - p0[0], p2[1] - p0[1], p2[2] - p0[2]];

      // Cross product
      const cross = [
        v1[1] * v2[2] - v1[2] * v2[1],
        v1[2] * v2[0] - v1[0] * v2[2],
        v1[0] * v2[1] - v1[1] * v2[0]
      ];

      // Magnitude of cross product / 2 = triangle area
      const triangleArea = Math.sqrt(cross[0] * cross[0] + cross[1] * cross[1] + cross[2] * cross[2]) / 2;
      totalArea += triangleArea;
    }

    return totalArea;
  }

  /**
   * Visualize area measurement
   */
  visualizeAreaMeasurement(points, area, measurementId) {
    const measurementGroup = new THREE.Group();
    measurementGroup.name = `area-measurement-${measurementId}`;

    // Get color
    const colors = [
      0xFFFF00, 0x00FFFF, 0xFF00FF, 0x00FF00, 0xFF8800, 0x8800FF,
      0xFF0088, 0x00FF88, 0x88FF00, 0x0088FF, 0xFF4444, 0x44FF44,
    ];
    const colorHex = colors[(measurementId - 1) % colors.length];
    const colorString = '#' + colorHex.toString(16).padStart(6, '0');

    // Create polygon outline
    const polyPoints = points.map(p => new THREE.Vector3(...p));
    polyPoints.push(new THREE.Vector3(...points[0])); // close the loop

    const geometry = new THREE.BufferGeometry().setFromPoints(polyPoints);
    const lineMaterial = new THREE.LineBasicMaterial({ color: colorHex, linewidth: 2 });
    measurementGroup.add(new THREE.Line(geometry, lineMaterial));

    // Calculate average edge length for sphere sizing
    let totalLength = 0;
    for (let i = 0; i < points.length; i++) {
      const p1 = new THREE.Vector3(...points[i]);
      const p2 = new THREE.Vector3(...points[(i + 1) % points.length]);
      totalLength += p1.distanceTo(p2);
    }
    const avgEdgeLength = totalLength / points.length;

    // Add spheres at vertices (size proportional to edge length)
    const sphereGeometry = new THREE.SphereGeometry(avgEdgeLength * 0.01, 16, 16);
    const sphereMaterial = new THREE.MeshBasicMaterial({ color: colorHex });

    points.forEach(p => {
      const sphere = new THREE.Mesh(sphereGeometry, sphereMaterial);
      sphere.position.set(p[0], p[1], p[2]);
      measurementGroup.add(sphere);
    });

    // Create text label (with unit conversion)
    const convertedArea = this.convertArea(area);
    const areaUnit = this.getAreaUnit();
    const decimals = this.measurementUnit === 'um' ? 0 : (this.measurementUnit === 'mm' ? 1 : (this.measurementUnit === 'km' ? 9 : 4));
    const areaText = `${measurementId}: ${convertedArea.toFixed(decimals)} ${areaUnit}`;
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');
    canvas.width = 120;
    canvas.height = 28;

    // Rounded rectangle background (matching distance measurement style)
    const padding = 4;
    const borderRadius = 3;

    context.fillStyle = 'rgba(0, 0, 0, 0.75)';
    context.beginPath();
    context.roundRect(padding, padding, canvas.width - padding * 2, canvas.height - padding * 2, borderRadius);
    context.fill();

    // Subtle border with measurement color
    context.strokeStyle = colorString;
    context.lineWidth = 1;
    context.beginPath();
    context.roundRect(padding, padding, canvas.width - padding * 2, canvas.height - padding * 2, borderRadius);
    context.stroke();

    // Text with nice font and measurement color
    context.font = '500 11px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Arial, sans-serif';
    context.fillStyle = colorString;
    context.textAlign = 'center';
    context.textBaseline = 'middle';
    context.fillText(areaText, canvas.width / 2, canvas.height / 2);

    const texture = new THREE.CanvasTexture(canvas);
    const spriteMaterial = new THREE.SpriteMaterial({
      map: texture,
      depthTest: false,
      depthWrite: false
    });
    const sprite = new THREE.Sprite(spriteMaterial);

    // Position label at centroid (offset proportional to measurement size)
    const centroid = new THREE.Vector3();
    points.forEach(p => centroid.add(new THREE.Vector3(...p)));
    centroid.divideScalar(points.length);

    sprite.position.copy(centroid);
    sprite.position.y += avgEdgeLength * 0.15;

    // Match distance measurement scaling
    const scaleFactor = Math.max(0.04, Math.min(0.12, avgEdgeLength * 0.015));
    sprite.scale.set(scaleFactor, scaleFactor * 0.23, 1);

    measurementGroup.add(sprite);

    // Add to scene
    this.engine.scene.add(measurementGroup);

    // Store measurement
    const measurement = {
      id: measurementId,
      type: 'area',
      points: points,
      area: area,
      visualGroup: measurementGroup,
      color: colorString
    };
    this.measurements.push(measurement);
    this.updateMeasurementsList();
  }

  // ============ VOLUME MEASUREMENT METHODS ============

  /**
   * Start volume point picking
   */
  startVolumePointPicking() {
    this.volumePickingActive = true;
    const btn = document.getElementById('btnAddVolumePoint');
    btn.textContent = 'Click on mesh...';
    btn.style.backgroundColor = '#ff6b35';

    this.setupVolumeMeasurementMouseHandler();
  }

  /**
   * Setup mouse handler for volume measurement point picking
   */
  setupVolumeMeasurementMouseHandler() {
    const viewport = document.getElementById('viewport');

    if (this.volumeMeasurementClickHandler) {
      return; // Already set up
    }

    const clickHandler = (event) => {
      if (!this.volumePickingActive) return;

      const rect = viewport.getBoundingClientRect();
      const x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
      const y = -((event.clientY - rect.top) / rect.height) * 2 + 1;

      this.mouse.set(x, y);
      this.raycaster.setFromCamera(this.mouse, this.engine.camera);

      // Get all visible raycastable objects (meshes, points, and groups)
      const meshObjects = this.getRaycastableObjects();

      const intersects = this.raycaster.intersectObjects(meshObjects, true);

      // Filter to only fully visible objects
      const visibleIntersects = intersects.filter(hit => this.isObjectFullyVisible(hit.object));

      if (visibleIntersects.length > 0) {
        const point = visibleIntersects[0].point;
        const pointArray = [point.x, point.y, point.z];
        this.volumePoints.push(pointArray);
        this.updateVolumePointsList();

        // Add persistent temp marker (stays visible until measurement is created)
        this.addPersistentTempMarker(pointArray, this.volumeTempMarkers);

        const btn = document.getElementById('btnAddVolumePoint');
        btn.textContent = 'Add Point';
        btn.style.backgroundColor = '';
        this.volumePickingActive = false;

        Utils.notify(`Volume point ${this.volumePoints.length} added`, 'success');
      }
    };

    viewport.addEventListener('click', clickHandler);
    this.volumeMeasurementClickHandler = true;
  }

  /**
   * Update volume points list UI
   */
  updateVolumePointsList() {
    const listElement = document.getElementById('volumePointsList');
    if (!listElement) return;

    if (this.volumePoints.length === 0) {
      listElement.innerHTML = '<div>No points added (need 4+)</div>';
      return;
    }

    let html = '';
    this.volumePoints.forEach((point, index) => {
      html += `
        <div style="display: flex; justify-content: space-between; padding: 2px; font-size: 10px;">
          <div>P${index + 1}: [${point[0].toFixed(2)}, ${point[1].toFixed(2)}, ${point[2].toFixed(2)}]</div>
          <button class="btn-small" style="padding: 1px 4px; font-size: 9px;" onclick="window.viewer.volumePoints.splice(${index}, 1); window.viewer.updateVolumePointsList();">×</button>
        </div>
      `;
    });

    listElement.innerHTML = html;
  }

  /**
   * Create volume measurement from picked points
   */
  createVolumeMeasurement() {
    if (this.volumePoints.length < 4) {
      Utils.notify('Please pick at least 4 points for volume measurement', 'warning');
      return;
    }

    // Calculate bounding box volume
    const volume = this.calculateBoundingBoxVolume(this.volumePoints);

    // Create measurement ID
    const measurementId = ++this.measurementIdCounter;

    // Visualize the volume
    this.visualizeVolumeMeasurement(this.volumePoints, volume, measurementId);

    // Clear temp markers now that final measurement is created
    this.clearPersistentTempMarkers(this.volumeTempMarkers);

    // Reset points
    this.volumePoints = [];
    this.updateVolumePointsList();

    Utils.notify(`Volume: ${volume.toFixed(6)} m³`, 'success');
  }

  /**
   * Calculate bounding box volume from points
   */
  calculateBoundingBoxVolume(points) {
    if (points.length < 2) return 0;

    // Find min and max in each dimension
    let minX = Infinity, minY = Infinity, minZ = Infinity;
    let maxX = -Infinity, maxY = -Infinity, maxZ = -Infinity;

    points.forEach(p => {
      minX = Math.min(minX, p[0]);
      minY = Math.min(minY, p[1]);
      minZ = Math.min(minZ, p[2]);
      maxX = Math.max(maxX, p[0]);
      maxY = Math.max(maxY, p[1]);
      maxZ = Math.max(maxZ, p[2]);
    });

    const dx = maxX - minX;
    const dy = maxY - minY;
    const dz = maxZ - minZ;

    return dx * dy * dz;
  }

  /**
   * Visualize volume measurement
   */
  visualizeVolumeMeasurement(points, volume, measurementId) {
    const measurementGroup = new THREE.Group();
    measurementGroup.name = `volume-measurement-${measurementId}`;

    // Get color
    const colors = [
      0xFFFF00, 0x00FFFF, 0xFF00FF, 0x00FF00, 0xFF8800, 0x8800FF,
      0xFF0088, 0x00FF88, 0x88FF00, 0x0088FF, 0xFF4444, 0x44FF44,
    ];
    const colorHex = colors[(measurementId - 1) % colors.length];
    const colorString = '#' + colorHex.toString(16).padStart(6, '0');

    // Create bounding box
    let minX = Infinity, minY = Infinity, minZ = Infinity;
    let maxX = -Infinity, maxY = -Infinity, maxZ = -Infinity;

    points.forEach(p => {
      minX = Math.min(minX, p[0]);
      minY = Math.min(minY, p[1]);
      minZ = Math.min(minZ, p[2]);
      maxX = Math.max(maxX, p[0]);
      maxY = Math.max(maxY, p[1]);
      maxZ = Math.max(maxZ, p[2]);
    });

    const boxWidth = maxX - minX;
    const boxHeight = maxY - minY;
    const boxDepth = maxZ - minZ;

    const boxGeometry = new THREE.BoxGeometry(boxWidth, boxHeight, boxDepth);
    const edges = new THREE.EdgesGeometry(boxGeometry);
    const lineMaterial = new THREE.LineBasicMaterial({ color: colorHex, linewidth: 2 });
    const wireframe = new THREE.LineSegments(edges, lineMaterial);

    wireframe.position.set(
      (minX + maxX) / 2,
      (minY + maxY) / 2,
      (minZ + maxZ) / 2
    );

    measurementGroup.add(wireframe);

    // Add semi-transparent box
    const boxMaterial = new THREE.MeshBasicMaterial({
      color: colorHex,
      transparent: true,
      opacity: 0.1,
      side: THREE.DoubleSide
    });
    const box = new THREE.Mesh(boxGeometry, boxMaterial);
    box.position.copy(wireframe.position);
    measurementGroup.add(box);

    // Calculate average box dimension for sphere sizing
    const avgDimension = (boxWidth + boxHeight + boxDepth) / 3;

    // Add spheres at picked points (size proportional to box dimensions)
    const sphereGeometry = new THREE.SphereGeometry(avgDimension * 0.01, 16, 16);
    const sphereMaterial = new THREE.MeshBasicMaterial({ color: colorHex });

    points.forEach(p => {
      const sphere = new THREE.Mesh(sphereGeometry, sphereMaterial);
      sphere.position.set(p[0], p[1], p[2]);
      measurementGroup.add(sphere);
    });

    // Create text label (with unit conversion)
    const convertedVolume = this.convertVolume(volume);
    const volumeUnit = this.getVolumeUnit();
    const decimals = this.measurementUnit === 'um' ? 0 : (this.measurementUnit === 'mm' ? 1 : (this.measurementUnit === 'km' ? 12 : 6));
    const volumeText = `${measurementId}: ${convertedVolume.toFixed(decimals)} ${volumeUnit}`;
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');
    canvas.width = 120;
    canvas.height = 28;

    // Rounded rectangle background (matching distance measurement style)
    const padding = 4;
    const borderRadius = 3;

    context.fillStyle = 'rgba(0, 0, 0, 0.75)';
    context.beginPath();
    context.roundRect(padding, padding, canvas.width - padding * 2, canvas.height - padding * 2, borderRadius);
    context.fill();

    // Subtle border with measurement color
    context.strokeStyle = colorString;
    context.lineWidth = 1;
    context.beginPath();
    context.roundRect(padding, padding, canvas.width - padding * 2, canvas.height - padding * 2, borderRadius);
    context.stroke();

    // Text with nice font and measurement color
    context.font = '500 11px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Arial, sans-serif';
    context.fillStyle = colorString;
    context.textAlign = 'center';
    context.textBaseline = 'middle';
    context.fillText(volumeText, canvas.width / 2, canvas.height / 2);

    const texture = new THREE.CanvasTexture(canvas);
    const spriteMaterial = new THREE.SpriteMaterial({
      map: texture,
      depthTest: false,
      depthWrite: false
    });
    const sprite = new THREE.Sprite(spriteMaterial);

    // Position label at box center (offset proportional to measurement size)
    sprite.position.copy(wireframe.position);
    sprite.position.y += (boxHeight / 2) + (avgDimension * 0.15);

    // Match distance measurement scaling
    const scaleFactor = Math.max(0.04, Math.min(0.12, avgDimension * 0.015));
    sprite.scale.set(scaleFactor, scaleFactor * 0.23, 1);

    measurementGroup.add(sprite);

    // Add to scene
    this.engine.scene.add(measurementGroup);

    // Store measurement
    const measurement = {
      id: measurementId,
      type: 'volume',
      points: points,
      volume: volume,
      visualGroup: measurementGroup,
      color: colorString
    };
    this.measurements.push(measurement);
    this.updateMeasurementsList();
  }

  /**
   * Visualize temporary start point marker (generic function for both tools)
   * @param {Array} point - [x, y, z] position
   * @param {string} toolType - 'measurement' or 'lineProbe'
   */
  visualizeTempStartMarker(point, toolType = 'measurement') {
    // Determine settings based on tool type
    const settings = {
      measurement: {
        color: 0xFFFF00, // Yellow
        markerRef: 'measurementTempMarker',
        callbackRef: 'measurementTempScaleCallback',
        depthTest: true,
        depthWrite: true,
        renderOrder: 0,
        targetPixels: 8
      },
      lineProbe: {
        color: 0x00FF00, // Green
        markerRef: 'lineProbeTempMarker',
        callbackRef: 'lineProbeTempScaleCallback',
        depthTest: false,
        depthWrite: false,
        renderOrder: 999,
        targetPixels: 8
      },
      annotation: {
        color: 0xFF6B35, // Orange
        markerRef: 'annotationTempMarker',
        callbackRef: 'annotationTempScaleCallback',
        depthTest: true,
        depthWrite: true,
        renderOrder: 0,
        targetPixels: 8
      }
    };

    const config = settings[toolType];
    if (!config) {
      console.error('Invalid tool type:', toolType);
      return;
    }

    // Clear any existing temp marker for this tool
    this.clearTempMarker(toolType);

    // Calculate screen-space size based on camera distance
    const pointPosition = new THREE.Vector3(point[0], point[1], point[2]);
    const cameraDistance = this.engine.camera.position.distanceTo(pointPosition);

    const viewport = document.getElementById('viewport');
    const viewportHeight = viewport ? viewport.clientHeight : 800;
    const fov = this.engine.camera.fov * Math.PI / 180; // Convert to radians
    const targetPixels = config.targetPixels; // Desired size in pixels

    const markerSize = (targetPixels / viewportHeight) * cameraDistance * 2 * Math.tan(fov / 2);

    // Create a temporary marker for the start point
    const markerGeometry = new THREE.SphereGeometry(markerSize, 32, 32);
    const markerMaterial = new THREE.MeshBasicMaterial({
      color: config.color,
      transparent: true,
      opacity: 0.8,
      depthTest: config.depthTest,
      depthWrite: config.depthWrite
    });

    const marker = new THREE.Mesh(markerGeometry, markerMaterial);
    marker.position.set(point[0], point[1], point[2]);
    marker.renderOrder = config.renderOrder;

    // Store reference and position for dynamic scaling
    marker.userData.baseSize = markerSize;
    marker.userData.pointPosition = pointPosition;

    this[config.markerRef] = marker;
    this.engine.scene.add(marker);

    // Add animation callback for dynamic scaling
    this[config.callbackRef] = this.engine.addAnimationCallback(() => {
      if (this[config.markerRef]) {
        const distance = this.engine.camera.position.distanceTo(pointPosition);
        const newSize = (targetPixels / viewportHeight) * distance * 2 * Math.tan(fov / 2);
        const scale = newSize / markerSize;
        this[config.markerRef].scale.set(scale, scale, scale);
      }
    });

    console.log(`${toolType} start point visualized with screen-space size`);
  }

  /**
   * Visualize temporary start point marker for measurements
   */
  visualizeMeasurementStartPoint(point) {
    this.visualizeTempStartMarker(point, 'measurement');
  }

  /**
   * Clear temporary marker for a specific tool
   * @param {string} toolType - 'measurement', 'lineProbe', or 'annotation'
   */
  clearTempMarker(toolType) {
    const refs = {
      measurement: {
        markerRef: 'measurementTempMarker',
        callbackRef: 'measurementTempScaleCallback'
      },
      lineProbe: {
        markerRef: 'lineProbeTempMarker',
        callbackRef: 'lineProbeTempScaleCallback'
      },
      annotation: {
        markerRef: 'annotationTempMarker',
        callbackRef: 'annotationTempScaleCallback'
      }
    };

    const config = refs[toolType];
    if (!config) return;

    if (this[config.markerRef]) {
      this.engine.scene.remove(this[config.markerRef]);
      this[config.markerRef] = null;
    }
    if (this[config.callbackRef]) {
      this.engine.removeAnimationCallback(this[config.callbackRef]);
      this[config.callbackRef] = null;
    }
  }

  /**
   * Clear temporary measurement marker
   */
  clearMeasurementTempMarker() {
    this.clearTempMarker('measurement');
  }

  /**
   * Add a persistent temp marker for multi-point measurements (angle, area, volume)
   * These markers stay visible until the measurement is finalized
   */
  addPersistentTempMarker(point, markersArray) {
    // Calculate screen-space size
    const pointPosition = new THREE.Vector3(point[0], point[1], point[2]);
    const cameraDistance = this.engine.camera.position.distanceTo(pointPosition);
    const viewport = document.getElementById('viewport');
    const viewportHeight = viewport ? viewport.clientHeight : 800;
    const fov = this.engine.camera.fov * Math.PI / 180;
    const targetPixels = 8;
    const markerSize = (targetPixels / viewportHeight) * cameraDistance * 2 * Math.tan(fov / 2);

    // Create marker
    const markerGeometry = new THREE.SphereGeometry(markerSize, 32, 32);
    const markerMaterial = new THREE.MeshBasicMaterial({
      color: 0xFFFF00, // Yellow
      transparent: true,
      opacity: 0.8,
      depthTest: true,
      depthWrite: true
    });

    const marker = new THREE.Mesh(markerGeometry, markerMaterial);
    marker.position.set(point[0], point[1], point[2]);
    marker.renderOrder = 0;

    this.engine.scene.add(marker);

    // Add animation callback for dynamic scaling
    const scaleCallback = this.engine.addAnimationCallback(() => {
      if (marker) {
        const distance = this.engine.camera.position.distanceTo(pointPosition);
        const newSize = (targetPixels / viewportHeight) * distance * 2 * Math.tan(fov / 2);
        const scale = newSize / markerSize;
        marker.scale.set(scale, scale, scale);
      }
    });

    // Store marker and callback in array
    markersArray.push({ marker, callback: scaleCallback });
  }

  /**
   * Clear all persistent temp markers from an array
   */
  clearPersistentTempMarkers(markersArray) {
    markersArray.forEach(({ marker, callback }) => {
      if (marker) {
        this.engine.scene.remove(marker);
      }
      if (callback) {
        this.engine.removeAnimationCallback(callback);
      }
    });
    markersArray.length = 0; // Clear array
  }

  /**
   * Calculate a nice round scale bar length based on scene size
   * @param {number} sceneSize - Total scene size (diagonal of bounding box)
   * @returns {Object} Object with { displayValue: number, unit: string }
   */
  calculateNiceScaleBarLength(sceneSize) {
    // Target scale bar to be about 20-30% of scene size
    const targetLength = sceneSize * 0.25;

    // Define nice round numbers for different scales
    const niceNumbers = [1, 2, 5, 10, 20, 50, 100, 200, 500, 1000, 2000, 5000, 10000];

    // Try different units
    const units = [
      { name: 'km', factor: 1000 },
      { name: 'm', factor: 1 },
      { name: 'mm', factor: 0.001 },
      { name: 'μm', factor: 0.000001 }
    ];

    let bestMatch = { displayValue: 1, unit: 'm' };
    let bestDiff = Infinity;

    for (const unit of units) {
      const lengthInUnit = targetLength / unit.factor;

      for (const nice of niceNumbers) {
        const diff = Math.abs(lengthInUnit - nice);
        if (diff < bestDiff) {
          bestDiff = diff;
          bestMatch = {
            displayValue: nice,  // Value in the display unit
            unit: unit.name
          };
        }
      }
    }

    return bestMatch;
  }

  /**
   * Calculate bounding box of all visible mesh objects in the scene
   * @returns {Object|null} Object with min and max THREE.Vector3, or null if no meshes
   */
  calculateSceneBounds() {
    const boundingBox = new THREE.Box3();
    let hasMeshes = false;

    this.engine.objects.forEach((entry, id) => {
      if (entry.object && entry.object.visible && (entry.object.isMesh || entry.object.isPoints || entry.object.type === 'Group')) {
        // Compute bounding box for this object (setFromObject recursively traverses Groups)
        const objectBox = new THREE.Box3().setFromObject(entry.object);
        // Check if bounding box is valid (not empty and has finite values)
        if (!objectBox.isEmpty() &&
          isFinite(objectBox.min.x) && isFinite(objectBox.min.y) && isFinite(objectBox.min.z) &&
          isFinite(objectBox.max.x) && isFinite(objectBox.max.y) && isFinite(objectBox.max.z)) {
          boundingBox.union(objectBox);
          hasMeshes = true;
        }
      }
    });

    if (!hasMeshes) {
      return null;
    }

    return boundingBox;
  }

  /**
   * Convert distance value from meters to selected unit
   */
  convertDistance(distanceInMeters) {
    if (this.measurementUnit === 'um') {
      return distanceInMeters * 1000000; // micrometers
    } else if (this.measurementUnit === 'mm') {
      return distanceInMeters * 1000;
    } else if (this.measurementUnit === 'km') {
      return distanceInMeters / 1000;
    }
    return distanceInMeters; // meters
  }

  /**
   * Convert area value from m² to selected unit
   */
  convertArea(areaInSquareMeters) {
    if (this.measurementUnit === 'um') {
      return areaInSquareMeters * 1000000000000; // µm²
    } else if (this.measurementUnit === 'mm') {
      return areaInSquareMeters * 1000000; // mm²
    } else if (this.measurementUnit === 'km') {
      return areaInSquareMeters / 1000000; // km²
    }
    return areaInSquareMeters; // m²
  }

  /**
   * Convert volume value from m³ to selected unit
   */
  convertVolume(volumeInCubicMeters) {
    if (this.measurementUnit === 'um') {
      return volumeInCubicMeters * 1000000000000000000; // µm³
    } else if (this.measurementUnit === 'mm') {
      return volumeInCubicMeters * 1000000000; // mm³
    } else if (this.measurementUnit === 'km') {
      return volumeInCubicMeters / 1000000000; // km³
    }
    return volumeInCubicMeters; // m³
  }

  /**
   * Get unit symbol for distance
   */
  getDistanceUnit() {
    if (this.measurementUnit === 'um') {
      return 'µm';
    }
    return this.measurementUnit;
  }

  /**
   * Get unit symbol for area
   */
  getAreaUnit() {
    if (this.measurementUnit === 'um') {
      return 'µm²';
    }
    return this.measurementUnit + '²';
  }

  /**
   * Get unit symbol for volume
   */
  getVolumeUnit() {
    if (this.measurementUnit === 'um') {
      return 'µm³';
    }
    return this.measurementUnit + '³';
  }

  /**
   * Update the measurements list UI
   */
  updateMeasurementsList() {
    const listElement = document.getElementById('measurementsList');
    if (!listElement) return;

    if (this.measurements.length === 0) {
      listElement.innerHTML = '<div style="color: #888; font-size: 11px; padding: 5px;">No measurements</div>';
    } else {
      let html = '';
      this.measurements.forEach(m => {
        const bgColor = m.color + '15';  // Add alpha for background

        // Get measurement value text based on type (convert to current units)
        let valueText = '';
        if (m.type === 'distance' || !m.type) {
          const converted = this.convertDistance(m.distance);
          const unit = this.getDistanceUnit();
          const decimals = this.measurementUnit === 'um' ? 1 : (this.measurementUnit === 'mm' ? 2 : (this.measurementUnit === 'km' ? 6 : 3));
          valueText = `${converted.toFixed(decimals)} ${unit}`;
        } else if (m.type === 'angle') {
          valueText = `${m.angle.toFixed(2)}°`;
        } else if (m.type === 'area') {
          const converted = this.convertArea(m.area);
          const unit = this.getAreaUnit();
          const decimals = this.measurementUnit === 'um' ? 0 : (this.measurementUnit === 'mm' ? 1 : (this.measurementUnit === 'km' ? 9 : 4));
          valueText = `${converted.toFixed(decimals)} ${unit}`;
        } else if (m.type === 'volume') {
          const converted = this.convertVolume(m.volume);
          const unit = this.getVolumeUnit();
          const decimals = this.measurementUnit === 'um' ? 0 : (this.measurementUnit === 'mm' ? 1 : (this.measurementUnit === 'km' ? 12 : 6));
          valueText = `${converted.toFixed(decimals)} ${unit}`;
        }

        // Get measurement type label
        const typeLabel = m.type ? m.type.charAt(0).toUpperCase() + m.type.slice(1) : 'Distance';

        html += `
          <div style="display: flex; justify-content: space-between; align-items: center; padding: 5px; background: ${bgColor}; border-left: 3px solid ${m.color}; margin-bottom: 3px; border-radius: 3px;">
            <div style="font-size: 11px;">
              <div style="color: ${m.color}; font-weight: bold;">${typeLabel} ${m.id}</div>
              <div style="color: #ffffff;">${valueText}</div>
            </div>
            <button class="btn-small" style="padding: 2px 6px; font-size: 10px; background: #d9534f;" onclick="window.viewer.clearMeasurement(${m.id})">×</button>
          </div>
        `;
      });

      listElement.innerHTML = html;
    }

    // Notify sync for collaboration (only if not applying state)
    if (this.viewLockManager && !this.viewLockManager.isApplyingState) {
      console.log('[Measurements] Triggering sync, count:', this.measurements.length);
      // Force immediate sync when measurements are cleared (important state change)
      const forceImmediate = this.measurements.length === 0;
      this.viewLockManager.onVisualizationChange(forceImmediate);
    }
  }

  // ============ ANNOTATION METHODS ============

  /**
   * Add annotation from UI inputs
   */
  addAnnotationFromUI() {
    if (!this.annotationRenderer) {
      Utils.notify('Annotation system not initialized', 'error');
      return;
    }

    const annotationType = document.getElementById('annotationType').value;
    const annotationId = `annotation-${++this.annotationIdCounter}`;

    try {
      if (annotationType === 'text') {
        // Text annotation
        const text = document.getElementById('annotationText').value;
        const fontSize = parseInt(document.getElementById('textFontSize').value);
        const hasBackground = document.getElementById('textBackground').checked;

        if (!text) {
          Utils.notify('Please enter text for the annotation', 'warning');
          return;
        }

        if (!this.annotationTextPosition) {
          Utils.notify('Please pick a position on the mesh', 'warning');
          return;
        }

        this.annotationRenderer.addTextAnnotation({
          id: annotationId,
          type: 'text',
          text: text,
          position: this.annotationTextPosition,
          font_size: fontSize,
          color: [1, 1, 1], // White
          background_color: hasBackground ? [0, 0, 0] : null,
          background_opacity: 0.7
        });

        // Clear the picked position and temp marker
        this.annotationTextPosition = null;
        document.getElementById('textPositionDisplay').textContent = 'Not set';
        this.clearTempMarker('annotation');

        Utils.notify('Text annotation added', 'success');

      } else if (annotationType === 'arrow') {
        // Arrow annotation
        const arrowLabel = document.getElementById('arrowLabel').value;
        const arrowSize = parseFloat(document.getElementById('arrowSize').value);

        if (!this.annotationArrowStart) {
          Utils.notify('Please pick a start point on the mesh', 'warning');
          return;
        }

        if (!this.annotationArrowEnd) {
          Utils.notify('Please pick an end point on the mesh', 'warning');
          return;
        }

        this.annotationRenderer.addArrowAnnotation({
          id: annotationId,
          type: 'arrow',
          start_point: this.annotationArrowStart,
          end_point: this.annotationArrowEnd,
          text: arrowLabel || null,
          arrow_size: arrowSize,
          color: [1, 0, 0], // Red
          line_width: 2
        });

        // Clear the picked points and temp marker
        this.annotationArrowStart = null;
        this.annotationArrowEnd = null;
        document.getElementById('arrowStartDisplay').textContent = 'Not set';
        document.getElementById('arrowEndDisplay').textContent = 'Not set';
        this.clearTempMarker('annotation');

        Utils.notify('Arrow annotation added', 'success');

      } else if (annotationType === 'scale_bar') {
        // Scale bar
        const length = parseFloat(document.getElementById('scaleBarLength').value);
        const unit = document.getElementById('scaleBarUnit').value;
        const divisions = parseInt(document.getElementById('scaleBarDivisions').value);

        this.annotationRenderer.addScaleBar({
          id: annotationId,
          type: 'scale_bar',
          length: length,
          unit: unit,
          position: this.annotationScaleBarPosition || null,
          num_divisions: divisions
        });

        // Clear the picked position and temp marker
        if (this.annotationScaleBarPosition) {
          this.annotationScaleBarPosition = null;
          document.getElementById('scaleBarPositionDisplay').textContent = 'Not set (auto-position)';
          this.clearTempMarker('annotation');
        }

        Utils.notify('Scale bar added', 'success');

      } else if (annotationType === 'axes') {
        // Coordinate axes
        let length = parseFloat(document.getElementById('axesLength').value);
        const labelX = document.getElementById('axesLabelX').value;
        const labelY = document.getElementById('axesLabelY').value;
        const labelZ = document.getElementById('axesLabelZ').value;

        // Use picked origin if available, otherwise default to [0, 0, 0]
        const origin = this.annotationAxesOrigin || [0, 0, 0];

        // Calculate scene bounds to set appropriate axes length
        const sceneBounds = this.calculateSceneBounds();
        if (sceneBounds) {
          const sceneSize = sceneBounds.max.distanceTo(sceneBounds.min);
          // Cap the axes length to 50% of scene size to keep them visible but not overwhelming
          const maxLength = sceneSize * 0.5;
          if (length > maxLength) {
            length = maxLength;
            Utils.notify(`Axes length capped to ${length.toFixed(2)} (50% of scene size)`, 'info');
          }
        }

        this.annotationRenderer.addCoordinateAxes({
          id: annotationId,
          type: 'coordinate_axes',
          origin: origin,
          length: length,
          labels: [labelX, labelY, labelZ],
          colors: [[1, 0, 0], [0, 1, 0], [0, 0, 1]] // RGB
        });

        // Clear the picked origin and temp marker
        this.annotationAxesOrigin = null;
        document.getElementById('axesOriginDisplay').textContent = 'Not set (using [0, 0, 0])';
        this.clearTempMarker('annotation');

        Utils.notify('Coordinate axes added', 'success');
      }

      // Update annotations list
      this.updateAnnotationsList();

    } catch (err) {
      console.error('Error adding annotation:', err);
      Utils.notify(`Failed to add annotation: ${err.message}`, 'error');
    }
  }

  /**
   * Update annotations list UI
   */
  updateAnnotationsList() {
    const listElement = document.getElementById('annotationsList');
    const listContainer = document.getElementById('activeAnnotationsList');

    if (!listElement || !this.annotationRenderer) return;

    const annotations = this.annotationRenderer.getAnnotations();

    if (annotations.length === 0) {
      listContainer.style.display = 'none';
    } else {
      listContainer.style.display = 'block';

      let html = '';
      annotations.forEach(annotation => {
        const typeName = annotation.type.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase());
        html += `
          <div style="display: flex; justify-content: space-between; padding: 6px; background: #2a2a2a; margin-bottom: 4px; border-radius: 4px; border-left: 3px solid #007bff;">
            <div style="font-size: 11px;">
              <div style="font-weight: bold; color: #007bff;">${typeName}</div>
              <div style="color: #ccc;">${annotation.id}</div>
            </div>
            <button class="btn-small" style="padding: 2px 6px; font-size: 10px; background: #d9534f;" onclick="window.viewer.removeAnnotation('${annotation.id}')">×</button>
          </div>
        `;
      });

      listElement.innerHTML = html;
    }

    // Notify sync for collaboration (only if not applying state)
    if (this.viewLockManager && !this.viewLockManager.isApplyingState) {
      console.log('[Annotations] Triggering sync, count:', annotations.length);
      // Force immediate sync when annotations are cleared (important state change)
      const forceImmediate = annotations.length === 0;
      this.viewLockManager.onVisualizationChange(forceImmediate);
    }
  }

  /**
   * Remove a specific annotation
   */
  removeAnnotation(id) {
    if (this.annotationRenderer) {
      this.annotationRenderer.removeAnnotation(id);
      this.updateAnnotationsList();
      Utils.notify('Annotation removed', 'success');
    }
  }

  /**
   * Start measurement point picking
   * @param {string} pointType - 'start' or 'end'
   */
  startMeasurementPicking(pointType) {
    this.measurementMode = pointType;
    this.measurementPickingActive = true;

    // Update UI feedback
    const btn = document.getElementById(`btnPickMeasure${pointType === 'start' ? 'Start' : 'End'}`);
    btn.textContent = 'Click on mesh...';
    btn.style.backgroundColor = '#ff6b35';

    // Enable raycaster picking
    this.setupMeasurementMouseHandler();

    console.log(`Measurement mode active: picking ${pointType} point`);
  }

  /**
   * Setup mouse handler for measurement point picking
   */
  setupMeasurementMouseHandler() {
    const viewport = document.getElementById('viewport');

    const clickHandler = (event) => {
      if (!this.measurementPickingActive) return;

      // Calculate mouse position in normalized device coordinates
      const rect = viewport.getBoundingClientRect();
      const x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
      const y = -((event.clientY - rect.top) / rect.height) * 2 + 1;

      this.mouse.set(x, y);

      // Update raycaster
      this.raycaster.setFromCamera(this.mouse, this.engine.camera);

      // Get all visible raycastable objects (meshes, points, and groups)
      const meshObjects = this.getRaycastableObjects();

      // Find intersections
      const intersects = this.raycaster.intersectObjects(meshObjects, true);

      // Filter to only fully visible objects (object and all parents must be visible)
      const visibleIntersects = intersects.filter(hit => this.isObjectFullyVisible(hit.object));

      if (visibleIntersects.length > 0) {
        const point = visibleIntersects[0].point;

        // Store the point
        if (this.measurementMode === 'start') {
          this.measurementStart = [point.x, point.y, point.z];
          document.getElementById('measureStartDisplay').textContent =
            `Start: [${point.x.toFixed(3)}, ${point.y.toFixed(3)}, ${point.z.toFixed(3)}]`;

          // Visualize start point immediately
          this.visualizeMeasurementStartPoint(this.measurementStart);

          // Reset button
          const btn = document.getElementById('btnPickMeasureStart');
          btn.textContent = 'Pick Start Point';
          btn.style.backgroundColor = '';

          // Show remove button
          document.getElementById('btnRemoveMeasureStart').style.display = 'inline-block';
        } else {
          this.measurementEnd = [point.x, point.y, point.z];
          document.getElementById('measureEndDisplay').textContent =
            `End: [${point.x.toFixed(3)}, ${point.y.toFixed(3)}, ${point.z.toFixed(3)}]`;

          // Reset button
          const btn = document.getElementById('btnPickMeasureEnd');
          btn.textContent = 'Pick End Point';
          btn.style.backgroundColor = '';

          // Show remove button
          document.getElementById('btnRemoveMeasureEnd').style.display = 'inline-block';
        }

        // Visualize the measurement line if both points are set
        if (this.measurementStart && this.measurementEnd) {
          // Clear temporary start point marker
          this.clearMeasurementTempMarker();

          const distance = this.visualizeMeasurement(this.measurementStart, this.measurementEnd);

          // Update measurements list
          this.updateMeasurementsList();

          // Reset points for next measurement
          this.measurementStart = null;
          this.measurementEnd = null;
          document.getElementById('measureStartDisplay').textContent = 'Start: Not set';
          document.getElementById('measureEndDisplay').textContent = 'End: Not set';
        }

        // Disable measurement mode
        this.measurementPickingActive = false;
        this.measurementMode = null;

        // Remove event listener
        viewport.removeEventListener('click', clickHandler);

        console.log(`Measurement point set:`, point);
      }
    };

    viewport.addEventListener('click', clickHandler);
  }

  /**
   * Update probe points scale based on camera distance
   */
  updateProbePointsScale() {
    if (!this.probePointMarkers || this.probePointMarkers.length === 0) return;

    const camera = this.engine.camera;
    if (!camera) return;

    this.probePointMarkers.forEach(group => {
      const pointPosition = group.userData.pointPosition;
      const baseSphereRadius = group.userData.baseSphereRadius;

      if (!pointPosition || !baseSphereRadius) return;

      // Calculate distance from camera to point
      const distance = camera.position.distanceTo(pointPosition);
      const referenceDistance = 10;

      // Calculate scale factor (clamped between 0.3x and 3x for smaller range)
      const scaleFactor = Math.max(0.3, Math.min(3.0, Math.pow(distance / referenceDistance, 0.8)));

      // Scale sphere (first child) and sprite (second child)
      if (group.children[0]) {
        group.children[0].scale.set(scaleFactor, scaleFactor, scaleFactor);
      }
      if (group.children[1]) {
        group.children[1].scale.set(scaleFactor * 0.05, scaleFactor * 0.05, 1);
      }
    });
  }

  /**
   * Update line probe scale based on camera distance
   */
  updateLineProbeScale() {
    if (!this.lineProbeHelper || !this.engine.camera) return;

    const group = this.lineProbeHelper;
    const camera = this.engine.camera;
    const lineCenter = group.userData.lineCenter;

    // Calculate distance from camera to line center
    const distance = camera.position.distanceTo(lineCenter);

    // Calculate scale factor (increases with distance)
    // Base scale at reference distance of 10 units
    const referenceDistance = 10;

    // Spheres scale more aggressively - they get bigger faster when camera is far
    // Using power function to make them grow more dramatically with distance
    const sphereScaleFactor = Math.max(0.8, Math.min(10.0, Math.pow(distance / referenceDistance, 1.3)));

    // Update spheres with more aggressive scaling
    const startSphere = group.userData.startSphere;
    const endSphere = group.userData.endSphere;
    if (startSphere) {
      startSphere.scale.set(sphereScaleFactor, sphereScaleFactor, sphereScaleFactor);
    }
    if (endSphere) {
      endSphere.scale.set(sphereScaleFactor, sphereScaleFactor, sphereScaleFactor);
    }
  }

  updateRepresentation() {
    const representation = this.displaySettings.representation;
    console.log('[updateRepresentation] Setting representation to:', representation);

    // Get all mesh objects (both surface and points), including solidify filter
    const meshObjects = ['grid-surface', 'grid-points', 'main-mesh', 'solidify-filter'];

    meshObjects.forEach(objectId => {
      const entry = this.engine.objects.get(objectId);
      if (!entry) {
        console.log('[updateRepresentation] Object not found:', objectId);
        return;
      }

      console.log('[updateRepresentation] Found object:', objectId, 'type:', entry.object.type);
      const obj = entry.object;

      // Helper function to apply representation to a mesh
      const applyToMesh = (mesh) => {
        if (!mesh.isMesh) {
          console.log('[updateRepresentation] Skipping non-mesh:', mesh.type);
          return;
        }

        const material = mesh.material;
        // Check both mesh.name and userData.partName for part identification
        const partName = mesh.userData.partName || mesh.name;
        // For the main mesh object (not individual parts), always consider it visible
        // Individual parts visibility is handled separately via applyMeshPartsVisibility
        const isMainMesh = partName === 'main-mesh' || partName === 'mesh';
        const isVisiblePart = isMainMesh || !partName || this.meshParts.visibleParts.has(partName);
        console.log('[updateRepresentation] Applying to mesh:', mesh.name, 'partName:', partName, 'isMainMesh:', isMainMesh, 'isVisiblePart:', isVisiblePart);

        switch (representation) {
          case 'surface':
            material.wireframe = false;
            material.needsUpdate = true;
            // Respect mesh part visibility settings
            mesh.visible = isVisiblePart;
            break;

          case 'wireframe':
            material.wireframe = true;
            material.needsUpdate = true;
            console.log('[updateRepresentation] Material wireframe set to:', material.wireframe, 'visible:', isVisiblePart);
            // Respect mesh part visibility settings
            mesh.visible = isVisiblePart;
            break;

          case 'points':
            // Hide surface mesh, show points
            mesh.visible = false;
            break;

          case 'surface-with-edges':
            material.wireframe = false;
            this.addEdges(mesh);
            // Respect mesh part visibility settings
            mesh.visible = isVisiblePart;
            break;
        }
      };

      // Recursive helper to apply to all meshes in nested groups
      const applyToGroupRecursive = (group) => {
        group.children.forEach(child => {
          if (child.isMesh) {
            applyToMesh(child);
          } else if (child.type === 'Group') {
            // Recursively apply to nested groups
            applyToGroupRecursive(child);
          }
        });
      };

      // Handle both single mesh and group (including nested groups)
      if (obj.type === 'Group' || obj.isGroup || (obj.children && obj.children.length > 0)) {
        // Apply to all meshes recursively
        console.log('[updateRepresentation] Processing as group with', obj.children?.length || 0, 'children');
        applyToGroupRecursive(obj);
      } else if (obj.isMesh) {
        applyToMesh(obj);
      } else if (obj.isPoints) {
        // Handle point cloud visibility
        obj.visible = (representation === 'points');
      } else {
        console.log('[updateRepresentation] Unknown object type:', obj.type, obj);
      }

      // Hide/show points based on representation
      if (objectId === 'grid-surface' && representation !== 'points') {
        const pointsEntry = this.engine.objects.get('grid-points');
        if (pointsEntry) pointsEntry.object.visible = false;
      } else if (objectId === 'grid-surface' && representation === 'points') {
        const pointsEntry = this.engine.objects.get('grid-points');
        if (pointsEntry) pointsEntry.object.visible = true;
      }
    });

    // Note: Scene is automatically rendered by engine's animate() loop
  }

  addEdges(mesh) {
    // Remove existing edges if any
    const existingEdges = mesh.children.find(child => child.isLineSegments);
    if (existingEdges) {
      mesh.remove(existingEdges);
    }

    // Add new edges
    const edges = new THREE.EdgesGeometry(mesh.geometry);
    const line = new THREE.LineSegments(edges, new THREE.LineBasicMaterial({ color: 0x000000 }));
    mesh.add(line);
  }

  updateOpacity() {
    const opacity = this.displaySettings.opacity;

    // Get all mesh objects
    const meshObjects = ['grid-surface', 'grid-points', 'main-mesh'];

    meshObjects.forEach(objectId => {
      const entry = this.engine.objects.get(objectId);
      if (!entry) return;

      const obj = entry.object;

      // Helper function to apply opacity to a mesh
      const applyToMesh = (mesh) => {
        if (mesh.material) {
          mesh.material.opacity = opacity;
          mesh.material.transparent = opacity < 1.0;
        }
      };

      // Recursive helper to apply to all meshes in nested groups
      const applyToGroupRecursive = (group) => {
        group.children.forEach(child => {
          if (child.isMesh) {
            applyToMesh(child);
          } else if (child.type === 'Group') {
            // Recursively apply to nested groups
            applyToGroupRecursive(child);
          }
        });
      };

      // Handle both single mesh and group (including nested groups)
      if (obj.type === 'Group') {
        applyToGroupRecursive(obj);
      } else {
        applyToMesh(obj);
      }
    });

    // Note: Scene is automatically rendered by engine's animate() loop
  }

  updateLighting() {
    if (!this.engine) return;
    this.engine.setLightLevel(this.displaySettings.lightLevel);
    this.engine.setShadowLevel(this.displaySettings.shadowLevel);
  }

  // ===== CAD Mode Methods =====

  async enterCADMode(documentId) {
    console.log('[Viewer] Entering CAD mode, document:', documentId);

    if (!this.cadViewer) {
      console.error('CADViewer not initialized');
      return;
    }

    // Hide visualization sidebar, show CAD sidebar
    const viewSidebar = document.getElementById('sidebarLeft');
    const cadSidebar = document.getElementById('cadSidebar');

    if (viewSidebar) viewSidebar.style.display = 'none';
    if (cadSidebar) cadSidebar.style.display = 'flex';

    // Add body class for CSS targeting
    document.body.classList.add('cad-mode');
    document.body.classList.remove('view-mode');

    // Enter CAD mode in CADViewer
    await this.cadViewer.enterCADMode();

    // Load CAD document if provided
    if (documentId) {
      await this.cadViewer.loadDocument(documentId);
    }

    Utils.notify('Entered CAD mode', 'success');
  }

  exitCADMode() {
    console.log('[Viewer] Exiting CAD mode');

    if (!this.cadViewer) return;

    // Show visualization sidebar, hide CAD sidebar
    const viewSidebar = document.getElementById('sidebarLeft');
    const cadSidebar = document.getElementById('cadSidebar');

    if (viewSidebar) viewSidebar.style.display = 'flex';
    if (cadSidebar) cadSidebar.style.display = 'none';

    // Update body class
    document.body.classList.remove('cad-mode');
    document.body.classList.add('view-mode');

    // Exit CAD mode in CADViewer
    this.cadViewer.exitCADMode();

    Utils.notify('Returned to View mode', 'info');
  }
}

// ============ VIEW CUBE CLASS ============

class ViewCube {
  constructor(viewer) {
    this.viewer = viewer;
    this.container = document.getElementById('viewCube');
    this.canvas = document.getElementById('viewCubeCanvas');
    this.title = document.querySelector('.view-cube-title');
    this.ctx = this.canvas.getContext('2d');
    this.rotation = { x: -0.5, y: 0.5 }; // Initial rotation

    // Cube rotation state
    this.isRotating = false;
    this.rotateStartPos = { x: 0, y: 0 };

    // Container drag state
    this.isDraggingContainer = false;
    this.dragStartPos = { x: 0, y: 0 };
    this.containerOffset = { x: 0, y: 0 };

    // Define cube vertices (3D coordinates)
    this.cubeSize = 40;
    this.vertices = [
      [-1, -1, -1], [1, -1, -1], [1, 1, -1], [-1, 1, -1], // Back face
      [-1, -1, 1], [1, -1, 1], [1, 1, 1], [-1, 1, 1]      // Front face
    ];

    // Define cube faces (vertex indices and colors)
    this.faces = [
      { indices: [4, 5, 6, 7], color: '#007AFF', label: 'F' }, // Front (Z+)
      { indices: [1, 0, 3, 2], color: '#30D158', label: 'B' }, // Back (Z-)
      { indices: [5, 1, 2, 6], color: '#FF453A', label: 'R' }, // Right (X+)
      { indices: [0, 4, 7, 3], color: '#FF9F0A', label: 'L' }, // Left (X-)
      { indices: [7, 6, 2, 3], color: '#BF5AF2', label: 'T' }, // Top (Y+)
      { indices: [0, 1, 5, 4], color: '#FFD60A', label: 'Bo' }  // Bottom (Y-)
    ];

    this.init();
  }

  init() {
    // Initialize container offset from current CSS position
    const rect = this.container.getBoundingClientRect();
    this.containerOffset = {
      x: rect.left,
      y: rect.top
    };

    // Add mouse event handlers for rotating the cube
    this.canvas.addEventListener('mousedown', (e) => this.onCanvasMouseDown(e));

    // Add mouse event handlers for dragging the container (on title bar)
    this.title.addEventListener('mousedown', (e) => this.onTitleMouseDown(e));

    // Global mouse handlers
    document.addEventListener('mousemove', (e) => this.onDocumentMouseMove(e));
    document.addEventListener('mouseup', () => this.onDocumentMouseUp());

    // Start animation loop
    this.animate();
  }

  onCanvasMouseDown(e) {
    e.stopPropagation();
    this.isRotating = true;
    const rect = this.canvas.getBoundingClientRect();
    this.rotateStartPos = {
      x: e.clientX - rect.left,
      y: e.clientY - rect.top
    };
    this.canvas.style.cursor = 'grabbing';
  }

  onTitleMouseDown(e) {
    e.stopPropagation();
    e.preventDefault();
    this.isDraggingContainer = true;
    this.dragStartPos = {
      x: e.clientX - this.containerOffset.x,
      y: e.clientY - this.containerOffset.y
    };
    this.container.classList.add('dragging');
  }

  onDocumentMouseMove(e) {
    if (this.isRotating) {
      const rect = this.canvas.getBoundingClientRect();
      const currentPos = {
        x: e.clientX - rect.left,
        y: e.clientY - rect.top
      };

      // Calculate rotation delta
      const deltaX = currentPos.x - this.rotateStartPos.x;
      const deltaY = currentPos.y - this.rotateStartPos.y;

      // Update cube rotation (no limits - infinite rotation)
      this.rotation.y += deltaX * 0.01;
      this.rotation.x += deltaY * 0.01;

      // Update camera to match cube rotation
      this.updateCameraFromCube();

      this.rotateStartPos = currentPos;
    }

    if (this.isDraggingContainer) {
      const containerRect = this.container.getBoundingClientRect();
      const containerWidth = containerRect.width;
      const containerHeight = containerRect.height;

      // Calculate new position
      let newX = e.clientX - this.dragStartPos.x;
      let newY = e.clientY - this.dragStartPos.y;

      // Constrain within screen boundaries
      const minX = 0;
      const maxX = window.innerWidth - containerWidth;
      const minY = 0;
      const maxY = window.innerHeight - containerHeight;

      newX = Math.max(minX, Math.min(maxX, newX));
      newY = Math.max(minY, Math.min(maxY, newY));

      this.containerOffset.x = newX;
      this.containerOffset.y = newY;

      // Update container position
      this.container.style.left = `${this.containerOffset.x}px`;
      this.container.style.bottom = 'auto';
      this.container.style.top = `${this.containerOffset.y}px`;
      this.container.style.transform = 'none';
    }
  }

  onDocumentMouseUp() {
    if (this.isRotating) {
      this.isRotating = false;
      this.canvas.style.cursor = '';
    }
    if (this.isDraggingContainer) {
      this.isDraggingContainer = false;
      this.container.classList.remove('dragging');
    }
  }

  updateCameraFromCube() {
    if (!this.viewer || !this.viewer.engine || !this.viewer.engine.camera) return;

    const camera = this.viewer.engine.camera;
    const controls = this.viewer.engine.controls;
    const target = controls.target.clone();
    const distance = camera.position.distanceTo(target);

    // Convert cube rotation to camera position
    // The cube rotation represents where the camera is looking FROM
    const x = distance * Math.sin(this.rotation.y) * Math.cos(this.rotation.x);
    const y = distance * Math.sin(this.rotation.x);
    const z = distance * Math.cos(this.rotation.y) * Math.cos(this.rotation.x);

    const newPosition = new THREE.Vector3(
      target.x + x,
      target.y + y,
      target.z + z
    );

    camera.position.copy(newPosition);
    camera.lookAt(target);
  }

  updateCubeFromCamera() {
    if (!this.viewer || !this.viewer.engine || !this.viewer.engine.camera) return;
    if (this.isRotating || this.isDraggingContainer) return; // Don't update while user is interacting

    const camera = this.viewer.engine.camera;
    const controls = this.viewer.engine.controls;
    const target = controls.target.clone();

    // Get camera direction vector
    const direction = camera.position.clone().sub(target).normalize();

    // Convert camera direction to cube rotation angles
    // Y rotation (horizontal)
    this.rotation.y = Math.atan2(direction.x, direction.z);

    // X rotation (vertical)
    const horizontalDistance = Math.sqrt(direction.x * direction.x + direction.z * direction.z);
    this.rotation.x = Math.atan2(direction.y, horizontalDistance);
  }

  animate() {
    // Update cube rotation from camera when not dragging
    this.updateCubeFromCamera();
    this.render();
    requestAnimationFrame(() => this.animate());
  }

  render() {
    const ctx = this.ctx;
    const w = this.canvas.width;
    const h = this.canvas.height;

    // Clear canvas
    ctx.clearRect(0, 0, w, h);

    const centerX = w / 2;
    const centerY = h / 2;

    // Transform and project vertices
    const projectedVertices = this.vertices.map(v => {
      const rotated = this.rotatePoint(v, this.rotation);
      // Simple perspective projection
      const scale = 200 / (200 + rotated[2] * this.cubeSize);
      return {
        x: centerX + rotated[0] * this.cubeSize * scale,
        y: centerY - rotated[1] * this.cubeSize * scale,
        z: rotated[2]
      };
    });

    // Calculate face depths and sort back to front
    const facesWithDepth = this.faces.map(face => {
      const avgZ = face.indices.reduce((sum, idx) =>
        sum + projectedVertices[idx].z, 0) / face.indices.length;
      return { ...face, avgZ };
    }).sort((a, b) => a.avgZ - b.avgZ);

    // Draw faces
    facesWithDepth.forEach((face, index) => {
      const points = face.indices.map(idx => projectedVertices[idx]);

      // Calculate face normal for backface culling
      const v1 = { x: points[1].x - points[0].x, y: points[1].y - points[0].y };
      const v2 = { x: points[2].x - points[0].x, y: points[2].y - points[0].y };
      const cross = v1.x * v2.y - v1.y * v2.x;

      // Only draw if facing camera
      if (cross > 0) {
        // Calculate lighting based on depth (closer = lighter)
        const depthFactor = (face.avgZ + 1) / 2; // Normalize to 0-1
        const brightness = 0.3 + depthFactor * 0.4; // Range: 0.3 to 0.7

        // Draw face with gradient
        ctx.beginPath();
        ctx.moveTo(points[0].x, points[0].y);
        points.forEach(p => ctx.lineTo(p.x, p.y));
        ctx.closePath();

        // Create gradient for professional look
        const faceCenterX = points.reduce((sum, p) => sum + p.x, 0) / points.length;
        const faceCenterY = points.reduce((sum, p) => sum + p.y, 0) / points.length;

        const gradient = ctx.createRadialGradient(
          faceCenterX, faceCenterY, 0,
          faceCenterX, faceCenterY, this.cubeSize * 0.8
        );

        // Professional blue-gray gradient similar to CoandaView branding
        const baseAlpha = brightness;
        gradient.addColorStop(0, `rgba(100, 130, 160, ${baseAlpha})`);
        gradient.addColorStop(0.5, `rgba(60, 80, 100, ${baseAlpha * 0.9})`);
        gradient.addColorStop(1, `rgba(40, 50, 65, ${baseAlpha * 0.8})`);

        ctx.fillStyle = gradient;
        ctx.fill();

        // Subtle border with slight glow
        ctx.strokeStyle = `rgba(120, 150, 180, ${brightness * 0.7})`;
        ctx.lineWidth = 1.5;
        ctx.stroke();

        // Draw label with subtle glow
        const labelCenterX = points.reduce((sum, p) => sum + p.x, 0) / points.length;
        const labelCenterY = points.reduce((sum, p) => sum + p.y, 0) / points.length;

        // Text shadow for depth
        ctx.shadowColor = 'rgba(0, 0, 0, 0.5)';
        ctx.shadowBlur = 4;
        ctx.shadowOffsetX = 1;
        ctx.shadowOffsetY = 1;

        ctx.fillStyle = '#ffffff';
        ctx.font = 'bold 12px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(face.label, labelCenterX, labelCenterY);

        // Reset shadow
        ctx.shadowColor = 'transparent';
        ctx.shadowBlur = 0;
        ctx.shadowOffsetX = 0;
        ctx.shadowOffsetY = 0;
      }
    });

    // Draw instruction text if not rotating
    if (!this.isRotating && !this.isDraggingContainer) {
      ctx.fillStyle = 'rgba(255, 255, 255, 0.5)';
      ctx.font = '9px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto';
      ctx.textAlign = 'center';
      ctx.fillText('Drag to rotate', w / 2, h - 8);
    }
  }

  rotatePoint(point, rotation) {
    let [x, y, z] = point;

    // Rotate around Y axis
    const cosY = Math.cos(rotation.y);
    const sinY = Math.sin(rotation.y);
    const x1 = x * cosY - z * sinY;
    const z1 = x * sinY + z * cosY;

    // Rotate around X axis
    const cosX = Math.cos(rotation.x);
    const sinX = Math.sin(rotation.x);
    const y2 = y * cosX - z1 * sinX;
    const z2 = y * sinX + z1 * cosX;

    return [x1, y2, z2];
  }
}

// Initialize viewer when DOM is ready, but wait for auth to complete first
document.addEventListener('DOMContentLoaded', async () => {
  // Wait for auth to be initialized first if we have a case parameter
  // This ensures guest tokens are available before loading the case
  const params = new URLSearchParams(window.location.search);
  if (params.has('case') && window.Auth && !window.Auth.isAuthenticated()) {
    console.log('[Viewer] Waiting for auth to complete before initializing...');
    // Wait for auth to complete (max 10 seconds)
    let waited = 0;
    while (!window.Auth.getToken() && waited < 10000) {
      await new Promise(resolve => setTimeout(resolve, 100));
      waited += 100;
    }
    console.log('[Viewer] Auth ready, initializing viewer...');
  }

  // Initialize all colormap select elements with centralized options
  if (window.Colormap && window.Colormap.initializeAllSelects) {
    window.Colormap.initializeAllSelects();
  }

  // Check if we're loading via share token - if so, wait for auth to complete
  const urlParams = new URLSearchParams(window.location.search);
  const shareToken = urlParams.get('token');

  if (shareToken) {
    // Share token detected - wait for index.html auth to complete before initializing
    console.log('[Viewer] Share token detected, waiting for auth completion...');
    // index.html will set window.guestAuthComplete = true when ready
    const waitForAuth = setInterval(() => {
      if (window.Auth && (window.Auth.guestToken || window.Auth.token)) {
        clearInterval(waitForAuth);
        console.log('[Viewer] Auth complete, initializing viewer');
        window.viewer = new CoandaViewer();
      }
    }, 100);
    // Timeout after 5 seconds
    setTimeout(() => {
      if (!window.viewer) {
        clearInterval(waitForAuth);
        console.error('[Viewer] Auth timeout, initializing anyway');
        window.viewer = new CoandaViewer();
      }
    }, 5000);
  } else {
    // Normal initialization
    window.viewer = new CoandaViewer();
  }

  // Initialize view cube after viewer is ready
  setTimeout(() => {
    if (window.viewer && window.viewer.engine) {
      window.viewCube = new ViewCube(window.viewer);
    }
  }, 500);

  // Camera control toggle button
  const btnToggleCameraControl = document.getElementById('btnToggleCameraControl');
  const viewCubeEl = document.getElementById('viewCube');

  if (btnToggleCameraControl && viewCubeEl) {
    btnToggleCameraControl.addEventListener('click', () => {
      const isVisible = viewCubeEl.classList.toggle('visible');
      btnToggleCameraControl.classList.toggle('active', isVisible);
    });
  }

  // Mobile menu toggle - works for both visualization and CAD sidebars
  const mobileMenuToggle = document.getElementById('mobileMenuToggle');
  const mobileOverlay = document.getElementById('mobileOverlay');
  const sidebarLeft = document.getElementById('sidebarLeft');
  const cadSidebar = document.getElementById('cadSidebar');

  function getActiveSidebar() {
    // Return whichever sidebar is currently visible
    if (cadSidebar && cadSidebar.style.display !== 'none') {
      return cadSidebar;
    }
    return sidebarLeft;
  }

  function closeMobileMenu() {
    if (sidebarLeft) sidebarLeft.classList.remove('mobile-open');
    if (cadSidebar) cadSidebar.classList.remove('mobile-open');
    if (mobileOverlay) mobileOverlay.classList.remove('active');
    document.body.style.overflow = '';
  }

  function openMobileMenu() {
    const activeSidebar = getActiveSidebar();
    if (activeSidebar) activeSidebar.classList.add('mobile-open');
    if (mobileOverlay) mobileOverlay.classList.add('active');
    document.body.style.overflow = 'hidden';
  }

  if (mobileMenuToggle) {
    mobileMenuToggle.addEventListener('click', () => {
      const activeSidebar = getActiveSidebar();
      const isOpen = activeSidebar && activeSidebar.classList.contains('mobile-open');
      if (isOpen) {
        closeMobileMenu();
      } else {
        openMobileMenu();
      }
    });
  }

  if (mobileOverlay) {
    mobileOverlay.addEventListener('click', closeMobileMenu);
  }

  // Close mobile menu on window resize (if switching to desktop)
  window.addEventListener('resize', () => {
    if (window.innerWidth > 768) {
      closeMobileMenu();
      closeMobileActionsDropdown();
    }
  });

  // Mobile actions dropdown
  const mobileMoreBtn = document.getElementById('mobileMoreBtn');
  const mobileActionsDropdown = document.getElementById('mobileActionsDropdown');

  function closeMobileActionsDropdown() {
    if (mobileActionsDropdown) mobileActionsDropdown.classList.remove('open');
  }

  if (mobileMoreBtn && mobileActionsDropdown) {
    mobileMoreBtn.addEventListener('click', (e) => {
      e.stopPropagation();
      mobileActionsDropdown.classList.toggle('open');
    });

    // Handle action clicks
    mobileActionsDropdown.querySelectorAll('.mobile-action-item').forEach(item => {
      item.addEventListener('click', () => {
        const action = item.dataset.action;
        closeMobileActionsDropdown();

        // Trigger the corresponding button
        switch (action) {
          case 'download':
            document.getElementById('btnDownload')?.click();
            break;
          case 'share':
            document.getElementById('btnShare')?.click();
            break;
          case 'screenshot':
            document.getElementById('btnSaveView')?.click();
            break;
          case 'record':
            document.getElementById('btnRecordVideo')?.click();
            break;
          case 'rotate':
            document.getElementById('btnRotateView')?.click();
            break;
          case 'camera':
            document.getElementById('btnToggleCameraControl')?.click();
            break;
          case 'settings':
            document.getElementById('btnSettings')?.click();
            break;
        }
      });
    });
  }

  // Close dropdown when clicking outside
  document.addEventListener('click', (e) => {
    if (mobileActionsDropdown && !mobileActionsDropdown.contains(e.target) && e.target !== mobileMoreBtn) {
      closeMobileActionsDropdown();
    }
  });
});
